import { useState, useEffect, useRef, Fragment } from "react";
import { openInAppCamera } from "./InAppCamera";
import { openPdfViewer, printPdfPages } from "./PdfViewer";
import { mergeExtractions } from "./lib/extraction";
// Pricing data + calculation live in one shared module so the displayed total
// and the Stripe charge always use identical rules (see src/lib/pricing.js).
import { ZIP_DATA, PRICING, DOC_SURCHARGES, ADDONS, calcPrice, orderTotal, packetPageFee, addonAllowedForTier, addonsForTier, hasLiveUpdates, normalizeAddon, requiresReview, REVIEW_REQUIRED_MESSAGE, tierAllowedForExParte, tierRestrictionMessage, tierUpgradeQuote, upgradeTargets, BUSINESS_PHONE_DISPLAY, BUSINESS_PHONE_TEL } from "./lib/pricing";
import * as UPLOAD_LIMITS from "./lib/uploadLimits";
import * as SUBJECT_PHOTO from "./lib/subjectPhoto";
import { chargeLines } from "./lib/chargeLines";
import { showNotice, askConfirm, askText } from "./dialogs";
import { parseCaseHeader, parseSummonsItems } from "./lib/caseHeader";

import ClientAccount from "./ClientAccount";
import { useAuth } from "./lib/useAuth";
// An OAuth return (?code=…) has to reach the Client Portal, not the order form.
import { readAuthCallback } from "./lib/authCallback";
import { evaluateDiligence, warningMessage } from "./lib/diligence";

// How a Proof form was chosen, in words. Mirrors lib/templateResolver.
const TEMPLATE_SOURCE_LABELS = {
  order_override: "Order override", client_override: "Client override", court_override: "Court rule",
  county_requirement: "County requirement", client_default: "Client default",
  authority_match: "Document classification", generic_fallback: "No rule matched",
};
// Proof/declaration forms an admin may choose as an override. Proof kinds only —
// an acknowledgment or a notice can never stand in for a proof of service.
const PROOF_FORM_CHOICES = [
  { code: "POS-010", title: "Proof of Service of Summons" },
  { code: "POS-030", title: "Proof of Service by First-Class Mail—Civil" },
  { code: "POS-040", title: "Proof of Service—Civil" },
  { code: "SC-104", title: "Proof of Service (Small Claims)" },
  { code: "SC-104A", title: "Proof of Mailing (Small Claims)" },
  { code: "FL-115", title: "Proof of Service of Summons (Family Law)" },
  { code: "FL-330", title: "Proof of Personal Service" },
  { code: "FL-335", title: "Proof of Service by Mail" },
];
import { isServed, isServedOutcome, diligenceDue } from "./lib/serviceState";
import { presentLabel, attemptNarrative, isBusiness, businessServiceMissing } from "./lib/attemptWording";
import { SUBJECT_AGE_BANDS, subjectAgeLabel, defaultsForKind } from "./lib/serviceIntake";

// Court options/grouping and order validation live in shared pure modules so the
// form and the test suite agree (see src/lib/courts.js, src/lib/orderValidation.js).
import { COURT_JURISDICTIONS, groupCourts } from "./lib/courts";
import { mapsUrl, nowLocalDateTime, cardOrder, detailOrder } from "./lib/fieldService";
import { exifFromFile } from "./lib/exif";
import * as COURT_MATCH from "./lib/courtMatch";
import { validateOrderForm, FIELD_ORDER } from "./lib/orderValidation";
import { SERVICE_AGREEMENT, SERVICE_AGREEMENT_URL } from "./lib/serviceAgreement";
import { sumNetRevenue, sumRefunded, sumGrossPaid } from "./lib/earnings";
import { isGooglePlacesConfigured, googlePlacesStatus, loadGooglePlaces, placeToAddress } from "./lib/googlePlaces";
import { mapNoteFormToAttempt, buildAttemptTimeline } from "./lib/serviceCaptureUi";
import AttemptWizard from "./AttemptWizard";
import DatePick from "./DatePick";
import ServerApp from "./ServerApp";

// PRICING, MILEAGE_RATE, DOC_SURCHARGES, and calcPrice now come from
// ./lib/pricing (imported above) so the frontend and the checkout function
// share one pricing source.

// ── STYLES ──────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&family=Inter:wght@300;400;500;600&family=Dancing+Script:wght@700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --navy: #0B1628;
    --navy-mid: #162040;
    --gold: #C8963E;
    --gold-light: #E8B55A;
    --cream: #F7F4EE;
    --gray: #8A8FA8;
    --light: #EEF0F5;
    --white: #FFFFFF;
    --green: #2A7A4F;
    --red: #C0392B;
    --amber: #D97706;
    --blue: #1D4ED8;
    --orange: #E8620A;
    --orange-hover: #FF7520;
  }

  body { font-family: 'Inter', sans-serif; background: var(--cream); color: var(--navy); }

  .app { min-height: 100vh; display: flex; flex-direction: column; }

  /* NAV */
  nav {
    background: var(--navy);
    padding: 0 2rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 64px;
    position: sticky;
    top: 0;
    z-index: 100;
    border-bottom: 2px solid var(--gold);
  }
  .nav-logo {
    font-family: 'Montserrat', sans-serif;
    font-weight: 800;
    font-size: 1rem;
    color: var(--white);
    letter-spacing: 0.04em;
    white-space: nowrap;
  }
  .nav-logo span { color: var(--gold); }
  .nav-links { display: flex; gap: 0.5rem; }
  .nav-btn {
    background: none;
    border: none;
    color: var(--gray);
    font-family: 'Inter', sans-serif;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    padding: 0.4rem 0.8rem;
    border-radius: 4px;
    transition: all 0.15s;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }
  .nav-btn:hover { color: var(--white); background: rgba(255,255,255,0.08); }
  .nav-btn.active { color: var(--gold); }
  .nav-cta {
    background: var(--orange);
    color: var(--white);
    font-family: 'Inter', sans-serif;
    font-size: 0.8rem;
    font-weight: 700;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    transition: background 0.15s;
    box-shadow: 0 2px 8px rgba(232,98,10,0.3);
  }
  .nav-cta:hover { background: var(--orange-hover); }

  /* HERO */
  .hero {
    background: var(--navy);
    color: var(--white);
    padding: 3rem 2rem 3rem;
    text-align: center;
    position: relative;
    overflow: visible;
  }
  .hero::before {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      90deg,
      transparent,
      transparent 60px,
      rgba(200,150,62,0.04) 60px,
      rgba(200,150,62,0.04) 61px
    );
  }
  .hero-badge {
    display: inline-block;
    background: rgba(200,150,62,0.15);
    border: 1px solid var(--gold);
    color: var(--gold);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    padding: 0.35rem 1rem;
    border-radius: 2px;
    margin-bottom: 1.5rem;
  }
  .hero h1 {
    font-family: 'Montserrat', sans-serif;
    font-size: clamp(2rem, 4.5vw, 4rem);
    font-weight: 800;
    line-height: 1.15;
    margin-bottom: 1rem;
    position: relative;
  }
  .hero h1 em { color: var(--gold); font-style: normal; }
  .hero p {
    color: rgba(255,255,255,0.65);
    font-size: 1rem;
    max-width: 700px;
    margin: 0 auto 2.5rem;
    line-height: 1.6;
  }

  /* ZIP CHECKER */
  .zip-checker { background: var(--white); border-radius: 8px; padding: 0.45rem;
    max-width: 560px;
    width: calc(100% - 2rem);
    margin: 0 auto;
    box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        position: relative;
        overflow: hidden;
      }
  .zip-checker h3 { font-family: 'Montserrat', sans-serif; font-size: 1rem; font-weight: 700; color: var(--navy); margin-bottom: 0.15rem; letter-spacing: 0.02em; line-height: 1.15; overflow: visible;
  }
  .zip-input-row { display: flex; gap: 0.5rem; }
  .zip-input { flex: 1; border: 2px solid var(--light); border-radius: 4px; padding: 0.4rem 1rem; font-size: 1.1rem; font-family: 'Montserrat', sans-serif; font-weight: 700; letter-spacing: 0.1em; color: var(--navy); transition: border-color 0.15s; outline: none;
  }
  .zip-input:focus { border-color: var(--gold); }
  .zip-submit { background: var(--orange); color: var(--white); border: none; border-radius: 4px; padding: 0.5rem 1.5rem; font-family: 'Inter', sans-serif; font-weight: 700; font-size: 0.85rem; cursor: pointer; letter-spacing: 0.05em; text-transform: uppercase; transition: background 0.15s; box-shadow: 0 4px 14px rgba(232,98,10,0.35);
  }
  .zip-submit:hover { background: var(--orange-hover); box-shadow: 0 6px 20px rgba(232,98,10,0.5); }

  .zip-result { margin-top: 0.3rem; padding: 0.4rem; border-radius: 4px; font-size: 0.9rem;
  }
  .zip-result.found { background: #EFF8F3; border-left: 3px solid var(--green); }
  .zip-result.not-found { background: #FEF2F2; border-left: 3px solid var(--red); }
  .zip-result .city-name { font-weight: 700; font-size: 1rem; color: var(--navy); }
  .zip-result .price-display { font-family: 'Montserrat', sans-serif; font-size: 1.5rem; font-weight: 800; color: var(--gold); }
  .zip-result .price-note { font-size: 0.75rem; color: var(--gray); margin-top: 0.25rem; }

  /* SECTIONS */
  .section { padding: 4rem 2rem; max-width: 1400px; margin: 0 auto; }
  .section-title {
    font-family: 'Montserrat', sans-serif;
    font-size: clamp(1.4rem, 3vw, 1.8rem);
    font-weight: 800;
    color: var(--navy);
    margin-bottom: 0.5rem;
  }
  .section-sub { color: var(--gray); font-size: 0.9rem; margin-bottom: 2.5rem; }

  /* HOW IT WORKS */
  .steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.5rem; }
  @media (max-width: 800px) { .steps { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 500px) { .steps { grid-template-columns: 1fr; } }
  .step {
    background: var(--white);
    border-radius: 8px;
    padding: 1.5rem;
    border-top: 3px solid var(--gold);
  }
  .step-num {
    font-family: 'Montserrat', sans-serif;
    font-size: clamp(2rem, 3.5vw, 3.5rem);
    font-weight: 800;
    color: var(--gold);
    opacity: 0.4;
    line-height: 1.3;
    margin-bottom: 0.5rem;
  }
  .step h4 { font-weight: 700; margin-bottom: 0.4rem; font-size: clamp(0.95rem, 1.5vw, 1.15rem); line-height: 1.4; }
  .step p { color: var(--gray); font-size: 0.82rem; line-height: 1.5; }

  /* PRICING TABLE */
  .price-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }
  @media (max-width: 600px) { .price-grid { grid-template-columns: 1fr; } }
  .price-card {
    background: var(--white);
    border-radius: 8px;
    padding: 1.5rem;
    border: 2px solid var(--light);
  }
  .price-card.featured { border-color: var(--gold); }
  .price-card h4 {
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 0.95rem;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .price-card .amount {
    font-family: 'Montserrat', sans-serif;
    font-size: 2rem;
    font-weight: 800;
    color: var(--gold);
    line-height: 1.4;
    overflow: visible;
    padding-bottom: 0.1em;
  }
  .price-card .area { color: var(--gray); font-size: 0.8rem; margin-top: 0.25rem; }
  .badge { background: var(--gold); color: var(--navy); font-size: 0.75rem; font-weight: 700; padding: 0.15rem 0.5rem; border-radius: 2px; letter-spacing: 0.06em; text-transform: uppercase; }

  /* PORTAL */
  .portal { background: var(--white); border-radius: 12px; border: 2px solid var(--light); overflow: hidden; }
  .portal-tabs { display: flex; border-bottom: 2px solid var(--light); background: var(--cream); }
  .portal-tab {
    flex: 1;
    background: none;
    border: none;
    padding: 1rem;
    font-family: 'Inter', sans-serif;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--gray);
    cursor: pointer;
    transition: all 0.15s;
    border-bottom: 3px solid transparent;
  }
  .portal-tab.active { color: var(--navy); border-bottom-color: var(--gold); background: var(--white); }
  .portal-body { padding: 2rem; }

  /* FORM */
  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  @media (max-width: 600px) { .form-grid { grid-template-columns: 1fr; } }
  .form-full { grid-column: 1 / -1; } .field input, .field select, .field textarea { width: 100%; box-sizing: border-box; max-width: 100%; }
  .field { display: flex; flex-direction: column; gap: 0.4rem; min-width: 0; }
  .field label { font-size: 0.75rem; font-weight: 600; color: var(--navy); letter-spacing: 0.04em; text-transform: uppercase; }
  .field input, .field select, .field textarea {
    border: 2px solid var(--light);
    border-radius: 4px;
    padding: 0.65rem 0.9rem;
    font-family: 'Inter', sans-serif;
    font-size: 0.9rem;
    color: var(--navy);
    outline: none;
    transition: border-color 0.15s;
    background: var(--white);
  }
  .field input:focus, .field select:focus, .field textarea:focus { border-color: var(--gold); }
  .field textarea { resize: vertical; min-height: 80px; }
  /* Checkboxes/radios sit beside their wording — never stretched to full width
     (the generic .field input rule above made them float far from the label). */
  .field input[type="checkbox"], .field input[type="radio"] { width: auto; flex: 0 0 auto; padding: 0; margin: 0; }

  .upload-zone {
    border: 2px dashed var(--light);
    border-radius: 4px;
    padding: 2rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.15s;
    background: var(--cream);
  }
  .upload-zone:hover { border-color: var(--gold); background: #FFFBF4; }
  .upload-zone p { color: var(--gray); font-size: 0.85rem; }
  .upload-zone strong { color: var(--navy); display: block; margin-bottom: 0.25rem; }

  .btn-primary {
    background: var(--orange);
    color: var(--white);
    border: none;
    border-radius: 4px;
    padding: 0.75rem 2rem;
    font-family: 'Inter', sans-serif;
    font-weight: 700;
    font-size: 0.9rem;
    cursor: pointer;
    letter-spacing: 0.03em;
    transition: background 0.15s;
    box-shadow: 0 4px 14px rgba(232,98,10,0.35);
  }
  .btn-primary:hover { background: var(--orange-hover); box-shadow: 0 6px 20px rgba(232,98,10,0.5); }
  .btn-secondary {
    background: var(--light);
    color: var(--navy);
    border: none;
    border-radius: 4px;
    padding: 0.6rem 1.2rem;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 0.8rem;
    cursor: pointer;
    letter-spacing: 0.03em;
    transition: all 0.15s;
  }
  .btn-secondary:hover { background: var(--gold); color: var(--navy); }
  .btn-gold {
    background: var(--gold);
    color: var(--navy);
    border: none;
    border-radius: 4px;
    padding: 0.75rem 1.5rem;
    font-family: 'Inter', sans-serif;
    font-weight: 700;
    font-size: 0.85rem;
    cursor: pointer;
    letter-spacing: 0.03em;
    transition: background 0.15s;
  }
  .btn-gold:hover { background: var(--gold-light); }

  /* STATUS CHIPS */
  .status-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 0.25rem 0.6rem;
    border-radius: 2px;
  }
  .status-pending_review { background: #EFF6FF; color: var(--blue); }
  .status-accepted { background: #EDE9FE; color: #6D28D9; }
  .status-awaiting_client { background: #FEF9EE; color: var(--amber); }
  .status-completed { background: #EFF8F3; color: var(--green); }
  .status-cancelled { background: #FEF2F2; color: var(--red); }
  .status-refund_issued { background: #F0FFF6; color: var(--green); }

  /* JOB CARDS */
  .job-card {
    border: 2px solid var(--light);
    border-radius: 8px;
    padding: 1.2rem;
    margin-bottom: 1rem;
    background: var(--cream);
    cursor: pointer;
    transition: border-color 0.15s;
  }
  .job-card:hover { border-color: var(--gold); }
  .job-card.selected { border-color: var(--navy); background: var(--white); }
  .job-card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; }
  .job-id { font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 0.85rem; color: var(--navy); line-height: 1.4; overflow: visible; }
  .job-meta { font-size: 0.78rem; color: var(--gray); line-height: 1.6; }

  /* PAYMENT OPTIONS */
  .payment-opts { display: flex; gap: 0.75rem; flex-wrap: wrap; margin-top: 1rem; }
  .pay-btn {
    flex: 1;
    min-width: 120px;
    padding: 0.75rem;
    border-radius: 6px;
    border: 2px solid var(--light);
    background: var(--white);
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.3rem;
    transition: border-color 0.15s;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--navy);
  }
  .pay-btn:hover { border-color: var(--gold); }
  .pay-btn .pay-icon { font-size: 1.5rem; }

  /* MESSAGING */
  .message-thread { background: var(--cream); border-radius: 6px; padding: 1rem; max-height: 280px; overflow-y: auto; margin-bottom: 1rem; }
  .msg { margin-bottom: 0.75rem; }
  .msg.from-client { text-align: left; }
  .msg.from-server { text-align: right; }
  .msg-bubble {
    display: inline-block;
    padding: 0.6rem 0.9rem;
    border-radius: 8px;
    font-size: 0.85rem;
    max-width: 80%;
    line-height: 1.5;
  }
  .from-client .msg-bubble { background: var(--white); border: 1px solid var(--light); }
  .from-server .msg-bubble { background: var(--navy); color: var(--white); }
  .msg-time { font-size: 0.75rem; color: var(--gray); margin-top: 0.2rem; }
  .msg-input-row { display: flex; gap: 0.5rem; }
  .msg-input {
    flex: 1;
    min-width: 0; /* flex items default to min-width:auto — without this the input's intrinsic width forces the row (and the admin grid column) wider than its container, which .portal's overflow:hidden then clips */
    border: 2px solid var(--light);
    border-radius: 4px;
    padding: 0.65rem 0.9rem;
    font-family: 'Inter', sans-serif;
    font-size: 0.85rem;
    outline: none;
  }
  .msg-input:focus { border-color: var(--gold); }

  /* ADMIN */
  /* minmax(0, …) lets both columns shrink below their content's intrinsic
     minimum (grid tracks default to min-width:auto, same trap as flex), so wide
     content can never push the grid past the viewport into .portal's
     overflow:hidden clipping. Collapse to one column at 900px — the two-column
     layout physically needs ~824px+ (360px left + right-column minimum + gap),
     so the old 700px breakpoint left a 700–824px band that clipped with no
     scrollbar. */
  /* PDF viewer docked on the right of the admin screen; full screen on a phone. */
  /* Compact buttons for action rows that should fit on one line. */
  .btn-row-compact { display: flex; flex-wrap: wrap; gap: 0.4rem; align-items: center; }
  .btn-row-compact .btn-primary, .btn-row-compact .btn-secondary { padding: 0.4rem 0.75rem; font-size: 0.78rem; white-space: nowrap; width: auto; }
  .admin-grid { display: grid; grid-template-columns: minmax(0, 360px) minmax(0, 1fr); gap: 1.5rem; }
  @media (max-width: 900px) { .admin-grid { grid-template-columns: 1fr; } }
  /* Wide screens: Client Messages get their own column to the RIGHT of the
     order details (sticky), so they are seen first. The admin dashboard widens
     past its old 1100px cap so that column has room. Narrower screens keep them
     as the first block of the order. */
  @media (min-width: 1100px) {
    .job-detail { display: grid; grid-template-columns: minmax(0, 1fr) 320px; column-gap: 1.25rem; row-gap: 0; align-items: start; align-content: start; }
    .job-detail > * { grid-column: 1; }
    .job-detail > .job-messages { grid-column: 2; grid-row: 1 / span 400; position: sticky; top: 1rem; margin-bottom: 0 !important; max-height: calc(100vh - 2rem); overflow-y: auto; }
  }
  /* Mobile field service: the list wrapper dissolves into the grid so each order's
     details render directly under ITS OWN card (CSS order), never at the bottom of
     the whole list. Desktop keeps the two-column list + details layout. */
  @media (max-width: 900px) {
    .admin-list { display: contents; }
    .admin-grid { row-gap: 0; }
    .admin-empty-detail { display: none !important; }
    .job-detail { border: 2px solid var(--navy); border-top: none; border-radius: 0 0 8px 8px; padding: 0.75rem; margin: 0 0 1rem; background: var(--white); min-width: 0; }
    .job-card.selected { border-radius: 8px 8px 0 0; margin-bottom: 0; }
    .note-form select, .note-form input, .note-form textarea { font-size: 16px; min-height: 44px; }
    .note-form .serve-grid, .note-form .serve-grid-3 { grid-template-columns: 1fr; }
    .job-detail img { max-width: 100%; }
  }
  .card-person { font-family: 'Montserrat', sans-serif; font-weight: 800; font-size: 1.05rem; color: var(--navy); line-height: 1.25; }
  .card-address { display: inline-block; margin-top: 0.2rem; font-weight: 600; font-size: 0.9rem; color: var(--blue); text-decoration: underline; word-break: break-word; }
  .card-tags { display: flex; flex-wrap: wrap; gap: 0.35rem; align-items: center; margin-top: 0.4rem; }
  .card-jobno { font-size: 0.75rem; color: var(--gray); margin-top: 0.35rem; }
  .chip-exparte { background: #FEF2F2; color: var(--red); font-weight: 800; }
  .btn-log-attempt { width: 100%; min-height: 48px; font-size: 1rem; margin-top: 0.75rem; }
  .photo-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; margin-top: 0.5rem; }
  .quick-outcomes { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 0.4rem; }
  .quick-outcomes button { text-transform: none; letter-spacing: 0; padding: 0.5rem; }
  .quick-block { margin-bottom: 0.9rem; }
  .wiz { background: #fff; border: 1px solid var(--border, #e5e7eb); border-radius: 10px; overflow: hidden; }
  .wiz-top { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; background: var(--navy); color: #fff; padding: 0.55rem 0.6rem; }
  .wiz-top button { background: none; border: none; color: #fff; font-weight: 700; font-size: 0.95rem; cursor: pointer; min-height: 40px; padding: 0 0.4rem; }
  .wiz-who { flex: 1; text-align: center; font-weight: 700; font-size: 0.95rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .wiz-title { background: #eef2f7; color: var(--navy); font-weight: 700; padding: 0.7rem 0.9rem; font-size: 1rem; }
  .wiz-body { padding: 0.5rem 0.6rem 0.9rem; }
  .wiz-row { display: flex; width: 100%; align-items: center; justify-content: space-between; gap: 0.75rem; text-align: left; background: #fff; border: none; border-bottom: 1px solid #eceff3; padding: 0.95rem 0.5rem; font-size: 1.02rem; color: #111827; cursor: pointer; text-transform: none; letter-spacing: 0; font-family: inherit; }
  .wiz-row.on { background: #f1f5fb; font-weight: 700; }
  .wiz-radio, .wiz-check { flex: 0 0 26px; height: 26px; border: 2px solid #9ca3af; display: flex; align-items: center; justify-content: center; font-size: 0.8rem; color: var(--navy); }
  .wiz-radio { border-radius: 50%; }
  .wiz-check { border-radius: 5px; }
  .wiz-row.on .wiz-radio, .wiz-row.on .wiz-check { border-color: var(--navy); }
  .wiz-next { width: 100%; min-height: 52px; margin-top: 0.9rem; font-size: 1rem; text-transform: none; letter-spacing: 0; }
  .wiz-fields { margin-top: 0.6rem; }
  .wiz-hint { font-size: 0.8rem; color: var(--gray); margin: 0.3rem 0.4rem 0.2rem; }
  .wiz-sub { font-size: 0.8rem; font-weight: 700; color: var(--gray); text-transform: uppercase; letter-spacing: 0.05em; margin: 0.4rem 0.2rem; }
  .wiz-vary-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.4rem; }
  .wiz-vary { position: relative; border: 1px solid #d1d5db; border-radius: 8px; padding: 0.55rem 0.2rem; text-align: center; font-size: 0.78rem; font-weight: 700; color: #374151; display: flex; flex-direction: column; align-items: center; gap: 0.2rem; }
  .wiz-vary.on { border-color: var(--green); background: #EFF8F3; }
  .wiz-vary-i { font-size: 1.3rem; }
  .wiz-vary-ok { position: absolute; top: -7px; left: -7px; background: var(--green); color: #fff; border-radius: 50%; width: 20px; height: 20px; font-size: 0.75rem; display: flex; align-items: center; justify-content: center; }
  .wiz-start { display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem; margin-top: 1rem; }
  .wiz-big { min-height: 64px; border: none; border-radius: 10px; font-size: 1.15rem; font-weight: 800; cursor: pointer; font-family: inherit; }
  .wiz-served { background: #6fbf73; color: #14532d; }
  .wiz-attempted { background: #fbc531; color: #4a3500; }
  .wiz-decl { width: 100%; min-height: max(320px, calc(100vh - 300px)); min-height: max(320px, calc(100dvh - 300px)); font-size: 1.05rem; line-height: 1.5; padding: 0.8rem; box-sizing: border-box; resize: vertical; }
  .wiz-link { background: none; border: none; color: var(--blue); text-decoration: underline; cursor: pointer; padding: 0.4rem 0; font-size: 0.85rem; }
  @media (max-width: 700px) {
    .wiz { position: fixed; inset: 0; z-index: 2000; border-radius: 0; border: none; overflow-y: auto; -webkit-overflow-scrolling: touch; padding-bottom: env(safe-area-inset-bottom); }
    .wiz-top { position: sticky; top: 0; z-index: 1; padding-top: calc(0.55rem + env(safe-area-inset-top)); }
  }
  @media (max-width: 600px) { .note-form { padding: 0.6rem; } .serve-section { padding: 0.6rem; } }
  .photo-actions button { min-height: 48px; }
  .photo-meta { font-size: 0.75rem; color: var(--gray); line-height: 1.3; margin-top: 0.15rem; word-break: break-word; }

  .note-form { background: var(--cream); border-radius: 6px; padding: 1rem; margin-top: 1rem; }
  .note-form h5 { font-size: 0.8rem; font-weight: 700; color: var(--navy); margin-bottom: 0.75rem; letter-spacing: 0.04em; text-transform: uppercase; }
  .note-log { margin-top: 1rem; }
  .note-entry {
    border-left: 3px solid var(--gold);
    padding: 0.75rem 1rem;
    background: var(--white);
    border-radius: 0 6px 6px 0;
    margin-bottom: 0.75rem;
    font-size: 0.82rem;
  }
  .note-entry .note-status { font-weight: 700; text-transform: capitalize; font-size: 0.75rem; color: var(--gold); }
  .note-entry .note-meta { color: var(--gray); font-size: 0.75rem; margin-bottom: 0.3rem; }

  /* PROOF */
  .proof-doc {
    border: 1px solid #CCC;
    border-radius: 4px;
    background: var(--white);
    padding: 2rem;
    font-size: 0.82rem;
    line-height: 1.8;
    max-width: 680px;
    font-family: 'Times New Roman', serif;
  }
  .proof-doc h3 { text-align: center; font-size: 1.1rem; margin-bottom: 0.25rem; font-weight: bold; }
  .proof-doc h4 { text-align: center; font-size: 0.9rem; margin-bottom: 1rem; font-weight: normal; }
  .proof-doc .proof-field { border-bottom: 1px solid #444; min-width: 120px; display: inline-block; }
  .proof-doc .proof-row { display: flex; gap: 2rem; margin-bottom: 0.5rem; }
  .proof-doc .attempt-block { border: 1px solid #CCC; padding: 0.75rem; margin: 0.5rem 0; border-radius: 4px; background: #FAFAFA; }
  .proof-doc .attempt-block .attempt-label { font-weight: bold; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.25rem; }
  .esign-modal { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 999; }
  .esign-box { background: var(--white); border-radius: 12px; padding: 2rem; max-width: 480px; width: 90%; box-shadow: 0 24px 80px rgba(0,0,0,0.3); }
  .esign-box h3 { font-family: 'Montserrat', sans-serif; font-weight: 800; margin-bottom: 0.5rem; }
  .sig-pad { border: 2px solid var(--navy); border-radius: 6px; background: #F8F8F8; height: 100px; display: flex; align-items: center; justify-content: center; cursor: crosshair; margin: 1rem 0; font-style: italic; color: var(--gray); font-size: 0.85rem; position: relative; overflow: hidden; }
  .sig-text { font-family: 'Dancing Script', cursive; font-size: 2rem; color: var(--navy); }

  /* PHOTO LOG */
  .photo-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(80px, 1fr)); gap: 0.5rem; margin-top: 0.5rem; }
  .photo-thumb { width: 100%; aspect-ratio: 1; object-fit: cover; border-radius: 4px; border: 2px solid var(--light); cursor: pointer; transition: border-color 0.15s; }
  .photo-thumb:hover { border-color: var(--gold); }
  .photo-upload-btn { border: 2px dashed var(--light); border-radius: 4px; aspect-ratio: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; cursor: pointer; font-size: 0.75rem; color: var(--gray); transition: all 0.15s; background: var(--cream); }
  .photo-upload-btn:hover { border-color: var(--gold); color: var(--navy); }
  .photo-modal { position: fixed; inset: 0; background: rgba(0,0,0,0.85); display: flex; align-items: center; justify-content: center; z-index: 1000; cursor: pointer; }
  .photo-modal img { max-width: 90vw; max-height: 90vh; border-radius: 8px; }

  /* AI EXTRACTION */
  .ai-extract-box { background: #F0F4FF; border: 2px solid #93C5FD; border-radius: 8px; padding: 1rem; margin-top: 1rem; }
  .ai-extract-box h5 { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #1D4ED8; margin-bottom: 0.75rem; }
  .ai-loading { display: flex; align-items: center; gap: 0.5rem; color: #1D4ED8; font-size: 0.85rem; }
  .spinner { width: 16px; height: 16px; border: 2px solid #93C5FD; border-top-color: #1D4ED8; border-radius: 50%; animation: spin 0.8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .verify-badge { background: #FEF9EE; border: 1px solid var(--amber); border-radius: 4px; padding: 0.4rem 0.75rem; font-size: 0.75rem; color: #92400E; font-weight: 600; margin-bottom: 0.75rem; }
  .field-flag-medium { box-shadow: 0 0 0 2px var(--amber); border-radius: 4px; }
  .field-flag-low { box-shadow: 0 0 0 2px var(--red); border-radius: 4px; }
  .review-hint { font-size: 0.75rem; margin-top: 0.2rem; font-weight: 600; }
  .review-hint.medium { color: #92400E; }
  .review-hint.low { color: var(--red); }
  .suggestion-chip { display: inline-block; margin: 0.25rem 0.3rem 0 0; padding: 0.2rem 0.55rem; font-size: 0.75rem; border: 1px solid var(--gold); border-radius: 12px; background: #FFFBF4; cursor: pointer; color: var(--navy); }
  .suggestion-chip:hover { background: var(--gold); }
  .conflict-note { display: block; font-size: 0.75rem; color: #92400E; font-weight: 600; margin-top: 0.25rem; }
  .reference-list { font-size: 0.75rem; color: var(--gray); margin-top: 0.75rem; line-height: 1.6; border-top: 1px solid #D6E0F5; padding-top: 0.5rem; }
  .reference-list strong { color: var(--navy); }

  /* ALERT */
  .alert { background: #EFF8F3; border: 1px solid var(--green); border-radius: 4px; padding: 1rem; color: var(--green); font-size: 0.85rem; font-weight: 500; margin-top: 1rem; }
  .alert-warn { background: #FEF9EE; border-color: var(--amber); color: #92400E; }

  /* FOOTER */
  footer { background: var(--navy); color: rgba(255,255,255,0.5); text-align: center; padding: 2rem; font-size: 0.8rem; margin-top: auto; }
  footer strong { color: var(--gold); }

  .divider { border: none; border-top: 2px solid var(--light); margin: 0; }
  .serve-section { background: var(--cream); border-radius: 6px; padding: 1rem; margin-bottom: 1rem; }
  .serve-section h6 { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--gold); margin-bottom: 0.75rem; }
  .serve-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
  .serve-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0.75rem; }
  .declaration-box { background: var(--white); border: 2px solid var(--navy); border-radius: 6px; padding: 1.25rem; margin-top: 1rem; font-size: 0.82rem; line-height: 1.8; font-family: 'Times New Roman', serif; }
  .declaration-box h5 { font-family: 'Montserrat', sans-serif; font-size: 0.8rem; font-weight: 700; text-align: center; margin-bottom: 1rem; text-transform: uppercase; letter-spacing: 0.05em; }
  .sticky-call { position: fixed; bottom: 1.5rem; right: 1.5rem; z-index: 999; display: none; }
  @media (max-width: 768px) { .sticky-call { display: block; } }
  .sticky-call a { display: flex; align-items: center; gap: 0.5rem; background: var(--orange); color: white; padding: 0.85rem 1.25rem; border-radius: 50px; font-family: 'Inter', sans-serif; font-weight: 700; font-size: 0.9rem; text-decoration: none; box-shadow: 0 4px 20px rgba(232,98,10,0.5); }

  /* GPS / MILEAGE / TAX */
  .gps-badge { display: inline-flex; align-items: center; gap: 0.35rem; background: #EFF8F3; border: 1px solid var(--green); border-radius: 4px; padding: 0.2rem 0.5rem; font-size: 0.75rem; color: var(--green); font-weight: 700; }
  .gps-badge.error { background: #FEF2F2; border-color: var(--red); color: var(--red); }
  .route-control-bar { background: var(--navy); border-radius: 8px; padding: 1rem 1.25rem; display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .route-control-bar .rc-stat { text-align: center; }
  .route-control-bar .rc-val { font-family: "Montserrat", sans-serif; font-size: 1.2rem; font-weight: 800; color: var(--gold); }
  .route-control-bar .rc-lbl { font-size: 0.75rem; color: rgba(255,255,255,0.5); text-transform: uppercase; letter-spacing: 0.06em; }
  .start-route-btn { background: var(--green); color: white; border: none; border-radius: 6px; padding: 0.65rem 1.5rem; font-family: "Inter", sans-serif; font-weight: 700; font-size: 0.85rem; cursor: pointer; transition: background 0.15s; }
  .start-route-btn:hover { background: #1e6b42; }
  .end-route-btn { background: var(--red); color: white; border: none; border-radius: 6px; padding: 0.65rem 1.5rem; font-family: "Inter", sans-serif; font-weight: 700; font-size: 0.85rem; cursor: pointer; }
  .tax-card { background: var(--white); border: 2px solid var(--light); border-radius: 8px; padding: 1.25rem; }
  .tax-card h4 { font-family: "Montserrat", sans-serif; font-weight: 700; font-size: 0.9rem; margin-bottom: 1rem; color: var(--navy); }
  .tax-row { display: flex; justify-content: space-between; align-items: center; padding: 0.5rem 0; border-bottom: 1px solid var(--light); font-size: 0.85rem; }
  .tax-row:last-child { border-bottom: none; font-weight: 700; }
  .tax-val { font-family: "Montserrat", sans-serif; font-weight: 700; color: var(--gold); }
  .earnings-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; margin-bottom: 1.5rem; }
  .earnings-card { background: var(--navy); border-radius: 8px; padding: 1.25rem; text-align: center; }
  .earnings-card .ev { font-family: "Montserrat", sans-serif; font-size: 1.6rem; font-weight: 800; color: var(--gold); }
  .earnings-card .el { font-size: 0.75rem; color: rgba(255,255,255,0.5); text-transform: uppercase; letter-spacing: 0.06em; margin-top: 0.25rem; }
  .mileage-log-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
  .mileage-log-table th { background: var(--navy); color: var(--white); padding: 0.6rem 0.75rem; text-align: left; font-size: 0.75rem; letter-spacing: 0.05em; text-transform: uppercase; }
  .mileage-log-table td { padding: 0.6rem 0.75rem; border-bottom: 1px solid var(--light); }
  .mileage-log-table tr:hover td { background: var(--cream); }
  .agreement-modal { position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 1rem; }
  .agreement-box { background: var(--white); border-radius: 12px; padding: 2rem; max-width: 640px; width: 100%; max-height: 80vh; display: flex; flex-direction: column; box-shadow: 0 24px 80px rgba(0,0,0,0.4); }
  .agreement-box h3 { font-family: "Montserrat", sans-serif; font-weight: 800; margin-bottom: 1rem; }
  .agreement-text { flex: 1; overflow-y: auto; font-size: 0.78rem; line-height: 1.8; white-space: pre-wrap; border: 1px solid var(--light); border-radius: 4px; padding: 1rem; background: var(--cream); margin-bottom: 1rem; color: var(--navy); }
  .refund-calc { background: #EFF8F3; border: 2px solid var(--green); border-radius: 8px; padding: 1.25rem; margin: 1rem 0; }
  .refund-calc h4 { font-family: "Montserrat", sans-serif; font-weight: 700; font-size: 0.9rem; margin-bottom: 0.5rem; color: var(--green); }
  .decline-modal { position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 1rem; }
  .decline-box { background: var(--white); border-radius: 12px; padding: 2rem; max-width: 520px; width: 100%; box-shadow: 0 24px 80px rgba(0,0,0,0.4); }
  .decline-box h3 { font-family: "Montserrat", sans-serif; font-weight: 800; margin-bottom: 1rem; }
  .address-update-box { background: #EFF6FF; border: 2px solid var(--blue); border-radius: 8px; padding: 1.25rem; margin-top: 1rem; }
  .address-update-box h4 { font-family: "Montserrat", sans-serif; font-weight: 700; color: var(--blue); margin-bottom: 0.75rem; font-size: 0.95rem; }
  .address-update-box h4 { font-family: "Montserrat", sans-serif; font-weight: 700; color: var(--blue); margin-bottom: 0.75rem; }
  .flex-between { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
  .mt1 { margin-top: 1rem; }
  .mt2 { margin-top: 2rem; }
  .gap1 { gap: 1rem; }
  .text-sm { font-size: 0.8rem; color: var(--gray); }

  /* ROUTE PLANNER */
  .route-planner { display: grid; grid-template-columns: 340px 1fr; gap: 1.5rem; }
  @media (max-width: 800px) { .route-planner { grid-template-columns: 1fr; } }

  .route-stop {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.9rem;
    border-radius: 6px;
    background: var(--white);
    border: 2px solid var(--light);
    margin-bottom: 0.6rem;
    transition: border-color 0.15s;
    cursor: grab;
  }
  .route-stop:hover { border-color: var(--gold); }
  .route-stop.done { opacity: 0.5; border-style: dashed; }
  .route-stop.active { border-color: var(--navy); background: #F0F4FF; }

  .stop-num {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--navy);
    color: var(--white);
    font-family: 'Montserrat', sans-serif;
    font-weight: 800;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .stop-num.done-num { background: var(--green); }
  .stop-num.current-num { background: var(--gold); color: var(--navy); }

  .stop-info { flex: 1; min-width: 0; }
  .stop-name { font-weight: 700; font-size: 0.88rem; color: var(--navy); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .stop-addr { font-size: 0.75rem; color: var(--gray); line-height: 1.4; margin-top: 0.1rem; }
  .stop-actions { display: flex; gap: 0.4rem; flex-shrink: 0; }

  .icon-btn {
    background: var(--light);
    border: none;
    border-radius: 4px;
    width: 30px;
    height: 30px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    transition: background 0.15s;
  }
  .icon-btn:hover { background: var(--gold); }
  .icon-btn.go { background: var(--navy); color: var(--white); }
  .icon-btn.go:hover { background: var(--gold); color: var(--navy); }

  .route-summary {
    background: var(--navy);
    color: var(--white);
    border-radius: 8px;
    padding: 1.2rem;
    margin-bottom: 1rem;
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
  }
  .route-stat { text-align: center; }
  .route-stat .rs-val { font-family: 'Montserrat', sans-serif; font-size: 1.4rem; font-weight: 800; color: var(--gold); }
  .route-stat .rs-lbl { font-size: 0.75rem; color: rgba(255,255,255,0.5); text-transform: uppercase; letter-spacing: 0.06em; }

  .map-preview {
    background: var(--light);
    border-radius: 8px;
    overflow: hidden;
    border: 2px solid var(--light);
    min-height: 320px;
    display: flex;
    flex-direction: column;
  }
  .map-preview iframe { flex: 1; border: none; width: 100%; min-height: 320px; }
  .map-placeholder {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    color: var(--gray);
    padding: 2rem;
    text-align: center;
  }
  .map-placeholder .map-icon { font-size: 3rem; }

  .route-btn-row { display: flex; gap: 0.75rem; flex-wrap: wrap; margin-bottom: 1rem; }

  .stop-checkbox {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: var(--green);
    flex-shrink: 0;
    margin-top: 5px;
  }
  /* Phones: the logo + menu used to be wider than the screen, so the whole page
     was zoomed out (tiny text, empty strip on the right). Wrap and shrink them,
     and never let anything make the page wider than the phone. */
  .app { overflow-x: clip; }
  .app > * { min-width: 0; max-width: 100%; }
  @media (max-width: 600px) {
    nav { padding: 0.4rem 0.75rem; height: auto; min-height: 56px; flex-wrap: wrap; row-gap: 0.25rem; column-gap: 0.5rem; }
    .nav-logo { font-size: 0.85rem; white-space: normal; }
    .nav-links { gap: 0.15rem; flex-wrap: wrap; }
    .nav-btn { padding: 0.35rem 0.5rem; font-size: 0.75rem; }
    .nav-cta { padding: 0.4rem 0.7rem; font-size: 0.75rem; }
    .section { padding: 1.5rem 0.75rem; }
    .admin-section { padding: 1rem 0.6rem !important; width: 100%; max-width: 100% !important; overflow-wrap: anywhere; }
    .admin-grid > *, .job-detail { min-width: 0; }
    .admin-section .section-sub { display: none; }
    .portal-body { padding: 0.75rem; }
    .portal-tabs { flex-wrap: wrap; }
    .portal-tab { flex: 1 1 45%; padding: 0.6rem 0.4rem; font-size: 0.8rem; }
  }
  /* Phones: an order opens directly under its own card, which already shows the
     client, person to serve, address, status, level and date — so the order
     does not repeat them (phone, email, court and case details still show). */
  /* Phones: tapping an order opens it as its OWN page — the list and the
     dashboard settings are hidden, with "← All orders" to go back. Client
     name, phone and email sit together at the top, then the person to serve. */
  .phone-only { display: none; }
  @media (max-width: 900px) {
    .phone-only { display: inline-block; }
    body.order-open .admin-chrome, body.order-open .job-card, body.order-open .admin-section > .section-title { display: none !important; }
    body.order-open .job-detail { border: none; padding: 0; margin: 0; display: flex; flex-direction: column; }
    body.order-open .job-detail > .phone-only { order: -3; align-self: flex-start; }
    body.order-open .job-detail > .order-head { order: -2; }
    body.order-open .job-detail > .job-messages { order: -1; margin-top: 0.75rem; }
  }
`;


// SERVICE_AGREEMENT now lives in src/lib/serviceAgreement.js (single source of
// truth shared with the standalone /service-agreement.html page and the tests).


// Client emails are sent by the SERVER (Resend) after the change they describe
// is saved — e.g. acceptance and cancellation in admin-order-action. The old
// browser-side sendEmail() was a no-op stub (EmailJS never configured), so no
// message it "sent" ever reached anyone; it has been removed.

// ── GPS & MILEAGE UTILITIES ───────────────────────────────────────────────────
const IRS_RATE_2026 = 0.70; // IRS standard mileage rate $/mile — update annually

function getCurrentGPS() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy) }),
      err => reject(err.message),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
}

function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 3958.8; // miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function formatCoords(lat, lng) {
  return lat.toFixed(6) + ", " + lng.toFixed(6);
}

// ── DOCUMENT READING (Azure Document Intelligence via /read-document) ───────────
// Automatic reading runs one Azure request per file (keeps each Netlify
// invocation within the 10s limit). Results are merged client-side.
const MAX_AUTOREAD_FILES = 8;   // generous ceiling; a typical packet is 1–2 files
const AUTOREAD_CONCURRENCY = 2; // in-flight requests at once
const SUPPORTED_DOC_EXT = ["pdf", "jpg", "jpeg", "png"];
const REQUIRED_EXTRACT_KEYS = ["plaintiff", "defendant", "caseNumber"];

// Build the [label, value] rows for ONE saved service attempt, from the columns
// persisted in service_attempts. Only facts that were actually recorded appear —
// a blank column contributes no row at all, so the display never shows an empty
// label. Successful-service facts and unsuccessful-attempt facts are grouped
// separately by the caller's styling.
function attemptDetailRows(a) {
  const r = a || {};
  const rows = [];
  const add = (label, v) => { const s = v == null ? "" : String(v).trim(); if (s) rows.push([label, s]); };
  const served = /^served_/.test(String(r.outcome || ""));

  // Where it happened.
  add("Location type", r.location_type);
  add("Address", r.address_line || r.service_address);

  // Who was involved. On a successful service this is the person served; on an
  // unsuccessful attempt it is whoever was encountered.
  // Codes ("subject", "family_member") are shown as words, never raw.
  const present = presentLabel(r.person_contacted_category, r.person_contacted_detail);
  add(served ? "Served" : "Person present", present ? present.charAt(0).toUpperCase() + present.slice(1) : "");
  add("Family relation", r.person_contacted_family_relation ? String(r.person_contacted_family_relation).replace(/_/g, " ") : "");
  if (String(r.person_contacted_category || "").toLowerCase() !== "other") add("Name / detail", r.person_contacted_detail);
  add("Relationship", r.relationship);
  add("Identified by", r.identification_method);
  add("Accepted / refused", r.service_acceptance);

  // Physical description — only the parts that were entered.
  const desc = [r.gender, r.approximate_age, r.ethnicity, r.height, r.weight, r.hair_color]
    .map((x) => (x == null ? "" : String(x).trim())).filter(Boolean).join(", ");
  add("Description", desc);
  const feats = Array.isArray(r.special_features) ? r.special_features.filter(Boolean).join(", ") : r.special_features;
  add("Distinguishing features", feats);
  add("Other feature", r.special_features_other);

  // What was said and seen.
  add("Statement made", r.statement_made);
  add("Observations", r.address_observations);
  add("Other person contacted", r.other_contact_category);
  add("Other contact detail", r.other_contact_detail);

  // Substituted-service mailing follow-up.
  add("Mailed on", r.mailed_on);
  add("Mailing method", r.mailing_method);
  add("Tracking number", r.tracking_number);

  // GPS: report availability honestly rather than pretending precision.
  if (r.gps_lat != null && r.gps_lng != null) {
    const acc = r.gps_accuracy_m != null ? ` (±${Math.round(Number(r.gps_accuracy_m))} m)` : "";
    rows.push(["GPS", `${Number(r.gps_lat).toFixed(6)}, ${Number(r.gps_lng).toFixed(6)}${acc}`]);
  } else if (r.gps_unavailable_reason) {
    rows.push(["GPS", `not captured — ${r.gps_unavailable_reason}`]);
  }

  add("Notes", r.internal_notes);
  return rows;
}

// Document subtypes, by document type. Module-level so BOTH the customer form
// and the admin Create Order panel use the same list — only these types have a
// subtype at all, so no other type ever asks for one.
// The picker's accept list is derived from the shared allowlist so it can never
// drift from what the resolver and the storage bucket accept.
const UPLOAD_ACCEPT = UPLOAD_LIMITS.ALLOWED_EXT.map((e) => "." + e).join(",");

// How each file's upload state reads to the customer. "waiting" is the state of
// a file that has been chosen but not yet sent — uploading starts at submit.
const UPLOAD_STATUS_LABEL = {
  waiting: "waiting", uploading: "uploading…", uploaded: "uploaded ✓", failed: "failed",
};
const UPLOAD_STATUS_COLOR = {
  waiting: "var(--gray)", uploading: "var(--blue)", uploaded: "var(--green)", failed: "var(--red)",
};

// How long an authenticated admin may be IDLE before the session ends and the
// "Your session has expired" message is shown. The clock is reset by real
// interaction (see the activity listeners below), so this bounds inactivity,
// not total session length. The server's separate 12-hour absolute lifetime
// (SESSION_TTL_MS in netlify/functions/lib/adminAuth.js) is the outer backstop
// and is unaffected by this value.
//   2 hours = 120 minutes = 7,200 seconds = 7,200,000 ms
const ADMIN_IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000;
const LAST_USED_KEY = "cpsAdminLastUsed";
const LAST_USED_GRACE_MS = 30 * 60 * 1000;

const ADMIN_DOC_SUBTYPES = {
  "TRO / Restraining Order": [["dv", "Domestic violence (DV-200)"], ["ch", "Civil harassment (CH-200)"], ["ea", "Elder/dependent adult abuse (EA-200)"], ["wv", "Workplace violence (WV-200)"], ["sv", "Postsecondary school violence (SV-200)"], ["gv", "Gun violence (GV-200)"]],
  "Family Law (Divorce/Custody)": [["initial", "Initial summons/petition (FL-115)"], ["post_filing", "Post-filing papers (FL-330)"]],
  "Unlawful Detainer / Eviction": [["named", "Named parties only"], ["occupants", "Includes unnamed occupants (adds CP10.5)"]],
};
// The customer form shows subtype labels; the admin panel stores codes.
const CUSTOMER_DOC_SUBTYPES = Object.fromEntries(
  Object.entries(ADMIN_DOC_SUBTYPES).map(([k, v]) => [k, v.map(([, label]) => label)])
);

// Customer-facing wording for each per-file reader outcome. "The upload failed"
// is never one of them — the file IS attached; only the analysis fell short.
const READ_FAILURE_TEXT = {
  unsupported: "that file type can't be read automatically. It's still attached for our team to review.",
  too_large: "the file is larger than our document reader supports. It's still attached and our team will review it.",
  unreadable: "we couldn't read the text (it may be a scan or password-protected). It's still attached for review.",
  timeout: "reading it took too long. It's still attached and our team will review it.",
  reader_unavailable: "automatic reading is unavailable right now. It's still attached for review.",
  no_fields: "no recognizable case information was found in it. It's still attached for review.",
  not_analyzed: "we only auto-read the first few documents. It's still attached for review.",
  empty: "the file appeared to be empty. It's still attached for review.",
  conflict: "it disagrees with another document — please confirm the correct details below.",
};

// ── DOCUMENT UPLOAD LIMIT (one shared constant) ────────────────────────────────
// Both upload paths — admin (admin-direct-upload) and customer review
// (submit-review-request) — send file bytes DIRECTLY to the private
// order-documents bucket via a signed URL, so the old ~4 MB base64 ceiling no
// longer applies. The limit is 25 MB PER FILE (not per packet) and lives in
// src/lib/uploadLimits.js so the picker, the endpoints, the reader handling, the
// error messages and the tests can never disagree.
const ADMIN_MAX_UPLOAD_MB = UPLOAD_LIMITS.MAX_UPLOAD_MB;
const ADMIN_MAX_UPLOAD_BYTES = UPLOAD_LIMITS.MAX_UPLOAD_BYTES;

function isSupportedDoc(file) {
  const name = (file.name || "").toLowerCase();
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1) : "";
  return (
    SUPPORTED_DOC_EXT.includes(ext) ||
    file.type === "application/pdf" ||
    (file.type || "").startsWith("image/")
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result.split(",")[1]) || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Run `worker` over `items` with a bounded number of concurrent tasks.
async function runPool(items, size, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  async function next() {
    const idx = cursor++;
    if (idx >= items.length) return;
    try {
      results[idx] = await worker(items[idx], idx);
    } catch (e) {
      results[idx] = null;
    }
    return next();
  }
  await Promise.all(Array.from({ length: Math.min(size, items.length) }, next));
  return results;
}

// Best-effort map of a detected court name to a fixed dropdown value; returns
// null when there is no confident match (caller then prompts for manual pick).
function matchCourtValue(text) {
  if (!text) return null;
  const t = String(text).toLowerCase();
  if (t.includes("district court")) {
    if (t.includes("riverside")) return "federal_cd_cal_riverside";
    if (t.includes("central district") || t.includes("central district of california")) return "federal_cd_cal";
  }
  if (t.includes("riverside")) {
    if (t.includes("corona")) return "riverside_corona";
    if (t.includes("banning")) return "riverside_banning";
    if (t.includes("hemet")) return "riverside_hemet";
    if (t.includes("indio")) return "riverside_indio";
    if (t.includes("murrieta") || t.includes("southwest")) return "riverside_murrieta";
    return "riverside_superior";
  }
  if (t.includes("orange")) {
    if (t.includes("santa ana") || t.includes("central")) return "orange_central";
    if (t.includes("fullerton") || t.includes("north")) return "orange_north";
    if (t.includes("westminster") || t.includes("west")) return "orange_west";
    if (t.includes("newport") || t.includes("harbor")) return "orange_harbor";
  }
  if (t.includes("san bernardino")) {
    if (t.includes("rancho")) return "sbcounty_rancho";
    if (t.includes("ontario")) return "sbcounty_ontario";
    return "sbcounty_san_bern";
  }
  if (t.includes("los angeles")) {
    if (t.includes("pomona")) return "la_pomona";
    if (t.includes("norwalk")) return "la_norwalk";
    if (t.includes("west covina")) return "la_west_covina";
    return "la_central";
  }
  return null;
}

// Map a detected document phrase to a known Document Type option; null if unsure.
function matchDocTypeValue(text) {
  if (!text) return null;
  const t = String(text).toLowerCase();
  if (t.includes("unlawful detainer") || t.includes("eviction")) return "Unlawful Detainer / Eviction";
  if (t.includes("subpoena")) return "Subpoena";
  if (t.includes("restraining") || t.includes("tro")) return "TRO / Restraining Order";
  if (t.includes("small claim")) return "Small Claims";
  if (t.includes("garnishment") || t.includes("levy")) return "Wage Garnishment / Levy";
  if (t.includes("divorce") || t.includes("custody") || t.includes("family")) return "Family Law (Divorce/Custody)";
  if (t.includes("summons") || t.includes("complaint")) return "Summons & Complaint";
  return null;
}

// ── COMPONENTS ─────────────────────────────────────────────────────────────────

function AdminRefundModal({ job, busy, onClose, onSubmit }) {
  const paid = Number(job.price) || 0;
  const refunded = Number(job.amountRefunded) || 0;
  const remaining = Math.max(0, paid - refunded);
  const [amount, setAmount] = useState(String(remaining));
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");

  async function submit() {
    setErr("");
    const amt = Number(amount);
    if (!amt || amt <= 0) return setErr("Enter a refund amount greater than zero.");
    if (amt > remaining) return setErr(`Refund cannot exceed the remaining balance ($${remaining}).`);
    if (!reason.trim()) return setErr("A refund reason is required.");
    if (!(await askConfirm(`Refund $${amt.toFixed(2)} for ${job.id}? This cannot be undone.`, "Refund"))) return;
    onSubmit(Math.round(amt * 100), reason.trim());
  }

  return (
    <div className="agreement-modal">
      <div className="agreement-box" style={{ maxWidth: 460 }}>
        <h3>Refund Payment — {job.id}</h3>
        <div className="refund-calc">
          <div className="text-sm">Original amount paid: <strong>${paid.toFixed(2)}</strong></div>
          <div className="text-sm">Already refunded: <strong>${refunded.toFixed(2)}</strong></div>
          <div className="text-sm">Remaining refundable: <strong>${remaining.toFixed(2)}</strong></div>
        </div>
        <div className="field" style={{ marginBottom: "0.6rem" }}>
          <label>Refund Amount (USD)</label>
          <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="field" style={{ marginBottom: "0.6rem" }}>
          <label>Refund Reason</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Customer request" />
        </div>
        {err && <div className="alert alert-warn" style={{ marginBottom: "0.6rem" }}>{err}</div>}
        <div style={{ display: "flex", gap: "0.75rem" }}>
          <button className="btn-secondary" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy || remaining <= 0} onClick={submit}>{busy ? "Processing…" : "Issue Refund"}</button>
        </div>
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const labels = {
    pending_review: "Pending Review",
    accepted: "Accepted / In Service",
    awaiting_client: "Awaiting Client Response",
    completed: "Service Completed",
    cancelled: "Cancelled",
    refund_issued: "Refund Issued",
  };
  return <span className={`status-chip status-${status}`}>{labels[status] || status}</span>;
}

// Client-facing Service Agreement page (from the emailed ?agreement= link on
// an order the admin created). Read → type name → I Agree → (Pay, if due).
function AgreementPage({ token }) {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  async function call(payload) {
    const res = await fetch("/.netlify/functions/client-agreement", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, ...payload }) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.ok) throw new Error(d.error || "Something went wrong. Please try again.");
    return d;
  }
  useEffect(() => { call({ action: "get" }).then(setInfo).catch(e => setErr(e.message)); }, []);
  async function accept() {
    setBusy(true); setErr("");
    try { setInfo(await call({ action: "accept", name, agree })); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  return (
    <div data-testid="agreement-page" style={{ maxWidth: 720, margin: "2rem auto", padding: "0 1rem" }}>
      <h2 style={{ fontFamily: "Montserrat, sans-serif", color: "var(--navy)" }}>Service Agreement</h2>
      {!info && !err && <p>Loading…</p>}
      {err && <div className="alert" style={{ marginBottom: "1rem" }}>{err}</div>}
      {info && (
        <>
          <p style={{ fontSize: "0.9rem" }}>Order <strong>{info.jobNumber}</strong>{info.personToServe ? <> · Person to serve: <strong>{info.personToServe}</strong></> : null}</p>
          {info.accepted ? (
            <div data-testid="agreement-accepted" style={{ padding: "0.9rem", border: "2px solid var(--green)", borderRadius: 8, background: "#F0FDF4", margin: "1rem 0" }}>
              <strong>✓ Accepted</strong>{info.acceptedName ? ` by ${info.acceptedName}` : ""}{info.acceptedAt ? ` on ${new Date(info.acceptedAt).toLocaleString()}` : ""}. Thank you!
            </div>
          ) : (
            <>
              <div className="agreement-text" style={{ maxHeight: "50vh", margin: "1rem 0" }}>{SERVICE_AGREEMENT}</div>
              <div className="field"><label>Your full name *</label><input value={name} onChange={e => setName(e.target.value)} placeholder="Full name" /></div>
              <label style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", fontSize: "0.88rem", margin: "0.75rem 0" }}>
                <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} />
                I have read and agree to the Corona Process Serving Service Agreement.
              </label>
              <button className="btn-primary" data-testid="agreement-accept" disabled={busy || !agree || name.trim().length < 2} onClick={accept}>{busy ? "Saving…" : "I Agree"}</button>
            </>
          )}
          {info.accepted && info.payUrl && (
            <div style={{ marginTop: "1rem" }}>
              <p style={{ marginBottom: "0.9rem", lineHeight: 1.5 }}>Next, complete secure payment{info.amountDueCents != null ? ` of $${(info.amountDueCents / 100).toFixed(2)}` : ""} to begin service.</p>
              <a className="btn-primary" data-testid="agreement-pay" href={info.payUrl} style={{ display: "inline-block", textDecoration: "none", position: "static" }}>Pay securely</a>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Client page for an extra charge (?charge=<token>). A skip trace asks for the
// person's details first (first + last name required, the rest optional),
// then sends the client to secure Stripe payment.
const SKIP_PERSON_BLANK = { firstName: "", lastName: "", address: "", city: "", state: "", phone: "", email: "", jobs: "" };
function ChargePage({ token }) {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [people, setPeople] = useState([{ ...SKIP_PERSON_BLANK }]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  async function call(payload) {
    const res = await fetch("/.netlify/functions/client-charge", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, ...payload }) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.ok) throw new Error(d.error || "Something went wrong. Please try again.");
    return d;
  }
  useEffect(() => {
    call({ action: "get" }).then(d => {
      setInfo(d);
      if (d.people && d.people.length) setPeople(d.people.map(p => ({ ...SKIP_PERSON_BLANK, ...p })));
      if (d.notes) setNotes(d.notes);
    }).catch(e => setErr(e.message));
  }, []);
  const setP = (i, k, v) => setPeople(list => list.map((p, j) => j === i ? { ...p, [k]: v } : p));
  const ready = people.length > 0 && people.every(p => p.firstName.trim() && p.lastName.trim());
  async function submit() {
    setBusy(true); setErr("");
    try {
      const d = await call({ action: "submit", people, notes });
      setInfo(d);
      if (d.payUrl) window.location.href = d.payUrl;
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  const F = (i, k, label, req, type) => (
    <div className="field"><label>{label}{req ? " *" : ""}</label><input type={type || "text"} value={people[i][k]} onChange={e => setP(i, k, e.target.value)} /></div>
  );
  return (
    <div data-testid="charge-page" style={{ maxWidth: 720, margin: "2rem auto", padding: "0 1rem" }}>
      <h2 style={{ fontFamily: "Montserrat, sans-serif", color: "var(--navy)" }}>{info ? info.description : "Additional Service"}</h2>
      {!info && !err && <p>Loading…</p>}
      {err && <div className="alert" style={{ marginBottom: "1rem" }}>{err}</div>}
      {info && (
        <>
          <p style={{ fontSize: "0.9rem" }}>Order <strong>{info.jobNumber}</strong> · <strong>${(info.amountCents / 100).toFixed(2)}</strong></p>
          {info.paid && <div data-testid="charge-paid" style={{ padding: "0.9rem", border: "2px solid var(--green)", borderRadius: 8, background: "#F0FDF4" }}><strong>✓ Paid.</strong> Thank you!</div>}
          {!info.paid && info.voided && <div className="alert">This charge is no longer open. Please contact Corona Process Serving.</div>}
          {!info.paid && !info.voided && info.needsDetails && !info.detailsDone && (
            <div data-testid="skip-trace-form">
              <p style={{ fontSize: "0.88rem" }}>Tell us who we're locating. First and last name are required — everything else is optional but helps.</p>
              {people.map((p, i) => (
                <div key={i} style={{ border: "2px solid var(--light)", borderRadius: 8, padding: "0.75rem", marginBottom: "0.75rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
                    <strong>Person {i + 1}</strong>
                    {people.length > 1 && <button type="button" className="btn-secondary" onClick={() => setPeople(list => list.filter((_, j) => j !== i))}>Remove</button>}
                  </div>
                  <div className="form-grid">
                    {F(i, "firstName", "First name", true)}
                    {F(i, "lastName", "Last name", true)}
                    <div className="field form-full"><label>Last known address</label><input value={p.address} onChange={e => setP(i, "address", e.target.value)} /></div>
                    {F(i, "city", "City")}
                    {F(i, "state", "State")}
                    {F(i, "phone", "Phone number", false, "tel")}
                    {F(i, "email", "Email", false, "email")}
                    <div className="field form-full"><label>Job(s) / employer(s)</label><input value={p.jobs} onChange={e => setP(i, "jobs", e.target.value)} /></div>
                  </div>
                </div>
              ))}
              <button type="button" className="btn-secondary" style={{ marginBottom: "0.75rem" }} onClick={() => setPeople(list => [...list, { ...SKIP_PERSON_BLANK }])}>＋ Add another person</button>
              <div className="field"><label>Anything else you'd like to share</label><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} /></div>
              <button className="btn-primary" data-testid="skip-trace-submit" disabled={busy || !ready} onClick={submit}>{busy ? "Saving…" : "Submit & Pay"}</button>
            </div>
          )}
          {!info.paid && !info.voided && info.payUrl && (
            <div style={{ marginTop: "1rem" }}>
              {info.needsDetails && <p>✓ Details received.</p>}
              <a className="btn-primary" data-testid="charge-pay" href={info.payUrl} style={{ display: "inline-block", textDecoration: "none" }}>Pay securely</a>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Client page for "Upload your documents" (?upload=<token>) — for an order the
// admin created without documents. Same private direct-to-storage upload.
function UploadPage({ token }) {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);   // [{ name, status, error }]
  const [busy, setBusy] = useState(false);
  async function call(payload) {
    let targetId = "";
    try { targetId = new URLSearchParams(window.location.search).get("job") || ""; } catch (e) {}
    const res = await fetch("/.netlify/functions/client-docs-upload", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, targetId, ...payload }) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.ok) throw new Error(d.error || (d.rejected && d.rejected[0] && d.rejected[0].reason) || "Something went wrong. Please try again.");
    return d;
  }
  useEffect(() => { call({ action: "get" }).then(setInfo).catch(e => setErr(e.message)); }, []);
  async function upload(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true); setErr("");
    for (const f of files) {
      const key = `${f.name}::${f.size}::${f.lastModified || 0}`;
      setRows(r => [...r.filter(x => x.key !== key), { key, name: f.name, status: "Uploading…" }]);
      try {
        const a = await call({ action: "authorize", files: [{ name: f.name, type: f.type, size: f.size, clientToken: key }] });
        const grant = a.grants[0];
        const put = await fetch(grant.uploadUrl, { method: "PUT", headers: { "Content-Type": grant.contentType, "x-upsert": grant.upsert ? "true" : "false" }, body: f });
        if (!put.ok) throw new Error(`upload failed (HTTP ${put.status})`);
        await call({ action: "finalize", path: grant.path, name: f.name, type: f.type, clientToken: key });
        setRows(r => r.map(x => x.key === key ? { ...x, status: "✓ Uploaded" } : x));
      } catch (e) {
        setRows(r => r.map(x => x.key === key ? { ...x, status: "✗ " + e.message } : x));
      }
    }
    setBusy(false);
  }
  return (
    <div data-testid="upload-page" style={{ maxWidth: 720, margin: "2rem auto", padding: "0 1rem" }}>
      <h2 style={{ fontFamily: "Montserrat, sans-serif", color: "var(--navy)" }}>Upload Your Documents</h2>
      {!info && !err && <p>Loading…</p>}
      {err && <div className="alert" style={{ marginBottom: "1rem" }}>{err}</div>}
      {info && (
        <>
          <p style={{ fontSize: "0.9rem" }}>Order <strong>{info.jobNumber}</strong>{info.personToServe ? <> · Person to serve: <strong>{info.personToServe}</strong></> : null}</p>
          <div className="upload-zone" onClick={() => !busy && document.getElementById("client-docs-input").click()}>
            <strong>{busy ? "Uploading…" : "Tap to choose your documents"}</strong>
            <p>PDF preferred — photos (JPG, PNG, HEIC) are fine. Add as many as you need.</p>
            <input id="client-docs-input" type="file" multiple accept={UPLOAD_ACCEPT} style={{ display: "none" }} onChange={e => { const f = e.target.files; upload(f); e.target.value = ""; }} />
          </div>
          {(rows.length > 0 || (info.received || []).length > 0) && (
            <ul data-testid="upload-page-files" style={{ listStyle: "none", padding: 0, marginTop: "0.75rem", fontSize: "0.9rem" }}>
              {(info.received || []).filter(n => !rows.some(r => r.name === n)).map(n => <li key={n}>✓ {n} (received)</li>)}
              {rows.map(r => <li key={r.key}>{r.name} — {r.status}</li>)}
            </ul>
          )}
          {rows.some(r => r.status === "✓ Uploaded") && !busy && <p style={{ color: "var(--green)", fontWeight: 700 }}>Thank you — we've received your documents.</p>}
        </>
      )}
    </div>
  );
}

// PDF viewer that slides in on the RIGHT, next to the order (full screen on a
// phone). Renders with PDF.js so it shows on every device — Android Chrome and
// some desktop settings download a PDF link instead of showing it.
const PDFJS_SRC = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
const PDFJS_WORKER = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
function loadPdfJs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (window.__pdfjsLoading) return window.__pdfjsLoading;
  window.__pdfjsLoading = new Promise((resolve, reject) => {
    const sc = document.createElement("script");
    sc.src = PDFJS_SRC;
    sc.onload = () => { if (window.pdfjsLib) { window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER; resolve(window.pdfjsLib); } else reject(new Error("viewer failed to load")); };
    sc.onerror = () => { window.__pdfjsLoading = null; reject(new Error("viewer failed to load")); };
    document.head.appendChild(sc);
  });
  return window.__pdfjsLoading;
}
// ── Photo stamp (AB 747): date, time and GPS printed ON the photo ─────────
function photoStampLines(whenIso, gps, source) {
  let when = "";
  try { when = new Date(whenIso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit", timeZoneName: "short" }); } catch (e) { when = String(whenIso || ""); }
  // Only photos taken with the portal's camera are stamped (Oct 29): date,
  // time and GPS — nothing else. Uploaded photos are never drawn on.
  return [when, gps ? `GPS ${Number(gps.lat).toFixed(6)}, ${Number(gps.lng).toFixed(6)}${gps.accuracy != null ? ` (±${Math.round(gps.accuracy)} m)` : ""}` : "GPS unavailable"];
}
function stampPhoto(dataUrl, lines) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const max = 2000;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      const fs = Math.max(16, Math.round(w / 38));
      const pad = Math.round(fs * 0.5), lh = Math.round(fs * 1.25);
      const boxH = pad * 2 + lh * lines.length;
      ctx.fillStyle = "rgba(0,0,0,0.6)"; ctx.fillRect(0, h - boxH, w, boxH);
      ctx.fillStyle = "#FFD54A"; ctx.font = `bold ${fs}px Arial, sans-serif`; ctx.textBaseline = "top";
      lines.forEach((t, i) => ctx.fillText(t, pad, h - boxH + pad + i * lh, w - pad * 2));
      try { resolve(c.toDataURL("image/jpeg", 0.88)); } catch (e) { reject(e); }
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

// Documents open in a NEW TAB (not inside the portal), and every document has
// a Print button. Print loads a private same-site copy of the file into a
// hidden frame and opens the print dialog; on phones (no in-page PDF printing)
// it opens the file in a new tab, where the phone's viewer can print it.
// PDFs open INSIDE the portal (see PdfViewer.jsx) — they used to download.
const isPdfUrl = (url) => /\.pdf(\?|#|$)/i.test(String(url || ""));
function openFileTab(url, title) {
  if (!url) return;
  if (isPdfUrl(url)) { openPdfViewer(url, { loadPdfJs, title: title || decodeURIComponent(String(url).split("?")[0].split("/").pop() || "Document") }); return; }
  window.open(url, "_blank", "noopener");
}
const isPhone = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
// Test hook: lets the viewer be checked in a browser without signing in.
if (typeof window !== "undefined") window.cpsOpenPdf = (u, t) => openFileTab(u, t);
async function printFile(url) {
  if (!url) return;
  // PDFs print from pictures of their pages — works on the phone too, and
  // whatever the browser's PDF download setting is.
  if (isPdfUrl(url)) { try { await printPdfPages(await loadPdfJs(), url); return; } catch (e) { /* fall back below */ } }
  if (isPhone()) { window.open(url, "_blank", "noopener"); return; }
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error("fetch failed");
    const blobUrl = URL.createObjectURL(await res.blob());
    const frame = document.createElement("iframe");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
    frame.onload = () => {
      setTimeout(() => {
        try { frame.contentWindow.focus(); frame.contentWindow.print(); }
        catch (e) { window.open(blobUrl, "_blank"); }
        setTimeout(() => { frame.remove(); URL.revokeObjectURL(blobUrl); }, 60000);
      }, 300);
    };
    frame.src = blobUrl;
    document.body.appendChild(frame);
  } catch (e) {
    showNotice("Could not load the file for printing — it opened in a new tab instead. Print from there.");
    window.open(url, "_blank", "noopener");
  }
}

// The server's script signature: the signer's name in Dancing Script (already
// loaded by the app), drawn the same way every time and sent with Sign.
async function scriptSignaturePng(name) {
  const text = String(name || "").trim();
  if (!text || typeof document === "undefined") return null;
  try {
    if (document.fonts && document.fonts.load) await document.fonts.load("700 72px 'Dancing Script'");
    const c = document.createElement("canvas");
    const ctx = c.getContext("2d");
    ctx.font = "700 72px 'Dancing Script'";
    const w = Math.ceil(ctx.measureText(text).width) + 24;
    c.width = w; c.height = 110;
    const g = c.getContext("2d");
    g.font = "700 72px 'Dancing Script'";
    g.fillStyle = "#0b1f4a";
    g.textBaseline = "alphabetic";
    g.fillText(text, 12, 80);
    return c.toDataURL("image/png");
  } catch (e) { return null; }
}

function ZipChecker({ onOrderClick, availability }) {
  const [zip, setZip] = useState("");
  const [tier, setTier] = useState("standard");
  const [docType, setDocType] = useState("");
  const [result, setResult] = useState(null);
  // Ex parte is a HEARING STATUS (reused from the shared pricing model), asked
  // ONLY for Family Law on this first pricing page. It restricts the service
  // level to Rush / Same-Day and is carried into the order prefill. It is never
  // a document type.
  const [exParte, setExParte] = useState(false);

  const FAMILY_LAW = "Family Law (Divorce/Custody)"; // stored value/key — unchanged
  const isFamilyLaw = docType === FAMILY_LAW;
  // Non-empty only for an ex-parte + Standard combination (Standard is refused,
  // never silently upgraded).
  const tierBlocked = tierRestrictionMessage(tier, exParte, docType);

  const tierMeta = {
    standard: { color: "var(--green)", badge: "Most Popular", icon: "📋" },
    rush:     { color: "var(--amber)", badge: "Rush",         icon: "⚡" },
    sameday:  { color: "var(--red)",   badge: "Emergency",    icon: "🚨" },
  };

  function check() {
    const z = zip.trim();
    const data = ZIP_DATA[z];
    if (data) {
      setResult({ found: true, ...data, zip: z, tier, price: calcPrice(data, tier) });
    } else {
      setResult({ found: false });
    }
  }

  function changeTier(t) {
    setTier(t);
    if (result && result.found) {
      const data = ZIP_DATA[result.zip];
      setResult(r => ({ ...r, tier: t, price: calcPrice(data, t) }));
    }
  }

  return (
    <div className="zip-checker">
      <h3>📍 Select Service Level & Check Your ZIP</h3>

      {/* A review-required document type ("Other") is never quoted here: the
          documents have to be read first. No tier price and no automatic quote
          is shown, and the customer is pointed at the review-request flow. */}
      {requiresReview(docType) && (
        <div data-testid="calc-review-required" style={{
          margin: "0.35rem 0", padding: "0.55rem 0.7rem", borderRadius: 6,
          border: "2px solid var(--gold)", background: "#FFFBEB",
          fontSize: "0.8rem", fontWeight: 700, color: "var(--navy)", textAlign: "center", lineHeight: 1.35,
        }}>
          {REVIEW_REQUIRED_MESSAGE}
          <div style={{ fontWeight: 500, fontSize: "0.75rem", marginTop: "0.2rem" }}>
            Start an order and upload your documents — we'll confirm the price and send a secure payment link.
          </div>
        </div>
      )}
      {/* Pricing tier cards — pick one */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.3rem", marginBottom: "0.15rem" }}>
        {Object.entries(PRICING).map(([key, val]) => {
          const meta = tierMeta[key];
          const selected = tier === key;
          // An ex parte hearing is heard before Standard's 48-hour attempt
          // intervals could reach the party, so Standard is made unavailable —
          // never silently upgraded.
          const tierOk = tierAllowedForExParte(key, exParte, docType);
          return (
            <button key={key} data-testid={`calc-tier-${key}`} disabled={!tierOk}
              onClick={() => { if (tierOk) changeTier(key); }} style={{
                            padding: "0.28rem 0.3rem", borderRadius: 6,
              border: `2px solid ${selected ? meta.color : "var(--light)"}`,
              background: selected ? "var(--navy)" : "var(--white)",
              cursor: tierOk ? "pointer" : "not-allowed",
              opacity: tierOk ? 1 : 0.45,
              textAlign: "center",
              transition: "all 0.15s",
              outline: "none",
            }}>
                              <div style={{ fontSize: "1.2rem", marginBottom: "0.03rem" }}>{meta.icon}</div>
              <div style={{
                fontFamily: "Montserrat, sans-serif", fontWeight: 800,
                fontSize: "1.1rem", color: selected ? meta.color : "var(--navy)",
                lineHeight: 1,
              }}>
                {/* A review-required document type shows NO figure — there is no
                    automatic price for it, and the message below explains why. */}
                {!tierOk
                  ? "—"
                  : requiresReview(docType)
                  ? "—"
                  : result && result.found
                    ? `$${calcPrice(ZIP_DATA[result.zip], key) + (DOC_SURCHARGES[docType]?.surcharge || 0)}`
                    : "—"}
              </div>
              <div style={{
                fontWeight: 700, fontSize: "0.75rem",
                color: selected ? "var(--white)" : "var(--navy)",
              marginTop: "0.04rem", textTransform: "uppercase", letterSpacing: "0.03em", lineHeight: 1.1,
              }}>
                {val.label}
              </div>
            <div style={{ fontSize: "0.75rem", color: selected ? "rgba(255,255,255,0.6)" : "var(--gray)", marginTop: "0.05rem" }}>
              {val.desc}
              </div>
              {val.updates && (
                <div data-testid={`tier-updates-${key}`} style={{ fontSize: "0.75rem", fontWeight: 700, color: selected ? "var(--gold)" : "var(--navy)", marginTop: "0.05rem" }}>
                  {val.updates}
                </div>
              )}
              <div style={{
              marginTop: "0.1rem", fontSize: "0.75rem", fontWeight: 700, color: selected ? "rgba(255,255,255,0.5)" : "var(--gray)", fontStyle: "italic"
              }}>
                Enter ZIP to see price
              </div>
              {selected && (
                <div style={{
              marginTop: "0.06rem", fontSize: "0.75rem", fontWeight: 700, background: meta.color, color: "var(--white)", borderRadius: 2, padding: "0.06rem 0.3rem", display: "inline-block",
                  letterSpacing: "0.06em", textTransform: "uppercase"
                }}>
                  ✓ Selected
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Ex parte + Standard is refused here, never converted to a paid tier. */}
      {tierBlocked && (
        <div data-testid="calc-tier-restriction" style={{
          margin: "0.35rem 0", padding: "0.5rem 0.7rem", borderRadius: 6,
          border: "2px solid var(--red)", background: "rgba(214,40,40,0.06)",
          fontSize: "0.8rem", fontWeight: 700, color: "var(--navy)", textAlign: "center", lineHeight: 1.35,
        }}>{tierBlocked}</div>
      )}

      {/* Same-Day availability prompt — attorneys call to confirm a server is free
          before ordering. Shown only for the Same-Day tier; does NOT change pricing
          or Rush/Standard messaging. Tap-to-call on mobile. */}
      {tier === "sameday" && (
        <div data-testid="sameday-call-prompt" style={{
          margin: "0.35rem 0 0.15rem", padding: "0.5rem 0.7rem", borderRadius: 6,
          border: "2px solid var(--red)", background: "rgba(214,40,40,0.06)",
          fontSize: "0.8rem", fontWeight: 700, color: "var(--navy)", textAlign: "center", lineHeight: 1.35,
        }}>
          🚨 Need same-day service? Call to confirm availability:{" "}
          <a href={`tel:${BUSINESS_PHONE_TEL}`} style={{ color: "var(--red)", fontWeight: 800, whiteSpace: "nowrap", textDecoration: "underline" }}>
            {BUSINESS_PHONE_DISPLAY}
          </a>
        </div>
      )}

      {/* Document type selector */} <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}> <div style={{ flex: "1 1 45%", minWidth: 140 }}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--gray)", marginBottom: "0.1rem", marginTop: "0.15rem" }}>Document Type
      </div>
      <select
        value={docType}
        data-testid="calc-doc-type"
        onChange={e => { const v = e.target.value; setDocType(v); if (v !== FAMILY_LAW) setExParte(false); }}
        style={{
          width: "100%", border: "2px solid var(--light)", borderRadius: 4,
                    padding: "0.32rem 0.9rem", fontFamily: "Inter, sans-serif", fontSize: "0.9rem", color: "var(--navy)", outline: "none", marginBottom: "0.15rem",
          background: "var(--white)", cursor: "pointer"
        }}
      >
        <option value="">Select document type…</option>
        {Object.entries(DOC_SURCHARGES).map(([type]) => (
          <option key={type} value={type}>{type === FAMILY_LAW ? "Family Law" : type}</option>
        ))}
      </select>
      </div>
      {/* Family Law only: ex parte hearing status. Reuses the shared exParte
          model — it is never stored as a document type. */}
      {isFamilyLaw && (
        <div className="field" data-testid="calc-ex-parte" style={{ flex: "1 1 100%" }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--gray)", marginBottom: "0.1rem", marginTop: "0.15rem" }}>
            Is this an Ex Parte matter?
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {[["no", false], ["yes", true]].map(([label, value]) => (
              <button key={label} type="button" data-testid={`calc-ex-parte-${label}`}
                onClick={() => setExParte(value)} aria-pressed={exParte === value}
                style={{
                  flex: "1 1 0", padding: "0.4rem 0.5rem", borderRadius: 6, cursor: "pointer",
                  textAlign: "center", background: "var(--white)", color: "var(--navy)",
                  fontFamily: "Inter, sans-serif", fontWeight: 700, textTransform: "capitalize",
                  border: exParte === value ? "3px solid var(--gold)" : "1px solid #d9d9d9",
                }}>{label}</button>
            ))}
          </div>
          <div style={{ fontSize: "0.75rem", color: "var(--gray)", marginTop: "0.3rem" }}>
            Ex parte hearings require Rush or Same-Day service. There is no extra charge for this.
          </div>
        </div>
      )}
      {/* ZIP input */} <div style={{ flex: "1 1 45%", minWidth: 140 }}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--gray)", marginBottom: "0.2rem" }}>
          Enter Service ZIP Code
      </div>
      <input
        className="zip-input"
        placeholder="e.g. 92882"
        value={zip}
        maxLength={5}
                    style={{ width: "100%", marginBottom: "0.1rem" }}
        onChange={e => { setZip(e.target.value.replace(/\D/g, "")); setResult(null); }}
        onKeyDown={e => e.key === "Enter" && check()}
      />
      </div> </div> <button className="zip-submit" onClick={check} style={{ width: "100%", padding: "0.4rem" }}>
        Check My ZIP →
      </button>

      {/* Result */}
      {result && (
                <div className={`zip-result ${result.found ? "found" : "not-found"}`} style={{ marginTop: "0.2rem" }}>
          {result.found ? (
            <>
              <div style={{ display: "flex", gap: "1rem", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}> <div style={{ flex: "1 1 0", minWidth: 0 }}> <div className="city-name">✓ {result.city} — We serve this area!</div>
              {(() => {
                if (tierBlocked) {
                  return <div className="price-note" data-testid="calc-price-blocked" style={{ color: "var(--red)", fontWeight: 700 }}>{tierBlocked}</div>;
                }
                const docFee = DOC_SURCHARGES[docType]?.surcharge || 0;
                const total = result.price + docFee;
                return (
                  <>
                                  <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", margin: "0.06rem 0" }}>
                      <div className="price-display">${total}</div>
                      <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--navy)" }}>
                        {PRICING[tier].label}
                      </div>
                    </div>
                    <div className="price-note">{PRICING[tier].desc}{PRICING[tier].updates ? ` · ${PRICING[tier].updates}` : ""}</div>
                  </>
                );
              })()}
              {availability === "closed" ? (
                <div style={{ marginTop: "0.75rem", background: "#FEF2F2", border: "1px solid var(--red)", borderRadius: 4, padding: "0.75rem", fontSize: "0.82rem", color: "var(--red)", fontWeight: 600 }}>
                  Not accepting new orders at this time. Please check back soon or call us directly.
                </div>
              ) : (
                <>
                  {availability === "limited" && (
                    <div style={{ marginTop: "0.75rem", background: "#FEF9EE", border: "1px solid var(--amber)", borderRadius: 4, padding: "0.6rem 0.75rem", fontSize: "0.78rem", color: "#92400E", fontWeight: 600 }}>
                      High demand - service may be delayed beyond standard timeframes.
                    </div>
                  )}
                          </>
              )}
              </div>
              {availability !== "closed" && (
                <div style={{ flexShrink: 0 }}>
                  <button className="btn-primary" data-testid="calc-place-order" disabled={!!tierBlocked} onClick={() => onOrderClick({ ...result, selectedDocType: docType, exParte })}>Place Order</button>
                </div>
              )}
            </div>
            </>
          ) : (
            <div style={{ color: "var(--red)" }}>❌ ZIP code <strong>{zip}</strong> is outside our current service area. Contact us to discuss.</div>
          )}
        </div>
      )}
    </div>
  );
}

function OrderForm({ prefillZip }) {
  const { user, accessToken } = useAuth();
  // Add-ons (e.g. Priority) are offered only AFTER the customer picks a service
  // level on this form — never listed up front alongside the prices.
  const [tierPicked, setTierPicked] = useState(false);
  const [form, setForm] = useState({
    clientName: "", clientEmail: "", clientPhone: "",
    plaintiff: "", defendantName: "", defendantAddress: "", defendantZip: prefillZip?.zip || "",
    subjectAge: "", gatedAccess: false, accessInstructions: "",
    caseNumber: "", court: "", courtAddress: "", docType: prefillZip?.selectedDocType || "", docSubtype: "", notes: "", payment: "",
    tier: PRICING[prefillZip?.tier] ? prefillZip.tier : "standard",
    // "weekend" | "holiday" | null — mutually exclusive, rush/sameday only.
    serviceAddon: null,
    // Ex parte is the HEARING's urgency, carried alongside docType — never
    // instead of it. Its only effect is to restrict the service level.
    // A "Yes" from the first pricing page arrives pre-selected; the question
    // is still shown here so the customer re-confirms it. Default: No.
    exParte: prefillZip?.exParte === true,
  });
  // Prefill name/email from the signed-in account, without overwriting anything
  // the customer has already typed.
  useEffect(() => {
    if (!user) return;
    const fullName = (user.user_metadata && (user.user_metadata.full_name || user.user_metadata.name)) || "";
    setForm((f) => ({
      ...f,
      clientName: f.clientName || fullName,
      clientEmail: f.clientEmail || user.email || "",
    }));
  }, [user]);
  const [submitted, setSubmitted] = useState(false);
  const [zipInfo, setZipInfo] = useState(prefillZip || null);
  const [files, setFiles] = useState([]);
  // Optional photo(s) of the person to serve — uploaded with the order, kept
  // apart from the court papers (never page-counted, read, or form-classified).
  const [photos, setPhotos] = useState([]);
  const [photoRejections, setPhotoRejections] = useState([]);
  const [fileRejections, setFileRejections] = useState([]);
  // Per-file upload state, keyed by fileKey: waiting | uploading | uploaded | failed.
  // A ref mirrors it because the upload loop reads the current state between
  // awaits, where a captured render value would be stale.
  const [uploadStates, setUploadStates] = useState({});
  // LARGE PACKET: page totals reported by the server as each file finalizes
  // (counted from storage, never from the browser). Pages over 50 add $0.50.
  const [packet, setPacket] = useState({ totalPages: 0, unknown: [] });
  const packetRef = useRef({ totalPages: 0, unknown: [] });
  const pageFeeAckRef = useRef(null);
  const [pageFeeNotice, setPageFeeNotice] = useState("");
  const uploadStatesRef = useRef({});
  useEffect(() => { uploadStatesRef.current = uploadStates; }, [uploadStates]);
  // The staging row, kept across attempts so a retry never re-uploads a file
  // that already arrived.
  const stagingRef = useRef(null);
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  // True once the customer has picked a verified suggestion; suppresses the
  // debounced lookup so the dropdown does not reopen until they edit again.
  const addrAcceptedRef = useRef(false);
  const [agreed, setAgreed] = useState(false);
  // Opt-in only, and off by default: a card is saved because the customer asked.
  const [saveCard, setSaveCard] = useState(false);
  // Inline field-level validation errors: { fieldName: message }
  const [errors, setErrors] = useState({});
  const fieldRefs = useRef({});
  const agreementBoxRef = useRef(null);
  const agreementTriggerRef = useRef(null);
  const [showAgreement, setShowAgreement] = useState(false);
  // Document-reading UI: "" | "uploading" | "reading" | "found" | "partial" | "failed"
  const [aiStage, setAiStage] = useState("");
  // One reader result per FILE, kept across selections so a later read never
  // erases an earlier file's outcome.
  const [fileReads, setFileReads] = useState([]);
  // Court text the reader found but could not map to a dropdown option —
  // preserved and shown rather than discarded.
  const [extractedCourtText, setExtractedCourtText] = useState("");
  // Fields the customer must confirm before paying (read or conflicting values).
  const [needsConfirm, setNeedsConfirm] = useState({});
  const [confirmedFields, setConfirmedFields] = useState({});
  const [extraction, setExtraction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Friendly failure state for a slow/failed submission: { stage } or null.
  // Entered form values are always preserved — Retry just resubmits.
  const [submitError, setSubmitError] = useState(null);
  // Set after a successful "Submit for Review" so the customer sees an
  // acknowledgement — never described as paid or active.
  const [reviewResult, setReviewResult] = useState(null);
  const [affirmBusy, setAffirmBusy] = useState(false);
  const [affirmMsg, setAffirmMsg] = useState("");
  // The customer affirms the identified type and price; only then does the
  // server create a checkout session (via the existing shared Stripe helper).
  // Classification alone never charges anything.
  async function affirmClassification() {
    if (affirmBusy || !reviewResult || !reviewResult.classified) return;
    setAffirmBusy(true); setAffirmMsg("");
    try {
      const res = await fetch("/.netlify/functions/submit-review-request", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "affirm", orderId: reviewResult.orderId, uploadToken: reviewResult.uploadToken,
          affirmedDocType: reviewResult.docType, affirmedAmountCents: reviewResult.amountDueCents,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.ok || !d.url) {
        setAffirmMsg(d.error || `Could not start secure payment (HTTP ${res.status}).`);
        if (d.reviewRequired) setReviewResult(r => ({ ...r, classified: false, reviewRequired: true }));
        return;
      }
      window.location.href = d.url;
    } catch (e) { setAffirmMsg("Could not start secure payment. Please try again."); }
    finally { setAffirmBusy(false); }
  }
  const [jobNumber, setJobNumber] = useState(null);
    const [paymentNotice, setPaymentNotice] = useState(null);

    useEffect(() => {
          const qs = new URLSearchParams(window.location.search);
          const paymentParam = qs.get("payment");
          const sessionId = qs.get("session_id");

          if (paymentParam === "success" && sessionId) {
                  setSubmitting(true);
                  // 202 = the order is being finalized by the payment webhook at
                  // this moment; ask again shortly instead of reporting a failure.
                  const confirmOnce = (left) => fetch(`/.netlify/functions/confirm-payment?session_id=${encodeURIComponent(sessionId)}`)
                    .then(async (res) => {
                      if (res.status === 202 && left > 0) {
                        await new Promise((r) => setTimeout(r, 2000));
                        return confirmOnce(left - 1);
                      }
                      return { ok: res.ok, data: await res.json() };
                    });
                  confirmOnce(5)
                    .then(({ ok, data }) => {
                                if (ok && data && data.success) {
                                              setJobNumber(data.jobNumber);
                                              setSubmitted(true);
                                } else {
                                              setPaymentNotice("We could not confirm your payment. Please contact us directly before resubmitting.");
                                }
                    })
                    .catch(() => {
                                setPaymentNotice("We could not confirm your payment. Please contact us directly before resubmitting.");
                    })
                    .finally(() => setSubmitting(false));
          } else if (paymentParam === "cancelled") {
                  setPaymentNotice("Payment was cancelled. Your order was not submitted.");
          }
    }, []);
  

  // Read one file via the server-side Azure Document Intelligence function.
  async function readOneFile(file) {
    try {
      const dataBase64 = await fileToBase64(file);
      const res = await fetch("/.netlify/functions/read-document", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename: file.name, mimeType: file.type, dataBase64 }),
      });
      if (!res.ok) return { file: file.name, ok: false };
      const data = await res.json();
      return { file: file.name, ok: !!data.ok, fields: data.fields || null };
    } catch (e) {
      return { file: file.name, ok: false };
    }
  }

  // Populate empty form fields from merged extraction results. Never overwrites
  // a value the customer already entered, and never auto-fills low-confidence
  // values (those are offered as dismissible suggestions in the review panel).
  // Apply packet-level extraction to the form.
  //
  // STALE-FORM RACE: this used to receive the `form` snapshot captured when
  // reading STARTED, so anything the customer typed during the read was compared
  // against an out-of-date copy and could be overwritten. It now runs inside a
  // single functional setForm update, so `latest` is the current state at the
  // moment of application. The rule is simple and absolute: a reader value may
  // only fill a field that is STILL EMPTY right now — a customer edit always
  // wins, whenever it happened.
  //
  // Returns nothing; the confirmation flags it raises are stored separately.
  function applyMergedToForm(merged) {
    const needsConfirm = {};
    let unmatchedCourt = "";
    setForm((latest) => {
      const next = { ...latest };
      const fill = (formKey, value) => {
        if (value == null || String(value).trim() === "") return;
        if (String(latest[formKey] || "").trim() !== "") return; // customer wins
        next[formKey] = String(value).trim();
      };
      // Straight text fields, packet-merged (never "the first file").
      const textMap = {
        plaintiff: "plaintiff",
        defendant: "defendantName",
        caseNumber: "caseNumber",
        courtAddress: "courtAddress",
      };
      for (const [key, formKey] of Object.entries(textMap)) {
        const m = merged[key];
        if (!m || !m.best || m.best.level === "low") continue;
        // A genuine conflict is never auto-applied — the customer picks.
        if (m.conflict) { needsConfirm[formKey] = true; continue; }
        fill(formKey, m.best.value);
      }
      // Court: map through the alias-aware matcher. An unmatched reading is
      // PRESERVED and surfaced instead of being discarded.
      const cm = merged.court;
      if (cm && cm.best && !String(latest.court || "").trim()) {
        if (cm.conflict) { needsConfirm.court = true; }
        else {
          const decision = COURT_MATCH.matchCourt(cm.best.value);
          if (decision.matched) {
            next.court = decision.value;
            // A county-only reading is a weaker match — ask for confirmation.
            if (decision.confidence < 0.9) needsConfirm.court = true;
          } else {
            unmatchedCourt = decision.extracted;
            needsConfirm.court = true;
          }
        }
      }
      // Document type + subtype.
      const dm = merged.docType;
      if (dm && dm.best && dm.best.level !== "low" && !String(latest.docType || "").trim()) {
        if (dm.conflict) needsConfirm.docType = true;
        else {
          const val = matchDocTypeValue(dm.best.value);
          if (val) { next.docType = val; needsConfirm.docType = true; } // always confirm a read type
        }
      }
      const sm = merged.docSubtype;
      if (sm && sm.best && sm.best.level !== "low" && !String(latest.docSubtype || "").trim()) {
        if (sm.conflict) needsConfirm.docSubtype = true;
        else { next.docSubtype = sm.best.value; needsConfirm.docSubtype = true; }
      }
      return next;
    });
    setExtractedCourtText(unmatchedCourt);
    setNeedsConfirm(needsConfirm);
  }

  async function handleDocUpload(uploadedFiles) {
    const all = Array.from(uploadedFiles || []);
    // EVERY file gets a result, including the ones that cannot be read at all —
    // an unsupported file no longer disappears just because another succeeded.
    const unsupported = all.filter((f) => !isSupportedDoc(f));
    const supported = all.filter(isSupportedDoc).slice(0, MAX_AUTOREAD_FILES);
    const overflow = all.filter(isSupportedDoc).slice(MAX_AUTOREAD_FILES);

    setAiStage(supported.length ? "reading" : "failed");
    const results = supported.length ? await runPool(supported, AUTOREAD_CONCURRENCY, readOneFile) : [];

    // Per-file status, keyed by filename, MERGED with anything already read so a
    // later selection never erases an earlier file's result.
    const statuses = []
      .concat(unsupported.map((f) => ({ file: f.name, ok: false, reason: "unsupported" })))
      .concat(overflow.map((f) => ({ file: f.name, ok: false, reason: "not_analyzed" })))
      .concat(results.map((r) => ({
        file: (r && r.file) || "(unnamed)",
        ok: !!(r && r.ok && r.fields),
        reason: r && r.ok && r.fields ? "ok" : ((r && r.reason) || "unreadable"),
        foundCount: r && r.fields ? Object.keys(r.fields).filter((k) => r.fields[k] && r.fields[k].found).length : 0,
      })));
    setFileReads((prev) => {
      const byName = new Map(prev.map((s) => [s.file, s]));
      for (const s of statuses) byName.set(s.file, s);
      return Array.from(byName.values());
    });

    const usable = results.filter((r) => r && r.ok && r.fields);
    if (!usable.length) {
      setAiStage("failed");
      return;
    }

    // Merge across the COMPLETE packet — every readable file contributes.
    const merged = mergeExtractions(usable.map((r) => ({ file: r.file, fields: r.fields })));
    applyMergedToForm(merged);
    setExtraction(merged);

    const conflicted = Object.keys(merged).some((k) => merged[k] && merged[k].conflict);
    const resolved = REQUIRED_EXTRACT_KEYS.every(
      (k) => merged[k] && merged[k].best && merged[k].best.level !== "low"
    );
    setAiStage(conflicted ? "conflict" : resolved ? "found" : "partial");
  }

  // Render one reviewable case-info field with confidence highlighting,
  // "please verify/complete" hints, conflict notes, and suggestion chips.
  function renderReviewField(key, label, formKey) {
    const m = extraction ? extraction[key] : null;
    const value = form[formKey] || "";
    const hasValue = String(value).trim() !== "";
    const level = m && m.best ? m.best.level : null;

    let cls = "";
    let hint = null;
    if (!hasValue) {
      cls = "field-flag-low";
      hint = <span className="review-hint low">Please complete</span>;
    } else if (level === "medium") {
      cls = "field-flag-medium";
      hint = <span className="review-hint medium">Please verify</span>;
    }

    const candidates = m && m.candidates ? m.candidates : [];
    const suggestions = candidates.filter(
      (c) => c.value.toLowerCase() !== String(value).toLowerCase()
    );

    return (
      <div className="field" key={key}>
        <label>{label}</label>
        <input className={cls} value={value} onChange={(e) => set(formKey, e.target.value)} />
        {hint}
        {m && m.conflict && (
          <span className="conflict-note">Multiple possible values found — please pick the correct one:</span>
        )}
        {suggestions.map((s, i) => (
          <span
            key={i}
            className="suggestion-chip"
            onClick={() => set(formKey, s.value)}
            title={s.file ? `From ${s.file}` : ""}
          >
            Use: {s.value}
          </span>
        ))}
      </div>
    );
  }

  // Read-only reference block: attorney/firm, detected court wording, detected
  // document names. Shown for the customer's reference only — not saved.
  function renderReference() {
    if (!extraction) return null;
    const refs = [];
    if (extraction.attorney && extraction.attorney.best) refs.push(["Attorney / Firm", extraction.attorney.best.value]);
    if (extraction.court && extraction.court.best) refs.push(["Detected court", extraction.court.best.value]);
    if (extraction.docType && extraction.docType.best) refs.push(["Detected document", extraction.docType.best.value]);
    if (!refs.length) return null;
    return (
      <div className="reference-list">
        {refs.map(([k, v], i) => (
          <div key={i}><strong>{k}:</strong> {v}</div>
        ))}
        <div style={{ fontStyle: "italic", marginTop: "0.25rem" }}>Reference only — not saved.</div>
      </div>
    );
  }

  // Google Places address autocomplete (approved provider). When the key is not
  // configured this stays empty and the customer types the address manually —
  // no other suggestion provider is called.
  const placesRef = useRef(null);
  const [placesReady, setPlacesReady] = useState(false);
  useEffect(() => {
    if (!isGooglePlacesConfigured()) return;
    let alive = true;
    loadGooglePlaces().then((google) => {
      if (!alive) return;
      placesRef.current = {
        autocomplete: new google.maps.places.AutocompleteService(),
        places: new google.maps.places.PlacesService(document.createElement("div")),
        sessionToken: new google.maps.places.AutocompleteSessionToken(),
      };
      setPlacesReady(true);
    }).catch(() => { if (alive) setPlacesReady(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const addr = form.defendantAddress;
    // A verified suggestion was just accepted — don't re-open the dropdown.
    if (addrAcceptedRef.current) { setAddrSuggestions([]); return; }
    if (!placesReady || !placesRef.current || addr.length < 4) { setAddrSuggestions([]); return; }
    const timer = setTimeout(() => {
      placesRef.current.autocomplete.getPlacePredictions(
        { input: addr, componentRestrictions: { country: "us" }, types: ["address"], sessionToken: placesRef.current.sessionToken },
        (res, status) => {
          const ok = window.google && status === window.google.maps.places.PlacesServiceStatus.OK;
          setAddrSuggestions(ok && Array.isArray(res) ? res.slice(0, 5) : []);
        }
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [form.defendantAddress, placesReady]);

  // Pull the Place details and fill address + ZIP from Google's own components.
  function acceptPlacePrediction(prediction) {
    addrAcceptedRef.current = true;
    setAddrSuggestions([]);
    if (!placesRef.current) return;
    placesRef.current.places.getDetails(
      { placeId: prediction.place_id, fields: ["address_components", "formatted_address"], sessionToken: placesRef.current.sessionToken },
      (place, status) => {
        const ok = window.google && status === window.google.maps.places.PlacesServiceStatus.OK && place;
        const a = ok ? placeToAddress(place) : { address: prediction.description || "", zip: "" };
        set("defendantAddress", a.address);
        if (a.zip) {
          set("defendantZip", a.zip);
          const zd = ZIP_DATA[a.zip];
          setZipInfo(zd ? { ...zd, zip: a.zip, price: calcPrice(zd, form.tier) } : null);
        }
        if (window.google) placesRef.current.sessionToken = new window.google.maps.places.AutocompleteSessionToken();
      }
    );
  }

  // Setting a field clears that field's inline error as soon as it's corrected.
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); clearError(k); }

  function clearError(name) {
    setErrors(e => {
      if (!name || !e[name]) return e;
      const next = { ...e };
      delete next[name];
      return next;
    });
  }

  // Scroll to + focus the first invalid field.
  function focusField(name) {
    const el = name && fieldRefs.current[name];
    if (!el) return;
    try {
      if (el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function") el.focus({ preventScroll: true });
    } catch (e) { /* non-fatal */ }
  }

  function fieldError(name) {
    return errors[name] ? <span className="review-hint low" role="alert">{errors[name]}</span> : null;
  }
  function errClass(name) { return errors[name] ? "field-flag-low" : ""; }

  // ── Service Agreement modal: focus in on open, ESC to close, focus trap,
  //    and focus returned to the control that opened it. ──
  function openAgreement(trigger) {
    agreementTriggerRef.current = trigger || null;
    setShowAgreement(true);
  }
  function closeAgreement() {
    setShowAgreement(false);
    const t = agreementTriggerRef.current;
    if (t && typeof t.focus === "function") {
      try { t.focus(); } catch (e) { /* non-fatal */ }
    }
  }
  useEffect(() => {
    if (!showAgreement) return;
    const box = agreementBoxRef.current;
    if (box && typeof box.focus === "function") {
      try { box.focus(); } catch (e) { /* non-fatal */ }
    }
  }, [showAgreement]);

  function onAgreementKeyDown(e) {
    if (e.key === "Escape") { e.stopPropagation(); closeAgreement(); return; }
    if (e.key !== "Tab") return;
    // Keep keyboard focus inside the dialog while it is open.
    const box = agreementBoxRef.current;
    if (!box) return;
    const focusables = box.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function checkZip(z) {
    const d = ZIP_DATA[z];
    setZipInfo(d ? { ...d, zip: z, price: calcPrice(d, form.tier) } : null);
  }

  // Changing service level re-prices the ZIP and clears any add-on that is no
  // longer allowed (Standard never carries one).
  // Ex parte changes ONE thing: which service levels are available. Everything
  // else the customer has entered — document type, ZIP, address, selected
  // files, every other field — is untouched, because this only writes exParte.
  //
  // If Standard was already selected it STAYS selected and simply loses its
  // price: the tile is disabled, the total goes blank and the restriction
  // message appears. It is deliberately not switched to Rush for them — that
  // would pick a more expensive service on the customer's behalf.
  function setExParte(value) {
    setForm((f) => ({ ...f, exParte: value === true }));
  }

  function selectTier(tier) {
    setTierPicked(true);
    setForm(f => ({
      ...f,
      tier,
      serviceAddon: normalizeAddon(f.serviceAddon, tier),
    }));
    const d = ZIP_DATA[form.defendantZip];
    setZipInfo(d ? { ...d, zip: form.defendantZip, price: calcPrice(d, tier) } : null);
  }

  // Add-ons are mutually exclusive: selecting one replaces the other; clicking
  // the selected one clears it.
  function toggleAddon(key) {
    if (!addonAllowedForTier(form.tier, key)) return;
    setForm(f => ({ ...f, serviceAddon: f.serviceAddon === key ? null : key }));
  }

  // Single source of truth for the displayed total — always recalculated from
  // the authoritative pricing function (never from a ?price= URL parameter).
  const baseTotal = orderTotal(
    form.defendantZip,
    form.tier,
    form.docType,
    normalizeAddon(form.serviceAddon, form.tier),
    // Restricts the level only — an ex parte order costs what the same
    // documents cost without it.
    form.exParte
  );
  // + the large-packet fee once the server has counted the uploaded pages.
  const pageFee = packetPageFee(packet.totalPages);
  const displayedTotal = baseTotal == null ? null : Math.round((baseTotal + pageFee) * 100) / 100;
  // "Other" (and any blank/unknown type) has no automatic price: the documents
  // must be reviewed first. In that mode the form shows the review message
  // instead of a total, hides payment entirely, never calls
  // create-checkout-session, and submits for review instead.
  const reviewRequired = requiresReview(form.docType);
  // A service level this document type may not use. Non-empty means there is no
  // payable quote: the total is already null, and submission is blocked with
  // this exact wording rather than the level being changed for the customer.
  const tierBlocked = tierRestrictionMessage(form.tier, form.exParte, form.docType);

  // Application-level ceiling for one submission attempt. If the server hasn't
  // answered by then we stop the spinner and show the friendly Retry state
  // instead of leaving the page looking frozen.
  const SUBMIT_TIMEOUT_MS = 45000;

  // ── Document packet selection ────────────────────────────────────────────
  // A stable identity for one chosen file. name+size+lastModified is what makes
  // "the same file picked twice" detectable without reading its contents.
  function fileKey(f) { return `${f.name}::${f.size}::${f.lastModified || 0}`; }
  function photoKey(f) { return `photo::${fileKey(f)}`; }
  function addPhotos(picked) {
    const rejects = [];
    setPhotos((prev) => {
      const next = prev.slice();
      for (const f of picked) {
        if (!SUBJECT_PHOTO.isPhotoFile(f.name, f.type)) { rejects.push(`"${f.name}" is not a photo (JPG, PNG or HEIC).`); continue; }
        if (f.size > UPLOAD_LIMITS.MAX_UPLOAD_BYTES) { rejects.push(`"${f.name}" is larger than ${UPLOAD_LIMITS.MAX_UPLOAD_MB} MB.`); continue; }
        if (next.some((p) => fileKey(p) === fileKey(f))) continue;
        if (next.length >= SUBJECT_PHOTO.MAX_SUBJECT_PHOTOS) { rejects.push(`Up to ${SUBJECT_PHOTO.MAX_SUBJECT_PHOTOS} photos can be attached.`); break; }
        next.push(f);
      }
      return next;
    });
    setPhotoRejections(rejects);
  }
  function removePhoto(target) {
    const k = photoKey(target);
    const wasUploaded = (uploadStatesRef.current[k] || {}).status === "uploaded";
    setPhotos((prev) => prev.filter((f) => photoKey(f) !== k));
    setUploadStates((prev) => { const next = { ...prev }; delete next[k]; return next; });
    if (wasUploaded && stagingRef.current) {
      fetch("/.netlify/functions/customer-upload", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "detach", stagingId: stagingRef.current.stagingId, uploadToken: stagingRef.current.uploadToken, clientToken: k }),
      }).catch(() => {});
    }
  }

  // ADD to the packet rather than replace it. Selecting files a second time used
  // to overwrite the first selection entirely. Over-limit and duplicate files are
  // reported by name instead of being dropped silently.
  function addFiles(picked) {
    const incoming = Array.from(picked || []);
    if (!incoming.length) return;
    const rejects = [];
    setFiles((prev) => {
      const seen = new Set(prev.map(fileKey));
      const next = prev.slice();
      for (const f of incoming) {
        // 25 MB PER FILE, from the shared constant — never a packet total.
        if (f.size > UPLOAD_LIMITS.MAX_UPLOAD_BYTES) { rejects.push(UPLOAD_LIMITS.oversizeMessage(f.name, f.size)); continue; }
        if (!UPLOAD_LIMITS.fileTypeAllowed(f.name, f.type)) { rejects.push(`"${f.name}" is not a supported file type (${UPLOAD_LIMITS.ALLOWED_EXT.join(", ")}).`); continue; }
        const k = fileKey(f);
        if (seen.has(k)) { rejects.push(`"${f.name}" is already attached.`); continue; }
        seen.add(k);
        next.push(f);
      }
      if (next.length) clearError("files");
      // Read only the newly accepted files; earlier results are kept.
      const added = next.slice(prev.length);
      if (added.length) handleDocUpload(added);
      return next;
    });
    setFileRejections(rejects);
  }

  // Remove ONE file without disturbing the rest of the packet.
  function removeFile(target) {
    const k = fileKey(target);
    const wasUploaded = (uploadStatesRef.current[k] || {}).status === "uploaded";
    setFiles((prev) => prev.filter((f) => fileKey(f) !== k));
    setUploadStates((prev) => { const next = { ...prev }; delete next[k]; return next; });
    setFileRejections([]);
    // If it had already reached staging, take it off there too — otherwise it
    // would still be promoted onto the paid order.
    if (wasUploaded && stagingRef.current) {
      fetch("/.netlify/functions/customer-upload", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "detach", stagingId: stagingRef.current.stagingId,
          uploadToken: stagingRef.current.uploadToken, clientToken: k,
        }),
      }).catch(() => { /* the checkout gate still counts what actually staged */ });
    }
  }

  // ── Per-file upload state ────────────────────────────────────────────────
  // Uploading used to happen inside submit as one all-or-nothing loop: if the
  // fourth of five files failed, the whole submission was abandoned and the next
  // attempt staged again from scratch, re-uploading the three that had already
  // arrived. Now each file carries its own state and a retry touches only what
  // actually failed.
  const setFileState = (key, patch) =>
    setUploadStates((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  // Upload ONE file against an existing staging row. Returns true on success.
  // fileKey is the stable client token: the server derives the object path from
  // it, so a retry overwrites the same object rather than orphaning the first.
  async function uploadOneFile(f, staged, role) {
    const key = role === SUBJECT_PHOTO.SUBJECT_PHOTO ? photoKey(f) : fileKey(f);
    setFileState(key, { status: "uploading", error: null });
    try {
      const aRes = await fetch("/.netlify/functions/customer-upload", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "authorize", stagingId: staged.stagingId, uploadToken: staged.uploadToken,
          files: [{ name: f.name, type: f.type, size: f.size, clientToken: key }],
        }),
      });
      const a = await aRes.json().catch(() => ({}));
      const grant = a && Array.isArray(a.grants) ? a.grants[0] : null;
      if (!aRes.ok || !grant) {
        const why = (a && a.error) || (a && a.rejected && a.rejected[0] && a.rejected[0].reason) || `HTTP ${aRes.status}`;
        setFileState(key, { status: "failed", error: why });
        return false;
      }
      const put = await fetch(grant.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": grant.contentType, "x-upsert": grant.upsert ? "true" : "false" },
        body: f,
      });
      if (!put.ok) { setFileState(key, { status: "failed", error: `upload failed (HTTP ${put.status})` }); return false; }
      const finRes = await fetch("/.netlify/functions/customer-upload", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "finalize", stagingId: staged.stagingId, uploadToken: staged.uploadToken,
          path: grant.path, name: f.name, type: f.type, clientToken: key,
          ...(role ? { role } : {}),
        }),
      });
      const fin = await finRes.json().catch(() => ({}));
      if (!finRes.ok || !fin.ok) {
        setFileState(key, { status: "failed", error: (fin && fin.error) || `HTTP ${finRes.status}` });
        return false;
      }
      setFileState(key, { status: "uploaded", error: null, path: grant.path });
      if (fin.totalPages != null) {
        const p = { totalPages: Number(fin.totalPages) || 0, unknown: Array.isArray(fin.unknownPages) ? fin.unknownPages : [] };
        packetRef.current = p; setPacket(p);
      }
      return true;
    } catch (e) {
      setFileState(key, { status: "failed", error: "the upload did not complete" });
      return false;
    }
  }

  // Create the staging row once and keep it, so a retry reuses the SAME staging
  // row and the files that already arrived stay where they are.
  async function ensureStaging() {
    if (stagingRef.current) return stagingRef.current;
    const res = await fetch("/.netlify/functions/customer-upload", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: JSON.stringify({
        action: "stage", expectedFiles: files.length + photos.length,
        clientName: form.clientName, clientEmail: form.clientEmail, clientPhone: form.clientPhone,
        plaintiff: form.plaintiff, caseNumber: form.caseNumber, court: form.court, courtAddress: form.courtAddress,
        defendantName: form.defendantName, defendantAddress: form.defendantAddress, zip: form.defendantZip,
        subjectAge: form.subjectAge, gatedAccess: form.gatedAccess, accessInstructions: form.accessInstructions,
        docType: form.docType, docSubtype: form.docSubtype, serviceTier: form.tier, exParte: form.exParte,
        serviceAddon: normalizeAddon(form.serviceAddon, form.tier),
        neededByDate: form.neededByDate || "", specialInstructions: form.notes || "",
      }),
    });
    const staged = await res.json().catch(() => ({}));
    if (!res.ok || !staged.ok) throw new Error(staged.error || "Could not start your submission.");
    stagingRef.current = { stagingId: staged.stagingId, uploadToken: staged.uploadToken };
    return stagingRef.current;
  }

  // Upload every file that is not already uploaded. Returns the names that failed.
  async function uploadPendingFiles(staged) {
    const failed = [];
    for (const f of files) {
      const key = fileKey(f);
      if (uploadStatesRef.current[key] && uploadStatesRef.current[key].status === "uploaded") continue;
      const ok = await uploadOneFile(f, staged);
      if (!ok) failed.push(f.name);
    }
    for (const f of photos) {
      const key = photoKey(f);
      if (uploadStatesRef.current[key] && uploadStatesRef.current[key].status === "uploaded") continue;
      const ok = await uploadOneFile(f, staged, SUBJECT_PHOTO.SUBJECT_PHOTO);
      if (!ok) failed.push(f.name);
    }
    return failed;
  }

  // Retry ONE failed file. Everything already uploaded is left alone.
  async function retryFile(f) {
    if (!stagingRef.current) return;
    setSubmitError("");
    await uploadOneFile(f, stagingRef.current);
  }
  async function retryPhoto(f) {
    if (!stagingRef.current) return;
    setSubmitError("");
    await uploadOneFile(f, stagingRef.current, SUBJECT_PHOTO.SUBJECT_PHOTO);
  }

  // What, if anything, currently stops this order from going to payment.
  // Non-empty means the submit button is disabled.
  const uploadBlock = (() => {
    const all = files.map((f) => fileKey(f)).concat(photos.map((f) => photoKey(f)));
    const states = all.map((k) => (uploadStates[k] || {}).status || "waiting");
    if (states.some((st) => st === "uploading")) return "Please wait — your documents are still uploading.";
    const failed = files.filter((f) => (uploadStates[fileKey(f)] || {}).status === "failed")
      .concat(photos.filter((f) => (uploadStates[photoKey(f)] || {}).status === "failed"));
    if (failed.length) {
      return `${failed.map((f) => `"${f.name}"`).join(", ")} did not upload. Use Retry on those file(s) — nothing has been charged.`;
    }
    return "";
  })();

  // Submit a review request: create the UNPRICED pending-review order first, then
  // upload each document DIRECTLY to the private bucket with a short-lived,
  // order-scoped signed URL (no base64 in a function body). The order and any
  // documents that landed always survive — a failed file is reported, never
  // silently dropped, and never reported as a complete success.
  async function submitForReview() {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await fetch("/.netlify/functions/submit-review-request", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
        body: JSON.stringify({
          action: "create",
          clientName: form.clientName, clientEmail: form.clientEmail, clientPhone: form.clientPhone,
          plaintiff: form.plaintiff, caseNumber: form.caseNumber, court: form.court,
          personToServe: form.defendantName, serviceAddress: form.defendantAddress, zip: form.defendantZip,
          subjectAge: form.subjectAge, gatedAccess: form.gatedAccess, accessInstructions: form.accessInstructions,
          docType: form.docType, serviceTier: form.tier,
          neededByDate: form.neededByDate || "", specialInstructions: form.notes || "",
          agreedToTerms: !!agreed,
        }),
      });
      const c = await created.json().catch(() => ({}));
      if (!created.ok || !c.ok) {
        setSubmitError((c.errors && c.errors[0]) || c.error || "We could not submit your request. Please try again.");
        return;
      }

      // Upload each selected document, one at a time, directly to storage.
      const uploaded = [], failed = [];
      for (const f of files) {
        try {
          const aRes = await fetch("/.netlify/functions/submit-review-request", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "authorize", orderId: c.orderId, uploadToken: c.uploadToken, files: [{ name: f.name, type: f.type, size: f.size }] }),
          });
          const a = await aRes.json().catch(() => ({}));
          const grant = a && Array.isArray(a.grants) ? a.grants[0] : null;
          if (!aRes.ok || !grant) {
            failed.push({ name: f.name, reason: (a && a.error) || (a && a.rejected && a.rejected[0] && a.rejected[0].reason) || `HTTP ${aRes.status}` });
            continue;
          }
          const put = await fetch(grant.uploadUrl, { method: "PUT", headers: { "Content-Type": grant.contentType, "x-upsert": "false" }, body: f });
          if (!put.ok) { failed.push({ name: f.name, reason: `upload failed (HTTP ${put.status})` }); continue; }
          const fRes = await fetch("/.netlify/functions/submit-review-request", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "finalize", orderId: c.orderId, uploadToken: c.uploadToken, path: grant.path, name: f.name, type: f.type }),
          });
          const fin = await fRes.json().catch(() => ({}));
          if (fRes.ok && fin.ok) uploaded.push({ name: f.name });
          else failed.push({ name: f.name, reason: (fin && fin.error) || `HTTP ${fRes.status}` });
        } catch (e) {
          failed.push({ name: f.name, reason: "the upload did not complete" });
        }
      }
      // Optional photo(s) of the person to serve — same private path, tagged.
      for (const f of photos) {
        try {
          const aRes = await fetch("/.netlify/functions/submit-review-request", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "authorize", orderId: c.orderId, uploadToken: c.uploadToken, files: [{ name: f.name, type: f.type, size: f.size }] }),
          });
          const a = await aRes.json().catch(() => ({}));
          const grant = a && Array.isArray(a.grants) ? a.grants[0] : null;
          if (!aRes.ok || !grant) { failed.push({ name: f.name, reason: (a && a.error) || `HTTP ${aRes.status}` }); continue; }
          const put = await fetch(grant.uploadUrl, { method: "PUT", headers: { "Content-Type": grant.contentType, "x-upsert": "false" }, body: f });
          if (!put.ok) { failed.push({ name: f.name, reason: `upload failed (HTTP ${put.status})` }); continue; }
          const fRes = await fetch("/.netlify/functions/submit-review-request", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "finalize", orderId: c.orderId, uploadToken: c.uploadToken, path: grant.path, name: f.name, type: f.type, role: SUBJECT_PHOTO.SUBJECT_PHOTO }),
          });
          const fin = await fRes.json().catch(() => ({}));
          if (fRes.ok && fin.ok) uploaded.push({ name: f.name });
          else failed.push({ name: f.name, reason: (fin && fin.error) || `HTTP ${fRes.status}` });
        } catch (e) {
          failed.push({ name: f.name, reason: "the upload did not complete" });
        }
      }

      const base = { jobNumber: c.jobNumber, orderId: c.orderId, uploadToken: c.uploadToken, uploaded, failed, total: files.length };
      // The request exists either way — a document that failed can be re-sent,
      // and nothing about it is described as paid or active.
      if (failed.length) {
        setReviewResult({ ...base, classified: false, analyzing: false, reviewRequired: true });
        setSubmitError(`Your request ${c.jobNumber} was received, but ${failed.length} of ${files.length} document(s) did not upload: ${failed.map((x) => `"${x.name}" (${x.reason})`).join("; ")}. Please contact us so we can collect them.`);
        return;
      }

      // Every file landed: classify the COMPLETE packet server-side. The reader
      // runs against the stored files, not anything the browser asserts.
      try {
        // Classification is resumable: each call analyses what fits inside the
        // platform's function deadline and reports progress. Poll until it is
        // finished. Giving up early is safe — the documents are already attached
        // and the order simply stays unpriced Pending Review.
        let cls = {};
        for (let attempt = 0; attempt < 10; attempt += 1) {
          const clsRes = await fetch("/.netlify/functions/submit-review-request", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "classify", orderId: c.orderId, uploadToken: c.uploadToken }),
          });
          cls = await clsRes.json().catch(() => ({}));
          if (!clsRes.ok || !cls.ok) break;
          if (!cls.inProgress) break;                       // finished
          setReviewResult({ ...base, classified: false, analyzing: true, analyzed: cls.analyzed, total: cls.total });
          await new Promise((r) => setTimeout(r, 1200));
        }
        if (cls.ok && cls.classified) {
          // Identified and priced — the customer must AFFIRM before paying.
          setReviewResult({ ...base, classified: true, docType: cls.docType, amountDueCents: cls.amountDueCents, requiresAffirmation: true });
        } else {
          setReviewResult({ ...base, classified: false, analyzing: false, reviewRequired: true });
        }
      } catch (e) {
        // A classification failure never blocks the submission: it simply stays
        // in manual review, unpriced.
        setReviewResult({ ...base, classified: false, analyzing: false, reviewRequired: true });
      }
    } catch (e) {
      setSubmitError("We could not submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit() {
    // Double-click guard: a second click while a submission is in flight is
    // ignored (the button is also disabled), so one click = at most one
    // checkout session request.
    if (submitting) return;
    setSubmitError(null);

    // Stage marker for safe diagnostics: identifies WHERE a submission failed
    // without ever logging form contents, documents, tokens, or secrets.
    let stage = "validate";

    // Inline, field-level validation (no blocking alerts). Every required field
    // is checked, ZIP gets specific messaging, and we focus the first problem.
    const { ok, errors: found, firstInvalid } = validateOrderForm({
      clientName: form.clientName,
      clientEmail: form.clientEmail,
      defendantName: form.defendantName,
      defendantAddress: form.defendantAddress,
      defendantZip: form.defendantZip,
      court: form.court,
      docType: form.docType,
      payment: form.payment,
      reviewRequired,
      files,
      agreed,
    });
    setErrors(found);
    if (!ok) {
      focusField(firstInvalid);
      return;
    }

    // Review-required document type: no price, no Stripe. Submit the documents
    // for review instead — a separate flow that creates an UNPRICED
    // pending-review order and uploads directly to private storage.
    if (reviewRequired) { await submitForReview(); return; }

    setSubmitting(true);
    // Abort the request if it exceeds the application timeout.
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS) : null;
    try {
      // Documents go DIRECTLY to private storage before any payment: stage the
      // submission, upload each file with its own signed URL, and only then
      // create the Stripe session. No document bytes travel through checkout,
      // and every selected file is carried — not files[0].
      stage = "stage_documents";
      // Staged ONCE and reused. A previous attempt's staging row still holds
      // whatever already uploaded, so retrying costs only the failed files.
      const staged = await ensureStaging();

      stage = "upload_documents";
      const uploadFailures = await uploadPendingFiles(staged);
      // An incomplete packet must NOT reach payment. The files that DID upload
      // stay staged and are not re-sent when the customer tries again.
      if (uploadFailures.length) {
        throw new Error(`${uploadFailures.map((n) => `"${n}"`).join(", ")} did not upload, so we have not taken any payment. Use Retry on the file(s) that failed — the documents that already uploaded are saved.`);
      }

      // A packet over 50 pages costs more. The customer sees the new total
      // BEFORE being sent to payment: the first time it appears, stop here so
      // they can review it, and continue only when they submit again.
      const fee = packetPageFee(packetRef.current.totalPages);
      if (fee > 0 && pageFeeAckRef.current !== packetRef.current.totalPages) {
        pageFeeAckRef.current = packetRef.current.totalPages;
        setPageFeeNotice(`Your documents are ${packetRef.current.totalPages} pages. Packets over 50 pages are $0.50 per extra page, so $${fee.toFixed(2)} has been added to your total. Review the new total and submit again to continue to payment.`);
        return;
      }

      stage = "create_session";
      const res = await fetch("/.netlify/functions/create-checkout-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Signed-in customers include their token so the order is linked to
          // their account (verified server-side). Guests omit it.
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        signal: controller ? controller.signal : undefined,
        body: JSON.stringify({
          clientName: form.clientName,
          clientEmail: form.clientEmail,
          clientPhone: form.clientPhone,
          plaintiff: form.plaintiff,
          defendantName: form.defendantName,
          caseNumber: form.caseNumber,
          defendantAddress: form.defendantAddress,
          court: form.court,
          docType: form.docType,
          notes: form.notes,
          zip: form.defendantZip,
          serviceTier: form.tier,
          serviceAddon: normalizeAddon(form.serviceAddon, form.tier),
          // Hearing status, alongside docType — never in place of it.
          exParte: form.exParte,
          paymentMethod: form.payment,
          // Sent for validation only; the server recomputes the authoritative
          // amount from zip + tier + docType + addon (never trusts a browser amount).
          displayedTotal,
          agreedToTerms: agreed,
          // Saving a card is opt-in and explicit. Only a signed-in customer who
          // ticked the box sends this; the server ignores it for a guest, and a
          // card is never saved silently.
          saveCard: Boolean(accessToken && saveCard),
          courtAddress: form.courtAddress,
          docSubtype: form.docSubtype,
          // Intake facts, carried onto the order at promotion.
          subjectAge: form.subjectAge,
          gatedAccess: form.gatedAccess,
          accessInstructions: form.accessInstructions,
          // The already-uploaded packet, by reference. No document bytes here —
          // the server reads the finalized documents off the staging row and
          // refuses to charge if any are missing.
          stagingId: staged.stagingId,
          expectedFiles: files.length + photos.length,
        })
      });

      stage = "read_response";
      if (!res.ok) {
        // Surface the server's specific validation message when it sent one
        // (e.g. price mismatch, missing field) instead of a generic failure.
        const serverErr = await res.json().catch(() => ({}));
        throw new Error(serverErr.error || "Submission failed");
      }
      const result = await res.json();
      if (!result.url) {
        throw new Error("Missing checkout URL");
      }
      stage = "redirect";
      window.location.href = result.url;
      return;
    } catch (err) {
      // Safe diagnostics: stage + timeout flag only. Never form data, documents,
      // tokens, or secrets.
      const timedOut = err && (err.name === "AbortError" || /aborted/i.test(String(err && err.message)));
      try { console.warn(`[order-submit] failed at stage=${stage}${timedOut ? " (timeout)" : ""}`); } catch (e) {}
      // No payment has been taken at this point: the card is only charged on
      // Stripe's hosted page AFTER the redirect, so telling the customer the
      // submission didn't complete is accurate — never a false failure.
      setSubmitError({ stage, timedOut });
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      setSubmitting(false);
    }
  }

  if (submitted) return (
    <div className="alert">
      <strong>✓ Order Submitted!</strong><br />
      You will receive a confirmation email shortly. Your job ID is <strong>{jobNumber}</strong>.
      Your payment has been received. Track status in the Client Portal below.
    </div>
  );

  return (
    <div>
      {paymentNotice && (
                <div style={{ marginBottom: "0.75rem", background: "#FEF2F2", border: "1px solid var(--red)", borderRadius: 4, padding: "0.75rem" }}>
                  {paymentNotice}
                </div>
              )}
      <h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, marginBottom: "1.5rem", fontSize: "1.1rem" }}>New Service Order</h3>
      {/* Validation summary: appears after a failed submit attempt, lists every
          problem, and each entry jumps to its field. Entered values are never
          cleared by validation. */}
      {Object.keys(errors).length > 0 && (
        <div role="alert" data-testid="validation-summary" style={{ marginBottom: "1rem", background: "#FEF2F2", border: "2px solid var(--red)", borderRadius: 6, padding: "0.75rem 1rem" }}>
          <div style={{ fontWeight: 700, marginBottom: "0.4rem", fontSize: "0.88rem" }}>
            Please fix the following before submitting ({Object.keys(errors).length}):
          </div>
          <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: "0.82rem" }}>
            {FIELD_ORDER.filter(f => errors[f]).map(f => (
              <li key={f} style={{ marginBottom: "0.2rem" }}>
                <button type="button" onClick={() => focusField(f)} style={{ background: "none", border: "none", padding: 0, color: "var(--navy)", cursor: "pointer", textDecoration: "underline", font: "inherit", textAlign: "left" }}>
                  {errors[f]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="form-grid">
        <div className="field"><label>Your Name *</label>
          <input ref={el => (fieldRefs.current.clientName = el)} className={errClass("clientName")}
            value={form.clientName} onChange={e => set("clientName", e.target.value)} placeholder="Full name or firm" />
          {fieldError("clientName")}</div>
        <div className="field"><label>Email *</label>
          <input ref={el => (fieldRefs.current.clientEmail = el)} className={errClass("clientEmail")}
            type="email" value={form.clientEmail} onChange={e => set("clientEmail", e.target.value)} placeholder="your@email.com" />
          {fieldError("clientEmail")}</div>
        <div className="field"><label>Phone</label><input type="tel" inputMode="tel" value={form.clientPhone} onChange={e => set("clientPhone", e.target.value)} placeholder="(951) 555-0000" /></div>
        <div className="field"><label>Person to Serve *</label>
          <input ref={el => (fieldRefs.current.defendantName = el)} className={errClass("defendantName")}
            value={form.defendantName} onChange={e => set("defendantName", e.target.value)} placeholder="Full legal name" />
          {fieldError("defendantName")}</div>
        {/* The CLIENT's understanding of the person's age. Optional, and kept
            separate from the process server's later description of whoever
            actually answers the door — those are two different facts. */}
        <div className="field"><label>Approximate Age</label>
          <select value={form.subjectAge} onChange={e => set("subjectAge", e.target.value)}>
            <option value="">Select if known…</option>
            {SUBJECT_AGE_BANDS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </div>
        <div className="field form-full" style={{ position: "relative" }}>
          <label>Service Address *</label>
          <input id="address-input" ref={el => (fieldRefs.current.defendantAddress = el)} className={errClass("defendantAddress")}
            value={form.defendantAddress}
            onChange={e => { addrAcceptedRef.current = false; set("defendantAddress", e.target.value); setAddrSuggestions([]); }}
            onBlur={() => setTimeout(() => setAddrSuggestions([]), 200)}
            placeholder="Start typing street address…" autoComplete="off" />
          {fieldError("defendantAddress")}
          {addrSuggestions.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50, background: "var(--white)", border: "2px solid var(--gold)", borderRadius: "0 0 6px 6px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>
              {addrSuggestions.map((p) => (
                <div key={p.place_id} onMouseDown={() => acceptPlacePrediction(p)}
                  style={{ padding: "0.65rem 1rem", cursor: "pointer", fontSize: "0.85rem", borderBottom: "1px solid var(--light)", color: "var(--navy)" }}
                  onMouseEnter={e => e.target.style.background = "#FFFBF4"}
                  onMouseLeave={e => e.target.style.background = "transparent"}>
                  📍 {p.description}
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Getting to the door. A gate the server cannot pass is a wasted trip,
            so this is asked once here rather than discovered on the doorstep.
            Access details are operational — they go to the person performing
            service and are not shown back in the client account. */}
        <div className="field form-full">
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
            <input type="checkbox" checked={form.gatedAccess} style={{ cursor: "pointer" }}
              onChange={e => set("gatedAccess", e.target.checked)} />
            This address is in a gated or access-controlled location
          </label>
        </div>
        {form.gatedAccess && (
          <div className="field form-full">
            <label>Access Instructions / Gate Code</label>
            <textarea rows={2} value={form.accessInstructions}
              onChange={e => set("accessInstructions", e.target.value)}
              placeholder="Gate code, call-box name, guard instructions, building/unit access…" />
            <span style={{ fontSize: "0.75rem", color: "var(--gray)" }}>
              Shared only with the process server assigned to this service.
            </span>
          </div>
        )}
        <div className="field">
          <label>ZIP Code *</label>
          <input ref={el => (fieldRefs.current.defendantZip = el)} className={errClass("defendantZip")}
            inputMode="numeric" value={form.defendantZip} onChange={e => { set("defendantZip", e.target.value.replace(/\D/, "")); if (e.target.value.length === 5) checkZip(e.target.value); }} maxLength={5} placeholder="92883" />
          {zipInfo && <span style={{ fontSize: "0.75rem", color: "var(--green)", marginTop: "0.2rem" }}>✓ {zipInfo.city}</span>}
          {!errors.defendantZip && form.defendantZip.length === 5 && !zipInfo && <span style={{ fontSize: "0.75rem", color: "var(--red)", marginTop: "0.2rem" }}>ZIP not in service area</span>}
          {fieldError("defendantZip")}
        </div>
        <div className="field">
          <label>Court Jurisdiction *</label>
          <select ref={el => (fieldRefs.current.court = el)} className={errClass("court")}
            value={form.court} onChange={e => set("court", e.target.value)}>
            <option value="">Select court…</option>
            {groupCourts(COURT_JURISDICTIONS).map(g => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </optgroup>
            ))}
          </select>
          {fieldError("court")}
        </div>
        <div className="field form-full">
          <label>Service Level *</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {Object.entries(PRICING).map(([key, val]) => {
              const selected = form.tier === key;
              // A level this document type may not use is offered as disabled
              // rather than hidden, so the customer can see it exists and why
              // it cannot be chosen.
              const tierOk = tierAllowedForExParte(key, form.exParte, form.docType);
              return (
                <button key={key} type="button" onClick={() => selectTier(key)}
                  aria-pressed={selected} disabled={!tierOk}
                  data-testid={`tier-${key}`}
                  title={tierOk ? undefined : tierRestrictionMessage(key, form.exParte, form.docType)}
                  style={{
                    flex: "1 1 30%", minWidth: 120, padding: "0.6rem 0.5rem", borderRadius: 6,
                    cursor: tierOk ? "pointer" : "not-allowed", opacity: tierOk ? 1 : 0.45,
                    textAlign: "center", background: "var(--white)", color: "var(--navy)",
                    fontFamily: "Inter, sans-serif", transition: "all 0.15s",
                    border: selected ? "3px solid var(--gold)" : "1px solid #d9d9d9",
                    boxShadow: selected ? "0 0 0 2px var(--gold)" : "0 2px 6px rgba(0,0,0,0.12)",
                  }}>
                  <div style={{ fontWeight: 800, fontSize: "0.95rem" }}>{val.label}</div>
                  <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, color: "var(--gold)" }}>${val.price}</div>
                  <div style={{ fontSize: "0.75rem", color: "var(--gray)" }}>{val.desc}</div>
                  {val.updates && <div data-testid={`form-tier-updates-${key}`} style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--navy)" }}>{val.updates}</div>}
                </button>
              );
            })}
          </div>
          {/* The selected level is not available for this document type. No
              price is shown and nothing can be paid until an allowed level is
              chosen — the choice is never made for the customer. */}
          {tierBlocked && (
            <div className="alert alert-warn" data-testid="tier-restriction" style={{ marginTop: "0.6rem", fontSize: "0.8rem" }}>
              {tierBlocked}
            </div>
          )}
          {/* One add-on per order: Weekend/Holiday on Rush or Same-Day; Priority
              (live attempt updates) on Standard only. */}
          {tierPicked && addonAllowedForTier(form.tier) && (
            <div style={{ marginTop: "0.6rem" }} data-testid="addon-offer">
              <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--gray)", marginBottom: "0.35rem" }}>
                Optional Add-On{addonsForTier(form.tier).length > 1 ? " (choose one)" : ""}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {addonsForTier(form.tier).map(([key, a]) => {
                  const selected = form.serviceAddon === key;
                  return (
                    <button key={key} type="button" onClick={() => toggleAddon(key)}
                      aria-pressed={selected} data-testid={`addon-${key}`} title={a.desc || undefined}
                      style={{
                        padding: "0.45rem 0.9rem", borderRadius: 20, cursor: "pointer",
                        fontSize: "0.8rem", fontWeight: 700, fontFamily: "Inter, sans-serif",
                        background: selected ? "var(--gold)" : "var(--white)", color: "var(--navy)",
                        border: selected ? "2px solid var(--gold)" : "1px solid #d9d9d9",
                      }}>
                      {a.label} +${a.price}{a.desc ? ` — ${a.desc}` : ""}
                    </button>
                  );
                })}
              </div>
              <span className="text-sm" style={{ color: "var(--gray)", marginTop: "0.25rem", display: "block" }}>
                {addonsForTier(form.tier).length > 1 ? "Weekend and Holiday cannot be combined. " : ""}Tap again to remove.
              </span>
            </div>
          )}
        </div>
        <div className="field">
          <label>Document Type</label>
          <select value={form.docType} onChange={e => set("docType", e.target.value)}>
            <option value="">Select type…</option>
            {Object.entries(DOC_SURCHARGES).map(([type]) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        {/* Ex parte is the HEARING's urgency, not the papers. It sits beside the
            document type — the documents are still a family-law petition, a TRO,
            a subpoena — and only restricts the service level. No surcharge. */}
        <div className="field">
          <label>Ex Parte Hearing</label>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {[["no", false], ["yes", true]].map(([label, value]) => (
              <button key={label} type="button" data-testid={`ex-parte-${label}`}
                onClick={() => setExParte(value)} aria-pressed={form.exParte === value}
                style={{
                  flex: "1 1 0", padding: "0.55rem 0.5rem", borderRadius: 6, cursor: "pointer",
                  textAlign: "center", background: "var(--white)", color: "var(--navy)",
                  fontFamily: "Inter, sans-serif", fontWeight: 700, textTransform: "capitalize",
                  border: form.exParte === value ? "3px solid var(--gold)" : "1px solid #d9d9d9",
                }}>{label}</button>
            ))}
          </div>
          <div style={{ fontSize: "0.75rem", color: "var(--gray)", marginTop: "0.3rem" }}>
            Ex parte hearings require Rush or Same-Day service. There is no extra charge for this.
          </div>
        </div>
        <div className="field">
          <label>Needed By Date</label>
          <input
            type="date" min={(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })()}
            value={form.neededByDate || ""}
            onClick={e => { const el = e.currentTarget; if (typeof el.showPicker === "function") { try { el.showPicker(); } catch (err) { /* unsupported or not allowed — native field still works */ } } }}
            onChange={e => { const v = e.target.value; const now = new Date(); const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`; if (v && v < today) { showNotice("Needed By Date cannot be in the past."); return; } set("neededByDate", v); }}
          />
        </div>
        <div className="field form-full">
          <label>Upload Court Documents *</label>
          <div ref={el => (fieldRefs.current.files = el)} tabIndex={-1} className={`upload-zone ${errClass("files")}`}
            onClick={() => document.getElementById("file-input").click()}>
            <strong>Click to upload your court paperwork</strong>
            <p>{files.length ? `${files.length} document(s) selected — click to add more` : "PDF preferred — Summons, Complaint, Subpoena, etc."}</p>
            {/* Selecting again ADDS to the packet. It used to call setFiles(picked),
                which replaced everything chosen before. e.target.value is cleared
                so re-picking the same file still fires onChange. */}
            <input id="file-input" type="file" multiple accept={UPLOAD_ACCEPT} style={{ display: "none" }}
              onChange={e => { const picked = Array.from(e.target.files); e.target.value = ""; addFiles(picked); }} />
          </div>
          {fieldError("files")}
          {fileRejections.length > 0 && (
            <div className="alert-warn alert" data-testid="file-rejections" style={{ marginTop: "0.5rem", fontSize: "0.8rem" }}>
              {fileRejections.map((r, i) => <div key={i}>{r}</div>)}
            </div>
          )}
          {files.length > 0 && (
            <ul data-testid="selected-files" style={{ listStyle: "none", padding: 0, margin: "0.5rem 0 0" }}>
              {files.map((f) => (
                <li key={fileKey(f)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem", padding: "0.3rem 0.5rem", border: "1px solid var(--light)", borderRadius: 4, marginBottom: "0.25rem" }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                  <span style={{ color: "var(--gray)", whiteSpace: "nowrap" }}>
                    {UPLOAD_LIMITS.mb(f.size)} MB
                    {/* Upload state for THIS file, so one failure is never hidden
                        by another file's success. */}
                    <span data-testid={`upload-status-${fileKey(f)}`} style={{ marginLeft: "0.6rem", color: UPLOAD_STATUS_COLOR[(uploadStates[fileKey(f)] || {}).status || "waiting"] }}>
                      {UPLOAD_STATUS_LABEL[(uploadStates[fileKey(f)] || {}).status || "waiting"]}
                    </span>
                    {(uploadStates[fileKey(f)] || {}).status === "failed" && (
                      <button type="button" data-testid={`retry-${fileKey(f)}`} onClick={() => retryFile(f)}
                        style={{ marginLeft: "0.5rem", background: "none", border: "none", color: "var(--blue)", cursor: "pointer", fontWeight: 700 }}>Retry</button>
                    )}
                    <button type="button" aria-label={`Remove ${f.name}`} onClick={() => removeFile(f)}
                      style={{ marginLeft: "0.6rem", background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontWeight: 700 }}>Remove</button>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {/* PER-FILE reader results. Every selected file appears with its own
              outcome, so one failure is never hidden by another file's success
              and a customer can see exactly which document could not be read. */}
          {fileReads.length > 0 && (
            <ul data-testid="file-read-status" style={{ listStyle: "none", padding: 0, margin: "0.5rem 0 0", fontSize: "0.78rem" }}>
              {fileReads.map((s) => (
                <li key={s.file} style={{ padding: "0.25rem 0" }}>
                  {s.ok
                    ? <span style={{ color: "var(--green)" }}>✓ Read “{s.file}” — case information found.</span>
                    : <span style={{ color: "var(--red)" }}>⚠ Couldn’t read “{s.file}”: {READ_FAILURE_TEXT[s.reason] || READ_FAILURE_TEXT.unreadable}</span>}
                </li>
              ))}
              <li style={{ color: "var(--gray)", paddingTop: "0.2rem" }}>
                Every uploaded document stays attached to your order either way — anything we couldn’t read is reviewed by our team.
              </li>
            </ul>
          )}
          {aiStage === "uploading" && (
            <div className="ai-extract-box"><div className="ai-loading"><div className="spinner"></div> Uploading document…</div></div>
          )}
          {aiStage === "reading" && (
            <div className="ai-extract-box"><div className="ai-loading"><div className="spinner"></div> Reading document…</div></div>
          )}
          {aiStage === "failed" && (
            <div className="ai-extract-box"><div className="alert-warn alert">Automatic document reading is unavailable right now. Please enter the case information below.</div></div>
          )}
          {/* Court text the reader found but could not map — PRESERVED, shown,
              and the customer picks the right option. Never silently dropped. */}
          {extractedCourtText && (
            <div className="alert-warn alert" data-testid="unmatched-court" style={{ marginTop: "0.5rem", fontSize: "0.8rem" }}>
              We read the court as <strong>“{extractedCourtText}”</strong> but couldn’t match it to a listed option.
              Please select the correct Court Jurisdiction above to confirm.
            </div>
          )}
          {(aiStage === "found" || aiStage === "partial" || aiStage === "conflict") && (
            <div className="ai-extract-box">
              <h5>{aiStage === "found"
                ? "⚡ Case information found. Please review."
                : aiStage === "conflict"
                  ? "⚡ Your documents disagree on some details. Please confirm the correct values."
                  : "⚡ We could not confidently read all information. Please complete the highlighted fields."}</h5>
              <div className="verify-badge">⚠ Review carefully before submitting. You are responsible for confirming case information is accurate.</div>
              {/* Defendant is intentionally NOT repeated here — the single
                  authoritative "Person to Serve" field above is
                  auto-filled by extraction (applyMergedToForm). */}
              <div className="form-grid">
                {renderReviewField("plaintiff", "Plaintiff *", "plaintiff")}
                {renderReviewField("caseNumber", "Case Number", "caseNumber")}
              </div>
              {extraction && extraction.court && extraction.court.best && !form.court && (
                <span className="conflict-note">Detected court: "{extraction.court.best.value}". Please select the correct Court Jurisdiction above.</span>
              )}
              {renderReference()}
            </div>
          )}
          {/* Manual case-information entry is ALWAYS available — idle, reading,
              success, partial, conflict or failure. Reader status never hides it. */}
          {true && (
            <div style={{ marginTop: "0.75rem" }} data-testid="manual-case-fields">
              <p style={{ fontSize: "0.75rem", color: "var(--gray)", marginBottom: "0.5rem" }}>
                {aiStage === "found" || aiStage === "partial" || aiStage === "conflict"
                  ? "Check or correct the case information — you can edit any field:"
                  : "Or enter case information manually:"}
              </p>
              {/* Defendant lives in the single authoritative field above. */}
              <div className="form-grid">
                <div className="field"><label>Plaintiff</label><input value={form.plaintiff} onChange={e => set("plaintiff", e.target.value)} placeholder="Plaintiff name" /></div>
                <div className="field"><label>Case Number</label><input value={form.caseNumber} onChange={e => set("caseNumber", e.target.value)} placeholder="Case No." /></div>
                {/* Court address: auto-filled from the caption when read, always
                    editable, and never overwritten once the customer types. */}
                <div className="field form-full">
                  <label>Court Address {needsConfirm.courtAddress && <span className="review-hint medium">please confirm</span>}</label>
                  <input data-testid="court-address" value={form.courtAddress} onChange={e => { set("courtAddress", e.target.value); setConfirmedFields(c => ({ ...c, courtAddress: true })); }}
                    placeholder="Courthouse street address (optional)" />
                </div>
                {/* Subtype is shown ONLY for document types that actually use one. */}
                {CUSTOMER_DOC_SUBTYPES[form.docType] && (
                  <div className="field form-full">
                    <label>Document Subtype {needsConfirm.docSubtype && <span className="review-hint medium">please confirm</span>}</label>
                    <select data-testid="doc-subtype" value={form.docSubtype}
                      onChange={e => { set("docSubtype", e.target.value); setConfirmedFields(c => ({ ...c, docSubtype: true })); }}>
                      <option value="">Select…</option>
                      {CUSTOMER_DOC_SUBTYPES[form.docType].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="field form-full" data-testid="subject-photo-field">
          <label>Photo of Person to Serve (optional)</label>
          <div className="upload-zone" onClick={() => document.getElementById("subject-photo-input").click()}>
            <strong>Click to add a photo of the person to serve</strong>
            <p>{photos.length ? `${photos.length} photo(s) selected — click to add more` : `If you have one — it helps us identify the right person. JPG, PNG or HEIC, up to ${SUBJECT_PHOTO.MAX_SUBJECT_PHOTOS}.`}</p>
            <input id="subject-photo-input" type="file" multiple accept="image/*,.jpg,.jpeg,.png,.heic" style={{ display: "none" }}
              onChange={e => { const picked = Array.from(e.target.files || []); e.target.value = ""; addPhotos(picked); }} />
          </div>
          {photoRejections.length > 0 && (
            <div className="alert-warn alert" style={{ marginTop: "0.5rem", fontSize: "0.8rem" }}>{photoRejections.map((r, i) => <div key={i}>{r}</div>)}</div>
          )}
          {photos.length > 0 && (
            <ul data-testid="selected-photos" style={{ listStyle: "none", padding: 0, margin: "0.5rem 0 0" }}>
              {photos.map((f) => (
                <li key={photoKey(f)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem", padding: "0.3rem 0.5rem", border: "1px solid var(--light)", borderRadius: 6, marginBottom: "0.25rem" }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📷 {f.name}</span>
                  <span style={{ whiteSpace: "nowrap" }}>
                    <span style={{ color: UPLOAD_STATUS_COLOR[(uploadStates[photoKey(f)] || {}).status || "waiting"] }}>{UPLOAD_STATUS_LABEL[(uploadStates[photoKey(f)] || {}).status || "waiting"]}</span>
                    {(uploadStates[photoKey(f)] || {}).status === "failed" && (
                      <button type="button" onClick={() => retryPhoto(f)} style={{ marginLeft: "0.5rem", background: "none", border: "none", color: "var(--blue)", cursor: "pointer", fontWeight: 700 }}>Retry</button>
                    )}
                    <button type="button" aria-label={`Remove ${f.name}`} onClick={() => removePhoto(f)}
                      style={{ marginLeft: "0.6rem", background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontWeight: 700 }}>Remove</button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="field form-full">
          <label>Special Instructions</label>
          <textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Gate codes, best times to serve, specific proof form needed, etc." />
        </div>
      </div>
      {/* Payment method is collected ONLY when there is a price to pay. A
          review-required type has no price yet, so no payment control is shown
          or permitted — a secure payment link follows after review. */}
      {reviewRequired ? (
        <div data-testid="review-required-notice" style={{ marginTop: "1.5rem", background: "#FFFBEB", border: "2px solid var(--gold)", borderRadius: 6, padding: "0.85rem 1rem" }}>
          <div style={{ fontWeight: 800, marginBottom: "0.3rem" }}>{REVIEW_REQUIRED_MESSAGE}</div>
          <div style={{ fontSize: "0.85rem" }}>
            Upload your documents and submit them for review. We'll confirm the price and email you a secure payment link —
            no payment is taken now.
          </div>
        </div>
      ) : (
      <div style={{ marginTop: "1.5rem" }}>
        <div style={{ fontSize: "0.8rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.75rem" }}>Select Payment Method *</div>
        {fieldError("payment")}
        <div ref={el => (fieldRefs.current.payment = el)} tabIndex={-1} className="payment-opts">
          {[
            { id: "stripe", text: "Credit / Debit Card", logos: ["https://cdn.simpleicons.org/visa", "https://cdn.simpleicons.org/mastercard", "https://cdn.simpleicons.org/americanexpress", "https://cdn.simpleicons.org/discover", "https://cdn.simpleicons.org/applepay", "https://cdn.simpleicons.org/googlepay"] },
            
            
            
].map(p => (
                        <button key={p.id}
              onClick={() => set("payment", p.id)}
              style={{
                flex: 1, minWidth: 100, padding: "0.75rem 0.5rem",
                borderRadius: 6, cursor: "pointer", textAlign: "center",
                border: form.payment === p.id ? "3px solid var(--gold)" : "1px solid #d9d9d9",
                background: "#FFFFFF", color: "var(--navy)",
                fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "0.95rem",
                transition: "all 0.15s", letterSpacing: "0.02em",
                boxShadow: form.payment === p.id ? "0 0 0 2px var(--gold)" : "0 2px 6px rgba(0,0,0,0.15)"
              }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", flexWrap: "wrap" }}>{p.logos.map((src) => (<img key={src} src={src} alt="" style={{ height: "22px" }} />))}</div>
              <div style={{ fontSize: "0.75rem", marginTop: "0.35rem", fontWeight: 600, color: "var(--navy)" }}>{p.text}</div>
            </button>
            ))}
        </div>
{form.payment === "stripe" && <div className="alert mt1">Available wallets such as Apple Pay and Google Pay will appear securely at checkout automatically when supported by your browser or device.</div>}


      </div>
      )}
      {showAgreement && (
        <div className="agreement-modal" role="presentation">
          <div className="agreement-box" role="dialog" aria-modal="true" aria-labelledby="agreement-title"
            ref={agreementBoxRef} tabIndex={-1} onKeyDown={onAgreementKeyDown}>
            <h3 id="agreement-title">Service Agreement</h3>
            <div className="agreement-text">{SERVICE_AGREEMENT}</div>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button className="btn-secondary" onClick={closeAgreement}>Cancel</button>
              <button className="btn-primary" onClick={() => { setAgreed(true); clearError("agreed"); closeAgreement(); }}>I Agree →</button>
            </div>
          </div>
        </div>
      )}
      {/* Friendly failure state: shown when a submission times out or errors.
          Nothing typed is lost; Retry resubmits. No payment is taken before the
          Stripe page, so "could not be completed" is always accurate here. */}
      {submitError && !submitting && (
        <div role="alert" data-testid="submit-error" style={{ marginTop: "1rem", background: "#FEF2F2", border: "2px solid var(--red)", borderRadius: 6, padding: "0.85rem 1rem" }}>
          <div style={{ fontWeight: 700, marginBottom: "0.35rem" }}>
            {submitError.timedOut ? "This is taking longer than expected." : "We couldn't complete your order."}
          </div>
          <div style={{ fontSize: "0.85rem", marginBottom: "0.6rem" }}>
            Your order could not be completed and <strong>you have not been charged</strong> — payment only happens on the secure Stripe page.
            Everything you entered has been kept. Please try again, or contact us if this keeps happening.
            <span style={{ color: "var(--gray)" }}> (Ref: {submitError.stage})</span>
          </div>
          <button className="btn-primary" type="button" onClick={handleSubmit}>Retry Submission</button>
        </div>
      )}
      {/* Review submitted: the request exists and is awaiting document review.
          Deliberately never described as paid or active. */}
      {reviewResult && !submitting && reviewResult.classified && (
        <div role="status" data-testid="review-classified" style={{ marginTop: "1rem", background: "#F0FDF4", border: "2px solid var(--green)", borderRadius: 6, padding: "0.85rem 1rem" }}>
          <div style={{ fontWeight: 700, marginBottom: "0.35rem" }}>Documents identified — {reviewResult.jobNumber}</div>
          <div style={{ fontSize: "0.85rem", marginBottom: "0.6rem" }}>
            We read your {reviewResult.total} document(s) and identified them as{" "}
            <strong>{reviewResult.docType}</strong>. The price for this service is{" "}
            <strong>${(reviewResult.amountDueCents / 100).toFixed(2)}</strong>.
            {" "}Please confirm this is correct — <strong>you have not been charged</strong> and nothing is submitted for payment until you do.
          </div>
          {affirmMsg && <div className="alert" style={{ marginBottom: "0.6rem", fontSize: "0.82rem" }}>{affirmMsg}</div>}
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button className="btn-primary" data-testid="affirm-classification" disabled={affirmBusy}
              onClick={() => affirmClassification()}>
              {affirmBusy ? "Starting secure payment…" : `Confirm & Pay $${(reviewResult.amountDueCents / 100).toFixed(2)} →`}
            </button>
            <button className="btn-secondary" disabled={affirmBusy}
              onClick={() => { setReviewResult(r => ({ ...r, classified: false, reviewRequired: true })); setAffirmMsg("Sent for manual review — we'll confirm the price and email a secure payment link."); }}>
              That's not right — review manually
            </button>
          </div>
        </div>
      )}
      {/* Analysis still running: documents are safely attached, nothing is priced
          and no payment exists. Shown while the resumable classify polls. */}
      {reviewResult && reviewResult.analyzing && !reviewResult.classified && (
        <div role="status" data-testid="review-analyzing" style={{ marginTop: "1rem", background: "#FFFBEB", border: "2px solid var(--gold)", borderRadius: 6, padding: "0.85rem 1rem" }}>
          <div style={{ fontWeight: 700, marginBottom: "0.35rem" }}>Analyzing your documents — {reviewResult.jobNumber}</div>
          <div style={{ fontSize: "0.85rem" }}>
            {reviewResult.analyzed != null && reviewResult.total != null && <>{reviewResult.analyzed} of {reviewResult.total} document(s) analyzed. </>}
            All {reviewResult.total || reviewResult.uploaded.length} document(s) are safely attached to your request.
            <strong> You have not been charged</strong>, and no price is set until analysis finishes.
          </div>
        </div>
      )}
      {reviewResult && !submitting && !reviewResult.classified && !reviewResult.analyzing && (
        <div role="status" data-testid="review-received" style={{ marginTop: "1rem", background: "#F0FDF4", border: "2px solid var(--green)", borderRadius: 6, padding: "0.85rem 1rem" }}>
          <div style={{ fontWeight: 700, marginBottom: "0.35rem" }}>Received for document review — {reviewResult.jobNumber}</div>
          <div style={{ fontSize: "0.85rem" }}>
            We have your {reviewResult.uploaded.length} of {reviewResult.total} document(s). {REVIEW_REQUIRED_MESSAGE} Our team will review them,
            confirm the price, and email you a secure payment link. <strong>You have not been charged</strong>, and service begins once payment is complete.
          </div>
        </div>
      )}
      <div className="flex-between mt2">
        {/* A review-required type has NO automatic total — the message replaces it. */}
        {reviewRequired ? (
          <div data-testid="review-required-total" style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, fontSize: "1.05rem", color: "var(--navy)" }}>
            {REVIEW_REQUIRED_MESSAGE}
          </div>
        ) : displayedTotal != null && (
          <div>
            <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, fontSize: "1.3rem", color: "var(--gold)" }}>
              Total: ${displayedTotal.toFixed(2).replace(/\.00$/, "")}
            </div>
            {pageFee > 0 ? (
              <div data-testid="page-fee-line" style={{ fontSize: "0.78rem", color: "var(--navy)" }}>
                Includes large packet: {packet.totalPages} pages ({packet.totalPages - 50} × $0.50 = ${pageFee.toFixed(2)})
              </div>
            ) : (
              <div data-testid="page-fee-note" style={{ fontSize: "0.75rem", color: "var(--gray)" }}>
                Packets over 50 pages: $0.50 per extra page (counted when your documents upload).
              </div>
            )}
            {pageFeeNotice && <div role="alert" data-testid="page-fee-notice" className="alert alert-warn" style={{ marginTop: "0.4rem", fontSize: "0.8rem" }}>{pageFeeNotice}</div>}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.5rem" }}>
          {/* SAVED CARDS — signed-in customers only, and opt-in. Stripe Checkout
              already offers any card they saved before, because the session
              carries their Stripe Customer; this box is only about keeping the
              NEW one. A guest never sees it and never gets a saved card. */}
          {accessToken && !reviewRequired && (
            <label data-testid="save-card" style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.78rem", color: "var(--gray)", cursor: "pointer" }}>
              <input type="checkbox" checked={saveCard} style={{ cursor: "pointer" }}
                onChange={(e) => setSaveCard(e.target.checked)} />
              Save this card to my account for future orders
            </label>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.78rem", color: "var(--gray)" }}>
            {/* Unchecking withdraws agreement; checking opens the agreement to read. */}
            <input type="checkbox" checked={agreed} id="agree-cb" style={{ cursor: "pointer" }}
              ref={el => (fieldRefs.current.agreed = el)}
              onChange={e => {
                if (agreed) { setAgreed(false); return; }
                openAgreement(e.currentTarget);
              }} />
            <label htmlFor="agree-cb" style={{ cursor: "pointer" }}>
              I agree to the{" "}
              {/* Real link: opens the standalone agreement in a NEW TAB so nothing
                  typed into the order form is lost. stopPropagation keeps the
                  label from toggling the checkbox when the link is followed. */}
              <a href={SERVICE_AGREEMENT_URL} target="_blank" rel="noopener noreferrer"
                aria-label="Open the Service Agreement in a new tab"
                onClick={e => e.stopPropagation()}
                style={{ color: "var(--navy)", fontWeight: 600, textDecoration: "underline" }}>Service Agreement</a>
            </label>
          </div>
          {fieldError("agreed")}
          {/* CHECKOUT GATE. Payment cannot be reached while a document is still
              uploading or has failed — an incomplete packet must never be
              charged for. */}
          {uploadBlock && (
            <div className="alert alert-warn" data-testid="upload-gate" style={{ fontSize: "0.8rem", marginBottom: "0.5rem" }}>{uploadBlock}</div>
          )}
          <button className="btn-primary" onClick={handleSubmit} disabled={submitting || !!uploadBlock || !!tierBlocked} aria-busy={submitting} data-testid="order-submit">
            {submitting
              ? (reviewRequired ? "Submitting for review…" : "Processing your order…")
              : (reviewRequired ? "Submit for Review →" : "Submit Request →")}
          </button>
          {submitting && (
            <div role="status" style={{ fontSize: "0.75rem", color: "var(--gray)" }}>
              {reviewRequired
                ? "Uploading your documents… please don't close this page. No payment is taken — we'll email a secure payment link after review."
                : "Processing your order… please don't close this page. You'll be taken to secure payment in a moment."}
            </div>
          )}
          <div style={{ fontSize: "0.75rem", color: "var(--gray)" }}>{form.tier === "sameday" || form.tier === "rush" ? "Rush requests reviewed same day" : "Standard requests reviewed within 48 hours"}</div>
        </div>
      </div>
    </div>
  );
}

// ── ROUTE PLANNER ──────────────────────────────────────────────────────────────
// Route stops: every open order with an address — never a test order. An
// order with several people to serve gives one stop per person still to be
// served, at that person's own address.
const ROUTE_CLOSED = ["completed", "cancelled", "refund_issued", "archived", "deleted"];
function routeStopsFor(jobs) {
  return (jobs || []).filter(j => !j.isTest && !ROUTE_CLOSED.includes(String(j.status || ""))).flatMap(j => {
    const all = j.serviceTargets || [];
    if (all.length > 1) return all.filter(t => t.service_status !== "completed" && String(t.service_address || j.address || "").trim())
      .map(t => ({ ...j, id: `${j.id}#${t.id}`, defendant: t.subject_name || j.defendant, address: t.service_address || j.address }));
    return String(j.address || "").trim() ? [j] : [];
  });
}

function RoutePlanner({ jobs }) {
  // Every open job with an address — anything not completed/cancelled/refunded —
  // and never a test order. (The old filter looked for statuses the portal
  // doesn't use, so the planner came up empty.)
  const eligible = routeStopsFor(jobs);
  // Extra stops the admin adds by hand (an errand, a second address, a court
  // filing…). Saved on this device so they survive a refresh.
  const [extras, setExtras] = useState(() => { try { return JSON.parse(localStorage.getItem("routeExtraStops") || "[]"); } catch { return []; } });
  useEffect(() => { try { localStorage.setItem("routeExtraStops", JSON.stringify(extras)); } catch {} }, [extras]);
  const [newAddr, setNewAddr] = useState("");
  const [newLabel, setNewLabel] = useState("");
  function addExtraStop() {
    const address = newAddr.trim();
    if (!address) return;
    setExtras(prev => [...prev, { id: `extra-${Date.now()}`, extra: true, label: newLabel.trim(), address }]);
    setNewAddr(""); setNewLabel("");
  }
  function removeExtraStop(id) { setExtras(prev => prev.filter(x => x.id !== id)); if (currentStop === id) setCurrentStop(null); }
  const eligibleKey = eligible.map(j => `${j.id}:${j.status}`).join("|") + "#" + extras.map(x => x.id).join(",");
  const [stops, setStops] = useState(() => [...eligible, ...extras].map((j, i) => ({ ...j, done: false, order: i })));
  const [currentStop, setCurrentStop] = useState(null);
  const [startAddr, setStartAddr] = useState("Corona, CA 92883");
  const [routeActive, setRouteActive] = useState(false);
  const [routeStartTime, setRouteStartTime] = useState(null);
  const [routeStartGPS, setRouteStartGPS] = useState(null);
  const [lastGPS, setLastGPS] = useState(null);
  const [totalMiles, setTotalMiles] = useState(0);
  const [legMiles, setLegMiles] = useState([]);
  const [routeLog, setRouteLog] = useState(() => {
    try { return JSON.parse(localStorage.getItem("routeLog") || "[]"); } catch { return []; }
  });
  const [routeGPSError, setRouteGPSError] = useState(null);

  // Refresh when the open jobs or extra stops change, keeping the order the
  // admin arranged and any stops already marked done; new stops go at the end.
  useEffect(() => {
    setStops(prev => {
      const all = [...eligible, ...extras];
      const byId = new Map(all.map(j => [j.id, j]));
      const kept = prev.filter(p => byId.has(p.id)).map(p => ({ ...byId.get(p.id), done: !!p.done, skipped: !!p.skipped }));
      const keptIds = new Set(kept.map(k => k.id));
      return [...kept, ...all.filter(j => !keptIds.has(j.id)).map(j => ({ ...j, done: false }))];
    });
  }, [eligibleKey]);

  async function startRoute() {
    try {
      const gps = await getCurrentGPS();
      setRouteStartGPS(gps);
      setLastGPS(gps);
      setRouteActive(true);
      setRouteStartTime(new Date());
      setTotalMiles(0);
      setLegMiles([]);
      setRouteGPSError(null);
    } catch(e) {
      setRouteGPSError("Could not get GPS to start route: " + e);
    }
  }

  async function navigateToStop(stop, idx) {
    navigateTo(stop.address);
    setCurrentStop(stop.id);
    if (lastGPS) {
      try {
        const gps = await getCurrentGPS();
        const dist = haversineDistance(lastGPS.lat, lastGPS.lng, gps.lat, gps.lng);
        setLastGPS(gps);
        setTotalMiles(m => parseFloat((m + dist).toFixed(2)));
        setLegMiles(prev => [...prev, { from: idx === 0 ? startAddr : stops[idx-1]?.defendant || "Previous Stop", to: stop.defendant || stop.label || stop.address, miles: parseFloat(dist.toFixed(2)), address: stop.address }]);
      } catch(e) { /* GPS optional mid-route */ }
    }
  }

  async function endRoute() {
    let finalGPS = lastGPS;
    let finalMiles = totalMiles;
    try {
      finalGPS = await getCurrentGPS();
      if (lastGPS) {
        const returnMiles = haversineDistance(lastGPS.lat, lastGPS.lng, finalGPS.lat, finalGPS.lng);
        finalMiles = parseFloat((totalMiles + returnMiles).toFixed(2));
      }
    } catch(e) {}
    const logEntry = {
      date: new Date().toLocaleDateString(),
      startTime: routeStartTime?.toLocaleTimeString(),
      endTime: new Date().toLocaleTimeString(),
      stops: stops.filter(s => s.done).length,
      miles: finalMiles,
      purpose: "Process Service",
      deduction: parseFloat((finalMiles * IRS_RATE_2026).toFixed(2)),
      legs: legMiles,
    };
    const updated = [...routeLog, logEntry];
    setRouteLog(updated);
    try { localStorage.setItem("routeLog", JSON.stringify(updated)); } catch {}
    setRouteActive(false);
    setTotalMiles(0);
    setLegMiles([]);
    setRouteStartTime(null);
  }

  // Choose today's stops: "Skip" takes a stop off today's route (it stays in
  // "Not today" and can be added back); only chosen stops go to Google Maps.
  const activeStops = stops.filter(s => !s.done && !s.skipped);
  const doneStops = stops.filter(s => s.done);
  const skippedStops = stops.filter(s => !s.done && s.skipped);
  function skipStop(id) { setStops(prev => prev.map(s => s.id === id ? { ...s, skipped: true } : s)); if (currentStop === id) setCurrentStop(null); }
  function unskipStop(id) { setStops(prev => prev.map(s => s.id === id ? { ...s, skipped: false } : s)); }

  function markDone(id) { setStops(prev => prev.map(s => s.id === id ? { ...s, done: true } : s)); if (currentStop === id) setCurrentStop(null); }
  function markUndone(id) { setStops(prev => prev.map(s => s.id === id ? { ...s, done: false } : s)); }
  // Move a stop up/down among TODAY's stops (skipped and done stops don't count).
  function moveAmongActive(id, dir) {
    setStops(prev => {
      const act = prev.map((p, i) => (!p.done && !p.skipped ? i : -1)).filter(i => i >= 0);
      const pos = act.indexOf(prev.findIndex(p => p.id === id));
      const other = act[pos + dir];
      if (pos < 0 || other === undefined) return prev;
      const arr = [...prev]; const i = act[pos]; [arr[i], arr[other]] = [arr[other], arr[i]]; return arr;
    });
  }

  function buildGoogleMapsUrl(stopsArr, start) {
    const active = stopsArr.filter(s => !s.done && !s.skipped);
    if (active.length === 0) return null;
    const origin = encodeURIComponent(start || "Corona, CA 92883");
    const destination = encodeURIComponent(active[active.length-1].address);
    const waypoints = active.slice(0,-1).map(s => encodeURIComponent(s.address)).join("|");
    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    if (waypoints) url += `&waypoints=${waypoints}`;
    return url;
  }

  function navigateTo(address) { window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`, "_blank"); }
  function openFullRoute() { const url = buildGoogleMapsUrl(stops, startAddr); if (url) window.open(url, "_blank"); }


  return (
    <div>
      <div className="flex-between" style={{ marginBottom: "1rem" }}>
        <div><h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>Today's Route</h3><p className="text-sm">Reorder stops · Check off when done · Launch in Google Maps</p></div>
      </div>
      {/* Route Control Bar */}
      <div className="route-control-bar">
        <div className="rc-stat"><div className="rc-val">{stops.length}</div><div className="rc-lbl">Stops</div></div>
        <div className="rc-stat"><div className="rc-val">{activeStops.length}</div><div className="rc-lbl">Remaining</div></div>
        <div className="rc-stat"><div className="rc-val">{doneStops.length}</div><div className="rc-lbl">Done</div></div>
        {routeActive && <div className="rc-stat"><div className="rc-val">{totalMiles.toFixed(1)}</div><div className="rc-lbl">Miles</div></div>}
        {routeActive && <div className="rc-stat"><div className="rc-val">${(totalMiles * IRS_RATE_2026).toFixed(2)}</div><div className="rc-lbl">Deduction</div></div>}
        <div style={{ flex: 1, textAlign: "left", paddingLeft: "0.5rem" }}>
          {!routeActive ? (
            <div>
              <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", marginBottom: "0.3rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>Starting From</div>
              <input value={startAddr} onChange={e => setStartAddr(e.target.value)} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 4, padding: "0.4rem 0.6rem", color: "var(--white)", fontSize: "0.82rem", width: "100%", outline: "none", fontFamily: "Inter, sans-serif", marginBottom: "0.5rem" }} />
              <button className="start-route-btn" onClick={startRoute}>▶ Start Route & Track Mileage</button>
              {routeGPSError && <div style={{ fontSize: "0.75rem", color: "#FCA5A5", marginTop: "0.3rem" }}>{routeGPSError}</div>}
            </div>
          ) : (
            <div>
              <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", marginBottom: "0.3rem" }}>
                Route started {routeStartTime?.toLocaleTimeString()} · GPS tracking active
              </div>
              <button className="end-route-btn" onClick={endRoute}>⏹ End Route & Save Mileage</button>
            </div>
          )}
        </div>
      </div>
      <div className="route-btn-row">
        <button className="btn-primary" onClick={openFullRoute} disabled={activeStops.length === 0} style={{ opacity: activeStops.length === 0 ? 0.4 : 1 }}>🗺️ Open Full Route in Google Maps</button>
      </div>
      <form data-testid="route-add-stop" onSubmit={e => { e.preventDefault(); addExtraStop(); }}
        style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "flex-end", margin: "0 0 1rem", padding: "0.75rem", border: "2px solid var(--light)", borderRadius: 8 }}>
        <div className="field" style={{ flex: "2 1 220px", margin: 0 }}><label>Add a stop — address</label><input value={newAddr} onChange={e => setNewAddr(e.target.value)} placeholder="Street, city, ZIP" /></div>
        <div className="field" style={{ flex: "1 1 140px", margin: 0 }}><label>Label (optional)</label><input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="e.g. Court filing" /></div>
        <button type="submit" className="btn-primary" data-testid="route-add-stop-btn" disabled={!newAddr.trim()}>＋ Add Stop</button>
      </form>
      {stops.length === 0 && <div style={{ textAlign: "center", padding: "1.5rem", color: "var(--gray)", fontSize: "0.85rem" }}>No open jobs to route. Add a stop above.</div>}
      <div className="route-planner">
        <div>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.75rem", color: "var(--gray)" }}>Active Stops ({activeStops.length})</div>
          {activeStops.map((stop, idx) => (
            <div key={stop.id} className={`route-stop${currentStop === stop.id ? " active" : ""}`}>
              <div className={`stop-num${currentStop === stop.id ? " current-num" : ""}`}>{idx+1}</div>
              <div className="stop-info">{stop.extra
                ? <><div className="stop-name">{stop.label || "Added stop"}</div><div className="stop-addr">{stop.address}</div></>
                : <><div className="stop-name">{stop.client || stop.defendant}</div><div className="text-sm">Serve: {stop.defendant || "—"}</div><div className="stop-addr">{stop.address}</div><div style={{ marginTop: "0.3rem" }}><StatusChip status={stop.status} /></div></>}</div>
              <div className="stop-actions">
                <button className="icon-btn" onClick={() => moveAmongActive(stop.id, -1)}>↑</button>
                <button className="icon-btn" onClick={() => moveAmongActive(stop.id, 1)}>↓</button>
                <button className="icon-btn go" onClick={() => navigateToStop(stop, activeStops.indexOf(stop))}>▶</button>
                <button className="icon-btn" style={{ background: "var(--green)", color: "var(--white)" }} onClick={() => markDone(stop.id)}>✓</button>
                <button className="icon-btn" data-testid="route-skip-stop" title="Not today" onClick={() => skipStop(stop.id)} style={{ width: "auto", padding: "0 0.5rem", fontSize: "0.75rem" }}>Skip</button>
                {stop.extra && <button className="icon-btn" data-testid="route-remove-stop" title="Remove this stop" onClick={() => removeExtraStop(stop.id)}>✕</button>}
              </div>
            </div>
          ))}
          {skippedStops.length > 0 && (<>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", margin: "1rem 0 0.75rem", color: "var(--gray)" }}>Not today ({skippedStops.length})</div>
            {skippedStops.map(stop => (
              <div key={stop.id} className="route-stop" style={{ opacity: 0.7 }}>
                <div className="stop-num">–</div>
                <div className="stop-info"><div className="stop-name">{stop.extra ? (stop.label || "Added stop") : (stop.client || stop.defendant)}</div>{!stop.extra && <div className="text-sm">Serve: {stop.defendant || "—"}</div>}<div className="stop-addr">{stop.address}</div></div>
                <button type="button" className="btn-secondary" data-testid="route-unskip-stop" style={{ fontSize: "0.75rem" }} onClick={() => unskipStop(stop.id)}>＋ Add to route</button>
              </div>
            ))}
          </>)}
          {doneStops.length > 0 && (<>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", margin: "1rem 0 0.75rem", color: "var(--gray)" }}>Completed ({doneStops.length})</div>
            {doneStops.map(stop => (
              <div key={stop.id} className="route-stop done">
                <div className="stop-num done-num">✓</div>
                <div className="stop-info"><div className="stop-name" style={{ textDecoration: "line-through" }}>{stop.extra ? (stop.label || "Added stop") : (stop.client || stop.defendant)}</div>{!stop.extra && <div className="text-sm">Serve: {stop.defendant || "—"}</div>}<div className="stop-addr">{stop.address}</div></div>
                <button className="icon-btn" onClick={() => markUndone(stop.id)}>↩</button>
                {stop.extra && <button className="icon-btn" title="Remove this stop" onClick={() => removeExtraStop(stop.id)}>✕</button>}
              </div>
            ))}
          </>)}
        </div>
        <div>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.75rem", color: "var(--gray)" }}>Current / Next Stop</div>
          {currentStop ? (() => {
            const s = stops.find(x => x.id === currentStop);
            return s ? (
              <div>
                <div style={{ background: "var(--navy)", borderRadius: 8, padding: "1rem", color: "var(--white)", marginBottom: "1rem" }}>
                  <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", textTransform: "uppercase" }}>Navigating to</div>
                  <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, fontSize: "1.1rem", margin: "0.3rem 0" }}>{s.defendant || s.label || "Added stop"}</div>
                  <div style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.7)" }}>{s.address}</div>
                  <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem" }}>
                    <button className="btn-gold" onClick={() => navigateTo(s.address)}>📍 Reopen in Maps</button>
                    <button className="btn-secondary" onClick={() => markDone(s.id)}>✓ Mark Done</button>
                  </div>
                </div>
                <div className="map-preview"><iframe title="map" src={`https://maps.google.com/maps?q=${encodeURIComponent(s.address)}&output=embed&z=15`} /></div>
              </div>
            ) : null;
          })() : activeStops.length > 0 ? (
            <div>
              <div style={{ background: "var(--cream)", borderRadius: 8, padding: "1rem", marginBottom: "1rem", border: "2px solid var(--light)" }}>
                <div style={{ fontSize: "0.75rem", color: "var(--gray)", textTransform: "uppercase" }}>Next Up</div>
                <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, fontSize: "1rem", margin: "0.3rem 0", color: "var(--navy)" }}>{activeStops[0].defendant || activeStops[0].label || "Added stop"}</div>
                <div style={{ fontSize: "0.82rem", color: "var(--gray)" }}>{activeStops[0].address}</div>
                <div style={{ marginTop: "0.75rem" }}>
                  <button className="btn-primary" onClick={() => navigateToStop(activeStops[0], 0)}>▶ Go Here</button>
                </div>
              </div>
              <div className="map-preview"><iframe title="map" src={`https://maps.google.com/maps?q=${encodeURIComponent(activeStops[0].address)}&output=embed&z=14`} /></div>
            </div>
          ) : (
            <div className="map-preview"><div className="map-placeholder"><div className="map-icon">🎉</div><div style={{ fontWeight: 700, color: "var(--navy)" }}>All stops complete!</div></div></div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── REUSABLE ADDRESS AUTOCOMPLETE (GOOGLE PLACES) ─────────────────────────────
// The approved provider is Google Places. Each mounted instance owns its own
// prediction state and session token, so two Service Jobs can never overwrite
// each other's selections. Selecting a prediction pulls the Place details and
// fills the standardized street address plus city/state/ZIP from Google's own
// address_components via onSelect.
//
// When REACT_APP_GOOGLE_MAPS_API_KEY is absent the field degrades to explicit,
// clearly-labeled MANUAL ENTRY — no other provider is called, and required
// address/ZIP validation still applies.
function AddressAutocomplete({ label, value, onChange, onSelect, required }) {
  const [predictions, setPredictions] = useState([]);
  const [googleReady, setGoogleReady] = useState(false);
  const acceptedRef = useRef(false);
  const svcRef = useRef(null);      // { autocomplete, places, sessionToken }
  const configured = isGooglePlacesConfigured();

  // Load Google once per page; each instance keeps its own service handles.
  useEffect(() => {
    if (!configured) return;
    let alive = true;
    loadGooglePlaces()
      .then((google) => {
        if (!alive) return;
        svcRef.current = {
          autocomplete: new google.maps.places.AutocompleteService(),
          places: new google.maps.places.PlacesService(document.createElement("div")),
          sessionToken: new google.maps.places.AutocompleteSessionToken(),
        };
        setGoogleReady(true);
      })
      .catch(() => { if (alive) setGoogleReady(false); });
    return () => { alive = false; };
  }, [configured]);

  useEffect(() => {
    const addr = String(value || "");
    if (acceptedRef.current) { acceptedRef.current = false; setPredictions([]); return; }
    if (!googleReady || !svcRef.current || addr.length < 4) { setPredictions([]); return; }
    const timer = setTimeout(() => {
      svcRef.current.autocomplete.getPlacePredictions(
        { input: addr, componentRestrictions: { country: "us" }, types: ["address"], sessionToken: svcRef.current.sessionToken },
        (res, status) => {
          const ok = typeof window !== "undefined" && window.google
            && status === window.google.maps.places.PlacesServiceStatus.OK;
          setPredictions(ok && Array.isArray(res) ? res.slice(0, 5) : []);
        }
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [value, googleReady]);

  function choose(prediction) {
    acceptedRef.current = true;
    setPredictions([]);
    if (!svcRef.current) return;
    svcRef.current.places.getDetails(
      { placeId: prediction.place_id, fields: ["address_components", "formatted_address"], sessionToken: svcRef.current.sessionToken },
      (place, status) => {
        const ok = typeof window !== "undefined" && window.google
          && status === window.google.maps.places.PlacesServiceStatus.OK && place;
        // Only Google-returned components populate the fields — nothing guessed.
        if (ok) onSelect(placeToAddress(place));
        else onChange(prediction.description || "");
        // A new session token starts the next billing session.
        if (window.google) svcRef.current.sessionToken = new window.google.maps.places.AutocompleteSessionToken();
      }
    );
  }

  return (
    <div className="field" style={{ position: "relative" }}>
      <label style={{ fontSize: "0.75rem" }}>{label}{required ? " *" : ""}</label>
      <input value={value} autoComplete="off"
        placeholder={configured ? "Start typing street address…" : "Enter the full service address"}
        onChange={e => onChange(e.target.value)}
        onBlur={() => setTimeout(() => setPredictions([]), 200)} />
      {!configured && (
        <div data-testid="places-manual-fallback" style={{ fontSize: "0.75rem", color: "var(--gray)", marginTop: "0.15rem" }}>
          Manual entry — Google address autocomplete is not configured. Enter the address and ZIP.
        </div>
      )}
      {predictions.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 30, background: "var(--white)", border: "2px solid var(--light)", borderRadius: 6, boxShadow: "0 6px 16px rgba(0,0,0,0.12)", maxHeight: 190, overflowY: "auto" }}>
          {predictions.map((p) => (
            <div key={p.place_id} style={{ padding: "0.45rem 0.6rem", cursor: "pointer", fontSize: "0.8rem", borderBottom: "1px solid var(--light)" }}
              onMouseDown={() => choose(p)}>
              {p.description}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Fingerprint / face login (passkeys) + installable admin app ────────────
// The phone checks the fingerprint/face; the server only ever sees a signature
// from the phone's key. See netlify/functions/admin-passkey.js.
const b64uToBuf = (s) => { const str = String(s).replace(/-/g, "+").replace(/_/g, "/"); const bin = atob(str + "===".slice((str.length + 3) % 4)); const a = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) a[i] = bin.charCodeAt(i); return a.buffer; };
const bufToB64u = (buf) => { const a = new Uint8Array(buf); let s = ""; for (let i = 0; i < a.length; i++) s += String.fromCharCode(a[i]); return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""); };
const passkeySupported = () => typeof window !== "undefined" && !!window.PublicKeyCredential && !!(navigator.credentials && navigator.credentials.create);
const isInstalledApp = () => { try { return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true; } catch (e) { return false; } };
const deviceLabel = () => { const ua = navigator.userAgent || ""; return /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad" : /Android/.test(ua) ? "Android phone" : "Computer"; };
async function passkeyCall(payload) {
  const res = await fetch("/.netlify/functions/admin-passkey", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || !d.ok) throw new Error(d.error || "Fingerprint login is not available right now.");
  return d;
}
// This device's own passkey id, remembered after it is set up or used. Naming
// it in the request lets the phone go STRAIGHT to the fingerprint, instead of
// first showing a "Use saved passkey? · Continue" account sheet.
const PASSKEY_ID_KEY = "cpsPasskeyId";
const savedPasskeyId = () => { try { return localStorage.getItem(PASSKEY_ID_KEY) || ""; } catch (e) { return ""; } };
const rememberPasskey = (rawId) => { try { localStorage.setItem("cpsPasskeyOn", "1"); if (rawId) localStorage.setItem(PASSKEY_ID_KEY, bufToB64u(rawId)); } catch (e) {} };
const passkeyOnHere = () => { try { return localStorage.getItem("cpsPasskeyOn") === "1" || !!localStorage.getItem(PASSKEY_ID_KEY); } catch (e) { return false; } };
// SPEED: the sign-in challenge is fetched the moment the app loads, in
// parallel with everything else, so the fingerprint opens without waiting on
// the server. A challenge is good for 5 minutes and used once.
let pkPrefetch = null;
function prefetchPasskeyOptions() {
  try {
    // Every device: a passkey saved in her Google account works on the computer too.
    if (!passkeySupported()) return;
    if (window.__cpsEarlyPk) return; // already asked for the fingerprint from index.html
    pkPrefetch = { at: Date.now(), p: passkeyCall({ action: "login-options" }).catch(() => null) };
  } catch (e) { pkPrefetch = null; }
}
if (typeof window !== "undefined" && /[?&]admin=true/.test(window.location.search || "")) prefetchPasskeyOptions();
async function passkeySignIn() {
  // The fingerprint may already have been asked for the instant the page
  // opened (public/index.html) — use that answer instead of asking again.
  const early = typeof window !== "undefined" ? window.__cpsEarlyPk : null;
  if (early && Date.now() - early.at < 4 * 60 * 1000) {
    window.__cpsEarlyPk = null;
    let got = null;
    try { got = await early.p; }
    catch (e) { if (e && e.name === "NotAllowedError") { e.early = true; throw e; } got = null; }
    if (got && got.cred) {
      const cred = got.cred, r = cred.response;
      const done = await passkeyCall({ action: "login-verify", challengeId: got.o.challengeId, credential: { id: bufToB64u(cred.rawId), clientDataJSON: bufToB64u(r.clientDataJSON), authenticatorData: bufToB64u(r.authenticatorData), signature: bufToB64u(r.signature) } });
      rememberPasskey(cred.rawId);
      return done;
    }
  }
  let o = null;
  if (pkPrefetch && Date.now() - pkPrefetch.at < 4 * 60 * 1000) o = await pkPrefetch.p;
  pkPrefetch = null;
  if (!o) o = await passkeyCall({ action: "login-options" });
  const id = savedPasskeyId();
  // "internal" only: this phone's own fingerprint — Android doesn't stop to
  // look for a nearby phone or a security key first.
  const allowCredentials = id ? [{ type: "public-key", id: b64uToBuf(id), transports: ["internal"] }] : [];
  const cred = await navigator.credentials.get({ publicKey: { challenge: b64uToBuf(o.options.challenge), rpId: o.options.rpId, userVerification: "required", timeout: o.options.timeout, allowCredentials } });
  const r = cred.response;
  const done = await passkeyCall({ action: "login-verify", challengeId: o.challengeId, credential: { id: bufToB64u(cred.rawId), clientDataJSON: bufToB64u(r.clientDataJSON), authenticatorData: bufToB64u(r.authenticatorData), signature: bufToB64u(r.signature) } });
  rememberPasskey(cred.rawId);
  return done;
}
async function passkeyRegister() {
  const o = await passkeyCall({ action: "register-options" });
  const op = o.options;
  const cred = await navigator.credentials.create({ publicKey: { ...op, challenge: b64uToBuf(op.challenge), user: { ...op.user, id: b64uToBuf(op.user.id) }, excludeCredentials: (op.excludeCredentials || []).map(c => ({ type: c.type, id: b64uToBuf(c.id) })) } });
  const r = cred.response;
  if (typeof r.getPublicKey !== "function" || !r.getPublicKey()) throw new Error("This browser can't set up fingerprint login. Use Chrome (Android) or Safari (iPhone).");
  rememberPasskey(cred.rawId);
  return passkeyCall({ action: "register-verify", challengeId: o.challengeId, deviceName: deviceLabel(), credential: { id: bufToB64u(cred.rawId), alg: r.getPublicKeyAlgorithm(), publicKey: bufToB64u(r.getPublicKey()), clientDataJSON: bufToB64u(r.clientDataJSON), authenticatorData: bufToB64u(r.getAuthenticatorData()) } });
}
// Opening the fingerprint by itself (no tap). Phones refuse it while the page
// is not yet in focus (e.g. the moment the app comes back on screen) and that
// refusal looks like a "cancel". So wait until the page has focus, and if the
// phone refuses straight away, try once more a moment later.
const waitForFocus = () => new Promise((res) => {
  if (typeof document === "undefined" || (document.visibilityState === "visible" && document.hasFocus())) return res();
  const done = () => { if (document.visibilityState === "visible") { window.removeEventListener("focus", done); document.removeEventListener("visibilitychange", done); setTimeout(res, 250); } };
  window.addEventListener("focus", done); document.addEventListener("visibilitychange", done);
});
async function passkeySignInAuto() {
  await waitForFocus();
  const t0 = Date.now();
  try { return await passkeySignIn(); }
  catch (e) {
    // A cancel on the early prompt was her choice — don't ask again by itself.
    if (!(e && e.name === "NotAllowedError") || e.early || Date.now() - t0 > 1500) throw e;
    await new Promise((r) => setTimeout(r, 700));
    await waitForFocus();
    return passkeySignIn();
  }
}
const passkeyErrorText = (e) => (e && e.name === "NotAllowedError") ? "Fingerprint login was cancelled." : (e && e.name === "InvalidStateError") ? "This phone already has fingerprint login." : ((e && e.message) || "Fingerprint login did not work.");
// Chrome's "Install app" offer, kept so the admin can tap our own button.
let deferredInstall = null;
if (typeof window !== "undefined") window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredInstall = e; });

// Always run the newest version: when the app/page is opened or brought back
// to the screen, check whether a newer version has been published and, if so,
// reload once. (Phones keep apps open for days, so they kept showing old
// versions.) Never reloads while the page is on screen, so typing isn't lost.
function AutoUpdate() {
  useEffect(() => {
    const loaded = Array.from(document.scripts).map(s => s.src).find(s => /\/static\/js\/main\.[^/]+\.js/.test(s));
    if (!loaded) return;   // local dev
    let busy = false;
    const check = async () => {
      if (busy) return; busy = true;
      try {
        const html = await fetch(`/index.html?v=${Date.now()}`, { cache: "no-store" }).then(r => r.text());
        const m = html.match(/\/static\/js\/main\.[^"']+\.js/);
        // Never reload in the middle of an entry; it updates next time.
        let busyEntry = false;
        try { const p = JSON.parse(localStorage.getItem("cpsAdminPlace") || "null"); busyEntry = !!(p && p.attemptOpenFor); } catch (e) {}
        if (m && !loaded.endsWith(m[0]) && !busyEntry) window.location.reload();
      } catch (e) {} finally { busy = false; }
    };
    const onShow = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onShow);
    window.addEventListener("pageshow", onShow);
    check();
    return () => { document.removeEventListener("visibilitychange", onShow); window.removeEventListener("pageshow", onShow); };
  }, []);
  return null;
}

// On the Admin page the browser installs "CPS Admin" (opens straight to the
// admin, see public/admin-manifest.json) instead of the client portal.
function AdminAppSetup() {
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prev = link && link.getAttribute("href");
    if (link) link.setAttribute("href", "/admin-manifest.json");
    if ("serviceWorker" in navigator && window.location.protocol === "https:") navigator.serviceWorker.register("/sw.js").catch(() => {});
    return () => { if (link && prev) link.setAttribute("href", prev); };
  }, []);
  return null;
}

function AdminPanel({ availability = "open", setAvailability = () => {} }) {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [noteForm, setNoteForm] = useState({ status: "attempted", date: "", time: "", location: "", who: "", desc: "", mailingDate: "", mailingMethod: "", mailingTracking: "" });
  const [showProof, setShowProof] = useState(false);
  // Which saved attempt is opened up to its full details.
  const [openAttemptId, setOpenAttemptId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  // Photo-upload failures, shown next to the photos that are still unsaved.
  const [photoMsg, setPhotoMsg] = useState("");
  // Substituted service before diligence is met: an explicit decision, with a
  // reason, confirmed once. Reset whenever an entry is saved or abandoned.
  const [diligenceOverride, setDiligenceOverride] = useState({ used: false, reason: "", confirmed: false });
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [altDate, setAltDate] = useState("");
  const [altPrice, setAltPrice] = useState("");
  const [altPriceReason, setAltPriceReason] = useState("");
  const [gpsLoading, setGpsLoading] = useState(false);
  // Phone layout: the order list becomes an accordion (details under the card).
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(max-width: 900px)").matches : false));
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 900px)");
    const on = () => setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", on); else mq.removeListener(on); };
  }, []);
  // A save that the server REFUSED (e.g. diligence not met) is shown, and the
  // form is kept, instead of silently resetting as if it had saved.
  const [attemptMsg, setAttemptMsg] = useState("");
  // Save refused for due diligence: show the override right there (Nov 2).
  const [needOverride, setNeedOverride] = useState(false);
  // Live attempt updates: email the client after this attempt (eligible orders
  // only — Rush / Same-Day / Standard + Priority). Unchecked = "I'll update
  // them personally". Resets to checked after every save.
  const [notifyClient, setNotifyClient] = useState(true);
  const [clientUpdateMsg, setClientUpdateMsg] = useState("");
  // Saved evidence photos per attempt id (admin-only projections, no paths).
  const [attemptPhotos, setAttemptPhotos] = useState({});
  const [adminMsg, setAdminMsg] = useState("");
  // Phase 1: structured field capture (gated by the Phase 0 field_capture flag,
  // reported by get-orders). savingAttemptRef prevents duplicate submissions.
  const [captureEnabled, setCaptureEnabled] = useState(false);
  const savingAttemptRef = useRef(false);
  const [adminPass, setAdminPass] = useState("");
  // Remember (not the session itself — just a hint) that this browser is signed
  // in, so a refresh shows the dashboard at once while the server confirms the
  // secure cookie in the background. All data still requires the real session.
  const adminHint = (() => { try { return localStorage.getItem("cpsAdminSignedIn") === "1"; } catch (e) { return false; } })();
  const [authed, setAuthed] = useState(adminHint);
  const [sessionChecking, setSessionChecking] = useState(!adminHint);
  useEffect(() => { try { if (authed) localStorage.setItem("cpsAdminSignedIn", "1"); else if (!sessionChecking) localStorage.removeItem("cpsAdminSignedIn"); } catch (e) {} }, [authed, sessionChecking]); const [sessionMsg, setSessionMsg] = useState(""); const lastActivityRef = useRef(Date.now());
  // --- New admin auth: email/password + mandatory 2FA + secure httpOnly session
  // cookie. No password/OTP is stored client-side; the session is server-verified. ---
  const [loginStep, setLoginStep] = useState("creds");   // creds | choose | otp
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [loginMethods, setLoginMethods] = useState(["email"]);
  const [loginMethod, setLoginMethod] = useState("email");
  const [maskedDest, setMaskedDest] = useState("");
  const [chooseDest, setChooseDest] = useState({});      // { email: masked, sms: masked }
  const [loginErr, setLoginErr] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [resetPw, setResetPw] = useState(""); const [resetPw2, setResetPw2] = useState(""); const [resetMsg, setResetMsg] = useState("");
  // Show/Hide password toggles (hidden by default).
  const [showLoginPw, setShowLoginPw] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);
  const [showResetPw2, setShowResetPw2] = useState(false);
  const AUTH_BASE = "/.netlify/functions";
  async function adminLoginSubmit(explicitMethod) {
    // explicitMethod: undefined on the first submit (server may return a method
    // choice); "email"/"sms" when the admin has picked where to receive the code.
    setLoginErr(""); setLoginBusy(true);
    try {
      const payload = { email: loginEmail.trim(), password: loginPassword };
      if (explicitMethod) payload.method = explicitMethod;
      const res = await fetch(`${AUTH_BASE}/admin-login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok && data.choose) {
        // Both factors available — let the admin choose before any code is sent.
        setLoginMethods(data.methods || ["email"]); setChooseDest(data.destinations || {}); setLoginStep("choose");
      } else if (res.ok && data.ok) {
        setChallengeId(data.challengeId); setLoginMethods(data.methods || ["email"]); setLoginMethod(data.method || "email"); setMaskedDest(data.maskedDestination || ""); setOtpCode(""); setLoginStep("otp");
      }
      else if (res.status === 429) setLoginErr("Too many attempts. Please wait and try again.");
      else if (res.status === 502) setLoginErr(data.error || "Could not send the code. Try another method.");
      else setLoginErr(data.error || "Invalid email or password.");
    } catch (e) { setLoginErr("Login error. Please try again."); } finally { setLoginBusy(false); }
  }
  async function adminVerifySubmit() {
    setLoginErr(""); setLoginBusy(true);
    try {
      const res = await fetch(`${AUTH_BASE}/admin-verify-2fa`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ challengeId, code: otpCode.trim() }) });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) { setAuthed(true); setLoginStep("creds"); setLoginPassword(""); setOtpCode(""); setChallengeId(""); setSessionMsg(""); }
      else setLoginErr(data.error || "Invalid or expired code.");
    } catch (e) { setLoginErr("Verification error. Please try again."); } finally { setLoginBusy(false); }
  }
  const [pkLoginBusy, setPkLoginBusy] = useState(false);
  const [showPwLogin, setShowPwLogin] = useState(() => !passkeySupported());
  async function adminPasskeyLogin(auto) {
    if (pkLoginBusy) return;
    setLoginErr(""); setPkLoginBusy(true);
    try {
      await (auto === true ? passkeySignInAuto() : passkeySignIn());
      pkSignedInRef.current = true;
      setAppLocked(false);
      setAuthed(true); setLoginStep("creds"); setLoginPassword(""); setSessionMsg("");
    } catch (e) {
      // Closing the fingerprint box is not an error — the icon just waits to be tapped.
      if (!(e && e.name === "NotAllowedError")) setLoginErr(passkeyErrorText(e));
    } finally { setPkLoginBusy(false); }
  }
  // APP LOCK: in the installed app with fingerprint login on, the app asks for
  // the fingerprint every time it is opened, and again after it has been in
  // the background for more than a minute — even if still signed in.
  // Android may close a background app to save memory, and the app refreshes
  // itself for a new version — both restart the page. If the app was in use in
  // the last 30 minutes, that restart is treated as the same visit: no fingerprint.
  const lockWanted = () => { try { return isInstalledApp() && passkeySupported() && localStorage.getItem("cpsPasskeyOn") === "1" && !(Date.now() - Number(localStorage.getItem(LAST_USED_KEY) || 0) < LAST_USED_GRACE_MS); } catch (e) { return false; } };
  const [appLocked, setAppLocked] = useState(() => lockWanted());
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [unlockErr, setUnlockErr] = useState("");
  async function unlockApp(auto) {
    if (unlockBusy) return;
    setUnlockBusy(true); setUnlockErr("");
    try { await (auto === true ? passkeySignInAuto() : passkeySignIn()); pkSignedInRef.current = true; setAppLocked(false); setAuthed(true); }
    catch (e) { if (!(e && e.name === "NotAllowedError")) setUnlockErr(passkeyErrorText(e)); }
    finally { setUnlockBusy(false); }
  }
  const unlockTried = useRef(false);
  // Remember when the app was last in use (while open, and as it goes to the background).
  useEffect(() => {
    if (!authed || appLocked) return;
    const mark = () => { try { localStorage.setItem(LAST_USED_KEY, String(Date.now())); } catch (e) {} };
    mark();
    const id = setInterval(() => { if (document.visibilityState === "visible") mark(); }, 20000);
    document.addEventListener("visibilitychange", mark); window.addEventListener("pagehide", mark);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", mark); window.removeEventListener("pagehide", mark); };
  }, [authed, appLocked]);
  // A fingerprint sign-in that finishes before the first session check comes
  // back must not be undone by that (older) "not signed in" answer.
  const pkSignedInRef = useRef(false);
  // Opening the app: ask for the fingerprint straight away — no waiting for the
  // session check first. The login page's own auto-prompt is skipped so the
  // phone never asks twice.
  useEffect(() => { if (appLocked && !unlockTried.current) { unlockTried.current = true; passkeyAutoTried.current = true; unlockApp(true); } }, [appLocked]);
  // Switching to another app and back does NOT lock the app (Kari looks things
  // up in other apps mid-entry). The fingerprint is asked only when the app is
  // opened fresh.
  // Fingerprint already set up on this device (app OR browser): ask for the
  // fingerprint as soon as the login page opens (once), no tap needed.
  const passkeyAutoTried = useRef(false);
  useEffect(() => {
    if (authed || sessionChecking || passkeyAutoTried.current || !passkeySupported()) return;
    // Asked on EVERY device, not only one that has signed in with it before —
    // a passkey synced through her Google account is on the computer as well.
    passkeyAutoTried.current = true;
    adminPasskeyLogin(true);
  }, [authed, sessionChecking]);
  async function adminResend(switchMethod) {
    // switchMethod (optional) lets the admin choose where the code is sent when
    // more than one verified method is available.
    setLoginErr("");
    try {
      const payload = { challengeId }; if (switchMethod) payload.method = switchMethod;
      const res = await fetch(`${AUTH_BASE}/admin-resend-2fa`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setChallengeId(data.challengeId); setMaskedDest(data.maskedDestination || maskedDest);
        if (data.methods) setLoginMethods(data.methods);
        if (data.method) setLoginMethod(data.method);
        setOtpCode("");
        setLoginErr(switchMethod === "sms" ? "A code was texted to your phone." : (switchMethod === "email" ? "A code was emailed to you." : "A new code was sent."));
      } else if (res.status === 429) setLoginErr("Too many codes requested. Please wait.");
      else if (res.status === 502) setLoginErr(data.error || "Could not send the code. Try another method.");
    } catch (e) {}
  }
  async function adminForgotSubmit() {
    setForgotMsg("");
    try { const res = await fetch(`${AUTH_BASE}/admin-forgot-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: loginEmail.trim() }) }); const data = await res.json().catch(() => ({})); setForgotMsg(data.message || "If that email is associated with an admin account, password reset instructions have been sent."); } catch (e) { setForgotMsg("If that email is associated with an admin account, password reset instructions have been sent."); }
  }
  async function adminResetSubmit() {
    setResetMsg("");
    if (resetPw.length < 10) { setResetMsg("Choose a password of at least 10 characters."); return; }
    if (resetPw !== resetPw2) { setResetMsg("Passwords do not match."); return; }
    try { const res = await fetch(`${AUTH_BASE}/admin-reset-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token: resetToken, newPassword: resetPw }) }); const data = await res.json().catch(() => ({})); if (res.ok && data.ok) { setResetToken(""); setResetPw(""); setResetPw2(""); setSessionMsg("Password changed. Please sign in."); try { const url = new URL(window.location.href); url.searchParams.delete("admin_reset"); window.history.replaceState({}, "", url.toString()); } catch (e) {} } else setResetMsg(data.error || "This reset link is invalid or has expired."); } catch (e) { setResetMsg("Reset error. Please try again."); }
  }
  useEffect(() => {
    try { const t = new URL(window.location.href).searchParams.get("admin_reset"); if (t) setResetToken(t); } catch (e) {}
    (async () => { try { const res = await fetch(`${AUTH_BASE}/admin-whoami`); if (res.ok) setAuthed(true); else if ((res.status === 401 || res.status === 403) && !pkSignedInRef.current) setAuthed(false); } catch (e) {} finally { setSessionChecking(false); } })();
  }, []);
  // --- Other process servers (their own login; jobs assigned to them) ---
  const [fieldServers, setFieldServers] = useState([]);
  const [fsForm, setFsForm] = useState(null);
  const [fsMsg, setFsMsg] = useState("");
  async function fsCall(payload) {
    const res = await fetch("/.netlify/functions/admin-servers", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok && data.ok, data };
  }
  async function loadFieldServers() { const r = await fsCall({ action: "list" }); if (r.ok) setFieldServers(r.data.servers || []); }
  useEffect(() => { if (authed) loadFieldServers(); }, [authed]);
  async function saveFieldServer() {
    setFsMsg("");
    const r = await fsCall({ action: "save", server: fsForm });
    if (!r.ok) { setFsMsg("✗ " + (r.data.error || "Could not save.")); return; }
    setFsForm(null); setFsMsg("✓ Saved. They sign in at portal.coronaprocessserving.com/?server=true with that email."); loadFieldServers();
  }
  async function setFieldServerActive(sv, active) {
    const r = await fsCall({ action: "set_active", id: sv.id, active });
    setFsMsg(r.ok ? (active ? `✓ ${sv.name} can sign in again.` : `${sv.name}'s login is turned off.`) : "✗ " + (r.data.error || "Could not save."));
    loadFieldServers();
  }
  async function assignTarget(job, t, serverId) {
    const r = await fsCall({ action: "assign", jobNumber: job.id, targetId: t.id, serverId: serverId || null });
    if (!r.ok) { showNotice(r.data.error || "Could not assign."); return; }
    const sv = fieldServers.find(x => x.id === serverId);
    showNotice(sv ? `✓ Assigned to ${sv.name}${r.data.emailed ? " — emailed them" : ""}.` : "✓ Assigned to you.");
    loadJobs();
  }
  // --- SMS 2FA phone enrollment (settings) ---
  const [pkList, setPkList] = useState([]);
  const [pkBusy, setPkBusy] = useState(false);
  const [pkMsg, setPkMsg] = useState("");
  const [installReady, setInstallReady] = useState(!!deferredInstall);
  useEffect(() => {
    const on = (e) => { e.preventDefault(); deferredInstall = e; setInstallReady(true); };
    window.addEventListener("beforeinstallprompt", on);
    return () => window.removeEventListener("beforeinstallprompt", on);
  }, []);
  async function installApp() {
    if (!deferredInstall) return;
    deferredInstall.prompt();
    try { await deferredInstall.userChoice; } catch (e) {}
    deferredInstall = null; setInstallReady(false);
  }
  async function loadPasskeys() { try { const d = await passkeyCall({ action: "list" }); setPkList(d.passkeys || []); } catch (e) {} }
  useEffect(() => { if (authed) loadPasskeys(); }, [authed]);
  async function addPasskey() {
    if (pkBusy) return;
    setPkBusy(true); setPkMsg("");
    try {
      await passkeyRegister();
      try { localStorage.setItem("cpsPasskeyOn", "1"); } catch (e) {}
      setPkMsg("✓ Fingerprint login is on for this device. Next time, tap “Sign in with fingerprint / face.”");
      await loadPasskeys();
    } catch (e) { setPkMsg("✗ " + passkeyErrorText(e)); } finally { setPkBusy(false); }
  }
  async function removePasskey(p) {
    if (!(await askConfirm(`Turn off fingerprint login for ${p.deviceName}?`, "Turn off"))) return;
    setPkBusy(true); setPkMsg("");
    try { await passkeyCall({ action: "remove", id: p.id }); setPkMsg(`Fingerprint login turned off for ${p.deviceName}.`); await loadPasskeys(); }
    catch (e) { setPkMsg("✗ " + e.message); } finally { setPkBusy(false); }
  }
  const [phoneStatus, setPhoneStatus] = useState(null);   // { configured, phoneMasked, verified, pending }
  const [phoneStep, setPhoneStep] = useState("idle");     // idle | entering | verifying
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneChallengeId, setPhoneChallengeId] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneMsg, setPhoneMsg] = useState("");
  const [phoneBusy, setPhoneBusy] = useState(false);
  async function loadPhoneStatus() {
    try { const res = await fetch(`${AUTH_BASE}/admin-phone-status`); if (res.ok) { const d = await res.json().catch(() => null); if (d && d.ok) setPhoneStatus(d); } } catch (e) {}
  }
  useEffect(() => { if (authed) loadPhoneStatus(); }, [authed]);
  async function phoneEnrollSubmit(isResend) {
    setPhoneMsg(""); setPhoneBusy(true);
    try {
      const res = await fetch(`${AUTH_BASE}/admin-phone-enroll`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone: phoneInput.trim() }) });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.ok) { setPhoneChallengeId(d.challengeId); setPhoneStep("verifying"); setPhoneCode(""); setPhoneMsg(isResend ? "A new code was texted to your phone." : `Code texted to ${d.maskedPhone}.`); }
      else if (res.status === 429) setPhoneMsg("Too many codes requested. Please wait and try again.");
      else if (res.status === 503) setPhoneMsg("SMS is not configured yet.");
      else setPhoneMsg(d.error || "Could not send the verification text.");
    } catch (e) { setPhoneMsg("Error sending the text. Please try again."); } finally { setPhoneBusy(false); }
  }
  async function phoneVerifySubmit() {
    setPhoneMsg(""); setPhoneBusy(true);
    try {
      const res = await fetch(`${AUTH_BASE}/admin-phone-verify`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ challengeId: phoneChallengeId, code: phoneCode.trim() }) });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.ok) { setPhoneStep("idle"); setPhoneInput(""); setPhoneCode(""); setPhoneChallengeId(""); setPhoneMsg("Phone verified. Text-message codes are now available at login."); loadPhoneStatus(); }
      else setPhoneMsg(d.error || "Invalid or expired code.");
    } catch (e) { setPhoneMsg("Verification error. Please try again."); } finally { setPhoneBusy(false); }
  }
  const loadedJobsRef = useRef([]);
  async function loadJobs() {
    try {
      const res = await fetch("/.netlify/functions/get-orders", { headers: {} });
      if (!res.ok) throw new Error("Failed to load orders");
      const data = await res.json();
      setJobs(data.jobs || []);
      loadedJobsRef.current = data.jobs || [];
      setRefundsAllowed(!!data.refundsEnabled);
      setCaptureEnabled(!!data.captureEnabled);
      lastActivityRef.current = Date.now();
    } catch (err) { console.error(err); }
  }
  // Refresh only the persisted attempts for one job (used after a capture), so
  // the admin attempt-history reflects the server without a full reload.
  async function refreshAttempts(jobNumber) {
    try {
      const res = await fetch("/.netlify/functions/get-attempts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobNumber }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setJobs(prev => prev.map(j => j.id === jobNumber ? { ...j, serviceAttempts: data.attempts || [] } : j));
      return data.attempts || [];
    } catch (e) { /* non-fatal */ }
    return null;
  }
  useEffect(() => {
    if (!authed) return; lastActivityRef.current = Date.now();
    loadJobs();
  }, [authed]); function logoutAdmin(message) { try { fetch("/.netlify/functions/admin-logout", { method: "POST" }); } catch (e) {} try { localStorage.removeItem(LAST_USED_KEY); } catch (e) {} setAuthed(false); setAdminPass(""); setLoginPassword(""); setOtpCode(""); setChallengeId(""); setLoginStep("creds"); setJobs([]); setSelected(null); setSessionMsg(message || ""); } // Inactivity policy: 2 hours idle, evaluated every 30 seconds.
  // The tracked event set was previously click/keydown/scroll only, so filling a
  // long form — typing into inputs, choosing selects, submitting — could look
  // "idle" and expire an admin who was actively working. These are the
  // interactions that reset the SAME idle clock; the server's separate
  // 12-hour absolute session limit is untouched.
  useEffect(() => {
    if (!authed) return;
    function markActivity() { lastActivityRef.current = Date.now(); }
    // Passive where the listener never calls preventDefault — scroll/touchstart
    // are the ones the browser can optimize; the rest are plain listeners.
    const PASSIVE = ["scroll", "touchstart"];
    const events = ["click", "mousedown", "keydown", "input", "change", "submit", "scroll", "touchstart"];
    events.forEach(evt => window.addEventListener(evt, markActivity, PASSIVE.includes(evt) ? { passive: true } : undefined));
    const intervalId = setInterval(() => {
      if (Date.now() - lastActivityRef.current > ADMIN_IDLE_TIMEOUT_MS) {
        logoutAdmin("Your session has expired. Please sign in again.");
      }
    }, 30000);
    return () => {
      // Remove with the SAME options shape used to add, so every listener is
      // detached and none leak across re-authentication.
      events.forEach(evt => window.removeEventListener(evt, markActivity, PASSIVE.includes(evt) ? { passive: true } : undefined));
      clearInterval(intervalId);
    };
  }, [authed]);
  // Back/forward-cache restore. This used to unconditionally sign the admin out,
  // which threw away a session the server still considered valid — a plain Back
  // navigation looked like an expiry. Ask the server instead and only clear when
  // it says the session is gone.
  useEffect(() => {
    let cancelled = false;
    async function handlePageShow(e) {
      if (!e.persisted) return;
      try {
        const res = await fetch(`${AUTH_BASE}/admin-whoami`);
        if (cancelled) return;
        if (res.ok) { setAuthed(true); return; }   // still valid — stay signed in
      } catch (err) {
        if (cancelled) return;
        // Network failure is NOT proof the session ended; leave state alone
        // rather than logging out a working admin. The 30-minute idle timer and
        // the server's 12-hour limit both still apply.
        return;
      }
      if (cancelled) return;
      setAuthed(false); setAdminPass(""); setJobs([]); setSelected(null);
    }
    window.addEventListener("pageshow", handlePageShow);
    return () => { cancelled = true; window.removeEventListener("pageshow", handlePageShow); };
  }, []);
  const [adminTab, setAdminTab] = useState("jobs");
  // --- Delete an incorrectly recorded service attempt (admin only) ---
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState("");
  // The delete message belongs to ONE order. Without this it survived switching
  // orders, so a failure reported against one job appeared under another —
  // which reads as a second, non-existent failure. Keyed on the selected job
  // number so it clears on every change of order (including back to the
  // original, so a stale message is never restored). Deliberately narrow: no
  // other admin alert is touched.
  useEffect(() => { setDeleteMsg(""); }, [selected]);
  async function confirmDeleteAttempt(job, row, t) {
    if (deleteBusy) return;
    // The confirmation names the attempt precisely so the wrong one cannot be
    // removed by mistake.
    const summary = `Attempt ${t.number} — ${t.date || "no date"} ${t.time || ""}\nOutcome: ${t.label}\nMethod: ${row.manner_attempted || "—"}`;
    if (!(await askConfirm(`Delete this service attempt? This cannot be undone.\n\n${summary}`, "Delete"))) return;
    setDeleteBusy(true); setDeleteMsg("");
    try {
      const res = await fetch("/.netlify/functions/admin-delete-attempt", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobNumber: job.id, targetId: row.target_id, attemptId: row.id }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.ok) { setDeleteMsg(d.error || `Could not delete the attempt (HTTP ${res.status}).`); return; }
      setDeleteMsg(
        `Attempt deleted.${d.renumbered ? ` ${d.renumbered} remaining attempt(s) renumbered.` : ""}` +
        `${d.documentsMarkedStale ? ` ${d.documentsMarkedStale} generated document(s) marked out of date — regenerate before signing.` : ""}`
      );
      // Reload immediately so the list reflects the server, including renumbering.
      await refreshAttempts(job.id);
      await loadJobs();
    } catch (e) { setDeleteMsg("Could not delete the attempt. Please try again."); }
    finally { setDeleteBusy(false); }
  }
  // --- Pricing review: approve a document type + price for an unpriced
  // "Other" submission. Sending the payment link afterwards is unchanged.
  const [priceForm, setPriceForm] = useState({ docType: "", useCalculated: true, amountDue: "" });
  const [priceBusy, setPriceBusy] = useState(false);
  const [priceMsg, setPriceMsg] = useState("");
  async function approvePrice(job) {
    if (priceBusy) return;
    if (!priceForm.docType) { setPriceMsg("Select the approved document type."); return; }
    setPriceBusy(true); setPriceMsg("");
    try {
      const res = await fetch("/.netlify/functions/admin-approve-price", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jobNumber: job.id, docType: priceForm.docType,
          useCalculatedPrice: priceForm.useCalculated,
          amountDue: priceForm.useCalculated ? undefined : priceForm.amountDue,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.ok) { setPriceMsg(d.error || `Could not save the approved price (HTTP ${res.status}).`); return; }
      setPriceMsg(`Approved: ${d.docType} — $${(d.amountDueCents / 100).toFixed(2)} (${d.priceSource}). Now send the payment link.`);
      setPriceForm({ docType: "", useCalculated: true, amountDue: "" });
      await loadJobs();
    } catch (e) { setPriceMsg("Could not save the approved price. Please try again."); }
    finally { setPriceBusy(false); }
  }
  // --- Process-server profile (Server Profile tab) ---
  // The Proof generator fails closed until this profile is complete: POS-010
  // item 7 prints the server's name, address and telephone, and items 7.e(i)/(ii)
  // the registration county/number. Readiness is NOT recomputed here — it comes
  // from the server, which uses the same serverProfileReadiness() the generator
  // uses, so this screen can never disagree with what actually blocks signing.
  const emptyProfileForm = { legal_name: "", registration_number: "", registration_county: "", address: "", phone: "", bond_information: "" };
  const [profileForm, setProfileForm] = useState(emptyProfileForm);
  const [profileReadiness, setProfileReadiness] = useState(null);
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMsg, setProfileMsg] = useState("");
  const [profileLoaded, setProfileLoaded] = useState(false);
  const setProfileField = (k, v) => setProfileForm(f => ({ ...f, [k]: v }));

  async function loadServerProfile() {
    setProfileBusy(true); setProfileMsg("");
    try {
      const res = await fetch("/.netlify/functions/get-server-profile", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.ok) { setProfileMsg(d.error || `Could not load the profile (HTTP ${res.status}).`); return; }
      if (d.profile) setProfileForm({ ...emptyProfileForm, ...Object.fromEntries(Object.keys(emptyProfileForm).map(k => [k, d.profile[k] == null ? "" : String(d.profile[k])])) });
      setProfileReadiness(d.readiness || null);
      setProfileLoaded(true);
    } catch (e) { setProfileMsg("Could not load the profile. Please try again."); }
    finally { setProfileBusy(false); }
  }
  async function saveServerProfile() {
    setProfileBusy(true); setProfileMsg("");
    try {
      const res = await fetch("/.netlify/functions/set-server-profile", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ profile: profileForm, adminName: "admin" }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.ok) { setProfileMsg(d.error || `Could not save the profile (HTTP ${res.status}).`); return; }
      if (d.readiness) setProfileReadiness(d.readiness);
      setProfileMsg(d.readiness && d.readiness.ready ? "Profile saved. Ready for Proof signing." : "Profile saved.");
    } catch (e) { setProfileMsg("Could not save the profile. Please try again."); }
    finally { setProfileBusy(false); }
  }
  // Load once when the tab is first opened; Reload re-fetches on demand.
  useEffect(() => { if (authed && adminTab === "profile" && !profileLoaded) loadServerProfile(); }, [authed, adminTab, profileLoaded]);
  const [proofTemplate, setProofTemplate] = useState('pos_summons');

  // Every change re-applies the defaults that follow from the KIND of attempt.
  // On a successful personal service the person contacted IS the person to
  // serve, so it is filled in rather than demanded — and only while the field is
  // still empty, so an admin correcting it is never overwritten.
  // One stable token per selected photo. It is the idempotency key the server
  // uses: a retried save finds the row that already exists instead of storing
  // the same photograph twice.
  const newPhotoToken = () =>
    (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `ph_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  // Send each not-yet-stored photo to the evidence endpoint. A failure is
  // reported and the photo is LEFT in the form so it can be retried — nothing is
  // marked saved that was not saved, and the attempt itself is unaffected either
  // way (a photo has never been what completes a service).
  async function uploadEvidencePhotos(jobNumber, attemptId, photos, fallbackGps) {
    const pending = (photos || []).filter((p) => p && p.dataUrl && !p.stored);
    if (!pending.length) return;
    const failures = [];
    for (const photo of pending) {
      try {
        const comma = String(photo.dataUrl).indexOf(",");
        const dataBase64 = comma > -1 ? String(photo.dataUrl).slice(comma + 1) : "";
        const res = await fetch("/.netlify/functions/admin-evidence-photo", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "upload", jobNumber, attemptId,
            clientToken: photo.token, filename: photo.filename, contentType: photo.contentType,
            // App-recorded evidence metadata: the moment the photo was taken or
            // added, the phone's position then (or the attempt's fix as a
            // fallback), and camera vs gallery. Never read from EXIF alone.
            capturedAtDevice: photo.capturedAtDevice || null,
            fileModifiedAt: photo.fileModifiedAt || null,
            source: photo.source || null,
            gps: photo.gps || fallbackGps || null,
            dataBase64,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.ok) {
          setNoteForm((f) => ({ ...f, photos: (f.photos || []).map((p) => (p.token === photo.token ? { ...p, stored: true, error: null } : p)) }));
        } else {
          const message = data.error || "That photo could not be saved.";
          failures.push(message);
          setNoteForm((f) => ({ ...f, photos: (f.photos || []).map((p) => (p.token === photo.token ? { ...p, stored: false, error: message } : p)) }));
        }
      } catch (e) {
        failures.push("A photo could not be saved.");
      }
    }
    setPhotoMsg(failures.length
      ? `${failures.length} photo(s) were not saved and are still attached below — press Save again to retry. ${failures[0]}`
      : "");
  }

  // Mailing (substitute service / posting) is done the day AFTER service:
  // the date fills in by itself as service date + 1 day, and follows the
  // service date until she changes the mailing date herself.
  useEffect(() => {
    const f = noteForm || {};
    if (!(f.serviceType === "substitute" || f.serviceType === "posting") || !f.date) return;
    if (f.mailingDate && !f.mailingDateAuto) return;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(f.date);
    if (!m) return;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 1);
    const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (f.mailingDate !== next) setNoteForm(x => ({ ...x, mailingDate: next, mailingDateAuto: true }));
  }, [noteForm && noteForm.serviceType, noteForm && noteForm.date, noteForm && noteForm.mailingDate]);
  const BLANK_NOTE_FORM = { status: "attempted", outcome: "", date: "", time: "", location: "", who: "", desc: "", gps: null, photos: [], photoStatement: "", locationType: "", serviceType: "", personPresent: "", identification: "", serviceAccepted: "", relationship: "", gender: "", age: "", ethnicity: "", height: "", weight: "", hair: "", features: [], featuresOther: "", personPresentFamily: "", personPresentDetail: "", personSaid: "", observed: "", observedOther: "", otherContact: "", mailingDate: "", mailingMethod: "", mailingTracking: "" };

  function setN(k, v) { setNoteForm(f => { const next = { ...f, [k]: v }; return { ...next, ...defaultsForKind(next) }; }); }
  const selectedJob = jobs.find(j => j.id === selected);
  // Opening an order stamps a fresh attempt with NOW (editable), loads its saved
  // evidence photos, and on a phone brings the opened card to the top.
  const selAttemptKey = selectedJob ? (selectedJob.serviceAttempts || []).map(a => a.id).join(",") : "";
  useEffect(() => { if (selected) setNoteForm(f => (f.date ? f : { ...f, ...nowLocalDateTime() })); }, [selected]);
  // Phone: an open order is its own page (see body.order-open CSS) — start at its top.
  useEffect(() => {
    try { document.body.classList.toggle("order-open", !!selected); } catch (e) {}
    return () => { try { document.body.classList.remove("order-open"); } catch (e) {} };
  }, [selected]);
  // Opening an order also loads each Service Job's documents, so a signed Proof
  // (View / Download) is visible on reopen without pressing "Show documents".
  useEffect(() => {
    if (selectedJob && Array.isArray(selectedJob.serviceTargets)) selectedJob.serviceTargets.forEach(t => loadSvcDocs(selectedJob, t));
  }, [selected]);
  useEffect(() => {
    if (selectedJob && selAttemptKey) loadAttemptPhotos(selectedJob.id, selectedJob.serviceAttempts);
  }, [selected, selAttemptKey]);
  useEffect(() => {
    if (!isMobile || !selected) return undefined;
    // The order opens as its own page on a phone — start at the top of it.
    const id = setTimeout(() => window.scrollTo(0, 0), 60);
    return () => clearTimeout(id);
  }, [selected, isMobile]);

  // Auto-populate the service-entry "Address" from the order's stored service
  // address whenever a different order is opened. Stays fully editable; whatever
  // value is in effect at save time is what gets stored with the entry (addNote).
  useEffect(() => {
    const addr = selectedJob && selectedJob.address;
    if (addr) setNoteForm(f => ({ ...f, location: addr }));
  }, [selected]);

  // ── Secure admin order actions (persisted server-side) ──
  const [showArchived, setShowArchived] = useState(false);
  const [refundJob, setRefundJob] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [refundsAllowed, setRefundsAllowed] = useState(false);
  const [proofFile, setProofFile] = useState(null);

  async function adminAction(action, extra) {
    if (actionBusy) return false;
    setActionBusy(true);
    try {
      const res = await fetch("/.netlify/functions/admin-order-action", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, jobNumber: selected, adminName: "admin", ...(extra || {}) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { showNotice(data.error || "Action could not be completed."); return false; }
      if (action === "delete_test") setSelected(null);
      await loadJobs();
      return true;
    } catch (e) {
      showNotice("Action could not be completed. Please try again.");
      return false;
    } finally { setActionBusy(false); }
  }

  // Upgrade a PAID order's service level: emails the client a Stripe link for
  // the difference only. The order switches when Stripe confirms the payment.
  async function sendTierUpgrade(job, toTier, amount) {
    if (actionBusy) return;
    const label = PRICING[toTier]?.label || toTier;
    if (!(await askConfirm(`Email ${job.client || "the client"} a $${amount} payment link to upgrade ${job.id} to ${label}?\n\nThe order switches to ${label} automatically once they pay.`, "Send"))) return;
    setActionBusy(true);
    try {
      const res = await fetch("/.netlify/functions/admin-upgrade-tier", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobNumber: job.id, toTier }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { showNotice(data.error || "Could not send the upgrade link."); return; }
      showNotice(data.emailed ? `Upgrade link for $${(data.amountCents / 100).toFixed(2)} emailed to the client.` : `Link created but the email did not send. Link:\n${data.url}`);
      await loadJobs();
    } catch (e) {
      showNotice("Could not send the upgrade link. Please try again.");
    } finally { setActionBusy(false); }
  }

  // ── Admin-created orders + payment links + invoicing ──
  const [showCreate, setShowCreate] = useState(false);
  // Parent order = the client's overall matter (client/case/billing). Each
  // SERVICE JOB below is one person to serve with its own address/pricing.
  const [createForm, setCreateForm] = useState({ clientName: "", clientEmail: "", clientPhone: "", plaintiff: "", caseNumber: "", court: "", specialInstructions: "", isTest: false, billingMode: "stripe" });
  const emptyService = () => ({ personToServe: "", serviceAddress: "", zip: "", city: "", state: "", docType: "", docSubtype: "", serviceTier: "standard", serviceAddon: "", neededByDate: "", instructions: "", useCalculatedPrice: true, amountDue: "" });
  // Document subtypes that change which California form applies later.
  const DOC_SUBTYPES = ADMIN_DOC_SUBTYPES;
  const [createServices, setCreateServices] = useState([emptyService()]);
  const [createErrors, setCreateErrors] = useState([]);
  const [createBusy, setCreateBusy] = useState(false);
  // Documents selected BEFORE Create Order is submitted; uploaded to the new
  // order immediately after it is created (never before, so a failed creation
  // can't orphan files in storage).
  const [createFiles, setCreateFiles] = useState([]);
  // If the order was created but the upload failed: { jobNumber, files } — the
  // retry re-uploads to the SAME order; it never creates a second order.
  const [createDocsPending, setCreateDocsPending] = useState(null);
  const [createDocsBusy, setCreateDocsBusy] = useState(false);
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingMsg, setBillingMsg] = useState("");
  // A message about one order never carries over to the next one opened.
  useEffect(() => { setBillingMsg(""); setPhForm(null); setPhMsg(""); }, [selected]);
  function cField(k, v) { setCreateForm(f => ({ ...f, [k]: v })); }
  // Per-service field updates; switching to a tier that doesn't allow add-ons
  // clears the add-on (same eligibility rule as the customer engine).
  function svcField(i, k, v) {
    setCreateServices(list => list.map((s, idx) => idx === i
      ? { ...s, [k]: v, ...(k === "serviceTier" && !normalizeAddon(s.serviceAddon, v) ? { serviceAddon: "" } : {}) }
      : s));
  }
  function addService() { setCreateServices(l => [...l, emptyService()]); }
  function removeService(i) { setCreateServices(l => (l.length > 1 ? l.filter((_, idx) => idx !== i) : l)); }

  // Each Service Job prices itself through the SAME engine as the public
  // calculator; the parent Order Total is always the sum of the job prices.
  function servicePrice(sv) {
    if (sv.useCalculatedPrice) {
      return orderTotal(sv.zip.trim(), sv.serviceTier, sv.docType, normalizeAddon(sv.serviceAddon || null, sv.serviceTier));
    }
    // No charge / billed outside the app: blank or $0 is a valid recorded amount.
    if (createForm.billingMode === "external") {
      const raw = String(sv.amountDue == null ? "" : sv.amountDue).trim();
      if (raw === "") return 0;
      return Number(raw) >= 0 ? Number(raw) : null;
    }
    return Number(sv.amountDue) > 0 ? Number(sv.amountDue) : null;
  }
  const createOrderTotal = createServices.reduce((sum, sv) => { const p = servicePrice(sv); return p != null ? sum + p : sum; }, 0);

  // Frontend required-field validation (the server enforces the same rules).
  function validateCreate() {
    const errs = [];
    if (!createForm.clientName.trim()) errs.push("Client name is required.");
    if (!/.+@.+\..+/.test(createForm.clientEmail.trim())) errs.push("A valid client email is required.");
    if (!createForm.clientPhone.trim()) errs.push("Client phone is required.");
    if (!createForm.plaintiff.trim()) errs.push("Plaintiff is required.");
    if (!createForm.caseNumber.trim()) errs.push("Case number is required.");
    if (!createForm.court.trim()) errs.push("Court is required.");
    createServices.forEach((sv, i) => {
      const label = `Service ${String(i + 1).padStart(2, "0")}`;
      if (!sv.personToServe.trim()) errs.push(`${label}: person to serve is required.`);
      if (!sv.serviceAddress.trim()) errs.push(`${label}: service address is required.`);
      if (!/^\d{5}$/.test(sv.zip.trim())) errs.push(`${label}: 5-digit service ZIP is required.`);
      if (!sv.docType) errs.push(`${label}: document type is required.`);
      if (!sv.neededByDate) errs.push(`${label}: needed-by / deadline is required.`);
      if (servicePrice(sv) == null) errs.push(`${label}: no service price — fix the ZIP/service level or enter a manual amount.`);
    });
    if (createForm.billingMode !== "external" && !(createOrderTotal > 0)) errs.push("Order total must exist before the order can be created.");
    return errs;
  }

  async function createOrder() {
    if (createBusy) return;
    const errs = validateCreate();
    setCreateErrors(errs);
    if (errs.length) return;
    setCreateBusy(true);
    try {
      // 1) Create the order (exactly once). Selected documents are NOT touched
      //    yet, so a failed creation cannot orphan anything in storage — the
      //    form and the selected files are both preserved for another attempt.
      const res = await fetch("/.netlify/functions/admin-create-order", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...createForm, services: createServices }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) { setCreateErrors(Array.isArray(data.errors) && data.errors.length ? data.errors : []); showNotice(data.error || "Could not create the order."); return; }

      // 2) Attach any documents selected during creation to the NEW order.
      const filesToUpload = createFiles;
      let docsOk = true;
      let docsResult = null;
      if (filesToUpload.length) {
        docsResult = await uploadDocsForJob(data.jobNumber, filesToUpload);
        docsOk = docsResult.ok;
      }

      await loadJobs();
      setSelected(data.jobNumber);
      setSendPromptJob(data.jobNumber); setPromptPrice(data.amountDueCents != null ? (data.amountDueCents / 100).toFixed(2) : ""); setPromptMsg("");
      setShowCreate(false);
      setCreateForm({ clientName: "", clientEmail: "", clientPhone: "", plaintiff: "", caseNumber: "", court: "", specialInstructions: "", isTest: false, billingMode: "stripe" });
      setCreateServices([emptyService()]);
      setCreateErrors([]);
      setCreateFiles([]);
      if (filesToUpload.length && !docsOk) {
        // Partial failure: the order EXISTS; keep the files for a retry that
        // re-uploads to this same order (never a second order). Retry only the
        // documents that did not land, and say exactly which ones those were.
        const stored = new Set((docsResult.uploaded || []).map((u) => u.name));
        const remaining = filesToUpload.filter((f) => !stored.has(f.name));
        setCreateDocsPending({ jobNumber: data.jobNumber, files: remaining.length ? remaining : filesToUpload });
        setBillingMsg(uploadDocsSummary(docsResult));
        showNotice(`Order ${data.jobNumber} was created, but not every document was attached. ${uploadDocsSummary(docsResult)}`);
      } else {
        setCreateDocsPending(null);
        const svcCount = `${data.services ? data.services.length : 1} service job(s)${filesToUpload.length ? ` and ${filesToUpload.length} document(s) attached` : ""}`;
        showNotice(createForm.billingMode === "external"
          ? `Order ${data.jobNumber} created (no charge / billed outside the app) with ${svcCount}. Accept it from the order panel to start service.`
          : `Order ${data.jobNumber} created (awaiting payment) with ${svcCount}. Send a payment link from the order panel.`);
      }
    } catch (e) { showNotice("Could not create the order. Please try again."); }
    finally { setCreateBusy(false); }
  }

  async function retryCreateDocs() {
    const p = createDocsPending;
    if (!p || createDocsBusy) return;
    setCreateDocsBusy(true);
    try {
      const r = await uploadDocsForJob(p.jobNumber, p.files);
      if (r.ok) { setCreateDocsPending(null); await loadJobs(); setBillingMsg("Documents uploaded."); }
      else {
        // Keep only what still has not landed pending, and name what went wrong.
        const stored = new Set((r.uploaded || []).map((u) => u.name));
        const remaining = p.files.filter((f) => !stored.has(f.name));
        if (r.uploaded.length) await loadJobs();
        setCreateDocsPending(remaining.length ? { jobNumber: p.jobNumber, files: remaining } : null);
        setBillingMsg(uploadDocsSummary(r));
      }
    } finally { setCreateDocsBusy(false); }
  }

  async function billingCall(path, payload, okMsg) {
    if (billingBusy) return null;
    setBillingBusy(true); setBillingMsg("");
    try {
      const res = await fetch(`/.netlify/functions/${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) { setBillingMsg(data.error || "Action failed."); return null; }
      setBillingMsg(okMsg || "Done.");
      await loadJobs();
      return data;
    } catch (e) { setBillingMsg("Action failed. Please try again."); return null; }
    finally { setBillingBusy(false); }
  }
  const sendPaymentLink = (job) => billingCall("admin-send-payment-link", { jobNumber: job.id }, "Payment link sent to the client.");
  // After Create Order: ask what to send the client — payment link + Service
  // Agreement, agreement only, or nothing. The price can be approved or changed
  // right there first. Shown on the page (no browser pop-ups).
  const [sendPromptJob, setSendPromptJob] = useState(null);
  const [promptPrice, setPromptPrice] = useState("");
  const [promptMsg, setPromptMsg] = useState("");
  const [promptBusy, setPromptBusy] = useState(false);
  async function postFn(path, payload) {
    const res = await fetch(`/.netlify/functions/${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.ok) throw new Error(d.error || `Action failed (HTTP ${res.status}).`);
    return d;
  }
  async function sendToClient(job, choice) {
    if (promptBusy) return;
    const wantDocs = askDocs && SUBJECT_PHOTO.courtDocs(job.documents).length === 0;
    if (choice === "nothing" && !wantDocs) { setSendPromptJob(null); setPromptMsg(""); return; }
    setPromptBusy(true); setPromptMsg("");
    let docsNote = "";
    if (wantDocs) {
      try { await postFn("admin-request-docs", { jobNumber: job.id }); docsNote = " + document upload link"; }
      catch (e) { docsNote = ` (upload link not sent: ${e.message})`; }
    }
    if (choice === "nothing") { setPromptMsg(`✓ Sent to ${job.clientEmail || "the client"}: document upload link.`); setSendPromptJob(null); setPromptBusy(false); await loadJobs(); return; }
    try {
      // Price: save it first when it was changed.
      const current = job.amountDueCents != null ? (job.amountDueCents / 100).toFixed(2) : "";
      if ((choice === "both" || choice === "payment") && promptPrice !== "" && Number(promptPrice).toFixed(2) !== current) {
        await postFn("admin-approve-price", { jobNumber: job.id, docType: job.docType, useCalculatedPrice: false, amountDue: promptPrice });
      }
      if (choice === "payment") {
        await postFn("admin-send-payment-link", { jobNumber: job.id });
        setPromptMsg(`✓ Payment link sent to ${job.clientEmail || "the client"}${docsNote}.`);
        setSendPromptJob(null);
        await loadJobs();
        return;
      }
      if (choice === "both") await postFn("admin-send-payment-link", { jobNumber: job.id, noEmail: true });
      const d = await postFn("admin-send-agreement", { jobNumber: job.id, includePayment: choice === "both" });
      setPromptMsg(`✓ Sent to ${d.sentTo}: ${choice === "both" ? "Service Agreement + payment link" : "Service Agreement"}${docsNote}.`);
      setSendPromptJob(null);
      await loadJobs();
    } catch (e) { setPromptMsg("✗ " + e.message); }
    finally { setPromptBusy(false); }
  }
  // Extra charge (Skip trace / other): manual price, emailed one-time link.
  const blankXc = { kind: "skip_trace", description: "Skip trace", amount: "", people: [{ firstName: "", lastName: "" }], emailClient: true };
  const [xcForm, setXcForm] = useState(null);
  const [xcMsg, setXcMsg] = useState("");
  const [xcBusy, setXcBusy] = useState(false);
  const [xcPayUrl, setXcPayUrl] = useState("");
  // mode "email": email the client the link. mode "take": open the secure
  // Stripe page straight away (new tab) so the admin enters the card — no email.
  async function sendExtraCharge(job, mode) {
    if (xcBusy || !xcForm) return;
    const take = mode === "take";
    // Open the tab inside the click so the browser doesn't block it; it is
    // pointed at Stripe as soon as the charge exists.
    const payTab = take ? window.open("", "_blank") : null;
    setXcBusy(true); setXcMsg(""); setXcPayUrl("");
    try {
      const d = await postFn("admin-extra-charge", { jobNumber: job.id, kind: xcForm.kind, description: xcForm.description, amount: xcForm.amount,
        people: xcForm.kind === "skip_trace" ? xcForm.people.filter(p => p.firstName.trim() || p.lastName.trim()) : [],
        noEmail: take });
      if (d.payUrl) {
        if (payTab) payTab.location.href = d.payUrl; else window.location.href = d.payUrl;
        setXcMsg(`✓ ${d.description} — $${(d.amountCents / 100).toFixed(2)}: the payment page opened in a new tab. Refresh Payment here after paying.`);
        setXcPayUrl(d.payUrl);
      } else {
        setXcMsg(`✓ Sent to ${d.sentTo}: ${d.description} — $${(d.amountCents / 100).toFixed(2)}.`);
      }
      setXcForm(null);
      await loadJobs();
    } catch (e) { if (payTab) payTab.close(); setXcMsg("✗ " + e.message); }
    finally { setXcBusy(false); }
  }
  async function emailOpenCharge(job, c) {
    setXcBusy(true); setXcMsg(""); setXcPayUrl("");
    try {
      const d = await postFn("admin-extra-charge", { action: "email", jobNumber: job.id, chargeId: c.id });
      setXcMsg(`✓ Sent to ${d.sentTo}: ${d.description} — $${(d.amountCents / 100).toFixed(2)}.`);
      await loadJobs();
    } catch (e) { setXcMsg("✗ " + e.message); }
    finally { setXcBusy(false); }
  }
  const requestDocs = (job) => billingCall("admin-request-docs", { jobNumber: job.id }, "Upload link sent to the client.");
  // Email history for the open order: every email sent to the client (subject,
  // date and time, delivery status). Reloads when the order list reloads.
  const [emailHist, setEmailHist] = useState({ id: "", rows: [], source: "", err: "" });
  useEffect(() => {
    if (!selected) return;
    let live = true;
    postFn("admin-email-history", { jobNumber: selected })
      .then(d => { if (live) setEmailHist({ id: selected, rows: d.emails || [], source: d.source || "", err: "" }); })
      .catch(e => { if (live) setEmailHist({ id: selected, rows: [], source: "", err: e.message }); });
    return () => { live = false; };
  }, [selected, jobs]);
  // "Ask Client to Upload" inside a Service Job: the emailed link files the
  // client's papers under that job. The result shows inside the job.
  const [jobDocsMsg, setJobDocsMsg] = useState({ id: "", text: "" });
  const [jobDocsBusy, setJobDocsBusy] = useState("");
  async function requestDocsForJob(job, t) {
    if (jobDocsBusy) return;
    setJobDocsBusy(t.id); setJobDocsMsg({ id: t.id, text: "" });
    try {
      const d = await postFn("admin-request-docs", { jobNumber: job.id, targetId: t.id });
      setJobDocsMsg({ id: t.id, text: `✓ Upload link emailed to ${d.sentTo || "the client"}.` });
      await loadJobs();
    } catch (e) { setJobDocsMsg({ id: t.id, text: "✗ " + e.message }); }
    finally { setJobDocsBusy(""); }
  }
  const [askDocs, setAskDocs] = useState(true);
  // Edit a Service Job's address. Unsigned proof drafts go stale on the server;
  // a served job gets a fresh draft right away so the next Sign uses the new address.
  const [addrEdit, setAddrEdit] = useState(null);
  async function saveTargetAddress(job, t) {
    if (!addrEdit) return;
    const d = await svcCall({ action: "update_address", jobNumber: job.id, targetId: t.id, address: addrEdit.address, zip: addrEdit.zip }, "Address updated.");
    if (!d) return;
    setAddrEdit(null);
    await loadJobs();
    if (["personal_service", "substituted_service", "posted_service"].includes(t.legal_service_outcome) && d.draftsMarkedStale > 0) await autoPrepareProof(job.id, t.id);
    loadSvcDocs(job, t);
  }
  // Court papers belong to a Service Job: shown inside that job, not in a pile.
  const [docConfirm, setDocConfirm] = useState("");
  const [docMsg, setDocMsg] = useState("");
  // Court papers only. Signed proofs ("service_doc", and the "proof" copies
  // attached when an order is completed) live in their job's green box.
  const paperDocs = (j) => SUBJECT_PHOTO.courtDocs(j.documents).filter(d => d.type !== "service_doc" && d.type !== "proof");
  // A paper not filed under a job is matched to one by name (e.g. "…_SHERIFF_…"
  // → the Sheriff's Department job, "…_ARMC_…" → Arrowhead Regional Medical
  // Center); only a paper that matches no job stays at the order level.
  const targetFor = (j, d) => {
    const targets = j.serviceTargets || [];
    if (d.targetId && targets.some(x => x.id === d.targetId)) return d.targetId;
    if (targets.length === 1) return targets[0].id;
    const fileWords = new Set(String(d.name || "").toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean));
    const wordsOf = (t) => {
      const words = String(t.subject_name || "").toUpperCase().replace(/'S\b/g, "").split(/[^A-Z0-9]+/).filter(w => w.length >= 3 && !["THE", "AND", "FOR", "INC", "LLC"].includes(w));
      const acro = String(t.subject_name || "").split(/\s+/).filter(w => /^[A-Z]/.test(w) && !/^(of|the|and)$/i.test(w)).map(w => w[0].toUpperCase()).join("");
      return { words, acro };
    };
    const info = targets.map(t => ({ t, ...wordsOf(t) }));
    const df = {}; info.forEach(x => new Set(x.words).forEach(w => { df[w] = (df[w] || 0) + 1; }));
    let best = null, bestScore = 0, tie = false;
    for (const x of info) {
      let sc = 0;
      x.words.forEach((w, i) => { if (fileWords.has(w)) sc += (1 / df[w]) * (1 + 1 / (1 + i)); });
      if (x.acro.length >= 3 && fileWords.has(x.acro)) sc += 3;
      if (sc > bestScore) { best = x.t.id; bestScore = sc; tie = false; } else if (sc > 0 && sc === bestScore) tie = true;
    }
    return bestScore > 0 && !tie ? best : null;
  };
  const docsForJob = (j, t) => paperDocs(j).filter(d => targetFor(j, d) === t.id);
  const unfiledDocs = (j) => (j.serviceTargets || []).length ? paperDocs(j).filter(d => !targetFor(j, d)) : [];
  async function docAction(job, d, action, targetId) {
    setDocMsg("");
    try { await postFn("admin-order-document", { jobNumber: job.id, path: d.path, action, targetId: targetId || null }); setDocConfirm(""); await loadJobs(); }
    catch (e) { setDocMsg("✗ " + e.message); }
  }
  const docRow = (job, d, extra) => (
    <div key={d.path} data-testid="paper-doc" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.4rem", padding: "0.15rem 0" }}>
      <button type="button" data-testid="open-doc" onClick={() => openFileTab(d.url)} style={{ background: "none", border: "none", padding: 0, color: "var(--blue)", textDecoration: "underline", cursor: "pointer", fontSize: "0.85rem", textAlign: "left" }}>📄 {d.name}</button>
      <button type="button" className="btn-secondary" data-testid="print-doc" style={{ fontSize: "0.75rem", padding: "0.2rem 0.6rem" }} onClick={() => printFile(d.url)}>🖨 Print</button>
      {extra}
      {docConfirm === d.path
        ? <><button className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.2rem 0.5rem" }} onClick={() => docAction(job, d, "remove")}>Tap again to remove</button><button className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.2rem 0.5rem" }} onClick={() => setDocConfirm("")}>Cancel</button></>
        : <button type="button" data-testid="remove-doc" onClick={() => setDocConfirm(d.path)} style={{ background: "none", border: "none", padding: 0, color: "var(--red)", cursor: "pointer", fontSize: "0.78rem" }}>Remove</button>}
    </div>
  );
  // Top of the Proof (filer box + courthouse). Saved per order; drafts re-prepared.
  const [phForm, setPhForm] = useState(null);
  const [phMsg, setPhMsg] = useState("");
  // Proof stage only: open the proof-header editor and bring it into view.
  function editProofHeaderNow(job) {
    openProofHeader(job);
    setTimeout(() => { const el = document.querySelector('[data-testid="proof-header"]'); if (el) el.scrollIntoView({ behavior: "smooth", block: "center" }); }, 50);
  }
  function openProofHeader(job) {
    const h = job.proofHeader || { filer: {} };
    const ca = String(h.courtAddress || "");
    const i = ca.indexOf(",");
    setPhMsg("");
    setPhForm({ name: h.filer.name || "", address: h.filer.address || "", phone: h.filer.phone || "", email: h.filer.email || "", attorneyFor: h.filer.attorneyFor || "",
      courtStreet: i > 0 ? ca.slice(0, i).trim() : ca, courtCityZip: i > 0 ? ca.slice(i + 1).trim() : "", courtBranch: h.courtBranch || "" });
  }
  // AUTO-FILL the proof header from the served papers when it's missing the
  // filer's address or the court's address: the Summons states both. Fills
  // blanks only — anything the admin saved is never overwritten. Once per order.
  const autoHeaderTried = useRef(new Set());
  async function autoFillProofHeader(job) {
    const h = job && job.proofHeader;
    if (!h || autoHeaderTried.current.has(job.id)) return;
    if (h.filer.address && h.courtAddress) return;
    const pdfs = paperDocs(job).filter(d => /\.pdf$/i.test(d.name || "") && d.url);
    if (!pdfs.length) return;
    autoHeaderTried.current.add(job.id);
    try {
      const pdfjs = await loadPdfJs();
      let found = null;
      for (const d of pdfs.slice(0, 3)) {
        const doc = await pdfjs.getDocument({ url: d.url }).promise;
        for (let n = 1; n <= Math.min(8, doc.numPages) && !found; n += 1) {
          const tc = await (await doc.getPage(n)).getTextContent();
          if (!tc.items.some(i => /address of the court (is|are)|attorney or party without attorney|party without attorney or attorney/i.test(i.str))) continue;
          const pos = parseSummonsItems(tc.items.map(i => ({ str: i.str, x: i.transform[4], y: i.transform[5] })));
          const txt = parseCaseHeader(tc.items.map(i => i.str + (i.hasEOL ? "\n" : " ")).join(""));
          const pick = (k) => pos[k] || txt[k] || "";
          if (pick("courtStreet") || pick("filerAddress")) found = { courtStreet: pick("courtStreet"), courtCityZip: pick("courtCityZip"), courtBranch: pick("courtBranch"), filerName: pick("filerName"), filerAddress: pick("filerAddress"), filerPhone: pick("filerPhone"), filerEmail: pick("filerEmail") };
        }
        if (found) break;
      }
      // A SCANNED paper has no text to read — and a typed Summons can still have
      // the court or filer filled in by hand or as a picture. Whatever is still
      // blank goes to the scan reader.
      if (!found || !found.courtStreet || !found.filerAddress) {
        const fromText = found;
        // The Summons isn't always near the front of a packet (page 5 on one
        // scan), so the reader goes through it 3 pages at a time, up to page 12.
        scan: for (const d of pdfs.slice(0, 2)) for (const pages of ["1-3", "4-6", "7-9", "10-12"]) {
          try {
            const r = await fetch("/.netlify/functions/read-document", { method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ sourceUrl: d.url, filename: d.name, mimeType: "application/pdf", sizeBytes: d.size || undefined, pages, header: true }) });
            const data = await r.json().catch(() => ({}));
            const hh = (data && data.header) || {};
            const ca = String((data && data.courtAddress) || "");
            const ci = ca.indexOf(",");
            const street = hh.courtStreet || (ci > 0 ? ca.slice(0, ci).trim() : ca);
            const cityZip = hh.courtCityZip || (ci > 0 ? ca.slice(ci + 1).trim() : "");
            if (street || hh.filerAddress) {
              const scanned = { courtStreet: street, courtCityZip: cityZip, courtBranch: hh.courtBranch || "", filerName: hh.filerName || "", filerAddress: hh.filerAddress || "", filerPhone: hh.filerPhone || "", filerEmail: hh.filerEmail || "" };
              found = { ...scanned };
              if (fromText) Object.keys(fromText).forEach(k => { if (fromText[k]) found[k] = fromText[k]; });
              break scan;
            }
            // Past the last page of a short paper: stop reading this one.
            if (data && data.reason === "invalid_pages") break;
          } catch (e) { /* try the next pages */ }
        }
        if (!found) found = fromText;
      }
      if (!found) {
        // Say so — never fail silently. "Fill in" stays right there.
        setPhMsg("Couldn't read the court or filer address from the papers. Tap Fill in to type them.");
        return;
      }
      const ca = String(h.courtAddress || ""); const ci = ca.indexOf(",");
      const header = {
        name: h.filer.name || found.filerName, address: h.filer.address || found.filerAddress,
        phone: h.filer.phone || found.filerPhone, email: h.filer.email || found.filerEmail, attorneyFor: h.filer.attorneyFor,
        courtStreet: ca ? (ci > 0 ? ca.slice(0, ci).trim() : ca) : found.courtStreet,
        courtCityZip: ca ? (ci > 0 ? ca.slice(ci + 1).trim() : "") : found.courtCityZip,
        courtBranch: h.courtBranch || found.courtBranch,
      };
      await postFn("admin-proof-header", { jobNumber: job.id, header });
      setPhMsg("✓ Proof header filled in from the summons. Updating the proof drafts…");
      await loadJobs();
      for (const t of job.serviceTargets || []) {
        if (["personal_service", "substituted_service", "posted_service"].includes(t.legal_service_outcome)) await autoPrepareProof(job.id, t.id);
      }
      setPhMsg("✓ Proof header filled in from the summons — proof drafts updated.");
    } catch (e) { /* stays editable by hand */ }
  }
  useEffect(() => { if (selectedJob && selectedJob.proofHeader) autoFillProofHeader(selectedJob); }, [selected, selectedJob && selectedJob.proofHeader && selectedJob.proofHeader.courtAddress]);
  async function saveProofHeader(job) {
    try {
      await postFn("admin-proof-header", { jobNumber: job.id, header: phForm });
      setPhForm(null); setPhMsg("✓ Saved. Updating the proof drafts…");
      await loadJobs();
      for (const t of job.serviceTargets || []) {
        if (["personal_service", "substituted_service", "posted_service"].includes(t.legal_service_outcome)) await autoPrepareProof(job.id, t.id);
      }
      setPhMsg("✓ Saved. Proof drafts updated — review and sign.");
    } catch (e) { setPhMsg("✗ " + e.message); }
  }   // { url, title } → right-side viewer
  const sendSignIn = (job) => billingCall("admin-send-signin", { jobNumber: job.id }, `Sign-in instructions sent to ${job.clientEmail || "the client"}.`);
  const sendAgreementOnly = (job) => billingCall("admin-send-agreement", { jobNumber: job.id, includePayment: false }, "Service Agreement sent to the client.");
  const refreshPayment = (job) => billingCall("admin-refresh-payment", { jobNumber: job.id }, "Payment state refreshed from Stripe.");
  // Multi-service parent orders invoice ONCE with a line item per Service Job
  // (Process Service — <person>: $x), so the client sees what makes up the
  // total. Single-service orders keep the plain single-line invoice.
  const createInvoice = (job) => {
    const priced = (job.serviceTargets || []).filter(t => t.amount_cents != null);
    const lineItems = priced.length > 1
      ? priced.map(t => ({ description: `Process Service — ${t.subject_name || "Person to Serve"}${t.job_label ? ` (${t.job_label})` : ""}`, amount: t.amount_cents / 100 }))
      : undefined;
    return billingCall("admin-invoice", { action: "create", jobNumber: job.id, ...(lineItems ? { lineItems } : {}) }, "Invoice created.");
  };
  // View the invoice inside the portal — the same one Resend Invoice emails.
  const [invoiceView, setInvoiceView] = useState(null);
  const viewInvoice = async (job) => { const d = await billingCall("admin-invoice", { action: "preview", jobNumber: job.id }, " "); if (d && d.html) { setBillingMsg(""); setInvoiceView({ subject: d.subject, html: d.html, to: d.to }); } };
  const resendInvoice = (job) => billingCall("admin-invoice", { action: "resend", jobNumber: job.id }, `Invoice emailed to ${job.clientEmail || "the client"}.`);
  const sendInvoice = (job) => billingCall("admin-invoice", { action: "send", jobNumber: job.id }, "Invoice emailed to the client.");
  const voidInvoice = async (job) => { if (await askConfirm("Void this invoice?", "Void")) return billingCall("admin-invoice", { action: "void", jobNumber: job.id }, "Invoice voided."); };

  // ── Record Payment (off-Stripe: cash / check / Zelle / other) ──
  const [payForm, setPayForm] = useState(null); // null = closed
  async function recordPayment(job) {
    if (!payForm) return;
    // One token per opened form => a double click can only ever record once.
    const data = await billingCall("admin-record-payment", { jobNumber: job.id, amount: payForm.amount, method: payForm.method, paidOn: payForm.paidOn, reference: payForm.reference, note: payForm.note, targetId: payForm.targetId || null, submissionToken: payForm.token },
      "Payment recorded.");
    if (data) setPayForm(null);
  }

  // ── Service Jobs (per-person outcome + documentation workflow) ──
  const [attemptTargetId, setAttemptTargetId] = useState("");
  // Which Service Job has its attempt form open (one at a time).
  const [attemptOpenFor, setAttemptOpenFor] = useState(null);
  // Tapping one person on an order's card opens the order showing just that
  // person's Service Job ("Show all people" brings the rest back).
  const [focusTarget, setFocusTarget] = useState(null);
  // Opening the attempt form: a fresh step-by-step entry starting at the first
  // screen, time = now, and the phone's location is taken right away. Photos
  // not yet saved are kept.
  const restoringEntryRef = useRef(false);
  useEffect(() => {
    if (!attemptOpenFor) return;
    if (restoringEntryRef.current) { restoringEntryRef.current = false; return; }
    setNoteForm(f => ({ ...BLANK_NOTE_FORM, location: f.location, photos: (f.photos || []).filter(ph => ph && !ph.stored), ...nowLocalDateTime(), gps: null }));
    getCurrentGPS().then(g => setNoteForm(f => (f.gps ? f : { ...f, gps: g }))).catch(() => {});
  }, [attemptOpenFor]);
  // STAY WHERE YOU WERE: if the phone closes the app while you're in another
  // app (or it reloads), reopen on the same order, person and entry screen with
  // what you'd typed. Kept on this phone only, for 12 hours.
  const PLACE_KEY = "cpsAdminPlace";
  const PLACE_KEEP_MS = 43200000; // half a day
  const placeRestored = useRef(false);
  useEffect(() => {
    if (!authed || placeRestored.current || !jobs.length) return;
    placeRestored.current = true;
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(PLACE_KEY) || "null"); } catch (e) { saved = null; }
    if (!saved || Date.now() - (saved.at || 0) > PLACE_KEEP_MS) return;
    if (saved.selected && jobs.some(j => j.id === saved.selected)) {
      setSelected(saved.selected);
      if (saved.focusTarget) setFocusTarget(saved.focusTarget);
      if (saved.attemptTargetId) setAttemptTargetId(saved.attemptTargetId);
      if (saved.attemptOpenFor) {
        restoringEntryRef.current = true;
        setAttemptOpenFor(saved.attemptOpenFor);
        if (saved.noteForm) setNoteForm(f => ({ ...f, ...saved.noteForm }));
      }
    }
    if (saved.scrollY) setTimeout(() => window.scrollTo(0, saved.scrollY), 300);
  }, [authed, jobs]);
  useEffect(() => {
    if (!authed || !placeRestored.current) return;
    const save = () => {
      try {
        const f = noteForm || {};
        let photos = (f.photos || []).filter(p => p && !p.stored);
        if (photos.reduce((n, p) => n + String(p.dataUrl || "").length, 0) > 3000000) photos = [];
        localStorage.setItem(PLACE_KEY, JSON.stringify({ at: Date.now(), selected, focusTarget, attemptTargetId, attemptOpenFor,
          noteForm: attemptOpenFor ? { ...f, photos } : null, scrollY: window.scrollY || 0 }));
      } catch (e) { /* storage full or blocked — nothing to do */ }
    };
    save();
    const onHide = () => { if (document.visibilityState === "hidden") save(); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", save);
    return () => { document.removeEventListener("visibilitychange", onHide); window.removeEventListener("pagehide", save); };
  }, [authed, selected, focusTarget, attemptTargetId, attemptOpenFor, noteForm]);
  const [svcMsg, setSvcMsg] = useState("");
  const [svcBusy, setSvcBusy] = useState(false);
  const [docPreview, setDocPreview] = useState(null); // { targetId, kind, content, missing, formCode }
  const [sigNameSvc, setSigNameSvc] = useState("");
  // Layer 1 of template resolution: the admin naming the form for this Service
  // Job. Requires a reason, and removing it falls back to the next rule.
  const [formOverrideForm, setFormOverrideForm] = useState(null);
  const [execPlace, setExecPlace] = useState("Corona");
  // Narrowly scoped recovery of a FAILED signed proof: the doc id being corrected and
  // the required correction reason. Null unless the admin explicitly opens it.
  const [recoverDoc, setRecoverDoc] = useState(null);
  const [recoverReason, setRecoverReason] = useState("");
  useEffect(() => { setAttemptTargetId(""); setDocPreview(null); setSvcMsg(""); setRecoverDoc(null); setRecoverReason(""); }, [selected]);
  // Which completed-service attempt the Proof describes, and which form to use
  // when the record does not determine it. Both are explicit admin choices —
  // generation refuses rather than guessing either one.
  const [proofAttemptId, setProofAttemptId] = useState("");
  const [proofAttemptChoices, setProofAttemptChoices] = useState(null);
  const [proofSubtype, setProofSubtype] = useState("");
  const [proofFormChoices, setProofFormChoices] = useState(null);
  // What the uploaded documents were classified as. The normal path: the portal
  // reads the packet and selects the Proof form itself, so the admin confirms
  // rather than decides. `showOverride` reveals the manual path, which is an
  // exception, not the workflow.
  const [proofClassification, setProofClassification] = useState(null);
  const [showOverride, setShowOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [classifyBusy, setClassifyBusy] = useState(false);
  // The choices belong to one order; never carry them across a selection.
  useEffect(() => {
    setProofAttemptId(""); setProofAttemptChoices(null);
    setProofSubtype(""); setProofFormChoices(null);
    setProofClassification(null); setShowOverride(false); setOverrideReason("");
    setMultiProofResult(null); setProofRequestId(null); setAnalysisReport(null);
  }, [selected]);

  // The last refusal from the server, so a failed automatic step can say WHY.
  const lastSvcErrorRef = useRef("");
  // Paper requests run ONE AT A TIME, in order. A request that arrives while
  // another is running WAITS its turn — it used to be dropped without a word
  // (e.g. "prepare the Proof" right after a save, while the papers list for
  // another person on the order was still loading).
  const svcQueueRef = useRef(Promise.resolve());
  const svcCountRef = useRef(0);
  function svcCall(payload, okMsg) {
    const run = svcQueueRef.current.then(() => svcCallNow(payload, okMsg));
    svcQueueRef.current = run.catch(() => {});
    return run;
  }
  async function svcCallNow(payload, okMsg) {
    lastSvcErrorRef.current = "";
    svcCountRef.current += 1;
    setSvcBusy(true); setSvcMsg("");
    try {
      const res = await fetch("/.netlify/functions/admin-service-job", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        // A refusal that just needs the admin to choose carries its options.
        // Surface them as a selector instead of a dead-end error — nothing was
        // generated and no document version was created.
        if (data.needsAttemptSelection && Array.isArray(data.attemptChoices)) setProofAttemptChoices(data.attemptChoices);
        if (data.needsFormSelection && Array.isArray(data.formChoices)) setProofFormChoices(data.formChoices);
        // The classification explains WHY the documents could not decide, so the
        // admin sees the recognized evidence rather than a bare list of forms.
        if (data.classification) setProofClassification(data.classification);
        setSvcMsg(data.error || "Action failed.");
        lastSvcErrorRef.current = data.error || "Action failed.";
        return null;
      }
      // A successful generation clears any pending prompts.
      setProofAttemptChoices(null); setProofFormChoices(null);
      if (data.classification) setProofClassification(data.classification);
      if (okMsg) setSvcMsg(okMsg);
      return data;
    } catch (e) { setSvcMsg("Action failed. Please try again."); lastSvcErrorRef.current = "Couldn't reach the server. Check your signal and try again."; return null; }
    finally { svcCountRef.current -= 1; if (svcCountRef.current <= 0) { svcCountRef.current = 0; setSvcBusy(false); } }
  }
  // Mailing facts for a substituted-service mailing proof. Entered and reviewed
  // by the process server — never inferred from an attempt record.
  const [mailingFacts, setMailingFacts] = useState({ mailedOn: "", mailedTo: "", mailedFrom: "" });
  // ── Enter Completed Service (work already performed) ──
  const [completedForm, setCompletedForm] = useState(null); // { targetId, ... }
  function openCompleted(target) {
    setCompletedForm({
      targetId: target.id, manner: "personal", serviceDate: "", serviceTime: "",
      documentsServed: target.doc_type || "", recipientName: "", recipientRelationship: "",
      recipientDescription: "", serviceFee: "", mailedOn: "", mailedFrom: "", notes: "",
    });
  }
  async function saveCompletedService(job, target) {
    const c = completedForm;
    if (!c) return;
    const payload = {
      manner: c.manner, serviceDate: c.serviceDate, serviceTime: c.serviceTime,
      documentsServed: c.documentsServed, recipientName: c.recipientName,
      recipientRelationship: c.recipientRelationship, recipientDescription: c.recipientDescription,
      serviceFeeCents: c.serviceFee ? Math.round(Number(c.serviceFee) * 100) : null,
      mailedOn: c.mailedOn, mailedFrom: c.mailedFrom, notes: c.notes,
    };
    const data = await svcCall({ action: "record_completed_service", jobNumber: job.id, targetId: target.id, completedService: payload }, "Completed service recorded.");
    if (data) { setCompletedForm(null); await loadJobs(); }
  }
  async function generateSvcDoc(job, target, kind, attemptIdOverride) {
    const today = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles" }).format(new Date()); // Pacific calendar day (execution/signature date)
    // Pass any just-entered completed-service facts so the official form items
    // populate from them; otherwise the stored record is used.
    const c = completedForm && completedForm.targetId === target.id ? {
      manner: completedForm.manner, serviceDate: completedForm.serviceDate, serviceTime: completedForm.serviceTime,
      documentsServed: completedForm.documentsServed, recipientName: completedForm.recipientName,
      recipientRelationship: completedForm.recipientRelationship, recipientDescription: completedForm.recipientDescription,
      serviceFeeCents: completedForm.serviceFee ? Math.round(Number(completedForm.serviceFee) * 100) : null,
      mailedOn: completedForm.mailedOn, mailedFrom: completedForm.mailedFrom,
    } : undefined;
    // attemptId names EXACTLY which completed-service attempt the Proof
    // describes — attempt number and array position are not stable identities.
    // docSubtype is the admin's form choice when the record does not already
    // determine it (e.g. FL-115 vs FL-330).
    const data = await svcCall({
      action: "generate_doc", jobNumber: job.id, targetId: target.id, kind,
      executionDate: today, executionPlace: execPlace, mailing: mailingFacts, completedService: c,
      attemptId: attemptIdOverride || proofAttemptId || undefined,
      // Sent ONLY when the admin has deliberately opened the override path. On
      // the normal workflow these are absent and the documents decide the form.
      docSubtype: (showOverride && proofSubtype) || undefined,
      overrideReason: (showOverride && overrideReason) || undefined,
    }, null);
    if (data) {
      setDocPreview({ targetId: target.id, kind, docId: data.docId || null, version: data.version || null, content: data.content, items: data.items, values: data.values, sc104Review: data.sc104Review || null, missing: data.missing || [], advisory: data.advisory || [], formCode: data.formCode, formTitle: data.formTitle, rule2150: data.rule2150, profileReadiness: data.profileReadiness, templateResolution: data.templateResolution });
      setSigNameSvc((data.values && data.values.serverName) || "");
      loadSvcDocs(job, target);
    }
    return data;
  }
  // Read the order's uploaded documents so the Proof form can be selected from
  // them. Reads and stores per-file reader results only — no draft is created,
  // nothing is sent to the client, and the order itself is not modified.
  // Large packets are analyzed across several calls; this polls until done.
  async function classifyDocs(job, target, reanalyze) {
    if (classifyBusy) return null;
    setClassifyBusy(true);
    setAnalysisReport(null);
    try {
      // One id for the WHOLE re-analysis, sent on every pass. A large packet is
      // read across several calls, and the server uses this to tell what it has
      // already refreshed from what is still due. Sending the flag only on the
      // first pass meant later passes reverted to "leave anything that has a
      // result alone", so the rest of the packet was never re-read.
      const runId = reanalyze ? `${target.id}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}` : null;
      let data = null;
      const totals = { analyzed: 0, refreshed: 0, cached: 0, failed: 0, staleRetained: 0, perFile: [] };
      for (let pass = 0; pass < 6; pass += 1) {
        data = await svcCall({
          action: "classify_docs", jobNumber: job.id, targetId: target.id,
          reanalyze: !!reanalyze, analysisId: runId || undefined,
        }, null);
        if (!data) break;
        totals.analyzed += data.analyzed || 0;
        totals.refreshed += data.refreshed || 0;
        totals.failed += data.failed || 0;
        totals.staleRetained += data.staleRetained || 0;
        totals.cached = data.cached || 0;
        if (Array.isArray(data.perFile)) {
          for (const f of data.perFile) {
            const i = totals.perFile.findIndex((x) => x.file === f.file);
            if (i >= 0) totals.perFile[i] = f; else totals.perFile.push(f);
          }
        }
        if (data.classification) setProofClassification(data.classification);
        if (!data.inProgress) break;
      }
      if (data) setAnalysisReport({ targetId: target.id, mode: reanalyze ? "refresh" : "fill", ...totals });
      return data;
    } finally { setClassifyBusy(false); }
  }
  // What the last analysis actually did, per file — so "re-analyzed" is never
  // confused with "reused what was already stored".
  const [analysisReport, setAnalysisReport] = useState(null);
  // True when the analyzed packet needs more than one Proof, so the Generate
  // button prepares them all instead of asking which group to keep.
  const multiProofNeeded = !!(proofClassification && proofClassification.multipleProofsRequired
    && proofClassification.separateProofs && proofClassification.separateProofs.length > 1);
  // One click = one requestId. A retry or a double-click reuses it, so the
  // server returns the drafts it already made rather than a second set.
  const [proofRequestId, setProofRequestId] = useState(null);
  async function generateAllProofs(job, target, attemptIdOverride) {
    const today = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles" }).format(new Date()); // Pacific calendar day (execution/signature date)
    const c = completedForm && completedForm.targetId === target.id ? {
      manner: completedForm.manner, serviceDate: completedForm.serviceDate, serviceTime: completedForm.serviceTime,
      documentsServed: completedForm.documentsServed, recipientName: completedForm.recipientName,
      recipientRelationship: completedForm.recipientRelationship, recipientDescription: completedForm.recipientDescription,
      serviceFeeCents: completedForm.serviceFee ? Math.round(Number(completedForm.serviceFee) * 100) : null,
      mailedOn: completedForm.mailedOn, mailedFrom: completedForm.mailedFrom,
    } : undefined;
    const requestId = proofRequestId || `${target.id}:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`;
    setProofRequestId(requestId);
    const data = await svcCall({
      action: "generate_proofs", jobNumber: job.id, targetId: target.id,
      executionDate: today, executionPlace: execPlace, completedService: c,
      attemptId: attemptIdOverride || proofAttemptId || undefined,
      requestId,
    }, null);
    if (data) {
      // A finished batch retires its request id, so the NEXT deliberate click
      // is a real regeneration rather than a replay of this one.
      setProofRequestId(null);
      setMultiProofResult({ targetId: target.id, drafts: data.drafts || [], alreadyGenerated: !!data.alreadyGenerated });
      loadSvcDocs(job, target);
    }
    return data;
  }
  const [multiProofResult, setMultiProofResult] = useState(null);
  // Signed/draft PDFs with short-lived signed URLs for review + download.
  const [svcDocs, setSvcDocs] = useState({});
  async function loadSvcDocs(job, target) {
    const d = await svcCall({ action: "list_docs", jobNumber: job.id, targetId: target.id }, null);
    if (d) setSvcDocs(prev => ({ ...prev, [target.id]: d.docs || [] }));
  }
  // Share a signed, current Proof with the client — or take it back. The server
  // re-checks eligibility and refuses drafts, stale and superseded documents;
  // this only asks.
  async function setDocVisibility(job, target, doc, visible) {
    const data = await svcCall(
      { action: "set_doc_visibility", jobNumber: job.id, targetId: target.id, docId: doc.id, visible },
      visible ? "Shared with the client." : "Hidden from the client."
    );
    if (data) await loadSvcDocs(job, target);
  }
  async function saveFormOverride(job, target) {
    if (!formOverrideForm || !formOverrideForm.formCode || !formOverrideForm.reason.trim()) return;
    const data = await svcCall({ action: "set_form_override", jobNumber: job.id, targetId: target.id, formCode: formOverrideForm.formCode, reason: formOverrideForm.reason.trim() }, "Proof form override saved.");
    if (data) { setFormOverrideForm(null); setDocPreview(null); }
  }
  async function removeFormOverride(job, target) {
    const data = await svcCall({ action: "remove_form_override", jobNumber: job.id, targetId: target.id }, "Override removed — the next rule now applies.");
    if (data) { setFormOverrideForm(null); setDocPreview(null); }
  }
  // Reopen a SPECIFIC existing unsigned draft into the review/signing panel — no
  // regeneration, no new version. Restores the exact reviewed payload keyed to the
  // draft's id so Sign & Finalize finalizes THIS draft.
  async function reopenDraft(job, target, doc) {
    const data = await svcCall({ action: "reopen_draft", jobNumber: job.id, targetId: target.id, docId: doc.id }, null);
    if (data) {
      setDocPreview({ targetId: target.id, kind: data.kind || doc.kind, docId: data.docId || doc.id, version: data.version || doc.version, content: data.content, items: data.items, values: data.values, sc104Review: data.sc104Review || null, missing: data.missing || [], advisory: data.advisory || [], formCode: data.formCode, formTitle: data.formTitle, rule2150: data.rule2150, profileReadiness: data.profileReadiness, templateResolution: data.templateResolution });
      // One-click signing: the name comes from the saved Server Profile.
      setSigNameSvc((data.values && data.values.serverName) || "");
    }
  }
  async function signSvcDoc(job, target) {
    if (!docPreview || !sigNameSvc.trim()) { setSvcMsg("Type the process server's signature name first."); return; }
    // Sign the EXACT reviewed draft. Its id is what the server finalizes from; the
    // form code disambiguates a packet that has several drafts of the same kind.
    if (!docPreview.docId) { setSvcMsg("Reopen the draft with Review / Sign Draft, then sign — no draft is selected."); return; }
    const today = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles" }).format(new Date()); // Pacific calendar day (execution/signature date)
    const data = await svcCall({ action: "sign_doc", jobNumber: job.id, targetId: target.id, kind: docPreview.kind, docId: docPreview.docId, formCode: docPreview.formCode || undefined, signatureName: sigNameSvc.trim(), signatureImage: await scriptSignaturePng(sigNameSvc.trim()), executionDate: today, executionPlace: execPlace, mailing: mailingFacts }, "Document signed and saved.");
    if (data) { setDocPreview(null); await loadSvcDocs(job, target); await loadJobs(); }
  }
  // Controlled recovery of a FAILED, PRIVATE signed proof. Deliberately separate from
  // normal signing: it requires an explicit correction reason and the signature name,
  // and re-finalizes from the proof's immutable source draft. It performs no
  // client-facing action.
  async function recoverSignedProof(job, target, doc) {
    if (!recoverReason.trim()) { setSvcMsg("Enter a correction reason before re-finalizing."); return; }
    if (!sigNameSvc.trim()) { setSvcMsg("Type the process server's signature name to re-finalize."); return; }
    if (!doc.sourceDraftId) { setSvcMsg("This signed proof has no recorded source draft, so it cannot be re-finalized this way."); return; }
    const today = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles" }).format(new Date());
    const data = await svcCall({ action: "recover_signed_proof", jobNumber: job.id, targetId: target.id, failedDocId: doc.id, sourceDraftId: doc.sourceDraftId, correctionReason: recoverReason.trim(), signatureName: sigNameSvc.trim(), executionDate: today, executionPlace: execPlace }, "Corrected signed proof created (kept private).");
    if (data) { setRecoverDoc(null); setRecoverReason(""); await loadSvcDocs(job, target); await loadJobs(); }
  }
  // ONE CLICK: open the exact draft, sign it with the Server Profile name, and
  // close the service job. The signature is the same typed /s/ signature as
  // Sign & Finalize; the draft is the one already generated and reviewable.
  // "Signing…" shows only on the paper whose Sign button was tapped.
  const [signingDocId, setSigningDocId] = useState(null);
  const signingRef = useRef(false);
  async function signDraftNow(job, target, doc) {
    if (signingRef.current) return;   // a second tap must not sign twice
    signingRef.current = true;
    setSigningDocId(doc.id);
    try { await signDraftSteps(job, target, doc); } finally { setSigningDocId(null); signingRef.current = false; }
  }
  async function signDraftSteps(job, target, doc) {
    // No browser pop-up: a blocked/suppressed confirm() silently cancels and
    // looks like "nothing happened". Tapping Sign IS the decision.
    setCompleteMsg("");
    const opened = await svcCall({ action: "reopen_draft", jobNumber: job.id, targetId: target.id, docId: doc.id }, null);
    const name = opened && opened.values && opened.values.serverName;
    if (!opened) return;
    if (!name) { setSvcMsg("Add your legal name in Server Profile first, then sign."); return; }
    const today = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles" }).format(new Date());
    const signedRes = await svcCall({ action: "sign_doc", jobNumber: job.id, targetId: target.id, kind: opened.kind || doc.kind, docId: opened.docId || doc.id, formCode: opened.formCode || doc.formCode || undefined, signatureName: name, signatureImage: await scriptSignaturePng(name), executionDate: today, executionPlace: execPlace, mailing: mailingFacts }, "Signed. Tap Mark Completed to send it to the client.");
    if (!signedRes) return;
    setDocPreview(null);
    // A signed Proof finishes this person's service job automatically.
    if ((opened.kind || doc.kind) === "proof_of_service") {
      await svcCall({ action: "complete_job", jobNumber: job.id, targetId: target.id }, null);
    }
    await loadJobs();
    await loadSvcDocs(job, target);
  }
  // RE-SIGN: make a fresh draft from the current record and sign it. The new
  // signed proof replaces the old one (kept on file as superseded). Only before
  // the order is completed — nothing already sent to the client is changed.
  async function resignNow(job, target, signedDoc) {
    setCompleteMsg("");
    const data = await generateSvcDoc(job, target, signedDoc.kind || "proof_of_service");
    setDocPreview(null);
    if (!data || !data.docId) return;
    await signDraftNow(job, target, { id: data.docId, kind: signedDoc.kind || "proof_of_service", formCode: data.formCode || signedDoc.formCode });
  }
  async function completeSvcJob(job, target) {
    const data = await svcCall({ action: "complete_job", jobNumber: job.id, targetId: target.id }, "Service job completed.");
    if (data) await loadJobs();
  }
  // Upload the selected documents DIRECTLY to the private order-documents bucket.
  // Per file: (1) request a short-lived signed upload authorization scoped to one
  // unique order-scoped path, (2) PUT the file bytes straight to storage with
  // that signed URL, (3) finalize with metadata only — the backend verifies the
  // object actually exists before recording it. File contents are never base64-
  // encoded into a function body, so the old ~4 MB Netlify ceiling is gone; each
  // file succeeds or fails on its own.
  async function uploadDocsForJob(jobNumber, fileList, targetId) {
    const files = Array.from(fileList || []);
    const result = { ok: false, total: files.length, uploaded: [], failed: [], rejected: [] };
    if (!files.length) return result;
    for (const f of files) {
      // Rejected BEFORE any request is made — over the per-file limit.
      if (f.size > ADMIN_MAX_UPLOAD_BYTES) {
        result.rejected.push({ name: f.name, reason: `exceeds the current ${ADMIN_MAX_UPLOAD_MB} MB upload limit (${(f.size / (1024 * 1024)).toFixed(1)} MB)` });
        continue;
      }
      try {
        // 1) Authorize: metadata only. The signed URL comes back scoped to one
        //    generated path; no storage credential ever reaches the browser.
        const authRes = await fetch("/.netlify/functions/admin-direct-upload", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "authorize", jobNumber, files: [{ name: f.name, type: f.type, size: f.size }] }) });
        const auth = await authRes.json().catch(() => ({}));
        const grant = auth && Array.isArray(auth.grants) ? auth.grants[0] : null;
        if (!authRes.ok || !grant) {
          const reason = (auth && auth.error) || (auth && Array.isArray(auth.rejected) && auth.rejected[0] && auth.rejected[0].reason) || `server returned HTTP ${authRes.status}`;
          result.failed.push({ name: f.name, status: authRes.status, reason });
          continue;
        }
        // 2) Direct upload: the file object itself is the request body — no
        //    reading into memory, no base64, no function in the byte path.
        const putRes = await fetch(grant.uploadUrl, { method: "PUT", headers: { "Content-Type": grant.contentType, "x-upsert": "false" }, body: f });
        if (!putRes.ok) {
          result.failed.push({ name: f.name, status: putRes.status, reason: `storage upload failed (HTTP ${putRes.status})` });
          continue;
        }
        // 3) Finalize: metadata + path only. Not attached until this succeeds —
        //    the backend verifies the stored object and cleans it up on failure.
        const finRes = await fetch("/.netlify/functions/admin-direct-upload", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "finalize", jobNumber, path: grant.path, name: f.name, type: f.type, size: f.size, targetId: targetId || undefined }) });
        const fin = await finRes.json().catch(() => ({}));
        if (finRes.ok && fin.ok) { result.uploaded.push({ name: f.name }); continue; }
        result.failed.push({ name: f.name, status: finRes.status, reason: (fin && fin.error) || `server returned HTTP ${finRes.status}` });
      } catch (e) {
        result.failed.push({ name: f.name, reason: "the network request did not complete" });
      }
    }
    // Complete success ONLY when every selected file was stored AND recorded.
    result.ok = result.uploaded.length === files.length && !result.failed.length && !result.rejected.length;
    return result;
  }
  // One admin-readable line naming exactly which files uploaded, failed, or were
  // rejected before upload, replacing the opaque retry message this path used to
  // show for every kind of failure.
  function uploadDocsSummary(r) {
    if (r.ok) return `${r.uploaded.length} document(s) uploaded.`;
    const parts = [];
    if (r.uploaded.length) parts.push(`Uploaded: ${r.uploaded.map((u) => u.name).join(", ")}.`);
    if (r.rejected.length) parts.push(`Not sent — ${r.rejected.map((x) => `"${x.name}" ${x.reason}`).join("; ")}.`);
    if (r.failed.length) parts.push(`Failed — ${r.failed.map((x) => `"${x.name}" ${x.status ? `(HTTP ${x.status}) ` : ""}${x.reason}`).join("; ")}.`);
    if (!parts.length) parts.push("No documents were uploaded.");
    parts.push(`${r.uploaded.length} of ${r.total} uploaded.`);
    return parts.join(" ");
  }
  async function uploadOrderDocs(job, fileList, targetId) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBillingBusy(true); setBillingMsg("");
    const r = await uploadDocsForJob(job.id, files, targetId);
    setBillingMsg(uploadDocsSummary(r));
    // Reload whenever anything actually landed, so the panel shows what stored.
    if (r.uploaded.length) await loadJobs();
    setBillingBusy(false);
  }

  async function submitRefund(amountCents, reason) {
    if (!refundJob) return;
    setActionBusy(true);
    try {
      const res = await fetch("/.netlify/functions/refund-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobNumber: refundJob.id, amountCents, reason, adminName: "admin" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) { showNotice(data.error || "The refund could not be processed."); return; }
      setRefundJob(null);
      await loadJobs();
      showNotice("Refund processed successfully.");
    } catch (e) {
      showNotice("The refund could not be processed. Please try again.");
    } finally { setActionBusy(false); }
  }

  // Mark Completed. One tap completes the order and emails the client the
  // signed proof(s). Result shows ON THE PAGE (no browser pop-ups).
  const [completeMsg, setCompleteMsg] = useState("");
  useEffect(() => { setCompleteMsg(""); }, [selected]);
  async function completeOrder() {
    // Normal path: the signed Proof(s) on the order are attached by the server.
    // A hand-attached PDF is optional and only for proofs made outside the portal.
    setActionBusy(true);
    setCompleteMsg("Completing the order and emailing the client…");
    try {
      const proof = proofFile ? { filename: proofFile.name, mimeType: proofFile.type, dataBase64: await fileToBase64(proofFile) } : undefined;
      const res = await fetch("/.netlify/functions/complete-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "complete", jobNumber: selected, adminName: "admin", proof }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) { setCompleteMsg("✗ " + (data.error || "Could not complete the order.")); return; }
      setProofFile(null);
      await loadJobs();
      setCompleteMsg(data.emailSent
        ? "✓ Order completed. The client was emailed the signed proof."
        : "Order completed, but the email did not send — use Retry Email.");
    } catch (e) {
      setCompleteMsg("✗ Could not complete the order. Please try again.");
    } finally { setActionBusy(false); }
  }


  async function retryCompletionEmail() {
    setActionBusy(true);
    try {
      const res = await fetch("/.netlify/functions/complete-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "retry_email", jobNumber: selected, adminName: "admin" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) { showNotice(data.error || "Could not resend the email."); return; }
      await loadJobs();
      showNotice(data.emailSent ? "Completion email sent." : "The email still did not send. Please try again later.");
    } catch (e) {
      showNotice("Could not resend the email.");
    } finally { setActionBusy(false); }
  }

  const visibleJobs = jobs.filter(j => (showArchived ? j.archived : !j.archived));

  // AB 747: a statement is required when the attempt has no photo, or a photo
  // without GPS coordinates.
  const photoStatementNeeded = (f) => { const ph = (f && f.photos) || []; return !!(f && f.outcome) && (ph.length === 0 || ph.some(p => p && p.gpsUnavailable && !p.gps)); };
  async function addNote() {
    // Date/time are auto-filled when the form opens and stay editable.
    if (!noteForm.date) { showNotice("Enter the date of service."); return; }
    if (captureEnabled && !noteForm.outcome) { setAttemptMsg("Pick the outcome of this attempt."); return; }
    if ((noteForm.photos || []).some(p => p && p.stamping)) { setAttemptMsg("One moment — the photo is still being stamped with the time and GPS."); return; }
    // No photo / no reason is NOT a hard stop — the entry always saves.
    // A substituted service at a business names whoever took the papers; only a
    // personal service on the business needs the authorized-person answer.
    if (isBusiness(noteForm.locationType) && noteForm.status === "served" && noteForm.serviceType !== "substitute") {
      const missing = businessServiceMissing(noteForm);
      if (missing.length) { setAttemptMsg(`Business service: enter ${missing.join(" and ")}.`); return; }
    }
    setAttemptMsg("");
    let gps = noteForm.gps;
    if (!gps) {
      try { gps = await getCurrentGPS(); } catch(e) { /* GPS optional — saved without it */ }
    }
    // AB 747 statement (no photo / no GPS) is kept with the attempt's notes.
    const stmt = photoStatementNeeded(noteForm) ? String(noteForm.photoStatement || "").trim() : "";
    const entryWithGPS = { ...noteForm, gps, desc: stmt ? [String(noteForm.desc || "").trim(), `Photo/GPS statement (AB 747): ${stmt}`].filter(Boolean).join("\n") : noteForm.desc };
    const photosAtSave = noteForm.photos || [];
    // Field capture off: the legacy local service log only (unchanged).
    if (!captureEnabled || !selected) {
      setJobs(prev => prev.map(j => (j.id === selected ? { ...j, notes: [...j.notes, entryWithGPS] } : j)));
      setNoteForm(f => ({ ...BLANK_NOTE_FORM, ...nowLocalDateTime(), location: (selectedJob && selectedJob.address) || "" }));
      return;
    }
    // One save in flight at a time (double-tap guard).
    if (captureEnabled && selected && !savingAttemptRef.current) savingAttemptRef.current = true;
    else return;
    let savedOk = false;
    let savedAttemptId = null;
    let savedAttemptsList = null;
    setClientUpdateMsg("");
    try {
      const token = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : String(Date.now());
      const attempt = mapNoteFormToAttempt(entryWithGPS, selected, token);
      // Multi-service orders: attach the attempt to the ADMIN-SELECTED Service
      // Job (server verifies the target belongs to this order).
      if (attemptTargetId) attempt.target_id = attemptTargetId;
      const res = await fetch("/.netlify/functions/log-attempt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // The override travels beside the attempt, never inside it: the
        // server applies these columns itself after building the payload.
        body: JSON.stringify({ attempt, adminName: "owner", notifyClient, reserve: true,
          diligenceOverride: diligenceOverride.used && diligenceOverride.confirmed
            ? { used: true, reason: diligenceOverride.reason } : null }),
      });
      const saved = await res.json().catch(() => null);
      if (!res.ok) {
        // The server REFUSED the attempt (e.g. substituted-service diligence
        // not met). Say so and keep everything the server typed in.
        setAttemptMsg((saved && saved.error) || "The attempt was NOT saved. Please try again.");
        if (saved && saved.overrideRequired) { setNeedOverride(true); setDiligenceOverride(o => (o.used ? o : { used: true, reason: "", confirmed: false })); }
        return;
      }
      savedOk = true;
      setNeedOverride(false);
      const cu = saved && saved.clientUpdate;
      setClientUpdateMsg(cu === "sent" ? "Update emailed to the client."
        : cu === "failed" ? "Attempt saved, but the client update email did NOT send — please update the client personally."
        : cu === "admin_skipped" ? "Attempt saved. No email sent — update the client personally."
        : cu === "no_email" ? "Attempt saved. No client email on file — update the client personally."
        : "");
      // Photos are uploaded only AFTER the attempt row exists, so every one is
      // tied to a real attempt id rather than to a form still in memory.
      const attemptId = saved && (saved.attemptId || (saved.attempt && saved.attempt.id) || saved.id);
      savedAttemptId = attemptId || null;
      if (attemptId) await uploadEvidencePhotos(selected, attemptId, photosAtSave, gps);
      savedAttemptsList = await refreshAttempts(selected);
    } catch (e) {
      setAttemptMsg("The attempt could not be saved (connection problem). Nothing was lost — press Save again.");
    } finally { savingAttemptRef.current = false; }
    if (!savedOk) return;
    // A photo that did NOT store is kept so it can be retried; everything else
    // resets. A failed upload must never look like a saved one.
    setDiligenceOverride({ used: false, reason: "", confirmed: false });
    setNotifyClient(true);
    setNoteForm(f => ({ ...BLANK_NOTE_FORM, ...nowLocalDateTime(), location: (selectedJob && selectedJob.address) || "",
      photos: (f.photos || []).filter(ph => ph && !ph.stored) }));
    // Saved: close the step-by-step entry and return to the SAME person's
    // saved attempts, with a confirmation (it used to reopen on the first
    // screen, which looked like nothing had been saved).
    const savedTarget = attemptTargetId;
    setAttemptOpenFor(null);
    showNotice(isServedOutcome(entryWithGPS.outcome) || entryWithGPS.status === "served" ? "✓ Service saved." : "✓ Attempt saved.");
    setTimeout(() => {
      const el = (savedTarget && document.querySelector(`[data-target-attempts="${savedTarget}"]`)) || document.getElementById("saved-attempts");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    if (entryWithGPS.status === "served" || isServedOutcome(entryWithGPS.outcome)) {
      setShowProof(true);
      autoPrepareProof(selected, attemptTargetId, savedAttemptId);
    } else if (diligenceDue(savedAttemptsList, attemptTargetId)) {
      // Three (or more) attempts and still not served: prepare the Declaration
      // of Due Diligence draft. Each later attempt refreshes it.
      setShowProof(true);
      autoPrepareDiligence(selected, attemptTargetId);
    }
  }

  async function autoPrepareDiligence(jobNumber, targetId) {
    setAutoProofMsg("Preparing the Declaration of Due Diligence…");
    try {
      await loadJobs();
      const job = (loadedJobsRef.current || []).find(j => j.id === jobNumber);
      const targets = (job && job.serviceTargets) || [];
      const target = targets.find(t => t.id === targetId) || targets[0];
      if (!job || !target) { setAutoProofMsg("Attempt saved. Open Service Jobs below to prepare the Due Diligence declaration."); return; }
      const data = await generateSvcDoc(job, target, "due_diligence");
      setAutoProofMsg(data
        ? "Declaration of Due Diligence draft prepared — review and sign it in Service Jobs below."
        : "Attempt saved. The Due Diligence declaration could not be prepared automatically — see Service Jobs below.");
    } catch (e) {
      setAutoProofMsg("Attempt saved. The Due Diligence declaration could not be prepared automatically — open Service Jobs below.");
    }
  }

  // SERVICE COMPLETED: prepare the Proof of Service draft automatically — the
  // same steps as "Analyze documents" + "Generate Proof of Service". The draft
  // still has to be reviewed and signed; nothing is sent to the client.
  const [autoProofMsg, setAutoProofMsg] = useState("");
  // Per person: is the Proof being prepared, or why couldn't it be? Shown in
  // that person's box so a failure is never silent.
  const [proofPrep, setProofPrep] = useState({});
  async function autoPrepareProof(jobNumber, targetId, attemptId) {
    setProofPrep(p => ({ ...p, [targetId]: { busy: true, msg: "" } }));
    let failMsg = "";
    try { await autoPrepareProofSteps(jobNumber, targetId, attemptId); }
    catch (e) { failMsg = "Something went wrong preparing the Proof."; }
    const err = failMsg || lastSvcErrorRef.current || "";
    setProofPrep(p => ({ ...p, [targetId]: { busy: false, msg: err } }));
    try { const job = (loadedJobsRef.current || []).find(j => j.id === jobNumber); const t = job && (job.serviceTargets || []).find(x => x.id === targetId); if (job && t) await loadSvcDocs(job, t); } catch (e) {}
  }
  async function autoPrepareProofSteps(jobNumber, targetId, attemptId) {
    setAutoProofMsg("Preparing the Proof of Service…");
    try {
      await loadJobs();
      const job = (loadedJobsRef.current || []).find(j => j.id === jobNumber);
      const targets = (job && job.serviceTargets) || [];
      const target = targets.find(t => t.id === targetId) || targets.find(t => ["personal_service", "substituted_service", "posted_service"].includes(t.legal_service_outcome)) || targets[0];
      if (!job || !target) { setAutoProofMsg("Service saved. Open Service Jobs below to prepare the Proof."); return; }
      const cls = await classifyDocs(job, target, false);
      const c = cls && cls.classification;
      const multi = !!(c && c.multipleProofsRequired && c.separateProofs && c.separateProofs.length > 1);
      const data = multi ? await generateAllProofs(job, target, attemptId) : await generateSvcDoc(job, target, "proof_of_service", attemptId);
      setAutoProofMsg(!data
        ? "Service saved. The Proof could not be prepared automatically — see the message in Service Jobs below."
        : "Proof of Service draft prepared — review and sign it in Service Jobs below.");
    } catch (e) {
      setAutoProofMsg("Service saved. The Proof could not be prepared automatically — open Service Jobs below.");
    }
  }

  // Saved evidence photos for the open order's attempts (admin-only, private).
  async function loadAttemptPhotos(jobNumber, attempts) {
    const next = {};
    for (const a of attempts || []) {
      if (!a || !a.id) continue;
      try {
        const res = await fetch("/.netlify/functions/admin-evidence-photo", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "list", jobNumber, attemptId: a.id }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.ok) next[a.id] = data.photos || [];
      } catch (e) { /* display-only */ }
    }
    setAttemptPhotos(prev => ({ ...prev, ...next }));
  }
  async function openAttemptPhoto(jobNumber, attemptId, photoId) {
    try {
      const res = await fetch("/.netlify/functions/admin-evidence-photo", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "view", jobNumber, attemptId, photoId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.url) setLightbox(data.url); else showNotice(data.error || "That photo could not be opened.");
    } catch (e) { showNotice("That photo could not be opened."); }
  }
  // Add photos from the camera or the gallery. Each one is stamped by the APP
  // with the time it was added and the phone's position at that moment.
  // AB 747: each photo carries a READABLE stamp of the date, time and GPS
  // coordinates. The app burns it into the picture itself the moment the
  // photo is added (camera: the moment it was taken).
  function addEvidenceFiles(fileList, source) {
    Array.from(fileList || []).forEach(file => {
      const token = newPhotoToken();
      const addedAt = new Date().toISOString();
      const fileModifiedAt = file.lastModified ? new Date(file.lastModified).toISOString() : null;
      // The time and location saved INSIDE the photo (a gallery photo was taken
      // earlier, at the address) win over the time/location of adding it.
      const exifP = exifFromFile(file);
      const reader = new FileReader();
      reader.onload = ev => {
        const original = ev.target.result;
        setNoteForm(f => ({
          ...f,
          photos: [...(f.photos || []), {
            token, dataUrl: original, filename: file.name, contentType: file.type,
            capturedAtDevice: addedAt, fileModifiedAt,
            source, gps: null, stored: false, stamping: true,
          }],
        }));
        const finish = async (gNow) => {
          let stamped = null;
          const ex = await exifP;
          const g = ex.gps || gNow;
          // A photo taken with the portal's camera gets the date, time and GPS
          // stamped ON it. Uploaded photos are never drawn on. The photo's own
          // time becomes the entry's time unless she changed the time herself.
          if (source === "camera") { try { stamped = await stampPhoto(original, photoStampLines(ex.takenAt || addedAt, g)); } catch (e) { stamped = null; } }
          setNoteForm(f => ({ ...f,
            ...(ex.takenAt && !f.whenEdited ? nowLocalDateTime(new Date(ex.takenAt)) : {}),
            photos: (f.photos || []).map(p => (p.token === token ? {
            ...p, gps: g || null, gpsUnavailable: !g, stamping: false,
            ...(ex.takenAt ? { capturedAtDevice: ex.takenAt, timeFromPhoto: true } : {}),
            ...(stamped ? { dataUrl: stamped, contentType: "image/jpeg", filename: String(file.name || "photo").replace(/\.[^.]+$/, "") + "-stamped.jpg", stamped: true } : {}),
          } : p)) }));
        };
        getCurrentGPS().then(g => finish(g)).catch(() => finish(null));
      };
      reader.readAsDataURL(file);
    });
  }
  // JOB PHOTOS: take or add a photo for one person's Service Job without
  // logging an attempt. Stamped with date, time and GPS, saved right away.
  const [jobPhotos, setJobPhotos] = useState({});
  const [jobPhotoBusy, setJobPhotoBusy] = useState({});
  async function jobPhotoCall(payload) {
    const res = await fetch("/.netlify/functions/admin-evidence-photo", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok && data.ok, data };
  }
  async function loadJobPhotos(jobNumber, targetId) {
    const r = await jobPhotoCall({ action: "list_job", jobNumber, targetId });
    if (r.ok) setJobPhotos(p => ({ ...p, [targetId]: r.data.photos || [] }));
  }
  function addJobPhotos(fileList, source, jobNumber, targetId) {
    Array.from(fileList || []).forEach(file => {
      const token = newPhotoToken();
      const addedAt = new Date().toISOString();
      const fileModifiedAt = file.lastModified ? new Date(file.lastModified).toISOString() : null;
      setJobPhotoBusy(b => ({ ...b, [targetId]: (b[targetId] || 0) + 1 }));
      const exifP = exifFromFile(file);
      const reader = new FileReader();
      reader.onload = async ev => {
        const original = ev.target.result;
        const ex = await exifP;
        let g = ex.gps || null;
        if (!g) { try { g = await getCurrentGPS(); } catch (e) { g = null; } }
        let dataUrl = original, contentType = file.type, filename = file.name;
        // Taken with the portal's camera: date, time and GPS stamped on it.
        // Uploaded photos are never drawn on.
        if (source === "camera") {
          try { dataUrl = await stampPhoto(original, photoStampLines(ex.takenAt || addedAt, g)); contentType = "image/jpeg"; filename = String(file.name || "photo").replace(/\.[^.]+$/, "") + "-stamped.jpg"; } catch (e) { dataUrl = original; }
        }
        const comma = String(dataUrl).indexOf(",");
        const r = await jobPhotoCall({ action: "upload", jobNumber, targetId, clientToken: token, filename, contentType,
          capturedAtDevice: ex.takenAt || addedAt, fileModifiedAt, source, gps: g, dataBase64: comma > -1 ? String(dataUrl).slice(comma + 1) : "" });
        setJobPhotoBusy(b => ({ ...b, [targetId]: Math.max(0, (b[targetId] || 1) - 1) }));
        if (!r.ok) showNotice(r.data.error || "That photo could not be saved. Try again.");
        else showNotice("✓ Photo saved.");
        loadJobPhotos(jobNumber, targetId);
      };
      reader.readAsDataURL(file);
    });
  }
  async function viewJobPhoto(jobNumber, targetId, photoId) {
    const r = await jobPhotoCall({ action: "view", jobNumber, targetId, photoId });
    if (r.ok && r.data.url) setLightbox(r.data.url); else showNotice(r.data.error || "That photo could not be opened.");
  }
  function removeEvidencePhoto(token) {
    setNoteForm(f => ({ ...f, photos: (f.photos || []).filter(p => p.token !== token || p.stored) }));
  }

  function sendAdminMsg() {
    if (!adminMsg.trim()) return;
    const msg = { from: "server", text: adminMsg, time: new Date().toLocaleString() };
    setJobs(prev => prev.map(j => j.id === selected ? { ...j, messages: [...j.messages, msg] } : j));
    setAdminMsg("");
  }

  const job = selectedJob;

  if (authed && appLocked) {
    return (
      <div data-testid="app-locked" style={{ maxWidth: 340, margin: "2rem auto", textAlign: "center" }}>
        <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>🔒</div>
        <h3 style={{ fontFamily: "Montserrat, sans-serif", marginBottom: "1rem" }}>Locked</h3>
        <button type="button" data-testid="app-unlock" aria-label="Unlock with fingerprint" disabled={unlockBusy} onClick={() => unlockApp()}
          style={{ width: 120, height: 120, borderRadius: "50%", border: "3px solid var(--gold, #c8a24a)", background: "#fff8ea", fontSize: "3.6rem", lineHeight: 1, cursor: "pointer", opacity: unlockBusy ? 0.6 : 1 }}>
          <span aria-hidden="true">👆</span>
        </button>
        <div style={{ fontSize: "0.85rem", color: "var(--navy)", fontWeight: 700, marginTop: "0.5rem" }}>{unlockBusy ? "Touch the sensor…" : "Tap to use your fingerprint"}</div>
        {unlockErr && <div className="alert alert-warn" style={{ marginTop: "0.75rem", fontSize: "0.85rem" }}>{unlockErr}</div>}
        <button type="button" onClick={() => { setAppLocked(false); logoutAdmin(); }} style={{ marginTop: "1rem", background: "none", border: "none", color: "var(--navy)", textDecoration: "underline", cursor: "pointer", fontSize: "0.85rem" }}>Sign in with password instead</button>
      </div>
    );
  }
  if (!authed && sessionChecking && !resetToken) {
    return <div data-testid="admin-session-checking" style={{ textAlign: "center", padding: "3rem 1rem", color: "var(--gray)" }}>Signing you in…</div>;
  }
  if (!authed) {
    // Password-reset screen (opened from the emailed link: /?admin_reset=<token>).
    if (resetToken) return (
      <div style={{ maxWidth: 340, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🔑</div>
        <h3 style={{ fontFamily: "Montserrat, sans-serif", marginBottom: "1rem" }}>Set a New Password</h3>
        {resetMsg && <div className="alert-warn alert" style={{ marginBottom: "1rem", fontSize: "0.82rem" }}>{resetMsg}</div>}
        <div className="field" style={{ marginBottom: "1rem" }}>
          <label>New password</label>
          <div style={{ position: "relative" }}>
            <input type={showResetPw ? "text" : "password"} autoComplete="new-password" value={resetPw} onChange={e => setResetPw(e.target.value)} style={{ width: "100%", paddingRight: "3.4rem" }} />
            <button type="button" onClick={() => setShowResetPw(v => !v)} aria-label={showResetPw ? "Hide password" : "Show password"} aria-pressed={showResetPw} style={{ position: "absolute", right: "0.5rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--navy)", fontSize: "0.75rem", fontWeight: 700 }}>{showResetPw ? "Hide" : "Show"}</button>
          </div>
        </div>
        <div className="field" style={{ marginBottom: "1rem" }}>
          <label>Confirm new password</label>
          <div style={{ position: "relative" }}>
            <input type={showResetPw2 ? "text" : "password"} autoComplete="new-password" value={resetPw2} onChange={e => setResetPw2(e.target.value)} onKeyDown={e => { if (e.key === "Enter") adminResetSubmit(); }} style={{ width: "100%", paddingRight: "3.4rem" }} />
            <button type="button" onClick={() => setShowResetPw2(v => !v)} aria-label={showResetPw2 ? "Hide password" : "Show password"} aria-pressed={showResetPw2} style={{ position: "absolute", right: "0.5rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--navy)", fontSize: "0.75rem", fontWeight: 700 }}>{showResetPw2 ? "Hide" : "Show"}</button>
          </div>
        </div>
        <button className="btn-primary" onClick={adminResetSubmit}>Update Password</button>
        <div style={{ marginTop: "0.75rem" }}><button onClick={() => { setResetToken(""); setResetMsg(""); try { const u = new URL(window.location.href); u.searchParams.delete("admin_reset"); window.history.replaceState({}, "", u.toString()); } catch (e) {} }} style={{ background: "none", border: "none", color: "var(--navy)", cursor: "pointer", fontSize: "0.82rem", textDecoration: "underline" }}>Back to login</button></div>
      </div>
    );
    // Forgot-password screen (enumeration-safe neutral response).
    if (showForgot) return (
      <div style={{ maxWidth: 340, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>📧</div>
        <h3 style={{ fontFamily: "Montserrat, sans-serif", marginBottom: "1rem" }}>Reset Password</h3>
        {forgotMsg
          ? <div className="alert alert-success" style={{ marginBottom: "1rem", fontSize: "0.82rem" }}>{forgotMsg}</div>
          : <p style={{ fontSize: "0.82rem", color: "var(--gray)", marginBottom: "1rem" }}>Enter your admin email and we'll send password reset instructions.</p>}
        {!forgotMsg && <div className="field" style={{ marginBottom: "1rem" }}><label>Email</label><input type="email" autoComplete="username" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} onKeyDown={e => { if (e.key === "Enter") adminForgotSubmit(); }} /></div>}
        {!forgotMsg && <button className="btn-primary" onClick={adminForgotSubmit}>Send Reset Link</button>}
        <div style={{ marginTop: "0.75rem" }}><button onClick={() => { setShowForgot(false); setForgotMsg(""); }} style={{ background: "none", border: "none", color: "var(--navy)", cursor: "pointer", fontSize: "0.82rem", textDecoration: "underline" }}>Back to login</button></div>
      </div>
    );
    // Step 1b: choose where to receive the 2FA code (only when both are available).
    if (loginStep === "choose") return (
      <div style={{ maxWidth: 340, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🔐</div>
        <h3 style={{ fontFamily: "Montserrat, sans-serif", marginBottom: "0.5rem" }}>Choose Verification Method</h3>
        <p style={{ fontSize: "0.82rem", color: "var(--gray)", marginBottom: "1rem" }}>Where should we send your one-time code?</p>
        {loginErr && <div className="alert-warn alert" style={{ marginBottom: "1rem", fontSize: "0.82rem" }}>{loginErr}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          {loginMethods.map(m => (
            <button key={m} className="btn-primary" disabled={loginBusy} onClick={() => adminLoginSubmit(m)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>{m === "sms" ? "📱 Text message" : "✉️ Email"}</span>
              <span style={{ fontWeight: 400, fontSize: "0.78rem", opacity: 0.9 }}>{chooseDest && chooseDest[m]}</span>
            </button>
          ))}
        </div>
        <div style={{ marginTop: "0.75rem" }}><button onClick={() => { setLoginStep("creds"); setLoginErr(""); }} style={{ background: "none", border: "none", color: "var(--navy)", cursor: "pointer", fontSize: "0.82rem", textDecoration: "underline" }}>← Back</button></div>
      </div>
    );
    // Step 2: 2FA one-time code.
    if (loginStep === "otp") return (
      <div style={{ maxWidth: 340, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🔐</div>
        <h3 style={{ fontFamily: "Montserrat, sans-serif", marginBottom: "0.5rem" }}>Enter Verification Code</h3>
        <p style={{ fontSize: "0.82rem", color: "var(--gray)", marginBottom: "1rem" }}>We sent a 6-digit code{maskedDest ? <> to <strong>{maskedDest}</strong></> : null} via {loginMethod === "sms" ? "text message" : "email"}. It expires shortly.</p>
        {loginMethods && loginMethods.length > 1 && (
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", marginBottom: "1rem" }}>
            {loginMethods.map(m => (
              <button key={m} onClick={() => { if (m !== loginMethod) adminResend(m); }} style={{ flex: 1, maxWidth: 140, padding: "0.4rem", borderRadius: 6, cursor: "pointer", fontSize: "0.78rem", fontWeight: 700, border: "2px solid " + (m === loginMethod ? "var(--navy)" : "var(--light)"), background: m === loginMethod ? "var(--navy)" : "var(--white)", color: m === loginMethod ? "#fff" : "var(--navy)" }}>{m === "sms" ? "📱 Text" : "✉️ Email"}</button>
            ))}
          </div>
        )}
        {loginErr && <div className="alert-warn alert" style={{ marginBottom: "1rem", fontSize: "0.82rem" }}>{loginErr}</div>}
        <div className="field" style={{ marginBottom: "1rem" }}><label>6-digit code</label><input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/[^0-9]/g, ""))} onKeyDown={e => { if (e.key === "Enter") adminVerifySubmit(); }} style={{ letterSpacing: "0.3em", textAlign: "center", fontSize: "1.1rem" }} /></div>
        <button className="btn-primary" disabled={loginBusy || otpCode.length < 6} onClick={adminVerifySubmit}>{loginBusy ? "Verifying…" : "Verify & Sign In"}</button>
        <div style={{ marginTop: "0.75rem", display: "flex", justifyContent: "space-between", fontSize: "0.82rem" }}>
          <button onClick={() => { setLoginStep("creds"); setLoginErr(""); setOtpCode(""); }} style={{ background: "none", border: "none", color: "var(--navy)", cursor: "pointer", textDecoration: "underline" }}>← Back</button>
          <button onClick={adminResend} style={{ background: "none", border: "none", color: "var(--navy)", cursor: "pointer", textDecoration: "underline" }}>Resend code</button>
        </div>
      </div>
    );
    // Step 1: email + password. A real <form> with named username/password
    // fields so the browser's password manager offers the saved login on the
    // EMAIL box too (without a form, Chrome treated the email box as an
    // address field and only recognized the password box). The box is
    // type="text" (email keyboard via inputMode): with type="email" Chrome
    // offered saved ADDRESSES there instead of saved logins.
    return (
      <form data-testid="admin-login-form" method="post" action="#" autoComplete="on"
        onSubmit={e => { e.preventDefault(); adminLoginSubmit(); }}
        style={{ maxWidth: 340, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🔒</div>
        <h3 style={{ fontFamily: "Montserrat, sans-serif", marginBottom: "1rem" }}>Admin Login</h3>
        {passkeySupported() && (
          <div style={{ marginBottom: "1rem" }}>
            {/* One big fingerprint. It opens by itself when this device has
                fingerprint login; tap it to try again. */}
            <button type="button" data-testid="passkey-login" aria-label="Sign in with fingerprint" disabled={pkLoginBusy} onClick={() => adminPasskeyLogin()}
              style={{ width: 120, height: 120, borderRadius: "50%", border: "3px solid var(--gold, #c8a24a)", background: "#fff8ea", fontSize: "3.6rem", lineHeight: 1, cursor: "pointer", opacity: pkLoginBusy ? 0.6 : 1 }}>
              <span aria-hidden="true">👆</span>
            </button>
            <div style={{ fontSize: "0.85rem", color: "var(--navy)", fontWeight: 700, marginTop: "0.5rem" }}>{pkLoginBusy ? "Touch the sensor…" : "Tap to use your fingerprint"}</div>
            {!showPwLogin && (
              <button type="button" data-testid="show-password-login" onClick={() => setShowPwLogin(true)}
                style={{ marginTop: "0.9rem", background: "none", border: "none", color: "var(--gray)", cursor: "pointer", fontSize: "0.8rem", textDecoration: "underline" }}>Use email and password instead</button>
            )}
          </div>
        )}
        {sessionMsg && <div className="alert-warn alert" style={{ marginBottom: "1rem", fontSize: "0.82rem" }}>{sessionMsg}</div>}
        {loginErr && <div className="alert-warn alert" style={{ marginBottom: "1rem", fontSize: "0.82rem" }}>{loginErr}</div>}
        {(showPwLogin || !passkeySupported()) && (<>
        <div className="field" style={{ marginBottom: "1rem" }}><label htmlFor="admin-login-email">Email</label><input id="admin-login-email" name="username" type="text" inputMode="email" autoCapitalize="none" spellCheck={false} autoComplete="username" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} /></div>
        <div className="field" style={{ marginBottom: "1rem" }}>
          <label htmlFor="admin-login-password">Password</label>
          <div style={{ position: "relative" }}>
            <input id="admin-login-password" name="password" type={showLoginPw ? "text" : "password"} autoComplete="current-password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} style={{ width: "100%", paddingRight: "3.4rem" }} />
            <button type="button" onClick={() => setShowLoginPw(v => !v)} aria-label={showLoginPw ? "Hide password" : "Show password"} aria-pressed={showLoginPw} style={{ position: "absolute", right: "0.5rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--navy)", fontSize: "0.75rem", fontWeight: 700 }}>{showLoginPw ? "Hide" : "Show"}</button>
          </div>
        </div>
        <button type="submit" className="btn-primary" disabled={loginBusy}>{loginBusy ? "Signing in…" : "Log In"}</button>
        <div style={{ marginTop: "0.75rem" }}><button type="button" onClick={() => { setShowForgot(true); setLoginErr(""); }} style={{ background: "none", border: "none", color: "var(--navy)", cursor: "pointer", fontSize: "0.82rem", textDecoration: "underline" }}>Forgot password?</button></div>
        </>)}
      </form>
    );
  }

  // Saved attempts are shown under the Service Job they belong to. The order-
  // level list keeps only attempts with no (known) Service Job, or everything
  // for an order that has no Service Jobs.
  const targetIds = (j) => new Set((j.serviceTargets || []).map(t => t.id));
  const attemptsForTarget = (j, tid) => (j.serviceAttempts || []).filter(r => r.target_id === tid);
  const orderLevelAttempts = (j) => { const ids = targetIds(j); return (j.serviceAttempts || []).filter(r => !r.target_id || !ids.has(r.target_id)); };
  const renderSavedAttempt = (row) => {
                  const t = buildAttemptTimeline([row])[0] || {};
                  const served = /^served_/.test(String(row.outcome || ""));
                  const detail = attemptDetailRows(row);
                  return (
                    <div key={row.id || t.number} className="note-entry" data-testid="saved-attempt"
                      style={{ borderLeft: `3px solid ${served ? "var(--green)" : "var(--gold)"}`, paddingLeft: "0.6rem" }}>
                      <button type="button" data-testid="attempt-toggle" onClick={() => setOpenAttemptId(openAttemptId === row.id ? null : row.id)}
                        style={{ background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer", width: "100%" }}>
                        <div className="note-status">
                          {served ? `✓ ${t.label}` : `Attempt ${t.number} — ${t.label}`}
                          <span style={{ fontWeight: 500, color: "var(--gray)" }}> · {row.manner_attempted || "—"}</span>
                          <span style={{ float: "right", fontSize: "0.75rem", color: "var(--blue)" }}>{openAttemptId === row.id ? "Hide details ▴" : "Details ▾"}</span>
                        </div>
                        <div className="note-meta">{t.date || "—"} {t.time || ""}</div>
                      </button>
                      {openAttemptId === row.id && (
                        <div data-testid="attempt-full" className="text-sm" style={{ margin: "0.35rem 0", padding: "0.5rem", background: "#F8FAFC", border: "1px solid var(--light)", borderRadius: 6 }}>
                          <div style={{ marginBottom: "0.3rem" }}>{attemptNarrative(row)}</div>
                          <div style={{ color: "var(--gray)", fontSize: "0.75rem" }}>
                            Attempt #{t.number}{row.outcome ? ` · outcome: ${String(row.outcome).replace(/_/g, " ")}` : ""}
                            {row.attempted_at_server ? ` · saved ${new Date(row.attempted_at_server).toLocaleString()}` : ""}
                            {row.created_by ? ` · by ${row.created_by}` : ""}
                          </div>
                        </div>
                      )}
                      {/* A substituted service resting on an override says so
                          here, permanently, with the counts as they stood at the
                          time. Admin/service-side only — never shown to a client. */}
                      {row.diligence_override_used === true && (
                        <div data-testid="override-badge" style={{ marginTop: "0.25rem", padding: "0.35rem 0.5rem", border: "1px solid var(--red)", borderRadius: 4, background: "#FEF2F2" }}>
                          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--red)" }}>⚠ Due diligence overridden</div>
                          <div className="text-sm" style={{ color: "#92400E" }}>
                            {row.diligence_qualifying_attempts_at_override} of {row.diligence_required_attempts} qualifying attempts at the time
                            {row.diligence_overridden_by ? ` · by ${row.diligence_overridden_by}` : ""}
                          </div>
                          {row.diligence_override_reason && (
                            <div className="text-sm" style={{ marginTop: "0.15rem" }}><strong>Reason:</strong> {row.diligence_override_reason}</div>
                          )}
                        </div>
                      )}
                      {/* Collapsed by default: one line per attempt; tap Details for
                          everything recorded, the photos and Delete. */}
                      {openAttemptId !== row.id && (attemptPhotos[row.id] || []).length > 0 && <div className="photo-meta">📷 {(attemptPhotos[row.id] || []).length} photo{(attemptPhotos[row.id] || []).length === 1 ? "" : "s"}</div>}
                      {openAttemptId === row.id && (<>
                      {detail.length > 0 && (
                        <dl style={{ margin: "0.4rem 0 0", display: "grid", gridTemplateColumns: "auto 1fr", gap: "0.15rem 0.6rem", fontSize: "0.78rem" }}>
                          {detail.map(([label, value]) => (
                            <Fragment key={label}>
                              <dt style={{ color: "var(--gray)", whiteSpace: "nowrap" }}>{label}</dt>
                              <dd style={{ margin: 0 }}>{value}</dd>
                            </Fragment>
                          ))}
                        </dl>
                      )}
                      {(attemptPhotos[row.id] || []).length > 0 && (
                        <div data-testid="saved-photos" style={{ marginTop: "0.4rem" }}>
                          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--gray)" }}>Evidence photos (private)</div>
                          {(attemptPhotos[row.id] || []).map(ph => (
                            <div key={ph.id} className="photo-meta" style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.2rem" }}>
                              <button type="button" className="btn-link" onClick={() => openAttemptPhoto(job.id, row.id, ph.id)}
                                style={{ background: "none", border: "none", padding: 0, color: "var(--blue)", textDecoration: "underline", cursor: "pointer", fontSize: "0.75rem", minHeight: 32 }}>
                                🖼️ View
                              </button>
                              <span>
                                {ph.capturedAtDevice ? new Date(ph.capturedAtDevice).toLocaleString() : (ph.capturedAtServer ? new Date(ph.capturedAtServer).toLocaleString() : "time not recorded")}
                                {" · "}{ph.gpsLat != null && ph.gpsLng != null ? `${Number(ph.gpsLat).toFixed(5)}, ${Number(ph.gpsLng).toFixed(5)}${ph.gpsAccuracyM != null ? ` (±${Math.round(ph.gpsAccuracyM)}m)` : ""}` : "no GPS"}
                                {ph.source ? ` · ${ph.source}` : ""}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <button type="button" data-testid="delete-attempt" disabled={deleteBusy}
                        onClick={() => confirmDeleteAttempt(job, row, t)}
                        style={{ marginTop: "0.4rem", background: "none", border: "none", padding: 0, color: "var(--red)", cursor: "pointer", fontSize: "0.75rem", fontWeight: 700 }}>
                        Delete Attempt
                      </button>
                      </>)}
                    </div>
                  );
                };
  // The attempt / service entry form. Rendered INSIDE the Service Job it is for
  // (tap "Log Attempt / Service" on that job) — the person is fixed, no picker.
  // Orders without Service Jobs (legacy) still show it in the order.
  const renderAttemptForm = (fixedTargetId) => (captureEnabled && job ? (
              <div className="note-form">
                <span id="attempt-form" />

                {/* Multi-service orders: pick WHICH Service Job this attempt is
                    for. Attempts/evidence stay with their own person to serve —
                    the server rejects a target from any other order. */}
                {!fixedTargetId && job.serviceTargets && job.serviceTargets.length > 1 && (
                  <div className="serve-section">
                    <h6>Service Job (Person to Serve)</h6>
                    <select data-testid="attempt-target-select" value={attemptTargetId} onChange={e => setAttemptTargetId(e.target.value)}>
                      <option value="">Select the person this attempt is for…</option>
                      {job.serviceTargets.map(t => <option key={t.id} value={t.id}>{t.job_label ? `${t.job_label} — ` : ""}{t.subject_name || "Person to Serve"}</option>)}
                    </select>
                  </div>
                )}

                {/* STEP-BY-STEP ENTRY: one question per screen; tap an answer and
                    it moves on. Back / swipe right returns with the answers kept. */}
                <AttemptWizard form={noteForm} setForm={setNoteForm}
                  subjectName={(fixedTargetId && ((job.serviceTargets || []).find(t => t.id === fixedTargetId) || {}).subject_name) || ((job.serviceTargets || []).find(t => t.id === attemptTargetId) || {}).subject_name || job.defendant || ""}
                  otherNames={(job.serviceTargets || []).filter(t => t.id !== (fixedTargetId || attemptTargetId)).map(t => t.subject_name).filter(Boolean)}
                  attempts={(fixedTargetId || attemptTargetId) ? attemptsForTarget(job, fixedTargetId || attemptTargetId) : (job.serviceAttempts || [])}
                  address={((job.serviceTargets || []).find(t => t.id === (fixedTargetId || attemptTargetId)) || {}).service_address || job.address || ""}
                  onCancel={() => setAttemptOpenFor(null)}
                  slots={{
                    photos: (
<>
                {/* PHOTOS */}
                <div className="quick-block">
                  {/* Each selected photo gets a stable client token the moment
                      it is picked. That token is the idempotency key: a retried
                      save finds the photo already stored instead of making a
                      second object and a second row. A photo that fails to
                      upload STAYS in this list so it can be retried — it is
                      never quietly marked as saved. */}
                  <div>
                    <button type="button" className="btn-primary" data-testid="take-photo" style={{ width: "100%", minHeight: 52, fontSize: "1rem", textTransform: "none", letterSpacing: 0 }}
                      onClick={() => openInAppCamera(files => addEvidenceFiles(files, "camera"), "photo-input-camera")}>📷 Take photo of the door</button>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", marginTop: "0.4rem", fontSize: "0.8rem" }}>
                      <span data-testid="gps-line" style={{ color: noteForm.gps ? "var(--green)" : "var(--red)" }}>
                        📍 {noteForm.gps ? `${noteForm.gps.lat.toFixed(5)}, ${noteForm.gps.lng.toFixed(5)} ±${Math.round(noteForm.gps.accuracy)}m` : (gpsLoading ? "Getting location…" : "No location yet")}
                        {" "}<button type="button" onClick={async () => { setGpsLoading(true); try { const c = await getCurrentGPS(); setN("gps", c); } catch (e) { showNotice("Could not get GPS: " + e); } setGpsLoading(false); }}
                          style={{ background: "none", border: "none", padding: 0, color: "var(--blue)", textDecoration: "underline", cursor: "pointer", fontSize: "0.8rem" }}>Update</button>
                      </span>
                      <button type="button" data-testid="upload-photo" onClick={() => document.getElementById("photo-input").click()}
                        style={{ background: "none", border: "none", padding: 0, color: "var(--blue)", textDecoration: "underline", cursor: "pointer", fontSize: "0.8rem", whiteSpace: "nowrap" }}>From gallery</button>
                    </div>
                    {/* Camera: opens the phone camera directly. Gallery: existing
                        photos/files — no capture attribute, so the picker offers them. */}
                    <input id="photo-input-camera" type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                      onChange={e => { addEvidenceFiles(e.target.files, "camera"); e.target.value = ""; }} />
                    <input id="photo-input" type="file" accept="image/*" multiple style={{ display: "none" }}
                      onChange={e => { addEvidenceFiles(e.target.files, "gallery"); e.target.value = ""; }} />
                  </div>
                  <div className="photo-grid">
                    {noteForm.photos && noteForm.photos.map((photo, i) => (
                      <div key={photo.token || i} style={{ position: "relative" }} data-testid="pending-photo">
                        <img src={photo.dataUrl || photo} className="photo-thumb" alt={`Evidence ${i + 1}`}
                          style={photo.error ? { outline: "2px solid var(--red)" } : undefined}
                          onClick={() => setLightbox(photo.dataUrl || photo)} />
                        {photo.stored && <span style={{ position: "absolute", top: 2, right: 2, fontSize: "0.75rem" }} title="Saved">✅</span>}
                        {photo.error && <span style={{ position: "absolute", top: 2, right: 2, fontSize: "0.75rem" }} title={photo.error}>⚠️</span>}
                        {!photo.stored && (
                          <button type="button" aria-label="Remove photo" data-testid="remove-photo" onClick={() => removeEvidencePhoto(photo.token)}
                            style={{ position: "absolute", top: 2, left: 2, width: 28, height: 28, borderRadius: 14, border: "none", background: "rgba(0,0,0,0.6)", color: "#fff", cursor: "pointer" }}>✕</button>
                        )}
                        <div className="photo-meta">
                          {photo.capturedAtDevice ? new Date(photo.capturedAtDevice).toLocaleString() : ""}
                          <br />{photo.gps ? `${photo.gps.lat.toFixed(5)}, ${photo.gps.lng.toFixed(5)}` : (photo.gpsUnavailable ? "Location unavailable — the attempt's GPS will be used" : "Getting location…")}
                          {photo.source ? ` · ${photo.source}` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                  {photoMsg && <div className="alert alert-warn" style={{ marginTop: "0.5rem" }} data-testid="photo-error">{photoMsg}</div>}
                </div>
</>
                    ),
                    statement: (
<>
                {/* AB 747 (from Jan 1, 2027): every attempt needs a stamped photo of
                    the site, or a detailed statement why not (no GPS/signal, or
                    taking a photo was unsafe). */}
                {photoStatementNeeded(noteForm) && (
                  <div className="serve-section" data-testid="photo-statement" style={{ border: "2px solid #D97706", background: "#FEF9EE" }}>
                    <h6>{(noteForm.photos || []).length ? "No GPS on the photo — explain why" : "No photo — explain why"}</h6>
                    <textarea data-testid="photo-statement-text" value={noteForm.photoStatement || ""} onChange={e => setN("photoStatement", e.target.value)} style={{ minHeight: 180, width: "100%", fontSize: "1rem", lineHeight: 1.5, boxSizing: "border-box" }}
                      placeholder={(noteForm.photos || []).length ? "e.g. No GPS or cell signal inside the building." : "e.g. Taking a photo was unsafe — dog loose in the yard. / Phone camera not working."} />
                  </div>
                )}
</>
                    ),
                    diligence: (
<>
                {/* DUE DILIGENCE — computed from the PERSISTED attempts the
                    server returned for this service job, never from what is
                    typed in this form. The server checks it again on save; this
                    exists so the admin is not surprised by that refusal. */}
                {noteForm.status === "served" && (noteForm.serviceType === "substitute" || noteForm.serviceType === "posting") && (() => {
                  const evaluation = evaluateDiligence(
                    (selectedJob && selectedJob.serviceAttempts) || [],
                    { targetId: attemptTargetId || undefined }
                  );
                  if (evaluation.met) {
                    return (
                      <div className="serve-section" data-testid="diligence-met" style={{ border: "1px solid var(--green)", background: "#EFF8F3" }}>
                        <span className="text-sm" style={{ color: "var(--green)", fontWeight: 700 }}>
                          ✓ Due diligence met — {evaluation.qualifying} qualifying attempt{evaluation.qualifying === 1 ? "" : "s"} on record.
                        </span>
                      </div>
                    );
                  }
                  return (
                    <div className="serve-section" data-testid="diligence-warning" style={{ border: "2px solid var(--red)", background: "#FEF2F2" }}>
                      <h6 style={{ color: "var(--red)" }}>⚠ Due Diligence Not Met</h6>
                      <p className="text-sm" style={{ color: "#92400E", marginBottom: "0.5rem" }}>
                        {warningMessage(evaluation)}
                      </p>
                      <p className="text-sm" style={{ color: "var(--gray)", marginBottom: "0.6rem" }}>
                        {evaluation.qualifying} of {evaluation.required} qualifying attempt-day{evaluation.required === 1 ? "" : "s"} recorded for this service job.
                      </p>
                      {!diligenceOverride.used ? (
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                          <button className="btn-secondary" onClick={() => { setNoteForm(f => ({ ...f, serviceType: "" })); setDiligenceOverride({ used: false, reason: "", confirmed: false }); }}>
                            Cancel / Go Back
                          </button>
                          <button className="btn-primary" data-testid="override-start"
                            onClick={() => setDiligenceOverride({ used: true, reason: "", confirmed: false })}>
                            Override Due Diligence
                          </button>
                        </div>
                      ) : (
                        <div>
                          <div className="field">
                            <label>Why are you overriding due diligence? *</label>
                            <textarea rows={2} data-testid="override-reason" value={diligenceOverride.reason}
                              onChange={e => setDiligenceOverride(o => ({ ...o, reason: e.target.value, confirmed: false }))}
                              placeholder="e.g. Client requested posting per court order, or the client's prior server made the attempts." />
                          </div>
                          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem", cursor: "pointer", marginTop: "0.4rem" }}>
                            <input type="checkbox" data-testid="override-confirm" checked={diligenceOverride.confirmed}
                              disabled={!diligenceOverride.reason.trim()}
                              onChange={e => setDiligenceOverride(o => ({ ...o, confirmed: e.target.checked }))} />
                            I confirm this {noteForm.serviceType === "posting" ? "posting" : "substituted service"} is being recorded as an override of due diligence.
                          </label>
                          <button className="btn-secondary" style={{ marginTop: "0.5rem", fontSize: "0.75rem", padding: "0.25rem 0.6rem" }}
                            onClick={() => setDiligenceOverride({ used: false, reason: "", confirmed: false })}>Cancel override</button>
                        </div>
                      )}
                    </div>
                  );
                })()}
</>
                    ),
                    mailing: (
<>
                {(noteForm.serviceType === "substitute" || noteForm.serviceType === "posting") && (
                  <div className="serve-section" style={{ border: "2px solid #D97706", background: "#FEF9EE" }}>
                    <h6 style={{ color: "#D97706" }}>{noteForm.serviceType === "posting" ? "[!] Posting — Mailing Required (CCP 415.45)" : "[!] Substitute Service — Mailing Required (CCP 415.20)"}</h6>
                    <p style={{ fontSize: "0.78rem", color: "#92400E", marginBottom: "0.75rem" }}>
                      {noteForm.serviceType === "posting"
                        ? "The court order for posting also requires mailing a copy (certified mail). Enter the mailing date so it prints on the Proof."
                        : "California law requires mailing a copy to the defendant within 10 days. Service is not complete until mailing is done."}
                    </p>
                    <div className="serve-grid">
                      <div className="field">
                        <label>Date Documents Mailed</label>
                        <DatePick value={noteForm.mailingDate || ""} onChange={v => setNoteForm(f => ({ ...f, mailingDate: v, mailingDateAuto: false }))} />
                      </div>
                      <div className="field">
                        <label>Mailing Method</label>
                        <select value={noteForm.mailingMethod || ""} onChange={e => setN("mailingMethod", e.target.value)}>
                          <option value="">Select...</option>
                          <option>First Class Mail — Postage Prepaid</option>
                          <option>Certified Mail — Return Receipt Requested</option>
                        </select>
                      </div>
                      {noteForm.mailingMethod === "Certified Mail — Return Receipt Requested" && (
                        <div className="field">
                          <label>Tracking Number</label>
                          <input inputMode="numeric" value={noteForm.mailingTracking || ""} onChange={e => setN("mailingTracking", e.target.value)} placeholder="9400 1000 0000 0000 0000 00" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
</>
                    ),
                    footer: (
<>
                {attemptMsg && <div className="alert alert-warn" style={{ marginTop: "0.75rem" }} data-testid="attempt-error">{attemptMsg}</div>}
                {attemptMsg && needOverride && diligenceOverride.used && (
                  <div className="serve-section" data-testid="override-at-save" style={{ border: "2px solid var(--red)", background: "#FEF2F2", marginTop: "0.6rem" }}>
                    <div style={{ fontWeight: 700, marginBottom: "0.4rem" }}>Override Due Diligence</div>
                    <div className="field">
                      <label>Why are you overriding due diligence? *</label>
                      <textarea rows={2} data-testid="override-reason-save" value={diligenceOverride.reason}
                        onChange={e => setDiligenceOverride(o => ({ ...o, used: true, reason: e.target.value, confirmed: false }))}
                        placeholder="e.g. Client requested posting, or the client's prior server made the attempts." />
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.9rem", cursor: "pointer", marginTop: "0.4rem" }}>
                      <input type="checkbox" data-testid="override-confirm-save" checked={diligenceOverride.confirmed}
                        disabled={!diligenceOverride.reason.trim()}
                        onChange={e => setDiligenceOverride(o => ({ ...o, confirmed: e.target.checked }))} />
                      I confirm this {noteForm.serviceType === "posting" ? "posting" : "substituted service"} is being recorded as an override of due diligence.
                    </label>
                    <div className="text-sm" style={{ marginTop: "0.4rem" }}>Then tap Save Entry again.</div>
                  </div>
                )}
                {selectedJob && hasLiveUpdates(selectedJob.tier, selectedJob.addon) && (
                  <label data-testid="notify-client" style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.75rem", fontSize: "0.85rem" }}>
                    <input type="checkbox" checked={notifyClient} onChange={e => setNotifyClient(e.target.checked)} />
                    Email this update to the client (live updates are included with this order)
                  </label>
                )}
                {clientUpdateMsg && <div className="alert" style={{ marginTop: "0.75rem" }} data-testid="client-update-msg">{clientUpdateMsg}</div>}
</>
                    ),
                    save: (
<>
                <div className="flex-between mt1">
                  <button className="btn-primary btn-log-attempt" data-testid="save-attempt" onClick={addNote}>Save Entry</button>
                </div>
</>
                    ),
                  }} />
              </div>
  ) : null);
  return (
    <div>
      {/* Admin Logout: top-right of the Admin portal (directly under the Home
          nav), clearly separated from order controls. Same logoutAdmin behavior
          — server-side session revocation is unchanged. */}
      <div data-testid="admin-logout-row" className="admin-chrome" style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.75rem" }}>
        <button onClick={() => logoutAdmin()} style={{ padding: "0.45rem 1.1rem", background: "var(--navy)", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "0.85rem" }}>🚪 Logout</button>
      </div>
      <div className="portal-tabs admin-chrome" style={{ marginBottom: "1.5rem", borderRadius: 8, overflow: "hidden", border: "2px solid var(--light)" }}>
        <button className={`portal-tab${adminTab === "jobs" ? " active" : ""}`} onClick={() => setAdminTab("jobs")}>📋 Jobs</button>
        <button className={`portal-tab${adminTab === "route" ? " active" : ""}`} onClick={() => setAdminTab("route")}>🗺️ Route</button>
        <button className={`portal-tab${adminTab === "tax" ? " active" : ""}`} onClick={() => setAdminTab("tax")}>💰 Earnings & Tax</button>
        <button className={`portal-tab${adminTab === "profile" ? " active" : ""}`} onClick={() => setAdminTab("profile")}>⚖️ Server Profile</button>
      </div>

      <div className="admin-chrome" style={{ background: "var(--white)", border: "2px solid var(--light)", borderRadius: 8, padding: "1.25rem", marginBottom: "1.5rem" }}>
        <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: "0.9rem", marginBottom: "0.75rem" }}>🔐 Two-Factor Authentication (Text Message)</div>
        {!phoneStatus && <div style={{ fontSize: "0.82rem", color: "var(--gray)" }}>Loading…</div>}
        {phoneStatus && !phoneStatus.configured && (
          <div style={{ fontSize: "0.82rem", color: "var(--gray)" }}>
            <strong>Email verification is active.</strong> Text-message (SMS) codes become available automatically once the SMS provider is configured. No action is needed here until then.
          </div>
        )}
        {phoneStatus && phoneStatus.configured && (
          <div style={{ fontSize: "0.85rem" }}>
            {phoneMsg && <div className="alert alert-success" style={{ marginBottom: "0.75rem", fontSize: "0.8rem" }}>{phoneMsg}</div>}
            {phoneStatus.verified && phoneStep === "idle" && (
              <div>
                <div style={{ marginBottom: "0.6rem" }}>✅ Verified phone <strong>{phoneStatus.phoneMasked}</strong> — you can receive login codes by text.</div>
                <button className="btn-secondary" onClick={() => { setPhoneStep("entering"); setPhoneInput(""); setPhoneMsg(""); }}>Change number</button>
              </div>
            )}
            {(!phoneStatus.verified || phoneStep === "entering") && phoneStep !== "verifying" && (
              <div>
                <p style={{ color: "var(--gray)", marginBottom: "0.6rem" }}>Add a mobile number to receive login codes by text. We'll text a code to confirm it's yours.</p>
                <div className="field" style={{ marginBottom: "0.75rem" }}><label>Mobile number (e.g. +15551234567)</label><input type="tel" value={phoneInput} onChange={e => setPhoneInput(e.target.value)} placeholder="+15551234567" /></div>
                <button className="btn-primary" disabled={phoneBusy || !phoneInput.trim()} onClick={() => phoneEnrollSubmit(false)}>{phoneBusy ? "Sending…" : "Send verification text"}</button>
                {phoneStep === "entering" && <button onClick={() => { setPhoneStep("idle"); setPhoneMsg(""); }} style={{ marginLeft: "0.75rem", background: "none", border: "none", color: "var(--navy)", cursor: "pointer", textDecoration: "underline", fontSize: "0.82rem" }}>Cancel</button>}
              </div>
            )}
            {phoneStep === "verifying" && (
              <div>
                <p style={{ color: "var(--gray)", marginBottom: "0.6rem" }}>Enter the 6-digit code we texted you.</p>
                <div className="field" style={{ marginBottom: "0.75rem" }}><label>6-digit code</label><input type="text" inputMode="numeric" maxLength={6} value={phoneCode} onChange={e => setPhoneCode(e.target.value.replace(/[^0-9]/g, ""))} onKeyDown={e => { if (e.key === "Enter") phoneVerifySubmit(); }} style={{ letterSpacing: "0.3em", textAlign: "center" }} /></div>
                <button className="btn-primary" disabled={phoneBusy || phoneCode.length < 6} onClick={phoneVerifySubmit}>{phoneBusy ? "Verifying…" : "Verify phone"}</button>
                <button onClick={() => phoneEnrollSubmit(true)} disabled={phoneBusy} style={{ marginLeft: "0.75rem", background: "none", border: "none", color: "var(--navy)", cursor: "pointer", textDecoration: "underline", fontSize: "0.82rem" }}>Resend</button>
                <button onClick={() => { setPhoneStep("idle"); setPhoneCode(""); setPhoneMsg(""); loadPhoneStatus(); }} style={{ marginLeft: "0.75rem", background: "none", border: "none", color: "var(--navy)", cursor: "pointer", textDecoration: "underline", fontSize: "0.82rem" }}>Cancel</button>
              </div>
            )}
          </div>
        )}
      </div>

      <div data-testid="field-servers-card" className="admin-chrome" style={{ background: "var(--white)", border: "2px solid var(--light)", borderRadius: 8, padding: "1.25rem", marginBottom: "1.5rem" }}>
        <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: "0.9rem", marginBottom: "0.5rem" }}>👥 Process Servers</div>
        <div style={{ fontSize: "0.8rem", color: "var(--gray)", marginBottom: "0.5rem" }}>Add another server, then assign them a person on any order. They only see the jobs you assign them: they print the papers, log attempts with photo + GPS, and sign the Proof with their own name.</div>
        {fieldServers.map(sv => (
          <div key={sv.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", padding: "0.3rem 0", fontSize: "0.85rem", borderBottom: "1px solid #eee" }}>
            <span style={{ flex: "1 1 200px", minWidth: 0 }}>{sv.active ? "✅" : "⛔"} <strong>{sv.name}</strong> · {sv.email}{sv.registrationNumber ? ` · ${sv.registrationNumber}` : ""}</span>
            <button type="button" className="btn-secondary" style={{ fontSize: "0.75rem" }} onClick={() => { setFsForm({ ...sv }); setFsMsg(""); }}>Edit</button>
            <button type="button" className="btn-secondary" style={{ fontSize: "0.75rem" }} onClick={() => setFieldServerActive(sv, !sv.active)}>{sv.active ? "Turn off login" : "Turn on login"}</button>
          </div>
        ))}
        {fsForm ? (
          <div data-testid="field-server-form" className="serve-grid" style={{ marginTop: "0.6rem" }}>
            {[["name", "Full legal name *"], ["email", "Email (their login) *"], ["phone", "Phone"], ["address", "Business address (prints on the Proof)"], ["registrationNumber", "Registration no."], ["registrationCounty", "Registration county"]].map(([k, label]) => (
              <div className="field" key={k}><label htmlFor={`fs-${k}`}>{label}</label>
                <input id={`fs-${k}`} value={fsForm[k] || ""} inputMode={k === "email" ? "email" : k === "phone" ? "tel" : undefined} type={k === "phone" ? "tel" : "text"} autoCapitalize={k === "email" ? "none" : undefined} onChange={e => setFsForm(f => ({ ...f, [k]: e.target.value }))} /></div>
            ))}
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "end" }}>
              <button type="button" className="btn-primary" data-testid="field-server-save" onClick={saveFieldServer}>Save</button>
              <button type="button" className="btn-secondary" onClick={() => setFsForm(null)}>Cancel</button>
            </div>
          </div>
        ) : (
          <button type="button" className="btn-primary" data-testid="field-server-add" style={{ marginTop: "0.6rem" }} onClick={() => { setFsForm({ name: "", email: "", phone: "", address: "", registrationNumber: "", registrationCounty: "" }); setFsMsg(""); }}>＋ Add a process server</button>
        )}
        {fsMsg && <div style={{ fontSize: "0.82rem", fontWeight: 700, marginTop: "0.4rem" }}>{fsMsg}</div>}
      </div>

      <div data-testid="passkey-card" className="admin-chrome" style={{ background: "var(--white)", border: "2px solid var(--light)", borderRadius: 8, padding: "1.25rem", marginBottom: "1.5rem" }}>
        <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: "0.9rem", marginBottom: "0.5rem" }}>📱 Phone App &amp; Fingerprint Login</div>
        {pkList.length > 0 && (
          <div style={{ fontSize: "0.85rem", marginBottom: "0.5rem" }}>
            {pkList.map(p => (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap", padding: "0.2rem 0" }}>
                <span>✅ <strong>{p.deviceName}</strong> · added {new Date(p.createdAt).toLocaleDateString()}{p.lastUsedAt ? ` · last used ${new Date(p.lastUsedAt).toLocaleDateString()}` : ""}</span>
                <button type="button" className="btn-secondary" style={{ fontSize: "0.75rem" }} disabled={pkBusy} onClick={() => removePasskey(p)}>Turn off</button>
              </div>
            ))}
          </div>
        )}
        <div className="btn-row-compact" style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {passkeySupported() && <button type="button" className="btn-primary" data-testid="passkey-add" disabled={pkBusy} onClick={addPasskey}>{pkBusy ? "Waiting for fingerprint…" : "👆 Turn on fingerprint login for this device"}</button>}
          {!isInstalledApp() && installReady && <button type="button" className="btn-secondary" data-testid="install-app" onClick={installApp}>📲 Install app</button>}
        </div>
        {!isInstalledApp() && !installReady && <div style={{ fontSize: "0.78rem", color: "var(--gray)", marginTop: "0.4rem" }}>To install on your phone: Android (Chrome) — menu ⋮ → <strong>Install app</strong>. iPhone (Safari) — Share → <strong>Add to Home Screen</strong>.</div>}
        {pkMsg && <div style={{ fontSize: "0.82rem", fontWeight: 700, marginTop: "0.4rem" }}>{pkMsg}</div>}
      </div>

      <div className="admin-chrome" style={{ background: "var(--white)", border: "2px solid var(--light)", borderRadius: 8, padding: "1.25rem", marginBottom: "1.5rem" }}>
        <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: "0.9rem", marginBottom: "0.75rem" }}>Order Availability</div>
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          {[
            { key: "open", label: "Open", desc: "Accepting all orders", color: "var(--green)" },
            { key: "limited", label: "Limited", desc: "High demand warning shown", color: "var(--amber)" },
            { key: "closed", label: "Closed", desc: "No new orders accepted", color: "var(--red)" },
          ].map(opt => (
            <button key={opt.key} onClick={() => setAvailability(opt.key)} style={{
              flex: 1, minWidth: 120, padding: "0.75rem", borderRadius: 6, cursor: "pointer",
              border: "2px solid " + (availability === opt.key ? opt.color : "var(--light)"),
              background: availability === opt.key ? opt.color : "var(--white)",
              color: availability === opt.key ? "white" : "var(--navy)",
              fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "0.8rem", transition: "all 0.15s", textAlign: "center"
            }}>
              <div>{opt.label}</div>
              <div style={{ fontSize: "0.75rem", opacity: 0.8, marginTop: "0.2rem", fontWeight: 400 }}>{opt.desc}</div>
            </button>
          ))}
        </div>
        {availability === "closed" && <div style={{ marginTop: "0.75rem", fontSize: "0.78rem", color: "var(--red)", fontWeight: 600 }}>New order submissions are currently disabled on the client site.</div>}
        {availability === "limited" && <div style={{ marginTop: "0.75rem", fontSize: "0.78rem", color: "#92400E", fontWeight: 600 }}>Clients will see a high-demand warning before placing orders.</div>}
      </div>
      {adminTab === "route" && <RoutePlanner jobs={jobs} />}

      {adminTab === "profile" && (
        <div data-testid="server-profile" style={{ background: "var(--white)", border: "2px solid var(--light)", borderRadius: 12, padding: "1.25rem" }}>
          <h3 style={{ marginTop: 0 }}>Process Server Profile</h3>
          <p className="text-sm" style={{ color: "var(--gray)", marginTop: "-0.35rem" }}>
            These details print on every Proof of Service (POS-010 item 7 — person who served papers).
            A Proof cannot be signed until the required fields are complete.
          </p>
          {profileReadiness && (
            <div className={`alert ${profileReadiness.ready ? "alert-success" : "alert-warn"}`} data-testid="profile-readiness" style={{ marginBottom: "1rem", fontSize: "0.85rem" }}>
              {profileReadiness.ready
                ? "✅ Ready for Proof Signing"
                : <>⚠️ Missing required information: <strong>{(profileReadiness.missing || []).join(", ")}</strong></>}
            </div>
          )}
          <div className="form-grid">
            <div className="field"><label>Legal Name *</label><input value={profileForm.legal_name} onChange={e => setProfileField("legal_name", e.target.value)} placeholder="Name exactly as it should appear on the Proof" /></div>
            <div className="field"><label>Telephone *</label><input type="tel" inputMode="tel" value={profileForm.phone} onChange={e => setProfileField("phone", e.target.value)} placeholder="951-555-0100" /></div>
            <div className="field"><label>Registration Number *</label><input value={profileForm.registration_number} onChange={e => setProfileField("registration_number", e.target.value)} /></div>
            <div className="field"><label>Registration County *</label><input value={profileForm.registration_county} onChange={e => setProfileField("registration_county", e.target.value)} placeholder="Riverside" /></div>
            <div className="field form-full"><label>Business / Mailing Address *</label><input value={profileForm.address} onChange={e => setProfileField("address", e.target.value)} placeholder="Address exactly as it should appear on the Proof" /></div>
            <div className="field form-full"><label>Bond Information (optional)</label><input value={profileForm.bond_information} onChange={e => setProfileField("bond_information", e.target.value)} /></div>
          </div>
          {profileMsg && <div className="alert" style={{ marginTop: "0.9rem", fontSize: "0.82rem" }}>{profileMsg}</div>}
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
            <button className="btn-primary" disabled={profileBusy} onClick={() => saveServerProfile()}>{profileBusy ? "Saving…" : "Save Profile"}</button>
            <button disabled={profileBusy} onClick={() => { setProfileLoaded(false); loadServerProfile(); }} style={{ padding: "0.6rem 1.1rem", background: "var(--white)", color: "var(--navy)", border: "2px solid var(--light)", borderRadius: 6, cursor: "pointer", fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "0.85rem" }}>Reload</button>
          </div>
        </div>
      )}

      {adminTab === "tax" && (() => {
        const completed = jobs.filter(j => j.status === "completed");
        // NET revenue only: refunded money is never counted as revenue.
        // Fully refunded => $0; partially refunded => amount paid − amount
        // refunded; unpaid orders contribute nothing (see src/lib/earnings.js).
        const totalRevenue = Math.round(sumNetRevenue(jobs) * 100) / 100;
        const grossPaid = Math.round(sumGrossPaid(jobs) * 100) / 100;
        const totalRefunded = Math.round(sumRefunded(jobs) * 100) / 100;
        const thisMonth = new Date().getMonth();
        const thisYear = new Date().getFullYear();
        const monthRevenue = Math.round(sumNetRevenue(jobs.filter(j => {
          const d = new Date(j.submitted);
          return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
        })) * 100) / 100;

        // Mileage from localStorage
        let mileageLog = [];
        try { mileageLog = JSON.parse(localStorage.getItem("routeLog") || "[]"); } catch {}
        const totalMilesAllTime = mileageLog.reduce((sum, r) => sum + (r.miles || 0), 0);
        const yearMiles = mileageLog.filter(r => new Date(r.date).getFullYear() === thisYear).reduce((sum, r) => sum + (r.miles || 0), 0);
        const mileageDeduction = parseFloat((yearMiles * IRS_RATE_2026).toFixed(2));

        return (
          <div>
            <h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, marginBottom: "1.5rem" }}>Earnings & Tax Summary</h3>

            {/* Earnings Cards */}
            <div className="earnings-grid">
              <div className="earnings-card"><div className="ev">${totalRevenue}</div><div className="el">Total Revenue (net of refunds)</div></div>
              <div className="earnings-card"><div className="ev">${monthRevenue}</div><div className="el">This Month</div></div>
              <div className="earnings-card"><div className="ev">{completed.length}</div><div className="el">Jobs Completed</div></div>
              <div className="earnings-card"><div className="ev">{yearMiles.toFixed(1)}</div><div className="el">Miles This Year</div></div>
              <div className="earnings-card"><div className="ev">${mileageDeduction}</div><div className="el">Mileage Deduction</div></div>
            </div>

            {/* Tax Summary */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem", marginBottom: "1.5rem" }}>
              <div className="tax-card">
                <h4>📊 {thisYear} Tax Summary (Schedule C)</h4>
                <div className="tax-row"><span>Gross Paid</span><span className="tax-val">${grossPaid}</span></div>
                <div className="tax-row"><span>Refunded</span><span className="tax-val">-${totalRefunded}</span></div>
                <div className="tax-row"><span>Net Revenue</span><span className="tax-val">${totalRevenue}</span></div>
                <div className="tax-row"><span>Business Miles</span><span className="tax-val">{yearMiles.toFixed(1)} mi</span></div>
                <div className="tax-row"><span>IRS Rate ({thisYear})</span><span className="tax-val">${IRS_RATE_2026}/mi</span></div>
                <div className="tax-row"><span>Mileage Deduction</span><span className="tax-val">-${mileageDeduction}</span></div>
                <div className="tax-row" style={{ marginTop: "0.5rem", paddingTop: "0.5rem", borderTop: "2px solid var(--navy)" }}>
                  <span>Est. Taxable Income</span>
                  <span className="tax-val">${Math.max(0, totalRevenue - mileageDeduction)}</span>
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--gray)", marginTop: "0.75rem" }}>
                  * Estimate only. Consult your tax professional for full Schedule C preparation.
                </div>
              </div>

              <div className="tax-card">
                <h4>📅 Monthly Breakdown</h4>
                {Array.from({ length: 12 }, (_, i) => {
                  const monthJobs = jobs.filter(j => {
                    const d = new Date(j.submitted);
                    return d.getMonth() === i && d.getFullYear() === thisYear;
                  });
                  const monthTotal = Math.round(sumNetRevenue(monthJobs) * 100) / 100;
                  const monthName = new Date(thisYear, i, 1).toLocaleString("default", { month: "short" });
                  return monthTotal > 0 ? (
                    <div key={i} className="tax-row">
                      <span>{monthName} {thisYear}</span>
                      <span className="tax-val">${monthTotal}</span>
                    </div>
                  ) : null;
                })}
                {jobs.filter(j => !["pending_review","refund_issued","cancelled"].includes(j.status)).length === 0 && (
                  <div style={{ color: "var(--gray)", fontSize: "0.82rem" }}>No completed jobs yet.</div>
                )}
              </div>
            </div>

            {/* Mileage Log */}
            <div className="tax-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <h4 style={{ margin: 0 }}>🚗 IRS Mileage Log</h4>
                <button className="btn-secondary" style={{ fontSize: "0.75rem" }} onClick={() => {
                  const rows = [["Date","Start Time","End Time","Stops","Miles","Purpose","Deduction"]];
                  mileageLog.forEach(r => rows.push([r.date, r.startTime, r.endTime, r.stops, r.miles, r.purpose, "$"+r.deduction]));
                  const csv = rows.map(r => r.join(",")).join("\n");
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a"); a.href = url; a.download = "mileage_log_" + thisYear + ".csv"; a.click();
                }}>Export CSV for Accountant</button>
              </div>
              {mileageLog.length === 0 ? (
                <div style={{ color: "var(--gray)", fontSize: "0.82rem" }}>No routes logged yet. Start a route in the Route Planner to begin tracking mileage.</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="mileage-log-table">
                    <thead><tr><th>Date</th><th>Start</th><th>End</th><th>Stops</th><th>Miles</th><th>Purpose</th><th>Deduction</th></tr></thead>
                    <tbody>
                      {mileageLog.map((r, i) => (
                        <tr key={i}>
                          <td>{r.date}</td>
                          <td>{r.startTime}</td>
                          <td>{r.endTime}</td>
                          <td>{r.stops}</td>
                          <td><strong>{r.miles}</strong></td>
                          <td>{r.purpose}</td>
                          <td style={{ color: "var(--green)", fontWeight: 700 }}>${r.deduction}</td>
                        </tr>
                      ))}
                      <tr style={{ background: "var(--navy)", color: "white" }}>
                        <td colSpan={4}><strong>TOTAL {thisYear}</strong></td>
                        <td><strong>{yearMiles.toFixed(1)} mi</strong></td>
                        <td></td>
                        <td style={{ color: "var(--gold)", fontWeight: 700 }}>${mileageDeduction}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })()}
      {adminTab === "jobs" && <>
        <div className="flex-between admin-chrome" style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>Job Dashboard</h3>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <span className="text-sm">{visibleJobs.length} {showArchived ? "archived" : "active"} orders</span>
            <button className="btn-primary" onClick={() => setShowCreate(v => !v)}>{showCreate ? "Close" : "➕ Create Order"}</button>
            <button className="btn-secondary" onClick={() => { setShowArchived(v => !v); setSelected(null); }}>
              {showArchived ? "View Active" : "View Archived"}
            </button>
          </div>
        </div>
        {showCreate && (
          <div style={{ background: "var(--white)", border: "2px solid var(--gold)", borderRadius: 8, padding: "1.25rem", marginBottom: "1.5rem" }}>
            <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: "0.95rem", marginBottom: "0.75rem" }}>Create Order (on behalf of a client)</div>
            {/* Safe provider-configuration state for admin/dev diagnostics —
                never prints the key itself. */}
            {!googlePlacesStatus().configured && (
              <div data-testid="places-config-state" className="alert-warn alert" style={{ marginBottom: "0.75rem", fontSize: "0.78rem" }}>
                {googlePlacesStatus().message}
              </div>
            )}
            {createErrors.length > 0 && (
              <div role="alert" data-testid="create-order-errors" style={{ background: "#FEF2F2", border: "2px solid var(--red)", borderRadius: 6, padding: "0.6rem 0.9rem", marginBottom: "0.75rem" }}>
                <div style={{ fontWeight: 700, fontSize: "0.82rem", marginBottom: "0.3rem" }}>Please fix the following ({createErrors.length}):</div>
                <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: "0.78rem" }}>{createErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              </div>
            )}
            {/* ── Parent order: client / case information (all required) ── */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
              {[
                ["clientName", "Client name *"], ["clientEmail", "Client email *"], ["clientPhone", "Client phone *"],
                ["plaintiff", "Plaintiff *"], ["caseNumber", "Case number *"], ["court", "Court *"],
              ].map(([k, label]) => (
                <div key={k} className="field"><label style={{ fontSize: "0.75rem" }}>{label}</label><input value={createForm[k]} onChange={e => cField(k, e.target.value)} /></div>
              ))}
            </div>
            {/* ── Service Jobs: one per person to serve ── */}
            {createServices.map((sv, i) => (
              <div key={i} data-testid={`service-job-${i}`} style={{ border: "2px solid var(--light)", borderRadius: 8, padding: "0.85rem", marginTop: "0.75rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                  <strong style={{ fontSize: "0.85rem" }}>Service {String(i + 1).padStart(2, "0")}{i === 0 ? " (required)" : ""}</strong>
                  {createServices.length > 1 && (
                    <button type="button" onClick={() => removeService(i)} style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: "0.78rem", fontWeight: 700, textDecoration: "underline" }}>Remove this service</button>
                  )}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
                  <div className="field"><label style={{ fontSize: "0.75rem" }}>Person to Serve *</label><input value={sv.personToServe} onChange={e => svcField(i, "personToServe", e.target.value)} /></div>
                  {/* Each Service Job gets its OWN autocomplete instance — a
                      selection here can never overwrite another service's address. */}
                  <AddressAutocomplete label="Service address" required
                    value={sv.serviceAddress}
                    onChange={t => svcField(i, "serviceAddress", t)}
                    onSelect={a => setCreateServices(list => list.map((s, idx) => idx === i ? { ...s, serviceAddress: a.address, zip: a.zip || s.zip, city: a.city || "", state: a.state || "" } : s))} />
                  <div className="field"><label style={{ fontSize: "0.75rem" }}>Service ZIP *</label><input value={sv.zip} onChange={e => svcField(i, "zip", e.target.value)} /></div>
                  <div className="field"><label style={{ fontSize: "0.75rem" }}>Document type *</label>
                    <select value={sv.docType} onChange={e => { const dt = e.target.value; setCreateServices(list => list.map((s, idx) => idx === i ? { ...s, docType: dt, docSubtype: "" } : s)); }}>
                      <option value="">Select…</option>
                      {Object.keys(DOC_SURCHARGES).map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  {/* Subtype decides which California proof form applies (e.g. the
                      restraining-order family, FL-115 vs FL-330, CP10.5). */}
                  {DOC_SUBTYPES[sv.docType] && (
                    <div className="field"><label style={{ fontSize: "0.75rem" }}>Document subtype (form selection)</label>
                      <select value={sv.docSubtype} onChange={e => svcField(i, "docSubtype", e.target.value)}>
                        <option value="">Select…</option>
                        {DOC_SUBTYPES[sv.docType].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="field"><label style={{ fontSize: "0.75rem" }}>Service level *</label>
                    <select value={sv.serviceTier} onChange={e => svcField(i, "serviceTier", e.target.value)}>
                      <option value="standard">Standard</option><option value="rush">Rush</option><option value="sameday">Same-Day</option>
                    </select>
                  </div>
                  <div className="field"><label style={{ fontSize: "0.75rem" }}>Add-on</label>
                    <select value={normalizeAddon(sv.serviceAddon, sv.serviceTier) || ""}
                      disabled={!addonAllowedForTier(sv.serviceTier)}
                      onChange={e => svcField(i, "serviceAddon", e.target.value)}>
                      <option value="">{addonAllowedForTier(sv.serviceTier) ? "None" : "Not available"}</option>
                      {addonsForTier(sv.serviceTier).map(([k, a]) => <option key={k} value={k}>{a.label} (+${a.price}){a.desc ? ` — ${a.desc}` : ""}</option>)}
                    </select>
                  </div>
                  <div className="field"><label style={{ fontSize: "0.75rem" }}>Needed by / deadline *</label><DatePick value={sv.neededByDate} onChange={v => svcField(i, "neededByDate", v)} /></div>
                  <div className="field"><label style={{ fontSize: "0.75rem" }}>Service price (USD) {sv.useCalculatedPrice ? "" : "*"}</label>
                    <input type="number" min="1" step="0.01"
                      value={sv.useCalculatedPrice ? (servicePrice(sv) != null ? String(servicePrice(sv)) : "") : sv.amountDue}
                      disabled={sv.useCalculatedPrice} onChange={e => svcField(i, "amountDue", e.target.value)} />
                  </div>
                </div>
                <div style={{ marginTop: "0.4rem", display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.78rem" }}>
                  <input type="checkbox" id={`use-web-price-${i}`} checked={sv.useCalculatedPrice} onChange={e => svcField(i, "useCalculatedPrice", e.target.checked)} style={{ cursor: "pointer" }} />
                  <label htmlFor={`use-web-price-${i}`} style={{ cursor: "pointer" }}>
                    Use website price{servicePrice(sv) != null && sv.useCalculatedPrice
                      ? <> — <strong>${servicePrice(sv)}</strong></>
                      : sv.useCalculatedPrice ? <> <span style={{ color: "var(--gray)" }}>(enter a serviced ZIP + service level)</span></> : null}
                  </label>
                </div>
                <div className="field" style={{ marginTop: "0.4rem" }}><label style={{ fontSize: "0.75rem" }}>Service instructions (this person only)</label><textarea rows={1} value={sv.instructions} onChange={e => svcField(i, "instructions", e.target.value)} style={{ width: "100%" }} /></div>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0.6rem" }}>
              <button type="button" className="btn-secondary" onClick={addService}>➕ Add Another Service</button>
              <div data-testid="create-order-total" style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, color: "var(--gold)" }}>
                Order Total: ${Math.round(createOrderTotal * 100) / 100}
              </div>
            </div>
            <div className="field" style={{ marginTop: "0.5rem" }}><label style={{ fontSize: "0.75rem" }}>Special instructions (order-wide)</label><textarea rows={2} value={createForm.specialInstructions} onChange={e => cField("specialInstructions", e.target.value)} style={{ width: "100%" }} /></div>
            <div className="field" style={{ marginTop: "0.5rem" }} data-testid="create-billing-mode">
              <label style={{ fontSize: "0.75rem" }}>Billing</label>
              <select value={createForm.billingMode} onChange={e => cField("billingMode", e.target.value)}>
                <option value="stripe">Client pays online (payment link / invoice)</option>
                <option value="external">No charge / billed outside the app (e.g. United Legal)</option>
              </select>
              {createForm.billingMode === "external" && (
                <div style={{ fontSize: "0.75rem", color: "var(--gray)", marginTop: "0.2rem" }}>
                  No payment link, invoice or Stripe charge. Enter the rate for your records, or leave it blank for $0. The order can be accepted and worked immediately.
                </div>
              )}
            </div>
            <div style={{ marginTop: "0.4rem", display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.78rem" }}>
              <input type="checkbox" id="create-is-test" checked={createForm.isTest} onChange={e => cField("isTest", e.target.checked)} style={{ cursor: "pointer" }} />
              <label htmlFor="create-is-test" style={{ cursor: "pointer" }}>Test order (deletable test data — never use for a real client)</label>
            </div>
            {/* Documents selected here are uploaded to the new order right after it
                is created — same private order-documents storage and the same
                permitted types as the customer order form. */}
            <div className="field" style={{ marginTop: "0.5rem" }} data-testid="create-order-docs">
              <label style={{ fontSize: "0.75rem" }}>Service documents (PDF, JPG, PNG — attached when the order is created)</label>
              <input type="file" multiple accept={UPLOAD_ACCEPT} onChange={e => setCreateFiles(Array.from(e.target.files || []))} />
              {createFiles.length > 0 && (
                <div style={{ fontSize: "0.75rem", color: "var(--gray)", marginTop: "0.25rem" }}>
                  {createFiles.length} file(s) selected: {createFiles.map(f => f.name).join(", ")}
                </div>
              )}
            </div>
            <button className="btn-primary" disabled={createBusy} onClick={createOrder} style={{ marginTop: "0.5rem" }}>{createBusy ? "Creating…" : "Create Order"}</button>
          </div>
        )}
        {createDocsPending && (
          <div role="alert" data-testid="create-docs-retry" style={{ background: "#FEF2F2", border: "2px solid var(--red)", borderRadius: 8, padding: "0.85rem 1rem", marginBottom: "1.5rem" }}>
            <div style={{ fontWeight: 700, marginBottom: "0.3rem" }}>Order {createDocsPending.jobNumber} was created, but its document upload failed.</div>
            <div style={{ fontSize: "0.82rem", marginBottom: "0.6rem" }}>
              The order exists and was <strong>not</strong> duplicated. Retry attaches the {createDocsPending.files.length} selected file(s) to that same order.
            </div>
            <button className="btn-primary" disabled={createDocsBusy} onClick={retryCreateDocs}>{createDocsBusy ? "Uploading…" : "Retry document upload"}</button>
            <button onClick={() => setCreateDocsPending(null)} style={{ marginLeft: "0.75rem", background: "none", border: "none", color: "var(--navy)", cursor: "pointer", textDecoration: "underline", fontSize: "0.82rem" }}>Dismiss (upload later from the order panel)</button>
          </div>
        )}
        <div className="admin-grid">
          <div className="admin-list">
            {visibleJobs.map((j, i) => {
              const mapHref = mapsUrl(j.address, j.zip, typeof navigator !== "undefined" ? navigator.userAgent : "");
              return (
              <div key={j.id} id={`job-card-${j.id}`} style={{ order: cardOrder(i) }}
                className={`job-card${selected === j.id ? " selected" : ""}`}
                onClick={() => {
                  // Tap again collapses (one open order at a time).
                  setSelected(selected === j.id ? null : j.id); setFocusTarget(null); setShowProof(false); setAttemptMsg("");
                }}>
                {/* WHO, then WHERE. The job number is secondary. */}
                {/* Client first (bold), then who is being served. */}
                <div className="card-person" data-testid="card-client">{j.client || "Client not set"}</div>
                {/* Every person to serve on the order, each with their own address
                    (an order can have several Service Jobs). */}
                {(j.serviceTargets || []).length > 1 ? (j.serviceTargets.map(t => {
                  const tHref = t.service_address ? mapsUrl(t.service_address, "", typeof navigator !== "undefined" ? navigator.userAgent : "") : null;
                  return (
                    <div key={t.id} data-testid="card-serve-target" role="button" tabIndex={0}
                      onClick={e => { e.stopPropagation(); setSelected(j.id); setFocusTarget(t.id); setShowProof(false); setAttemptMsg(""); }}
                      style={{ marginTop: "0.35rem", padding: "0.4rem 0.5rem", border: "1px solid var(--light)", borderRadius: 6, background: "var(--white)", cursor: "pointer" }}>
                      <div className="text-sm" style={{ color: "var(--navy)", fontWeight: 600 }}>Serve: {t.subject_name || t.job_label}{t.service_status === "completed" ? " ✓" : ""} <span style={{ float: "right", color: "var(--blue)" }}>›</span></div>
                      {t.service_address && (tHref
                        ? <a className="card-address" href={tHref} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>📍 {t.service_address}</a>
                        : <div className="card-address">{t.service_address}</div>)}
                      {/* The address opens GPS; this big button opens the order on this person. */}
                      <button type="button" data-testid="card-open-target" className="btn-primary"
                        onClick={e => { e.stopPropagation(); setSelected(j.id); setFocusTarget(t.id); setShowProof(false); setAttemptMsg(""); }}
                        style={{ display: "block", width: "100%", marginTop: "0.4rem", minHeight: 44, fontSize: "0.95rem" }}>Open ›</button>
                    </div>
                  );
                })) : <>
                <div className="text-sm" data-testid="card-serve" style={{ color: "var(--navy)" }}>Serve: {j.defendant || "not set"}</div>
                {j.address && (mapHref
                  ? <a className="card-address" href={mapHref} target="_blank" rel="noreferrer" data-testid="card-maps-link"
                      onClick={e => e.stopPropagation()}>📍 {j.address}{j.zip && !String(j.address).includes(j.zip) ? ` ${j.zip}` : ""}</a>
                  : <div className="card-address">{j.address}</div>)}
                {selected !== j.id && (
                  <button type="button" data-testid="card-open-order" className="btn-primary"
                    onClick={e => { e.stopPropagation(); setSelected(j.id); setFocusTarget(null); setShowProof(false); setAttemptMsg(""); }}
                    style={{ display: "block", width: "100%", marginTop: "0.4rem", minHeight: 44, fontSize: "0.95rem" }}>Open order ›</button>
                )}
                </>}
                <div className="card-tags">
                  <StatusChip status={j.status} />
                  {isServed(j) && <span className="status-chip" data-testid="card-served" style={{ background: "#DCFCE7", color: "#166534", fontWeight: 800 }}>✓ SERVED</span>}
                  {j.tier && <span className="status-chip" style={{ background: "#F3F4F6", color: "var(--navy)" }}>{(PRICING[j.tier] && PRICING[j.tier].label) || j.tier}</span>}
                  {j.exParte && <span className="status-chip chip-exparte" data-testid="card-ex-parte">EX PARTE</span>}
                  {j.neededBy && <span className="status-chip" style={{ background: "#FEF9EE", color: "#92400E" }}>Needed by {j.neededBy}</span>}
                </div>
                <div className="card-jobno">{j.id}{j.isTest ? " · TEST" : ""}</div>
              </div>
              );
            })}
            {visibleJobs.length === 0 && <div className="text-sm" style={{ color: "var(--gray)" }}>No {showArchived ? "archived" : "active"} orders.</div>}
          </div>
          {job ? (
            <div className="job-detail" id="job-detail" style={{ order: detailOrder(visibleJobs.findIndex(v => v.id === job.id)) }}>
              {/* Decline Modal */}
              {showDecline && (
                <div className="decline-modal">
                  <div className="decline-box">
                    <h3>Decline / Modify Request</h3>
                    <div className="field" style={{ marginBottom: "1rem" }}>
                      <label>Reason</label>
                      <select value={declineReason} onChange={e => setDeclineReason(e.target.value)}>
                        <option value="">Select reason…</option>
                        <option value="outside_area">Outside Service Area — Cancel Request</option>
                        <option value="deadline">Cannot Meet Deadline — Propose Alternative Date</option>
                        <option value="pricing">Pricing Adjustment Required — Propose New Price</option>
                      </select>
                    </div>
                    {declineReason === "deadline" && (
                      <div className="field" style={{ marginBottom: "1rem" }}>
                        <label>Proposed Alternative Date</label>
                        <DatePick value={altDate} onChange={v => setAltDate(v)} />
                      </div>
                    )}
                    {declineReason === "pricing" && (<>
                      <div className="field" style={{ marginBottom: "0.75rem" }}>
                        <label>New Proposed Price ($)</label>
                        <input type="number" value={altPrice} onChange={e => setAltPrice(e.target.value)} placeholder="0.00" />
                      </div>
                      <div className="field" style={{ marginBottom: "1rem" }}>
                        <label>Reason for Price Adjustment *</label>
                        <textarea value={altPriceReason} onChange={e => setAltPriceReason(e.target.value)}
                          placeholder="e.g. Subject located at additional address requiring extra mileage. Original quote did not account for…"
                          style={{ minHeight: 70 }} />
                      </div>
                    </>)}
                    {declineReason === "outside_area" && (
                      <div className="alert-warn alert" style={{ marginBottom: "1rem", fontSize: "0.82rem" }} data-testid="decline-outside-note">
                        This cancels the request and the client is emailed a cancellation notice.
                        {job.paymentStatus === "paid" ? " This order is PAID — a refund is NOT automatic; issue it with the Refund button after cancelling." : " No payment has been taken."}
                      </div>
                    )}
                    {(declineReason === "deadline" || declineReason === "pricing") && (
                      <div className="alert" style={{ marginBottom: "1rem", fontSize: "0.82rem" }}>
                        This opens an email to the client in your mail app with the proposal filled in. The order itself is not changed.
                        {declineReason === "pricing" && job.amountDueCents == null ? " To set the price on this request, use Pricing Review above." : ""}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: "0.75rem" }}>
                      <button className="btn-secondary" onClick={() => setShowDecline(false)}>Cancel</button>
                      <button className="btn-primary" data-testid="decline-send" disabled={actionBusy} onClick={async () => {
                        if (!declineReason) { showNotice("Please select a reason."); return; }
                        if (declineReason === "pricing" && !altPriceReason.trim()) { showNotice("Please provide a reason for the price adjustment."); return; }
                        if (declineReason === "outside_area") {
                          // A REAL cancellation: saved on the server, which emails the
                          // client. Nothing here claims a refund that was not issued.
                          const ok = await adminAction("cancel", { reason: "Outside service area" });
                          if (!ok) return;
                        } else {
                          // No server workflow exists for counter-offers yet, so nothing
                          // is changed on the order; the proposal goes to the client from
                          // the admin's own mail app.
                          const subject = declineReason === "deadline"
                            ? `Alternative date for your service request ${selected}`
                            : `Pricing adjustment for your service request ${selected}`;
                          const body = declineReason === "deadline"
                            ? `We are unable to meet your requested deadline for job ${selected}. We can serve on ${altDate || "[date]"}. Please reply to let us know whether that works for you.`
                            : `A pricing adjustment is required for job ${selected}. Reason: ${altPriceReason}. Proposed new price: $${altPrice || "[amount]"}. Please reply to let us know whether you would like to proceed.`;
                          if (job.clientEmail) window.location.href = `mailto:${encodeURIComponent(job.clientEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                          else showNotice("This order has no client email on file.");
                        }
                        setShowDecline(false); setDeclineReason(""); setAltDate(""); setAltPrice(""); setAltPriceReason("");
                      }}>{declineReason === "outside_area" ? "Cancel Request" : "Email Client"}</button>
                    </div>
                  </div>
                </div>
              )}

              {/* CLIENT MESSAGES — first thing in the order, newest first. On a wide
                  screen it sits in its own column to the RIGHT of the order. */}
              <button type="button" className="btn-secondary phone-only" data-testid="back-to-orders" onClick={() => setSelected(null)} style={{ marginBottom: "0.75rem" }}>← All orders</button>
              <div data-testid="client-messages" className="job-messages" style={{ marginBottom: "1rem", border: "2px solid var(--gold)", borderRadius: 8, padding: "0.75rem" }}>
                <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.75rem" }}>Client Messages</div>
                <div className="message-thread">
                  {job.messages.length === 0 && <p style={{ color: "var(--gray)", fontSize: "0.82rem", textAlign: "center" }}>No messages.</p>}
                  {[...job.messages].reverse().map((m, i) => (
                    <div key={i} className={`msg from-${m.from}`}>
                      <div className="msg-bubble">{m.text}</div>
                      <div className="msg-time">{m.time}</div>
                    </div>
                  ))}
                </div>
                <div className="msg-input-row">
                  <input className="msg-input" placeholder="Reply to client…" value={adminMsg} onChange={e => setAdminMsg(e.target.value)} onKeyDown={e => e.key === "Enter" && sendAdminMsg()} />
                  <button className="btn-gold" onClick={sendAdminMsg}>Send</button>
                </div>
                <p className="text-sm" style={{ marginTop: "0.4rem" }}>Reply is sent to client's email and logged here.</p>
              </div>
              <div className="flex-between order-head">
                {/* What the server needs at a glance before leaving: WHO, WHERE,
                    and whether they can even get to the door. Gate details sit
                    directly under the address because that is where they matter. */}
                <div>
                  <div className="job-id dup-on-phone" style={{ fontSize: "1rem" }}>{job.id}</div>
                  {/* CLIENT first — who to call — then who is being served. */}
                  <div data-testid="detail-client" style={{ marginTop: "0.2rem", marginBottom: "0.5rem" }}>
                    <div className="dup-on-phone" style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, fontSize: "1.1rem", color: "var(--navy)" }}>{job.client || "Client not set"}</div>
                    <div style={{ fontSize: "0.92rem", display: "flex", flexWrap: "wrap", gap: "0.25rem 1rem" }}>
                      {job.clientPhone ? <a href={`tel:${String(job.clientPhone).replace(/[^0-9+]/g, "")}`} data-testid="detail-client-phone">📞 {job.clientPhone}</a> : <span style={{ color: "var(--gray)" }}>📞 no phone</span>}
                      {job.clientEmail ? <a href={`mailto:${job.clientEmail}`} data-testid="detail-client-email">✉ {job.clientEmail}</a> : <span style={{ color: "var(--gray)" }}>✉ no email</span>}
                    </div>
                  </div>
                  <div className="dup-on-phone" style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--gray)" }}>Person to serve</div>
                  <div data-testid="detail-subject" className={job.subjectAge ? "" : "dup-on-phone"} style={{ fontWeight: 700, fontSize: "1rem", color: "var(--navy)" }}>
                    {job.defendant}
                    {job.subjectAge && <span style={{ fontWeight: 600, fontSize: "0.82rem", color: "var(--gray)" }}> · approx. {subjectAgeLabel(job.subjectAge) || job.subjectAge}</span>}
                  </div>
                  <div className="dup-on-phone" style={{ fontWeight: 700, fontSize: "0.92rem", color: "var(--navy)", marginTop: "0.2rem" }}>{mapsUrl(job.address, job.zip, typeof navigator !== "undefined" ? navigator.userAgent : "")
                    ? <a href={mapsUrl(job.address, job.zip, typeof navigator !== "undefined" ? navigator.userAgent : "")} target="_blank" rel="noreferrer" data-testid="detail-maps-link" style={{ color: "var(--blue)" }}>📍 {job.address}</a>
                    : job.address}</div>
                  {job.gatedAccess && (
                    <div data-testid="gated-access" style={{ marginTop: "0.25rem" }}>
                      <span className="status-chip" style={{ background: "#FEF9EE", color: "#92400E" }}>🔒 Gated / access-controlled</span>
                      {job.accessInstructions && (
                        <div className="text-sm" style={{ marginTop: "0.2rem", color: "var(--navy)" }}>
                          <strong>Access:</strong> {job.accessInstructions}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Plaintiff / court / case number are for the proof, not the field — not shown here. */}
                  {job.tier && <div className="text-sm dup-on-phone">Service level: <strong>{(PRICING[job.tier] && PRICING[job.tier].label) || job.tier}</strong>{job.exParte ? " · EX PARTE" : ""}</div>}
                  {job.neededBy && <div className="text-sm dup-on-phone" data-testid="needed-by">Needed by: <strong>{job.neededBy}</strong></div>}
                </div>
                <div className="dup-on-phone" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.3rem" }}>
                  <StatusChip status={job.status} />
                  {isServed(job) && <span className="status-chip" data-testid="detail-served" style={{ background: "#DCFCE7", color: "#166534", fontWeight: 800 }}>✓ SERVED</span>}
                </div>
              </div>
              {/* ── SEND TO CLIENT — asked right after Create Order ── */}
              {sendPromptJob === job.id && (
                <div data-testid="send-to-client-prompt" style={{ marginTop: "0.75rem", padding: "0.75rem", border: "2px solid var(--gold)", borderRadius: 8, background: "#FFFBF4" }}>
                  <div style={{ fontWeight: 700, marginBottom: "0.4rem" }}>Order created. What should we send the client?</div>
                  {job.paymentStatus !== "billed_externally" && (
                    <div className="field" style={{ maxWidth: 220, marginBottom: "0.5rem" }}>
                      <label style={{ fontSize: "0.75rem" }}>Price (approve or change)</label>
                      <input type="number" min="0" step="0.01" data-testid="prompt-price" value={promptPrice} onChange={e => setPromptPrice(e.target.value)} />
                    </div>
                  )}
                  {SUBJECT_PHOTO.courtDocs(job.documents).length === 0 && (
                    <label data-testid="ask-docs" style={{ display: "flex", gap: "0.4rem", alignItems: "center", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
                      <input type="checkbox" checked={askDocs} onChange={e => setAskDocs(e.target.checked)} />
                      No documents yet — also email the client a link to upload them
                    </label>
                  )}
                  <div className="btn-row-compact">
                    {job.paymentStatus !== "billed_externally" && job.paymentStatus !== "paid" && (
                      <button className="btn-primary" data-testid="send-both" disabled={promptBusy} onClick={() => sendToClient(job, "both")}>Payment link + Agreement</button>
                    )}
                    {job.paymentStatus !== "billed_externally" && job.paymentStatus !== "paid" && (
                      <button className="btn-secondary" data-testid="send-payment-only" disabled={promptBusy} onClick={() => sendToClient(job, "payment")}>Payment link only</button>
                    )}
                    <button className="btn-secondary" data-testid="send-agreement-only" disabled={promptBusy} onClick={() => sendToClient(job, "agreement")}>Agreement only</button>
                    <button className="btn-secondary" data-testid="send-nothing" disabled={promptBusy} onClick={() => sendToClient(job, "nothing")}>Nothing</button>
                  </div>
                  {promptBusy && <div style={{ fontSize: "0.8rem", marginTop: "0.4rem" }}>Sending…</div>}
                </div>
              )}
              {promptMsg && <div data-testid="send-to-client-result" className="alert" style={{ marginTop: "0.6rem", fontSize: "0.82rem" }}>{promptMsg}</div>}
              {/* Accept / Decline — FIRST thing under who/where for a new request */}
              {job.status === "pending_review" && (
                <div style={{ background: "#EFF6FF", border: "2px solid var(--blue)", borderRadius: 8, padding: "1rem", marginTop: "1rem" }}>
                  <div style={{ fontWeight: 700, color: "var(--blue)", marginBottom: "0.75rem", fontSize: "0.9rem" }}>New Request — Awaiting Your Review</div>
                  <div style={{ fontSize: "0.82rem", color: "var(--gray)", marginBottom: "0.75rem" }}>
                    <strong>Client:</strong> {job.client} &nbsp;|&nbsp; <strong>Fee:</strong> {job.price != null ? `$${job.price}` : "not priced yet"} &nbsp;|&nbsp; <strong>Submitted:</strong> {job.submitted ? new Date(job.submitted).toLocaleString() : "—"}
                  </div>
                  <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                    <button className="btn-primary" data-testid="accept-request" disabled={actionBusy} onClick={async () => {
                      // Saved on the server first; the screen follows the record.
                      // The client is told only once the acceptance is real.
                      // The server emails the customer once the acceptance is saved.
                      await adminAction("accept");
                    }}>
                      Accept Request
                    </button>
                    <button style={{ background: "var(--red)", color: "white", border: "none", borderRadius: 4, padding: "0.65rem 1.25rem", fontWeight: 700, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                      onClick={() => setShowDecline(true)}>
                      Decline / Modify
                    </button>
                  </div>
                </div>
              )}
              {/* ── Complete Order — near the TOP of the order, right under who/where ── */}
              {/* Only for orders without Service Jobs (each job has its own Sign / Mark Completed), or to retry a failed completion email. */}
              {(!(job.serviceTargets || []).length ? job.status !== "pending_review" : (job.status === "completed" && !job.completionEmailSent)) && <div data-testid="complete-order-top" style={{ marginTop: "0.75rem", border: "2px solid var(--light)", borderRadius: 8, padding: "0.6rem 0.75rem" }}>
                {job.status === "completed" ? (
                  <div className="text-sm">
                    <div><strong style={{ color: "var(--green)" }}>✓ Completed</strong>
                      {job.completedAt ? ` on ${new Date(job.completedAt).toLocaleDateString()}` : ""}
                      {job.completedBy ? ` by ${job.completedBy}` : ""}
                    </div>
                    <div style={{ marginTop: "0.35rem" }}>
                      Completion email:{" "}
                      {job.completionEmailSent
                        ? <strong style={{ color: "var(--green)" }}>sent</strong>
                        : <><strong style={{ color: "var(--red)" }}>not sent</strong>{" "}
                            <button className="btn-secondary" disabled={actionBusy} onClick={retryCompletionEmail}>Retry Email</button></>}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm">
                    <div style={{ fontWeight: 700, marginBottom: "0.35rem" }}>Complete Order</div>
                    <div style={{ marginBottom: "0.4rem", color: "var(--gray)" }}>Sign the Proof in Service Jobs, then tap Mark Completed — the signed Proof is attached and emailed to the client automatically.</div>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem" }}>
                      {!(job.serviceTargets || []).some(t => (svcDocs[t.id] || []).some(d => d.status === "draft" || d.status === "signed"))
                        ? <button className="btn-primary" data-testid="mark-completed" disabled={actionBusy} onClick={completeOrder}>{actionBusy ? "Completing…" : "Mark Completed"}</button>
                        : <span>Sign and complete in the green box under Service Jobs.</span>}
                      {completeMsg && <span data-testid="complete-result-top" style={{ fontWeight: 700 }}>{completeMsg}</span>}
                      <details>
                        <summary style={{ cursor: "pointer", fontSize: "0.75rem" }}>Proof made outside the portal? Attach PDF</summary>
                        <input type="file" accept="application/pdf,.pdf" onChange={(e) => setProofFile((e.target.files && e.target.files[0]) || null)} />
                      </details>
                    </div>
                  </div>
                )}
              </div>}
              {SUBJECT_PHOTO.subjectPhotos(job.documents).length > 0 && (
                <div data-testid="subject-photos" style={{ marginTop: "0.75rem" }}>
                  <strong>Photo of person to serve:</strong>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.35rem" }}>
                    {SUBJECT_PHOTO.subjectPhotos(job.documents).map((d, i) => (
                      <a key={i} href={d.url} target="_blank" rel="noreferrer" title={d.name}>
                        <img src={d.url} alt={`Person to serve — ${d.name}`} style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 6, border: "2px solid var(--light)" }} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {/* ── Service Jobs: one per person to serve, each with its own
                    outcome, documentation, and completion gate ── */}
              {job.serviceTargets && job.serviceTargets.length > 0 && (
                <div data-testid="service-jobs-panel" style={{ marginTop: "1rem" }}>
                  {focusTarget && job.serviceTargets.some(t => t.id === focusTarget) && job.serviceTargets.length > 1
                    ? <button type="button" className="btn-secondary" data-testid="show-all-targets" onClick={() => setFocusTarget(null)} style={{ marginBottom: "0.5rem", fontSize: "0.8rem" }}>Show all {job.serviceTargets.length} people on this order</button>
                    : <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--gray)", marginBottom: "0.5rem" }}>Service Jobs ({job.serviceTargets.length})</div>}
                  {svcMsg && <div className="alert" style={{ marginBottom: "0.6rem", fontSize: "0.8rem" }}>{svcMsg}</div>}
                  {job.serviceTargets.filter(t => !focusTarget || !job.serviceTargets.some(x => x.id === focusTarget) || t.id === focusTarget).map((t) => (
                    <div key={t.id} style={{ border: "2px solid var(--light)", borderRadius: 8, padding: "0.75rem", marginBottom: "0.6rem" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.4rem" }}>
                        <div>
                          <strong style={{ fontSize: "1rem" }}>{t.subject_name || "Person to serve not set"}</strong>
                          {t.amount_cents != null && <span className="text-sm"> &nbsp;·&nbsp; ${(t.amount_cents / 100).toFixed(2)}</span>}
                          {addrEdit && addrEdit.targetId === t.id ? (
                            <div data-testid="target-address-edit" style={{ marginTop: "0.35rem", display: "flex", flexWrap: "wrap", gap: "0.4rem", alignItems: "center" }}>
                              <input style={{ flex: "1 1 260px" }} value={addrEdit.address} onChange={e => setAddrEdit(a => ({ ...a, address: e.target.value }))} placeholder="Street, city, state ZIP" />
                              <input style={{ width: 90 }} value={addrEdit.zip} onChange={e => setAddrEdit(a => ({ ...a, zip: e.target.value }))} placeholder="ZIP" />
                              <span className="btn-row-compact">
                                <button className="btn-primary" disabled={svcBusy} onClick={() => saveTargetAddress(job, t)}>Save</button>
                                <button className="btn-secondary" disabled={svcBusy} onClick={() => setAddrEdit(null)}>Cancel</button>
                              </span>
                            </div>
                          ) : (
                            <div className="text-sm" data-testid="target-address" style={{ marginTop: "0.15rem" }}>
                              {t.service_address ? <>📍 {mapsUrl(t.service_address, "", typeof navigator !== "undefined" ? navigator.userAgent : "")
                                ? <a href={mapsUrl(t.service_address, "", typeof navigator !== "undefined" ? navigator.userAgent : "")} target="_blank" rel="noreferrer" style={{ color: "var(--blue)" }}>{t.service_address}</a>
                                : t.service_address}</> : <span style={{ color: "var(--gray)" }}>📍 No address</span>}
                              {" "}<button type="button" data-testid="edit-target-address" onClick={() => setAddrEdit({ targetId: t.id, address: t.service_address || "", zip: "" })}
                                style={{ background: "none", border: "none", padding: 0, color: "var(--blue)", textDecoration: "underline", cursor: "pointer", fontSize: "0.8rem" }}>✎ Edit</button>
                            </div>
                          )}
                        </div>
                        <span className="text-sm">Status: <strong>{t.service_status || "open"}</strong>{t.legal_service_outcome && t.legal_service_outcome !== "pending" ? <> · Outcome: <strong>{t.legal_service_outcome.replace(/_/g, " ")}</strong></> : null}</span>
                      </div>
                      {fieldServers.some(sv => sv.active || sv.id === t.assigned_server_id) && (
                        <div data-testid="assign-server" style={{ marginTop: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", fontSize: "0.85rem" }}>
                          <label htmlFor={`assign-${t.id}`} style={{ fontWeight: 700 }}>Assigned to</label>
                          <select id={`assign-${t.id}`} value={t.assigned_server_id || ""} onChange={e => assignTarget(job, t, e.target.value)} style={{ flex: "1 1 160px", minWidth: 0 }}>
                            <option value="">Me</option>
                            {fieldServers.filter(sv => sv.active || sv.id === t.assigned_server_id).map(sv => <option key={sv.id} value={sv.id}>{sv.name}</option>)}
                          </select>
                        </div>
                      )}
                      {/* Nov 3: a person already marked served can be re-served (e.g. Two-Nine). */}
                      {captureEnabled && t.service_status !== "completed" && (
                        <div style={{ marginTop: "0.5rem" }}>
                          <button type="button" className={attemptOpenFor === t.id ? "btn-secondary" : "btn-primary btn-log-attempt"} data-testid="log-attempt-for-target"
                            onClick={() => { const open = attemptOpenFor === t.id; setAttemptOpenFor(open ? null : t.id); if (!open) setAttemptTargetId(t.id); }}>
                            {attemptOpenFor === t.id ? "✕ Close" : ["personal_service", "substituted_service", "posted_service"].includes(t.legal_service_outcome) ? "＋ Re-serve / Log Service" : "＋ Log Attempt / Service"}
                          </button>
                        </div>
                      )}
                      {attemptOpenFor === t.id && renderAttemptForm(t.id)}
                      {/* PHOTOS for this person — no attempt needed. */}
                      <div data-testid="job-photos" style={{ marginTop: "0.5rem" }}>
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                          <button type="button" className="btn-secondary" data-testid="job-photo-take" onClick={() => openInAppCamera(files => addJobPhotos(files, "camera", job.id, t.id), `job-photo-cam-${t.id}`)}>📷 Take photo</button>
                          <button type="button" className="btn-secondary" data-testid="job-photo-upload" onClick={() => document.getElementById(`job-photo-gal-${t.id}`).click()}>🖼️ Upload photos</button>
                          {!jobPhotos[t.id] && <button type="button" className="btn-link" onClick={() => loadJobPhotos(job.id, t.id)} style={{ background: "none", border: "none", color: "var(--blue)", textDecoration: "underline", cursor: "pointer", fontSize: "0.8rem" }}>Show photos</button>}
                          {jobPhotoBusy[t.id] > 0 && <span className="text-sm">Saving photo…</span>}
                        </div>
                        <input id={`job-photo-cam-${t.id}`} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={e => { addJobPhotos(e.target.files, "camera", job.id, t.id); e.target.value = ""; }} />
                        <input id={`job-photo-gal-${t.id}`} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={e => { addJobPhotos(e.target.files, "gallery", job.id, t.id); e.target.value = ""; }} />
                        {jobPhotos[t.id] && (jobPhotos[t.id].length === 0
                          ? <div className="text-sm" style={{ marginTop: "0.3rem" }}>No photos yet.</div>
                          : <div style={{ marginTop: "0.3rem" }}>{jobPhotos[t.id].map(ph => (
                              <div key={ph.id} className="photo-meta" style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                                <button type="button" onClick={() => viewJobPhoto(job.id, t.id, ph.id)} style={{ background: "none", border: "none", padding: 0, color: "var(--blue)", textDecoration: "underline", cursor: "pointer", fontSize: "0.75rem", minHeight: 32 }}>🖼️ View</button>
                                <span>{ph.capturedAtDevice ? new Date(ph.capturedAtDevice).toLocaleString() : ""} · {ph.hasGps ? `${Number(ph.gpsLat).toFixed(5)}, ${Number(ph.gpsLng).toFixed(5)}` : "no GPS"}{ph.attemptId ? " · with an attempt" : ""}</span>
                              </div>))}</div>)}
                      </div>
                      <div data-testid="target-attempts" data-target-attempts={t.id} style={{ marginTop: "0.5rem" }}>
                        {clientUpdateMsg && attemptOpenFor !== t.id && attemptTargetId === t.id && <div className="alert" style={{ marginBottom: "0.5rem" }} data-testid="client-update-msg-saved">{clientUpdateMsg}</div>}
                        <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--gray)" }}>Attempts — {t.subject_name || t.job_label}</div>
                        {attemptsForTarget(job, t.id).length === 0
                          ? <p className="text-sm" style={{ margin: "0.2rem 0" }}>No attempts yet.</p>
                          : attemptsForTarget(job, t.id).map(renderSavedAttempt)}
                      </div>
                      {/* NEXT STEP — what to do now, in one place: Review → Sign →
                          Mark Completed side by side, with the result shown here. */}
                      {(() => {
                        // Once the person is served, the attempts are attached to the
                        // Proof — an older Due Diligence draft is no longer a paper to sign.
                        const isServedTarget = ["personal_service", "substituted_service", "posted_service"].includes(t.legal_service_outcome);
                        const docs = (svcDocs[t.id] || []).filter(d => d.status !== "superseded" && !(isServedTarget && d.kind === "due_diligence"));
                        // Every paper waiting to be signed — a substitute service has two
                        // (Proof + Due Diligence). The Proof is listed first.
                        const drafts = docs.filter(d => d.status === "draft").sort((a, b) => (a.kind === "proof_of_service" ? 0 : 1) - (b.kind === "proof_of_service" ? 0 : 1));
                        const draft = drafts[0];
                        const signedDoc = docs.filter(d => d.status === "signed").slice(-1)[0];
                        if (!draft && !signedDoc) {
                          // Served but no papers yet: say what's happening, or why
                          // they couldn't be made, with a button to try again.
                          if (!isServedTarget) {
                            // Attempts but not served: a Declaration of Due Diligence can
                            // be made at any time.
                            if (!attemptsForTarget(job, t.id).length) return null;
                            return (
                              <div data-testid="make-diligence" style={{ marginTop: "0.5rem" }}>
                                <button type="button" className="btn-secondary" disabled={svcBusy}
                                  onClick={async () => { await generateSvcDoc(job, t, "due_diligence"); await loadSvcDocs(job, t); }}>
                                  Make Declaration of Due Diligence
                                </button>
                              </div>
                            );
                          }
                          const pp = proofPrep[t.id] || {};
                          return (
                            <div data-testid="proof-prep-status" style={{ marginTop: "0.5rem", padding: "0.6rem", border: "2px solid " + (pp.msg ? "var(--red)" : "var(--light)"), borderRadius: 6, background: pp.msg ? "#FEF2F2" : "#F8FAFC" }}>
                              <div style={{ fontWeight: 700, fontSize: "0.85rem" }}>{pp.busy ? "Preparing the Proof of Service…" : pp.msg ? "The Proof of Service couldn't be prepared" : "Proof of Service not prepared yet"}</div>
                              {pp.msg && <div style={{ fontSize: "0.82rem", marginTop: "0.25rem" }}>{pp.msg}</div>}
                              {!pp.busy && <button type="button" className="btn-primary" data-testid="prepare-proof" style={{ marginTop: "0.4rem" }} disabled={svcBusy} onClick={() => autoPrepareProof(job.id, t.id)}>Prepare Proof</button>}
                            </div>
                          );
                        }
                        const done = job.status === "completed";
                        const label = (d) => `${d.kind === "due_diligence" ? "Declaration of Due Diligence" : "Proof of Service"}${d.formCode ? ` (${d.formCode})` : ""}`;
                        return (
                          <div data-testid="next-step-sign" style={{ marginTop: "0.5rem", padding: "0.6rem", border: "2px solid var(--green)", borderRadius: 6, background: "#F0FDF4" }}>
                            {draft ? (<>
                              <div style={{ fontWeight: 700, fontSize: "0.85rem" }} data-testid="ready-to-sign">{drafts.length > 1 ? `Ready to sign: ${drafts.map(label).join(" and ")}` : `${label(draft)} is ready to sign`}</div>
                              {job.proofHeader && (!job.proofHeader.filer.address || !job.proofHeader.courtAddress) ? (
                                <div data-testid="proof-header-missing" style={{ fontSize: "0.8rem", color: "var(--red)", marginTop: "0.25rem" }}>
                                  ⚠ The top of the proof is missing the {[!job.proofHeader.filer.address && "filer's address", !job.proofHeader.courtAddress && "court address"].filter(Boolean).join(" and ")}.{" "}
                                  <button type="button" className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.15rem 0.5rem" }} onClick={() => editProofHeaderNow(job)}>Fill in</button>
                                  {phMsg && <div style={{ color: "var(--gray)", marginTop: "0.2rem" }}>{phMsg}</div>}
                                </div>
                              ) : (
                                <button type="button" data-testid="edit-proof-header" onClick={() => editProofHeaderNow(job)}
                                  style={{ background: "none", border: "none", padding: 0, marginTop: "0.2rem", color: "var(--blue)", textDecoration: "underline", cursor: "pointer", fontSize: "0.78rem" }}>✎ Edit proof header</button>
                              )}
                            </>) : (
                              <div data-testid="signed-confirmation" style={{ fontWeight: 700, fontSize: "0.85rem", color: "#166534" }}>
                                ✓ {label(signedDoc)} signed{signedDoc.signedBy ? ` by ${signedDoc.signedBy}` : ""}{signedDoc.signedAt ? ` on ${new Date(signedDoc.signedAt).toLocaleDateString()}` : ""}
                                {done ? " · Order completed" : ""}
                              </div>
                            )}
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center", marginTop: "0.4rem" }}>
                              {drafts.map(d => {
                                const many = drafts.length > 1;
                                // Each paper says WHICH one it is (form) and WHEN it was made.
                                const short = `${d.kind === "due_diligence" ? "Due Diligence" : "Proof"}${many && d.formCode ? ` (${d.formCode})` : ""}`;
                                const made = d.createdAt ? new Date(d.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
                                return (<div key={d.id} data-testid="draft-row" style={{ width: "100%", display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
                                  {d.url && <button type="button" className="btn-secondary" data-testid="review-pdf" onClick={() => openFileTab(d.url, `${label(d)} — ${t.subject_name || ""}`)}>{many ? `Review ${short}` : "Review PDF"}</button>}
                                  <button className="btn-primary" data-testid="one-click-sign" disabled={svcBusy} onClick={() => signDraftNow(job, t, d)}>{signingDocId === d.id ? "Signing…" : (many ? `Sign ${short}` : "Sign")}</button>
                                  {made && <span className="text-sm" data-testid="draft-made" style={{ color: "var(--gray)" }}>made {made}</span>}
                                  {/* Make this draft again from the current information (e.g. after
                                      a fix, or a corrected header). The new draft replaces this one. */}
                                  <button type="button" data-testid="prepare-again" disabled={svcBusy}
                                    onClick={async () => { if (d.kind === "proof_of_service") await autoPrepareProof(job.id, t.id); else { await generateSvcDoc(job, t, d.kind); await loadSvcDocs(job, t); } }}
                                    style={{ background: "none", border: "none", padding: 0, color: "var(--blue)", textDecoration: "underline", cursor: "pointer", fontSize: "0.8rem" }}>↻ Prepare again</button>
                                </div>);
                              })}
                              {!draft && signedDoc.url && <button type="button" className="btn-secondary" data-testid="signed-pdf-download" onClick={() => openFileTab(signedDoc.url)}>View signed PDF</button>}
                              {!draft && signedDoc.url && <button type="button" className="btn-secondary" data-testid="print-signed" onClick={() => printFile(signedDoc.url)}>🖨 Print</button>}
                              {!draft && !done && <button type="button" className="btn-secondary" data-testid="resign" disabled={svcBusy} onClick={() => resignNow(job, t, signedDoc)}>{svcBusy ? "Working…" : "Re-sign"}</button>}
                              {!done && (
                                <button className="btn-primary" data-testid="mark-completed-next" disabled={actionBusy || !!draft}
                                  title={draft ? "Sign first" : ""} onClick={completeOrder}>
                                  {actionBusy ? "Completing…" : "Mark Completed"}
                                </button>
                              )}
                            </div>
                            {svcMsg && <div style={{ fontSize: "0.8rem", marginTop: "0.35rem" }}>{svcMsg}</div>}
                            {completeMsg && <div data-testid="complete-result" style={{ fontSize: "0.82rem", fontWeight: 700, marginTop: "0.35rem" }}>{completeMsg}</div>}
                          </div>
                        );
                      })()}
                      {/* Generated/signed PDFs for THIS Service Job — downloadable
                          via short-lived signed URLs from the private bucket. */}
                      <div style={{ marginTop: "0.4rem" }}>
                        {(() => {
                          // The green box already shows the draft to sign and the
                          // latest signed proof; list only OTHER documents here.
                          const live = (svcDocs[t.id] || []).filter(d => d.status !== "superseded");
                          // Only ONE paper is ever shown (the green box). The single
                          // exception: a signed paper that is being corrected — while
                          // its new draft is in the green box, the signed one is listed
                          // here so it isn't lost from view.
                          const hasDraft = live.some(d => d.status === "draft");
                          const signedBeingCorrected = hasDraft ? live.filter(d => d.status === "signed").slice(-1)[0] : null;
                          const others = signedBeingCorrected ? [signedBeingCorrected] : [];
                          return others.length > 0 && (
                          <div data-testid="svc-doc-list" style={{ marginTop: "0.35rem", fontSize: "0.76rem" }}>
                            <div style={{ color: "var(--gray)" }}>Signed copy being corrected:</div>
                            {others.map(d => (
                              <div key={d.id} style={{ padding: "0.15rem 0" }}>
                                <strong>{d.kind.replace(/_/g, " ")}</strong> v{d.version}{d.formCode ? ` · ${d.formCode}` : ""}{d.createdAt ? ` · ${new Date(d.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""} —{" "}
                                <span style={{ color: d.status === "signed" ? "var(--green)" : d.status === "superseded" ? "var(--gray)" : "var(--red)" }}>{d.status}</span>
                                {d.signedBy ? ` · signed by ${d.signedBy}` : ""}
                                {d.url && <> — <a href={d.url} target="_blank" rel="noreferrer">Download PDF</a></>}
                                {/* An unsigned draft can be REOPENED for review and
                                    signing — this exact draft, no regeneration — so a
                                    reviewed draft survives a page reload. */}
                                {d.status === "draft" && (
                                  <>
                                    {" "}<button className="btn-secondary" data-testid="review-sign-draft" disabled={svcBusy} style={{ fontSize: "0.75rem", padding: "0.15rem 0.5rem" }}
                                      onClick={() => reopenDraft(job, t, d)}>Review / Sign Draft</button>
                                  </>
                                )}
                                {/* CLIENT VISIBILITY. Signing does not share a
                                    document; a person decides. Offered only on
                                    a signed, current, non-stale Proof — the
                                    backend refuses anything else regardless. */}
                                {d.clientVisible ? (
                                  <>
                                    {" "}<span className="status-chip" style={{ background: "#EFF8F3", color: "var(--green)" }}>Visible to client</span>{" "}
                                    <button className="btn-secondary" disabled={svcBusy} style={{ fontSize: "0.75rem", padding: "0.15rem 0.5rem" }}
                                      onClick={() => setDocVisibility(job, t, d, false)}>Hide from client</button>
                                  </>
                                ) : d.canShare ? (
                                  <>
                                    {" "}<button className="btn-secondary" data-testid="share-doc" disabled={svcBusy} style={{ fontSize: "0.75rem", padding: "0.15rem 0.5rem" }}
                                      onClick={() => setDocVisibility(job, t, d, true)}>Share with client</button>
                                  </>
                                ) : (
                                  <span style={{ color: "var(--gray)" }} title={d.shareBlockedReason || ""}> · not shareable</span>
                                )}
                                {/* CONTROLLED RECOVERY. A rarely used corrective path
                                    for a FAILED, still-private signed proof — it
                                    re-finalizes from the proof's immutable source draft.
                                    Deliberately low-key and separate from normal signing;
                                    requires a correction reason and a confirmation. */}
                                {d.status === "signed" && !d.clientVisible && d.sourceDraftId && (
                                  recoverDoc === d.id ? null : (
                                    <>
                                      {" · "}<button className="btn-link" data-testid="correct-signed-proof" disabled={svcBusy} style={{ fontSize: "0.75rem", padding: 0, background: "none", border: "none", color: "var(--gray)", textDecoration: "underline", cursor: "pointer" }}
                                        onClick={() => { setRecoverDoc(d.id); setRecoverReason(""); }}>Correct signed proof…</button>
                                    </>
                                  )
                                )}
                                {recoverDoc === d.id && (
                                  <div data-testid="recover-proof-panel" style={{ marginTop: "0.4rem", padding: "0.5rem", border: "1px solid var(--gold)", borderRadius: 4, background: "#FFFDF5" }}>
                                    <div style={{ fontSize: "0.75rem", color: "var(--navy)", fontWeight: 700 }}>Re-finalize this signed proof from its source draft</div>
                                    <div style={{ fontSize: "0.75rem", color: "var(--gray)", margin: "0.2rem 0 0.4rem" }}>Creates a corrected signed version from the immutable source draft and supersedes this one. The failed proof is kept for audit. This does not share anything with the client.</div>
                                    <textarea data-testid="recover-reason" placeholder="Correction reason (required)" value={recoverReason} onChange={e => setRecoverReason(e.target.value)} style={{ width: "100%", minHeight: 44, fontSize: "0.75rem" }} />
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", alignItems: "center", marginTop: "0.3rem" }}>
                                      <input placeholder="Process server signature name" value={sigNameSvc} onChange={e => setSigNameSvc(e.target.value)} style={{ flex: 1, minWidth: 160, fontSize: "0.75rem" }} />
                                      <input placeholder="Place of execution (city)" value={execPlace} onChange={e => setExecPlace(e.target.value)} style={{ width: 140, fontSize: "0.75rem" }} />
                                      <button className="btn-primary" data-testid="confirm-recover" disabled={svcBusy || !recoverReason.trim() || !sigNameSvc.trim()} style={{ fontSize: "0.75rem", padding: "0.2rem 0.6rem" }}
                                        onClick={() => recoverSignedProof(job, t, d)}>Confirm correction</button>
                                      <button className="btn-secondary" disabled={svcBusy} style={{ fontSize: "0.75rem", padding: "0.2rem 0.6rem" }}
                                        onClick={() => { setRecoverDoc(null); setRecoverReason(""); }}>Cancel</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                        })()}
                      </div>
                      {/* Manual tools. The normal path is automatic (the Served
                          entry prepares the Proof; one click signs it), so these
                          stay folded away unless something needs a decision. */}
                      <div data-testid="target-docs" style={{ marginTop: "0.9rem", paddingTop: "0.6rem", borderTop: "1px solid var(--light)" }}>
                        <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--gray)" }}>Documents</div>
                        {docsForJob(job, t).length === 0 && <div style={{ color: "var(--gray)", fontSize: "0.8rem" }}>None yet.</div>}
                        {docsForJob(job, t).map(d => docRow(job, d))}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.3rem" }}>
                          <label data-testid="upload-to-job" className="btn-secondary" style={{ cursor: "pointer", margin: 0, display: "inline-block", fontSize: "0.75rem", padding: "0.35rem 0.7rem", whiteSpace: "nowrap" }}>
                            ＋ Upload
                            <input type="file" multiple style={{ display: "none" }} onChange={e => { uploadOrderDocs(job, e.target.files, t.id); e.target.value = ""; }} />
                          </label>
                          {job.status !== "completed" && t.service_status !== "completed" && (
                            <button type="button" className="btn-secondary" data-testid="request-docs-job" style={{ fontSize: "0.75rem", padding: "0.35rem 0.7rem", whiteSpace: "nowrap" }}
                              disabled={!!jobDocsBusy} onClick={() => requestDocsForJob(job, t)}>
                              {jobDocsBusy === t.id ? "Sending…" : "Ask Client to Upload"}
                            </button>
                          )}
                        </div>
                        {jobDocsMsg.id === t.id && jobDocsMsg.text && <div style={{ fontWeight: 700, fontSize: "0.8rem", margin: "0.25rem 0" }}>{jobDocsMsg.text}</div>}
                      </div>
                      <details data-testid="svc-manual-tools" open={!!(completedForm && completedForm.targetId === t.id) || !!(proofAttemptChoices && proofAttemptChoices.length) || !!(proofFormChoices && proofFormChoices.length) || showOverride} style={{ marginTop: "0.5rem" }}>
                        <summary style={{ cursor: "pointer", fontSize: "0.75rem", color: "var(--gray)" }}>More options (manual)</summary>
                      {/* ENTER COMPLETED SERVICE — for work already performed,
                          including service that happened before this order was
                          entered. Records the real service event; no fake prior
                          attempts are created. */}
                      {(t.service_status || "open") === "open" && (
                        <div style={{ marginTop: "0.5rem" }}>
                          <button className="btn-primary" data-testid="enter-completed-service" disabled={svcBusy}
                            onClick={() => (completedForm && completedForm.targetId === t.id ? setCompletedForm(null) : openCompleted(t))}>
                            {completedForm && completedForm.targetId === t.id ? "Cancel" : "✅ Enter Completed Service"}
                          </button>
                        </div>
                      )}
                      {completedForm && completedForm.targetId === t.id && (
                        <div data-testid="completed-service-form" style={{ marginTop: "0.5rem", border: "2px solid var(--gold)", borderRadius: 6, padding: "0.75rem" }}>
                          <div style={{ fontWeight: 700, fontSize: "0.82rem", marginBottom: "0.4rem" }}>Actual completed-service details ({t.subject_name})</div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem" }}>
                            <div className="field"><label style={{ fontSize: "0.75rem" }}>Manner of service *</label>
                              <select value={completedForm.manner} onChange={e => setCompletedForm(f => ({ ...f, manner: e.target.value }))}>
                                <option value="personal">Personal service</option>
                                <option value="substituted">Substituted service</option>
                              </select>
                            </div>
                            <div className="field"><label style={{ fontSize: "0.75rem" }}>Date of service *</label><DatePick value={completedForm.serviceDate} onChange={v => setCompletedForm(f => ({ ...f, serviceDate: v }))} /></div>
                            <div className="field"><label style={{ fontSize: "0.75rem" }}>Time of service *</label><input placeholder="e.g. 2:15 PM" value={completedForm.serviceTime} onChange={e => setCompletedForm(f => ({ ...f, serviceTime: e.target.value }))} /></div>
                            <div className="field" style={{ gridColumn: "span 2" }}><label style={{ fontSize: "0.75rem" }}>Documents served *</label><input value={completedForm.documentsServed} onChange={e => setCompletedForm(f => ({ ...f, documentsServed: e.target.value }))} /></div>
                            <div className="field"><label style={{ fontSize: "0.75rem" }}>Fee for service (USD)</label><input type="number" inputMode="decimal" step="0.01" value={completedForm.serviceFee} onChange={e => setCompletedForm(f => ({ ...f, serviceFee: e.target.value }))} /></div>
                            {completedForm.manner === "substituted" && (
                              <>
                                <div className="field"><label style={{ fontSize: "0.75rem" }}>Person who accepted *</label><input value={completedForm.recipientName} onChange={e => setCompletedForm(f => ({ ...f, recipientName: e.target.value }))} /></div>
                                <div className="field"><label style={{ fontSize: "0.75rem" }}>Relationship / title</label><input value={completedForm.recipientRelationship} onChange={e => setCompletedForm(f => ({ ...f, recipientRelationship: e.target.value }))} /></div>
                                <div className="field"><label style={{ fontSize: "0.75rem" }}>Description</label><input value={completedForm.recipientDescription} onChange={e => setCompletedForm(f => ({ ...f, recipientDescription: e.target.value }))} /></div>
                                <div className="field"><label style={{ fontSize: "0.75rem" }}>Date mailed</label><DatePick value={completedForm.mailedOn} onChange={v => setCompletedForm(f => ({ ...f, mailedOn: v }))} /></div>
                                <div className="field"><label style={{ fontSize: "0.75rem" }}>Place of mailing</label><input value={completedForm.mailedFrom} onChange={e => setCompletedForm(f => ({ ...f, mailedFrom: e.target.value }))} /></div>
                              </>
                            )}
                          </div>
                          <div className="field" style={{ marginTop: "0.4rem" }}><label style={{ fontSize: "0.75rem" }}>Notes (factual)</label><input value={completedForm.notes} onChange={e => setCompletedForm(f => ({ ...f, notes: e.target.value }))} /></div>
                          <button className="btn-primary" disabled={svcBusy} onClick={() => saveCompletedService(job, t)} style={{ marginTop: "0.5rem" }}>Save Completed Service</button>
                          <span style={{ fontSize: "0.75rem", color: "var(--gray)", marginLeft: "0.6rem" }}>Saves the real service event and sets the outcome. No prior attempts are invented.</span>
                        </div>
                      )}
                      {(t.service_status || "open") === "open" && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center", marginTop: "0.5rem" }}>
                          <select value={t.legal_service_outcome || "pending"} disabled={svcBusy}
                            onChange={e => svcCall({ action: "set_outcome", jobNumber: job.id, targetId: t.id, outcome: e.target.value }, "Outcome saved.").then(d => { if (d) loadJobs(); })}>
                            <option value="pending">Outcome…</option>
                            <option value="personal_service">Personal Service</option>
                            <option value="substituted_service">Substituted Service</option>
                            <option value="non_service">Unsuccessful / Non-Service</option>
                            <option value="other">Other</option>
                          </select>
                          {/* Generation refuses when it would have to guess. These
                              selectors appear only after that refusal, carrying the
                              options the server returned — the admin decides, and
                              nothing was generated in the meantime. */}
                          {proofAttemptChoices && proofAttemptChoices.length > 0 && (
                            <div data-testid="proof-attempt-choice" style={{ width: "100%", marginTop: "0.3rem" }}>
                              <label style={{ fontSize: "0.75rem", fontWeight: 700 }}>Which service attempt does this Proof describe? *</label>
                              <select value={proofAttemptId} onChange={e => setProofAttemptId(e.target.value)} style={{ width: "100%" }}>
                                <option value="">Select the attempt…</option>
                                {proofAttemptChoices.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    Attempt {c.attemptNumber} — {c.date || "no date"}{c.time ? ` at ${c.time}` : ""} ({String(c.outcome || "").replace(/_/g, " ")})
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          {/* What the uploaded documents were read as. The portal
                              selects the Proof form itself whenever the packet
                              supports it; the admin sees the evidence and can
                              generate without making the classification. */}
                          {proofClassification && (
                            <div data-testid="proof-classification" style={{ width: "100%", marginTop: "0.4rem", padding: "0.5rem 0.6rem", borderRadius: 8, border: "1px solid var(--line)", background: proofClassification.reviewRequired ? "rgba(200,150,62,0.08)" : "rgba(40,160,90,0.07)" }}>
                              <div style={{ fontSize: "0.75rem", fontWeight: 800, letterSpacing: "0.02em" }}>
                                {proofClassification.reviewRequired ? "DOCUMENT REVIEW NEEDED" : "DETECTED FROM UPLOADED DOCUMENTS"}
                              </div>
                              {proofClassification.docCategory && (
                                <div style={{ fontSize: "0.78rem", marginTop: "0.25rem" }}>
                                  Detected packet: <strong>{proofClassification.docCategory}</strong>
                                  {proofClassification.docSubtype ? <> — {String(proofClassification.docSubtype).replace(/_/g, " ")}</> : null}
                                </div>
                              )}
                              {proofClassification.recognizedForms && proofClassification.recognizedForms.length > 0 && (
                                <div style={{ fontSize: "0.78rem" }} data-testid="classification-forms">
                                  Detected forms: <strong>{proofClassification.recognizedForms.join(", ")}</strong>
                                </div>
                              )}
                              {proofClassification.formCode && (
                                <div style={{ fontSize: "0.78rem" }}>
                                  Proof form: <strong>{proofClassification.formCode}</strong>
                                  {proofClassification.formTitle ? <span style={{ color: "var(--gray)" }}> — {proofClassification.formTitle}</span> : null}
                                </div>
                              )}
                              <div style={{ fontSize: "0.78rem" }}>
                                Confidence: <strong style={{ textTransform: "capitalize" }}>{proofClassification.confidenceLevel}</strong>
                                {proofClassification.source === "admin_override" && <span> (manual override)</span>}
                                {proofClassification.source === "stored_record" && <span> (from the service job record, not the documents)</span>}
                              </div>
                              {proofClassification.evidence && proofClassification.evidence.length > 0 && (
                                <div style={{ fontSize: "0.75rem", color: "var(--gray)", marginTop: "0.2rem" }} data-testid="classification-evidence">
                                  Evidence: {proofClassification.evidence.join("; ")}
                                </div>
                              )}
                              {proofClassification.reviewRequired && (
                                <div style={{ fontSize: "0.75rem", marginTop: "0.25rem" }} data-testid="classification-problem">
                                  {proofClassification.reasonDetail}
                                </div>
                              )}
                              {/* A packet holding several supported document groups
                                  needs one Proof each. When the whole packet was
                                  served on the selected attempt — the normal case —
                                  every group is included and both Proofs are
                                  prepared together. The admin reviews; they do not
                                  have to choose one group and drop the other. */}
                              {proofClassification.separateProofs && proofClassification.separateProofs.length > 0 && (
                                <div style={{ fontSize: "0.76rem", marginTop: "0.35rem" }} data-testid="classification-separate">
                                  <strong>
                                    {proofClassification.multipleProofsRequired
                                      ? `Detected service packet requires ${proofClassification.separateProofs.length} Proofs`
                                      : "Separate Proofs required:"}
                                  </strong>
                                  {proofClassification.separateProofs.map((g, gi) => (
                                    <div key={g.groupId || gi} style={{ marginTop: "0.2rem" }} data-testid="proof-group">
                                      <div>
                                        <strong>Proof {gi + 1}: {g.formCode || "no form mapped"}</strong>
                                        {g.label ? ` — ${g.label}` : ""}
                                        {proofClassification.multipleProofsRequired && g.included !== false && (
                                          <span style={{ color: "var(--gray)" }}> · included</span>
                                        )}
                                      </div>
                                      <div style={{ color: "var(--gray)" }}>
                                        Documents detected: {(g.recognizedForms && g.recognizedForms.length ? g.recognizedForms : (g.forms || [])).concat(g.recognizedTitles && !(g.recognizedForms || []).length ? g.recognizedTitles : []).join(", ") || "—"}
                                        {(g.sourceFiles || g.files || []).length ? ` (${(g.sourceFiles || g.files).join(", ")})` : ""}
                                      </div>
                                    </div>
                                  ))}
                                  {proofClassification.multipleProofsRequired && (
                                    <div style={{ marginTop: "0.25rem" }} data-testid="multi-proof-notice">
                                      Separate unsigned Proofs will be prepared for each group, using the same service attempt. Review each before signing.
                                    </div>
                                  )}
                                  {multiProofResult && multiProofResult.targetId === t.id && multiProofResult.drafts.length > 0 && (
                                    <div style={{ marginTop: "0.3rem" }} data-testid="multi-proof-result">
                                      <strong>{multiProofResult.alreadyGenerated ? "Already prepared" : "Prepared"}:</strong>
                                      {multiProofResult.drafts.map((d) => (
                                        <div key={d.docId || d.groupId} style={{ color: "var(--gray)" }}>
                                          {d.formCode} v{d.version} — unsigned draft{d.sourceDocuments && d.sourceDocuments.length ? ` (${d.sourceDocuments.join(", ")})` : ""}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              {/* What the last run actually DID, per file. A
                                  re-read that reused stored evidence, or failed
                                  and kept the previous result, must never look
                                  the same as a fresh read. */}
                              {analysisReport && analysisReport.targetId === t.id && (
                                <div style={{ fontSize: "0.75rem", marginTop: "0.3rem" }} data-testid="analysis-report">
                                  {analysisReport.mode === "refresh"
                                    ? `Re-read ${analysisReport.analyzed} document(s) through the current reader`
                                    : `Analyzed ${analysisReport.analyzed} document(s)${analysisReport.cached ? `, reused ${analysisReport.cached} already read` : ""}`}
                                  {analysisReport.failed > 0 && (
                                    <span style={{ color: "var(--gold)" }}> · {analysisReport.failed} could not be read</span>
                                  )}
                                  {analysisReport.staleRetained > 0 && (
                                    <span style={{ color: "var(--gold)" }} data-testid="stale-retained">
                                      {" "}· {analysisReport.staleRetained} kept its previous result — the packet is NOT freshly analyzed
                                    </span>
                                  )}
                                  {analysisReport.perFile.map((f) => (
                                    <div key={f.file} style={{ color: "var(--gray)" }}>
                                      {f.file}: {f.outcome.replace(/_/g, " ")}{f.reason ? ` (${f.reason})` : ""}
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.35rem", flexWrap: "wrap" }}>
                                <button className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
                                  disabled={classifyBusy || svcBusy}
                                  onClick={() => classifyDocs(job, t, true)}
                                  data-testid="reanalyze-documents"
                                  title="Reads every uploaded document again through the current document reader">
                                  {classifyBusy ? "Re-reading documents…" : "Re-read documents"}
                                </button>
                                {proofFormChoices && proofFormChoices.length > 0 && (
                                  <button className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
                                    onClick={() => setShowOverride(v => !v)} data-testid="toggle-form-override">
                                    {showOverride ? "Cancel override" : "Override form manually"}
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Manual selection is the EXCEPTION path — shown only on
                              request, labelled as an override, and recorded with a
                              reason. It is never the normal way to pick a form. */}
                          {showOverride && proofFormChoices && proofFormChoices.length > 0 && (
                            <div data-testid="proof-form-choice" style={{ width: "100%", marginTop: "0.3rem" }}>
                              <label style={{ fontSize: "0.75rem", fontWeight: 700 }}>Override — select the form manually *</label>
                              <select value={proofSubtype} onChange={e => setProofSubtype(e.target.value)} style={{ width: "100%" }}>
                                <option value="">Select the form…</option>
                                {proofFormChoices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                              </select>
                              <input placeholder="Reason for the override (recorded)" value={overrideReason}
                                onChange={e => setOverrideReason(e.target.value)}
                                data-testid="override-reason" style={{ width: "100%", marginTop: "0.25rem" }} />
                              <div style={{ fontSize: "0.75rem", color: "var(--gray)", marginTop: "0.2rem" }}>
                                This overrides what the documents were read as. Your name, the time and this reason are recorded with the draft.
                              </div>
                            </div>
                          )}
                          {/* The document check runs BEFORE generation so the
                              detected packet and Proof form are on screen first.
                              Once classified, this control moves into the panel
                              above as "Re-analyze documents". */}
                          {!proofClassification && ["personal_service", "substituted_service", "posted_service"].includes(t.legal_service_outcome) && (
                            <button className="btn-secondary" disabled={classifyBusy || svcBusy}
                              onClick={() => classifyDocs(job, t, false)} data-testid="analyze-documents">
                              {classifyBusy ? "Analyzing documents…" : "Analyze documents"}
                            </button>
                          )}
                          {t.legal_service_outcome === "personal_service" && (
                            multiProofNeeded ? (
                              <button className="btn-secondary" disabled={svcBusy} data-testid="generate-all-proofs"
                                onClick={() => generateAllProofs(job, t)}>
                                Generate All {proofClassification.separateProofs.length} Proofs
                              </button>
                            ) : (
                              <button className="btn-secondary" disabled={svcBusy} onClick={() => generateSvcDoc(job, t, "proof_of_service")}>Generate Proof of Service</button>
                            )
                          )}
                          {t.legal_service_outcome === "substituted_service" && (
                            <>
                              {multiProofNeeded ? (
                              <button className="btn-secondary" disabled={svcBusy} data-testid="generate-all-proofs"
                                onClick={() => generateAllProofs(job, t)}>
                                Generate All {proofClassification.separateProofs.length} Proofs
                              </button>
                            ) : (
                              <button className="btn-secondary" disabled={svcBusy} onClick={() => generateSvcDoc(job, t, "proof_of_service")}>Generate Proof of Service</button>
                            )}
                              <button className="btn-secondary" disabled={svcBusy} onClick={() => generateSvcDoc(job, t, "mailing_proof")}>Generate Mailing Proof</button>
                              <button className="btn-secondary" disabled={svcBusy} onClick={() => generateSvcDoc(job, t, "due_diligence")}>Generate Diligence Declaration</button>
                              <div data-testid="mailing-facts" style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", width: "100%", marginTop: "0.3rem" }}>
                                <DatePick title="Date mailed" value={mailingFacts.mailedOn} onChange={v => setMailingFacts(f => ({ ...f, mailedOn: v }))} />
                                <input placeholder="Mailed to (address)" value={mailingFacts.mailedTo} onChange={e => setMailingFacts(f => ({ ...f, mailedTo: e.target.value }))} style={{ flex: 1, minWidth: 180 }} />
                                <input placeholder="Place of mailing" value={mailingFacts.mailedFrom} onChange={e => setMailingFacts(f => ({ ...f, mailedFrom: e.target.value }))} style={{ width: 160 }} />
                              </div>
                            </>
                          )}
                          {t.legal_service_outcome === "non_service" && (
                            <button className="btn-secondary" disabled={svcBusy} onClick={() => generateSvcDoc(job, t, "non_service")}>Generate Non-Service Declaration</button>
                          )}
                          {t.legal_service_outcome === "other" && (
                            <span className="text-sm" style={{ color: "var(--gray)" }}>"Other" outcomes: review and attach the appropriate document manually before completing.</span>
                          )}
                          {t.legal_service_outcome && t.legal_service_outcome !== "pending" && (
                            <button className="btn-primary" disabled={svcBusy} onClick={() => completeSvcJob(job, t)}>Complete Service Job</button>
                          )}
                        </div>
                      )}
                      </details>
                      {/* Document review + electronic signature: the generated draft
                          shows the factual record + flagged gaps; it is NOT signed
                          until the process server reviews and signs here. */}
                      {docPreview && docPreview.targetId === t.id && (
                        <div data-testid="svc-doc-review" style={{ marginTop: "0.6rem", border: "2px solid var(--gold)", borderRadius: 6, padding: "0.75rem" }}>
                          <div style={{ fontWeight: 700, fontSize: "0.82rem", marginBottom: "0.4rem" }}>Review before signing — {docPreview.kind.replace(/_/g, " ")}{docPreview.formCode ? ` (CA form: ${docPreview.formCode}${docPreview.formTitle ? ` — ${docPreview.formTitle}` : ""})` : ""}</div>
                          {docPreview.missing.length > 0 && (
                            <div className="alert-warn alert" style={{ fontSize: "0.78rem", marginBottom: "0.5rem" }}>
                              <strong>Missing information (fix in the records — facts are never invented):</strong>
                              <ul style={{ margin: "0.25rem 0 0", paddingLeft: "1.1rem" }}>{docPreview.missing.map((m, i) => <li key={i}>{m}</li>)}</ul>
                            </div>
                          )}
                          {/* WHICH form, and WHY. The resolver reports the layer
                              that decided, so the admin can see whether this is
                              an override, a standing rule, or the classifier. */}
                          {docPreview.templateResolution && (
                            <div data-testid="template-resolution" className="alert" style={{ fontSize: "0.78rem", marginBottom: "0.5rem", background: "var(--cream)", borderColor: "var(--gold)", color: "var(--navy)" }}>
                              <div>
                                <strong>Form source:</strong> {TEMPLATE_SOURCE_LABELS[docPreview.templateResolution.source] || docPreview.templateResolution.source}
                                {docPreview.templateResolution.source === "order_override" && <span className="status-chip" style={{ marginLeft: "0.4rem", background: "#FEF9EE", color: "#92400E" }}>Override active</span>}
                              </div>
                              <div style={{ marginTop: "0.15rem" }}>{docPreview.templateResolution.reason}</div>
                              {(docPreview.templateResolution.conflict || []).map((c, i) => (
                                <div key={i} style={{ color: "var(--red)", marginTop: "0.15rem" }}>⚠ {c.reason} (from {TEMPLATE_SOURCE_LABELS[c.layer] || c.layer}) — the next rule was used instead.</div>
                              ))}
                              <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.4rem", flexWrap: "wrap" }}>
                                <button className="btn-secondary" data-testid="set-form-override" style={{ fontSize: "0.75rem", padding: "0.2rem 0.55rem" }}
                                  onClick={() => setFormOverrideForm({ targetId: t.id, formCode: docPreview.formCode || "", reason: "" })}>
                                  {docPreview.templateResolution.source === "order_override" ? "Change Order Override" : "Set Order Override"}
                                </button>
                                {docPreview.templateResolution.source === "order_override" && (
                                  <button className="btn-secondary" data-testid="remove-form-override" style={{ fontSize: "0.75rem", padding: "0.2rem 0.55rem" }}
                                    onClick={() => removeFormOverride(job, t)}>Remove Order Override</button>
                                )}
                              </div>
                              {formOverrideForm && formOverrideForm.targetId === t.id && (
                                <div style={{ marginTop: "0.5rem" }}>
                                  <div className="field"><label>Proof form</label>
                                    <select data-testid="override-form-code" value={formOverrideForm.formCode}
                                      onChange={e => setFormOverrideForm(f => ({ ...f, formCode: e.target.value }))}>
                                      <option value="">Select a form…</option>
                                      {PROOF_FORM_CHOICES.map(c => <option key={c.code} value={c.code}>{c.code} — {c.title}</option>)}
                                    </select>
                                  </div>
                                  <div className="field"><label>Reason *</label>
                                    <input data-testid="override-form-reason" value={formOverrideForm.reason}
                                      onChange={e => setFormOverrideForm(f => ({ ...f, reason: e.target.value }))}
                                      placeholder="e.g. Clerk rejected FL-115 and requires FL-330 for this filing" />
                                  </div>
                                  <div style={{ display: "flex", gap: "0.4rem" }}>
                                    <button className="btn-primary" style={{ fontSize: "0.75rem", padding: "0.2rem 0.55rem" }}
                                      disabled={!formOverrideForm.formCode || !formOverrideForm.reason.trim()}
                                      onClick={() => saveFormOverride(job, t)}>Save Override</button>
                                    <button className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.2rem 0.55rem" }}
                                      onClick={() => setFormOverrideForm(null)}>Cancel</button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          {/* Process-server profile gaps shown BEFORE signing. */}
                          {docPreview.profileReadiness && !docPreview.profileReadiness.ready && (
                            <div data-testid="profile-readiness-warning" className="alert-warn alert" style={{ fontSize: "0.78rem", marginBottom: "0.5rem" }}>
                              <strong>Process-server profile incomplete.</strong> {docPreview.profileReadiness.message}
                            </div>
                          )}
                          {/* SC-104 (Small Claims Proof of Service) has its OWN
                              review/readiness model: items 1–6, the resolved
                              caption/court/hearing, split service address and
                              normalized registration — the SAME resolved values the
                              SC-104 PDF is filled from. POS-010's rule-2.150 banner
                              and item labels are never shown for an SC-104. */}
                          {docPreview.sc104Review && (
                            <div data-testid="sc104-review" style={{ marginBottom: "0.5rem" }}>
                              <div data-testid="sc104-readiness" className="alert alert-success" style={{ fontSize: "0.78rem", marginBottom: "0.5rem" }}>
                                <strong>Filing-ready:</strong> {docPreview.sc104Review.readiness && docPreview.sc104Review.readiness.message}
                              </div>
                              <div style={{ fontSize: "0.75rem", background: "var(--cream)", padding: "0.6rem", borderRadius: 4, marginBottom: "0.5rem" }}>
                                {docPreview.sc104Review.caption && (
                                  <div><span style={{ color: "var(--gray)" }}>Case: </span><strong>{docPreview.sc104Review.caption}</strong>{docPreview.sc104Review.caseNumber ? ` (No. ${docPreview.sc104Review.caseNumber})` : ""}</div>
                                )}
                                {docPreview.sc104Review.courtLines && docPreview.sc104Review.courtLines.length > 0 && (
                                  <div data-testid="sc104-court" style={{ marginTop: "0.3rem" }}>
                                    <span style={{ color: "var(--gray)" }}>Court:</span>
                                    {docPreview.sc104Review.courtLines.map((l, i) => <div key={i}>{l}</div>)}
                                  </div>
                                )}
                                {docPreview.sc104Review.hearing && (docPreview.sc104Review.hearing.date || docPreview.sc104Review.hearing.time || docPreview.sc104Review.hearing.dept) && (
                                  <div data-testid="sc104-hearing" style={{ marginTop: "0.3rem" }}>
                                    <span style={{ color: "var(--gray)" }}>Hearing: </span>
                                    <strong>{[docPreview.sc104Review.hearing.date, docPreview.sc104Review.hearing.time, docPreview.sc104Review.hearing.dept ? `Dept. ${docPreview.sc104Review.hearing.dept}` : ""].filter(Boolean).join(" · ")}</strong>
                                  </div>
                                )}
                              </div>
                              <div data-testid="sc104-item-review" style={{ fontSize: "0.75rem", background: "var(--cream)", padding: "0.6rem", borderRadius: 4, maxHeight: 300, overflowY: "auto" }}>
                                {docPreview.sc104Review.items.map((it) => {
                                  const isArr = Array.isArray(it.value);
                                  const empty = isArr ? it.value.length === 0 : !it.value;
                                  return (
                                    <div key={it.item + it.key} style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem", padding: "0.15rem 0", borderBottom: "1px solid var(--light)" }}>
                                      <span style={{ color: "var(--gray)" }}>Item {it.item} — {it.label}</span>
                                      <span style={{ color: (it.blankUntilSigned || it.informational) ? "var(--gray)" : (empty ? "var(--red)" : "var(--navy)"), textAlign: "right", fontWeight: it.informational ? 400 : 600, fontStyle: it.informational ? "italic" : "normal" }}>
                                        {it.blankUntilSigned
                                          ? "— added when you sign —"
                                          : (isArr ? it.value.map((d, di) => <div key={di}>{d}</div>) : (it.value || "[blank]"))}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {/* Rule 2.150 filing-ready status for POS-010 jobs (never
                              rendered for an SC-104, which has its own model above). */}
                          {docPreview.rule2150 && docPreview.formCode !== "SC-104" && (
                            <div data-testid="rule2150-status" className={docPreview.rule2150.compliant ? "alert alert-success" : "alert-warn alert"} style={{ fontSize: "0.78rem", marginBottom: "0.5rem" }}>
                              {docPreview.rule2150.official
                                ? <><strong>Official {docPreview.rule2150.form} form</strong> (Judicial Council of California), filled from this job. Review it with Download PDF, then sign below.</>
                                : docPreview.rule2150.compliant
                                ? <><strong>Filing-ready:</strong> computer-generated Proof of Service of Summons under Cal. Rules of Court, rule 2.150 ({docPreview.rule2150.version}). Sign below — no transcription onto another form is needed.</>
                                : <><strong>Not yet filing-ready under rule 2.150.</strong> {docPreview.rule2150.reason || "The rule 2.150 path is not available for this document."}</>}
                            </div>
                          )}
                          {docPreview.advisory && docPreview.advisory.length > 0 && (
                            <div style={{ fontSize: "0.75rem", color: "var(--gray)", marginBottom: "0.5rem" }}>
                              {docPreview.advisory.map((a, i) => <div key={i}>• {a}</div>)}
                            </div>
                          )}
                          {/* Populated official-form items — the admin reviews and
                              can correct the facts before signing. */}
                          {docPreview.items && docPreview.items.length > 0 && (
                            <div data-testid="form-item-review" style={{ fontSize: "0.75rem", background: "var(--cream)", padding: "0.6rem", borderRadius: 4, maxHeight: 260, overflowY: "auto", marginBottom: "0.5rem" }}>
                              {docPreview.items.map((it) => (
                                <div key={it.item + it.key} style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem", padding: "0.15rem 0", borderBottom: "1px solid var(--light)" }}>
                                  <span style={{ color: "var(--gray)" }}>Item {it.item} — {it.label}</span>
                                  <strong style={{ color: (docPreview.values || {})[it.key] ? "var(--navy)" : "var(--red)", textAlign: "right" }}>
                                    {(docPreview.values || {})[it.key] || "[blank]"}
                                  </strong>
                                </div>
                              ))}
                            </div>
                          )}
                          {docPreview.content && <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.75rem", background: "var(--cream)", padding: "0.6rem", borderRadius: 4, maxHeight: 240, overflowY: "auto" }}>{docPreview.content}</pre>}
                          {/* SIGNING READINESS. The server refuses to sign with
                              an incomplete identity block, so the refusal is
                              shown here rather than discovered by pressing the
                              button. The state comes from the server — this only
                              displays it. */}
                          {docPreview.profileReadiness && !docPreview.profileReadiness.ready && (
                            <div className="alert alert-warn" data-testid="signing-blocked" style={{ marginTop: "0.6rem" }}>
                              <div style={{ fontWeight: 700 }}>Proof cannot be signed until the required server information is complete.</div>
                              <ul style={{ margin: "0.4rem 0 0.4rem 1.1rem", fontSize: "0.8rem" }}>
                                {(docPreview.profileReadiness.missingFields || []).map((f) => (
                                  <li key={`m-${f.key}`}>{f.label} — missing</li>
                                ))}
                                {(docPreview.profileReadiness.invalidFields || []).map((f) => (
                                  <li key={`i-${f.key}`}>{f.reason}</li>
                                ))}
                                {/* Older responses carry labels only. */}
                                {!(docPreview.profileReadiness.missingFields || []).length
                                  && !(docPreview.profileReadiness.invalidFields || []).length
                                  && (docPreview.profileReadiness.missing || []).map((label) => <li key={label}>{label} — missing</li>)}
                              </ul>
                              <button className="btn-secondary" data-testid="fix-profile" style={{ fontSize: "0.75rem", padding: "0.25rem 0.6rem" }}
                                onClick={() => { setAdminTab("profile"); loadServerProfile(); }}>
                                Edit Server Profile
                              </button>
                            </div>
                          )}
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center", marginTop: "0.5rem" }}>
                            <button className="btn-primary" data-testid="sign-finalize"
                              disabled={svcBusy || !sigNameSvc.trim() || (docPreview.profileReadiness && !docPreview.profileReadiness.ready)}
                              onClick={() => signSvcDoc(job, t)}>{sigNameSvc.trim() ? `Sign as ${sigNameSvc.trim()}` : "Sign & Finalize"}</button>
                            <details style={{ fontSize: "0.75rem" }}>
                              <summary style={{ cursor: "pointer" }}>Change name or city</summary>
                              <input placeholder="Process server signature name" value={sigNameSvc} onChange={e => setSigNameSvc(e.target.value)} style={{ minWidth: 180 }} />
                              <input placeholder="Place of execution (city)" value={execPlace} onChange={e => setExecPlace(e.target.value)} style={{ width: 160 }} />
                            </details>
                            <button className="btn-secondary" onClick={() => setDocPreview(null)}>Close</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Orders without Service Jobs: the same one-tap attempt button as
                  inside a job, right under the order info (not at the bottom). */}
              {captureEnabled && !(job.serviceTargets && job.serviceTargets.length) && job.status !== "completed" && (
                <div style={{ marginTop: "0.75rem" }}>
                  <button type="button" className={attemptOpenFor === "order" ? "btn-secondary" : "btn-primary btn-log-attempt"} data-testid="log-attempt-order"
                    onClick={() => { const open = attemptOpenFor === "order"; setAttemptOpenFor(open ? null : "order"); if (!open) setAttemptTargetId(""); }}>
                    {attemptOpenFor === "order" ? "✕ Close" : "＋ Log Attempt / Service"}
                  </button>
                </div>
              )}
              {attemptOpenFor === "order" && !(job.serviceTargets && job.serviceTargets.length) && renderAttemptForm(null)}
              {/* Order-level attempts only for orders without Service Jobs (or
                  attempts not tied to one) — otherwise each job shows its own. */}
              {(!(job.serviceTargets || []).length || orderLevelAttempts(job).length > 0) && (
              <div className="note-log" id="saved-attempts" style={{ marginTop: "1rem" }}>
                <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.5rem" }}>
                  Saved Attempts{" "}
                  <span style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: captureEnabled ? "var(--blue)" : "var(--gray)" }}>
                    ({captureEnabled ? "capture on" : "capture off"})
                  </span>
                </div>
                {orderLevelAttempts(job).length === 0 && <p className="text-sm">{(job.serviceTargets || []).length ? "Attempts are listed under each Service Job above." : "No saved attempts yet."}</p>}
                {/* Every RECORDED fact is shown, not just number/outcome/date —
                    that is what makes a saved entry reviewable and a declaration
                    reproducible. Facts that were never recorded are omitted
                    entirely rather than rendered as empty labels. */}
                {orderLevelAttempts(job).map(renderSavedAttempt)}
              </div>
              )}
              {deleteMsg && <div className="alert" style={{ marginTop: "0.5rem", fontSize: "0.8rem" }}>{deleteMsg}</div>}
              {/* The legacy Service Log repeats what each Service Job already shows — only for orders without jobs. */}
              {!captureEnabled && !(job.serviceTargets || []).length && <div className="note-log" style={{ marginTop: "1rem" }}>
                <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.5rem" }}>Service Log</div>
                {job.notes.length === 0 && <p className="text-sm">No entries yet.</p>}
                {job.notes.map((n, i) => (
                  <div key={i} className="note-entry">
                    <div className="note-status">{n.status}</div>
                    <div className="note-meta">{n.date} {n.time} — {n.location}</div>
                    {n.who && <div><strong>Contact:</strong> {n.who}</div>}
                    <div>{n.desc}</div>
                  </div>
                ))}
              </div>}
              {/* Phase 1: the structured attempt-capture form is shown ONLY when
                  field capture is enabled. When disabled, no active GPS / photo /
                  attempt-field / Save Entry controls are exposed. The read-only
                  Saved Attempts + Service Log above, and the completion/proof
                  workflow below, are unaffected. */}
              {/* PROOF HEADER — what prints at the top of every Proof on this order.
                  Filled in automatically from the Summons and NOT shown on the order;
                  it only appears here while being edited (from "Fill in" in the
                  Sign box when something is missing). */}
              {(phForm || phMsg) && (
              <div data-testid="proof-header" style={{ marginTop: "0.75rem", fontSize: "0.88rem", border: "1px solid var(--light)", borderRadius: 6, padding: "0.5rem 0.75rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <strong>Proof header</strong>
                  {!phForm && <button type="button" data-testid="edit-proof-header" onClick={() => openProofHeader(job)} style={{ background: "none", border: "none", padding: 0, color: "var(--blue)", textDecoration: "underline", cursor: "pointer", fontSize: "0.82rem" }}>✎ Edit</button>}
                </div>
                {phForm && (
                  <div data-testid="proof-header-form" className="form-grid" style={{ marginTop: "0.4rem" }}>
                    <div className="field"><label>Attorney / party name</label><input value={phForm.name} onChange={e => setPhForm(f => ({ ...f, name: e.target.value }))} /></div>
                    <div className="field"><label>Attorney for</label><input value={phForm.attorneyFor} onChange={e => setPhForm(f => ({ ...f, attorneyFor: e.target.value }))} placeholder="e.g. Plaintiff in Pro Per" /></div>
                    <div className="field form-full"><label>Address (street, city, state ZIP)</label><textarea rows={2} value={phForm.address} onChange={e => setPhForm(f => ({ ...f, address: e.target.value }))} /></div>
                    <div className="field"><label>Phone</label><input value={phForm.phone} onChange={e => setPhForm(f => ({ ...f, phone: e.target.value }))} /></div>
                    <div className="field"><label>Email</label><input value={phForm.email} onChange={e => setPhForm(f => ({ ...f, email: e.target.value }))} /></div>
                    <div className="field"><label>Court street address</label><input value={phForm.courtStreet} onChange={e => setPhForm(f => ({ ...f, courtStreet: e.target.value }))} /></div>
                    <div className="field"><label>Court city and ZIP</label><input value={phForm.courtCityZip} onChange={e => setPhForm(f => ({ ...f, courtCityZip: e.target.value }))} /></div>
                    <div className="field form-full"><label>Branch name</label><input value={phForm.courtBranch} onChange={e => setPhForm(f => ({ ...f, courtBranch: e.target.value }))} placeholder="e.g. San Bernardino Justice Center" /></div>
                    <div className="btn-row-compact form-full">
                      <button className="btn-primary" data-testid="save-proof-header" onClick={() => saveProofHeader(job)}>Save</button>
                      <button className="btn-secondary" onClick={() => setPhForm(null)}>Cancel</button>
                    </div>
                  </div>
                )}
                {phMsg && <div style={{ marginTop: "0.3rem", fontWeight: 700 }}>{phMsg}</div>}
              </div>
              )}
              <div data-testid="email-history" style={{ marginTop: "0.75rem", border: "1px solid var(--light)", borderRadius: 8, padding: "0.6rem 0.75rem", fontSize: "0.85rem" }}>
                <div style={{ fontWeight: 700, marginBottom: "0.3rem" }}>Emails to client{job.clientEmail ? ` (${job.clientEmail})` : ""}</div>
                {emailHist.id !== job.id ? <div style={{ color: "var(--gray)" }}>Loading…</div>
                  : emailHist.err ? <div style={{ color: "var(--red)" }}>Could not load email history: {emailHist.err}</div>
                  : emailHist.rows.length === 0 ? <div style={{ color: "var(--gray)" }}>No emails sent yet.</div>
                  : emailHist.rows.map((m, i) => (
                    <div key={i} data-testid="email-history-row" style={{ padding: "0.3rem 0", borderTop: i ? "1px solid var(--light)" : "none" }}>
                      <div>{m.subject}</div>
                      <div style={{ fontSize: "0.75rem", color: "var(--gray)" }}>
                        {new Date(m.sentAt).toLocaleString([], { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        {m.status && <> · <span style={{ fontWeight: 700, color: /bounced|failed|complained/.test(m.status) ? "var(--red)" : "var(--green)" }}>{m.status}</span></>}
                      </div>
                    </div>
                  ))}
              </div>
              <div data-testid="order-documents" style={{ marginTop: "0.75rem", fontSize: "0.92rem" }}>
                {/* Papers live inside their Service Job. Only orders without jobs,
                    or a paper whose job can't be told from its name, show here. */}
                {(!(job.serviceTargets || []).length || unfiledDocs(job).length > 0) && <strong>Documents: </strong>}
                {(job.serviceTargets || []).length === 0 && paperDocs(job).length === 0 && <span style={{ color: "var(--gray)" }}>none yet </span>}
                {(job.serviceTargets || []).length === 0 && paperDocs(job).map(d => docRow(job, d))}
                {unfiledDocs(job).map(d => docRow(job, d, (
                  <select data-testid="file-doc-under" value="" onChange={e => e.target.value && docAction(job, d, "assign", e.target.value)} style={{ fontSize: "0.78rem", padding: "0.1rem 0.3rem", width: "auto" }}>
                    <option value="">Which job?</option>
                    {(job.serviceTargets || []).map(t => <option key={t.id} value={t.id}>{t.subject_name || t.job_label}</option>)}
                  </select>
                )))}
                {docMsg && <div style={{ fontWeight: 700 }}>{docMsg}</div>}
                {(job.serviceTargets || []).length === 0 && (
                <label className="btn-secondary" style={{ cursor: "pointer", margin: "0.35rem 0 0", display: "inline-block", fontSize: "0.8rem" }}>
                  ＋ Upload Documents
                  <input type="file" multiple style={{ display: "none" }} onChange={e => { uploadOrderDocs(job, e.target.files); e.target.value = ""; }} />
                </label>
                )}
                {(job.serviceTargets || []).length === 0 && job.status !== "completed" && (<>{" "}<button type="button" className="btn-secondary" data-testid="request-docs" style={{ fontSize: "0.8rem", marginTop: "0.35rem" }} disabled={billingBusy} onClick={() => requestDocs(job)}>{job.docsRequestedAt ? "Resend Upload Link to Client" : "Ask Client to Upload"}</button>
                {job.docsRequestedAt && <span style={{ fontSize: "0.8rem", color: "var(--gray)" }}> · upload link sent {new Date(job.docsRequestedAt).toLocaleDateString()}</span>}</>)}
              </div>

              {/* ── Document Reader Review ──
                  What the reader concluded vs what was finally used. Summary
                  only — no raw model output, no credentials. */}
              {/* exParte is in the gate because the EX PARTE chip lives in this
                  panel. Without it an ex parte order that had no reader
                  suggestion, reason, court address or subtype rendered no panel
                  at all — so the status was invisible to admin. */}
              {(job.readerSuggestedDocType || job.readerReason || job.courtAddress || job.docSubtype || job.exParte) && (
                <div data-testid="reader-review" style={{ marginTop: "1.25rem", borderTop: "2px solid var(--light)", paddingTop: "1rem" }}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--gray)", marginBottom: "0.5rem" }}>
                    Document Reader Review
                  </div>
                  <div className="text-sm" style={{ lineHeight: 1.7 }}>
                    {job.readerSuggestedDocType && <>Reader suggested: <strong>{job.readerSuggestedDocType}</strong>{job.readerConfidence != null && <> (confidence {Number(job.readerConfidence).toFixed(2)})</>}<br /></>}
                    Final document type: <strong>{job.docType || "—"}</strong>
                    {job.docSubtype && <> &nbsp;|&nbsp; Subtype: <strong>{job.docSubtype}</strong></>}
                    {/* Shown BESIDE the document type, never replacing it. */}
                    {job.exParte && <> &nbsp;|&nbsp; <span data-testid="admin-ex-parte" className="status-chip" style={{ background: "var(--gold)", color: "var(--navy)", fontWeight: 700 }}>EX PARTE</span></>}<br />
                    Court: <strong>{job.court || "—"}</strong>
                    {job.courtAddress && <> &nbsp;|&nbsp; {job.courtAddress}</>}<br />
                    {job.readerReason && <>Review reason: <strong>{job.readerReason}</strong><br /></>}
                    Customer confirmed: <strong>{job.priceApprovedBy === "customer_affirmed" ? "yes" : "no"}</strong>
                    {job.priceApprovedBy && job.priceApprovedBy !== "customer_affirmed" && <> &nbsp;|&nbsp; Priced by: <strong>{job.priceApprovedBy}</strong></>}
                    {job.readerEvidence && <><br /><span style={{ color: "var(--gray)", fontSize: "0.78rem" }}>{job.readerEvidence}</span></>}
                  </div>
                </div>
              )}

              {/* ── Pricing Review (unpriced "Other" submissions) ──
                  Shown only while the order is awaiting review and unpaid. The
                  admin picks the real document type and the approved amount;
                  the EXISTING Send Payment Link workflow then does the rest. */}
              {job.status === "pending_review" && job.paymentStatus !== "paid" && job.amountDueCents == null && (
                <div data-testid="pricing-review" style={{ marginTop: "1.25rem", borderTop: "2px solid var(--gold)", paddingTop: "1rem" }}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--gray)", marginBottom: "0.5rem" }}>
                    Pricing Requires Review
                  </div>
                  <div className="text-sm" style={{ marginBottom: "0.6rem" }}>
                    This request was submitted as <strong>{job.docType || "Other"}</strong> with no price. Review the attached documents,
                    set the approved document type and amount, then send the payment link.
                  </div>
                  <div className="form-grid">
                    <div className="field">
                      <label style={{ fontSize: "0.75rem" }}>Approved document type *</label>
                      <select value={priceForm.docType} onChange={e => setPriceForm(f => ({ ...f, docType: e.target.value }))}>
                        <option value="">Select type…</option>
                        {Object.keys(DOC_SURCHARGES).filter(t => !requiresReview(t)).map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="field">
                      <label style={{ fontSize: "0.75rem" }}>Approved amount</label>
                      <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                        <label style={{ fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                          <input type="checkbox" checked={priceForm.useCalculated} onChange={e => setPriceForm(f => ({ ...f, useCalculated: e.target.checked }))} />
                          Use calculated
                        </label>
                        <input type="number" min="0" step="0.01" disabled={priceForm.useCalculated}
                          value={priceForm.amountDue} placeholder="0.00"
                          onChange={e => setPriceForm(f => ({ ...f, amountDue: e.target.value }))} />
                      </div>
                    </div>
                  </div>
                  {priceMsg && <div className="alert" style={{ marginTop: "0.6rem", fontSize: "0.82rem" }}>{priceMsg}</div>}
                  <button className="btn-primary" style={{ marginTop: "0.6rem" }} disabled={priceBusy}
                    onClick={() => approvePrice(job)}>{priceBusy ? "Saving…" : "Save Approved Price"}</button>
                </div>
              )}

              {/* ── Billing — one card: what's charged, what's paid, what's next ── */}
              {(() => {
                const ext = job.paymentStatus === "billed_externally";
                const extras = job.extraCharges || [];
                const paidExtras = extras.filter(c => c.status === "paid").reduce((a, c) => a + c.amount_cents, 0);
                const openExtras = extras.filter(c => c.status === "sent").reduce((a, c) => a + c.amount_cents, 0);
                const due = job.amountDueCents;
                const base = due != null ? due - paidExtras : null;
                const lines = base != null ? (chargeLines(job.serviceTargets, base) || [{ name: (job.serviceTargets || []).length > 1 ? "Process Service (total)" : "Process Service", amountCents: base }]) : [];
                const paid = job.amountPaidCents ?? 0;
                const balance = due != null ? Math.max(0, due - paid) + openExtras : null;
                const money = (c) => `$${(c / 100).toFixed(2)}`;
                const pill = ext ? ["Billed outside app", "#F3F4F6", "var(--navy)"]
                  : balance === 0 && due != null ? ["✓ Paid", "#DCFCE7", "#166534"]
                  : ["Awaiting payment", "#FEF3C7", "#92400E"];
                return (
              <div data-testid="billing-card" style={{ marginTop: "1.25rem", border: "2px solid var(--light)", borderRadius: 8, padding: "0.9rem 1rem", fontSize: "0.92rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
                  <strong style={{ fontSize: "1rem" }}>Billing</strong>
                  <span data-testid="payment-status" className="status-chip" style={{ background: pill[1], color: pill[2], fontWeight: 700 }}>{pill[0]}</span>
                </div>
                {!ext && due != null && (
                  <div data-testid="billing-charges">
                    {lines.map((l, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: "1rem", padding: "0.15rem 0" }}><span>{l.name}</span><span>{money(l.amountCents)}</span></div>
                    ))}
                    {extras.filter(c => c.status === "paid" || c.status === "sent").map(c => (
                      <div key={c.id} data-testid="extra-charge-row" style={{ padding: "0.15rem 0" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}>
                          <span>{c.description} <span style={{ color: c.status === "paid" ? "var(--green)" : "#92400E", fontSize: "0.82rem" }}>{c.status === "paid" ? "✓ paid" : "· awaiting payment"}</span>
</span>
                          <span>{money(c.amount_cents)}</span>
                        </div>
                        {c.status === "sent" && (
                          <div className="btn-row-compact" style={{ margin: "0.25rem 0" }}>
                            {c.payment_link_url && <a className="btn-primary" data-testid="extra-charge-pay" href={c.payment_link_url} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>Take Payment</a>}
                            <button type="button" className="btn-secondary" data-testid="extra-charge-email-client" disabled={xcBusy} onClick={() => emailOpenCharge(job, c)}>Email Client</button>
                          </div>
                        )}
                        {c.kind === "skip_trace" && (
                          <div style={{ fontSize: "0.85rem", marginTop: "0.15rem", color: c.details_submitted_at ? "inherit" : "var(--gray)" }}>
                            {c.details_submitted_at ? "Client's details:" : "Waiting for the client's details."}
                            {((c.details && c.details.people) || []).map((p, i) => (
                              <div key={i} style={{ paddingLeft: "0.5rem" }}>
                                <strong>{[p.firstName, p.lastName].filter(Boolean).join(" ") || "—"}</strong>
                                {[p.address, p.city, p.state].filter(Boolean).length > 0 && <> · {[p.address, p.city, p.state].filter(Boolean).join(", ")}</>}
                                {p.phone && <> · 📞 {p.phone}</>}{p.email && <> · ✉ {p.email}</>}{p.jobs && <> · Job(s): {p.jobs}</>}
                              </div>
                            ))}
                            {c.details && c.details.notes && <div style={{ paddingLeft: "0.5rem" }}>Other info: {c.details.notes}</div>}
                          </div>
                        )}
                      </div>
                    ))}
                    <div style={{ borderTop: "1px solid var(--light)", marginTop: "0.35rem", paddingTop: "0.35rem" }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}><span>Paid</span><span>{money(paid)}</span></div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: "1rem" }}><span>Balance Due</span><span data-testid="balance-due">{money(balance)}</span></div>
                    </div>
                  </div>
                )}
                <div data-testid="billing-sent" style={{ marginTop: "0.6rem", fontSize: "0.85rem", color: "var(--gray)", lineHeight: 1.6 }}>
                  <div data-testid="agreement-status">Agreement: <strong style={{ color: "var(--navy)" }}>{
                    job.agreementStatus === "accepted" ? `✓ Accepted${job.agreementAcceptedName ? ` by ${job.agreementAcceptedName}` : ""}${job.agreementAcceptedAt ? ` ${new Date(job.agreementAcceptedAt).toLocaleDateString()}` : ""}`
                    : job.agreementStatus === "accepted_at_checkout" ? "✓ Accepted at checkout"
                    : job.agreementStatus === "sent" ? `Waiting for client (sent ${job.agreementSentAt ? new Date(job.agreementSentAt).toLocaleDateString() : ""})`
                    : "Not sent"}</strong></div>
                  {!ext && (job.paymentLinkSentAt || balance > 0) && <div>Payment link: <strong style={{ color: "var(--navy)" }}>{job.paymentLinkSentAt ? `Sent ${new Date(job.paymentLinkSentAt).toLocaleDateString()} to ${job.clientEmail || "client"}` : "Not sent"}</strong>{job.paymentLinkUrl && <> · <a href={job.paymentLinkUrl} target="_blank" rel="noreferrer">view</a></>}</div>}
                </div>
                {job.clientEmail && <div style={{ marginTop: "0.6rem" }}><button type="button" className="btn-secondary" data-testid="send-signin" disabled={billingBusy} onClick={() => sendSignIn(job)}>✉ Send sign-in instructions</button></div>}
                {billingMsg && <div className="alert" style={{ marginTop: "0.6rem", fontSize: "0.85rem" }}>{billingMsg}</div>}
                {xcMsg && <div data-testid="extra-charge-result" style={{ marginTop: "0.5rem", fontWeight: 700 }}>{xcMsg}
                  {xcPayUrl && <> <a className="btn-primary" href={xcPayUrl} target="_blank" rel="noreferrer" data-testid="extra-charge-take-payment" style={{ display: "inline-block", textDecoration: "none", padding: "0.4rem 0.8rem", fontSize: "0.8rem", marginLeft: "0.4rem" }}>Open payment page</a></>}
                </div>}
                {!ext && (
                  <div className="btn-row-compact" style={{ marginTop: "0.75rem" }}>
                    {balance > 0 && job.paymentStatus !== "paid" && (
                      <button className="btn-primary" disabled={billingBusy} onClick={() => sendPaymentLink(job)}>{job.paymentLinkUrl ? "Resend Payment Link" : "Send Payment Link"}</button>
                    )}
                    {!xcForm && <button className="btn-secondary" data-testid="add-extra-charge" onClick={() => { setXcForm({ ...blankXc, people: [{ firstName: "", lastName: "" }] }); setXcMsg(""); }}>＋ Add Charge</button>}
                    {/* All billing actions shown as buttons — only the ones that apply. */}
                        {(job.agreementStatus === "sent" || job.agreementStatus === "not_sent") && (
                          <button className="btn-secondary" disabled={billingBusy} onClick={() => sendAgreementOnly(job)}>{job.agreementStatus === "sent" ? "Resend Agreement" : "Send Agreement"}</button>
                        )}
                        {job.paymentStatus !== "paid" && !job.stripeInvoiceId && !job.paymentLinkSentAt && (
                          <button className="btn-secondary" disabled={billingBusy} onClick={() => createInvoice(job)}>Create Invoice</button>
                        )}
                        {/* Nov 3: resend an invoice that was already sent and/or paid —
                            also for jobs paid by payment link (no Stripe invoice). */}
                        {job.clientEmail && (job.amountDueCents || 0) > 0 && !(job.stripeInvoiceId && job.invoiceStatus !== "voided" && job.paymentStatus !== "paid" && !((job.amountPaidCents || 0) > 0))
                          && (job.paymentStatus === "paid" || (job.amountPaidCents || 0) > 0 || job.paymentLinkSentAt || job.stripeInvoiceId) && (
                          <>
                            <button className="btn-secondary" data-testid="view-invoice" disabled={billingBusy} onClick={() => viewInvoice(job)}>View Invoice</button>
                            <button className="btn-secondary" data-testid="resend-invoice" disabled={billingBusy} onClick={() => resendInvoice(job)}>Resend Invoice</button>
                          </>
                        )}
                        {invoiceView && (
                          <div data-testid="invoice-view" style={{ position: "fixed", inset: 0, zIndex: 100000, background: "#525659", display: "flex", flexDirection: "column" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "8px 12px", background: "#0B1628", color: "#fff" }}>
                              <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.9rem" }}>{invoiceView.subject}</div>
                              <button type="button" data-testid="invoice-view-close" onClick={() => setInvoiceView(null)}
                                style={{ background: "#fff", color: "#0B1628", border: "none", borderRadius: 6, padding: "8px 14px", fontWeight: 700, minHeight: 44 }}>✕ Close</button>
                            </div>
                            <iframe title="Invoice" sandbox="" srcDoc={`<!doctype html><meta name=viewport content="width=device-width"><body style="margin:0;padding:16px;background:#fff">${invoiceView.html}</body>`}
                              style={{ flex: 1, border: 0, background: "#fff", width: "100%", maxWidth: 700, margin: "10px auto" }} />
                          </div>
                        )}
                        {job.stripeInvoiceId && job.invoiceStatus !== "voided" && (
                          <>
                            {!(job.paymentStatus === "paid" || (job.amountPaidCents || 0) > 0) && <button className="btn-secondary" disabled={billingBusy} onClick={() => sendInvoice(job)}>Send/Resend Invoice</button>}
                            {job.invoiceUrl && <a className="btn-secondary" href={job.invoiceUrl} target="_blank" rel="noreferrer">View Stripe Invoice</a>}
                            {job.paymentStatus !== "paid" && <button className="btn-secondary" disabled={billingBusy} onClick={() => voidInvoice(job)}>Void Invoice</button>}
                          </>
                        )}
                        {balance > 0 && (job.hasStripeSession || job.paymentLinkUrl || job.stripeInvoiceId) && (
                          <button className="btn-secondary" disabled={billingBusy} onClick={() => refreshPayment(job)}>Refresh Payment</button>
                        )}
                        {(job.paymentStatus !== "paid" || (due != null && due - paid > 0)) && (
                          <button className="btn-secondary" data-testid="record-payment" disabled={billingBusy} onClick={() => setPayForm(payForm ? null : { amount: due != null && due - paid > 0 ? ((due - paid) / 100).toFixed(2) : "", method: "check", paidOn: new Date().toISOString().slice(0, 10), reference: "", note: "", targetId: "", token: (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : String(Date.now()) })}>
                            {payForm ? "Cancel Record Payment" : "Record Payment"}
                          </button>
                        )}
                  </div>
                )}
                    {xcForm && (
                      <div data-testid="extra-charge-form" style={{ borderTop: "1px solid var(--light)", marginTop: "0.75rem", paddingTop: "0.6rem" }}>
                        <div className="form-grid">
                          <div className="field"><label>Type</label>
                            <select value={xcForm.kind} onChange={e => { const k = e.target.value; setXcForm(f => ({ ...f, kind: k, description: k === "skip_trace" ? "Skip trace" : (f.description === "Skip trace" ? "" : f.description) })); }}>
                              <option value="skip_trace">Skip trace</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div className="field"><label>Price (USD) *</label><input type="number" min="1" step="0.01" data-testid="extra-charge-amount" value={xcForm.amount} onChange={e => setXcForm(f => ({ ...f, amount: e.target.value }))} placeholder="e.g. 100.00" /></div>
                          <div className="field form-full"><label>Description (shown to the client) *</label><input value={xcForm.description} onChange={e => setXcForm(f => ({ ...f, description: e.target.value }))} /></div>
                        </div>
                        {xcForm.kind === "skip_trace" && (
                          <div>
                            <div style={{ fontSize: "0.75rem", color: "var(--gray)", margin: "0.2rem 0" }}>Name(s) to locate — optional here; the client is asked to fill in the details (first + last name required).</div>
                            {xcForm.people.map((p, i) => (
                              <div key={i} className="form-grid">
                                <div className="field"><label>First name</label><input value={p.firstName} onChange={e => setXcForm(f => ({ ...f, people: f.people.map((q, j) => j === i ? { ...q, firstName: e.target.value } : q) }))} /></div>
                                <div className="field"><label>Last name</label><input value={p.lastName} onChange={e => setXcForm(f => ({ ...f, people: f.people.map((q, j) => j === i ? { ...q, lastName: e.target.value } : q) }))} /></div>
                              </div>
                            ))}
                            <button type="button" className="btn-secondary" style={{ fontSize: "0.75rem" }} onClick={() => setXcForm(f => ({ ...f, people: [...f.people, { firstName: "", lastName: "" }] }))}>＋ Add another name</button>
                          </div>
                        )}
                        <div className="btn-row-compact" style={{ marginTop: "0.5rem" }}>
                          <button className="btn-primary" data-testid="take-extra-payment" disabled={xcBusy} onClick={() => sendExtraCharge(job, "take")}>{xcBusy ? "Working…" : "Take Payment"}</button>
                          <button className="btn-secondary" data-testid="send-extra-charge" disabled={xcBusy} onClick={() => sendExtraCharge(job, "email")}>Email Client</button>
                          <button className="btn-secondary" disabled={xcBusy} onClick={() => setXcForm(null)}>Cancel</button>
                        </div>
                      </div>
                    )}
                {/* Record a payment received OUTSIDE Stripe (cash/check/Zelle/other).
                    Never faked as Stripe activity — stored as a manual payment with
                    an immutable audit record. */}
                {payForm && (
                  <div data-testid="record-payment-form" style={{ marginTop: "0.75rem", border: "2px solid var(--light)", borderRadius: 6, padding: "0.75rem" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem" }}>
                      <div className="field"><label style={{ fontSize: "0.75rem" }}>Amount (USD) *</label><input type="number" min="1" step="0.01" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} /></div>
                      <div className="field"><label style={{ fontSize: "0.75rem" }}>Method *</label>
                        <select value={payForm.method} onChange={e => setPayForm(f => ({ ...f, method: e.target.value }))}>
                          <option value="cash">Cash</option><option value="check">Check</option><option value="zelle">Zelle</option><option value="other">Other</option>
                        </select>
                      </div>
                      <div className="field"><label style={{ fontSize: "0.75rem" }}>Payment date *</label><DatePick value={payForm.paidOn} onChange={v => setPayForm(f => ({ ...f, paidOn: v }))} /></div>
                      <div className="field"><label style={{ fontSize: "0.75rem" }}>Reference / check # (optional)</label><input value={payForm.reference} onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))} /></div>
                      <div className="field" style={{ gridColumn: "span 2" }}><label style={{ fontSize: "0.75rem" }}>Note (optional)</label><input value={payForm.note} onChange={e => setPayForm(f => ({ ...f, note: e.target.value }))} /></div>
                      {/* Optional bookkeeping attribution — billing stays on the
                          parent order; a combined payment leaves this blank. */}
                      {job.serviceTargets && job.serviceTargets.length > 1 && (
                        <div className="field"><label style={{ fontSize: "0.75rem" }}>Apply to service job (optional)</label>
                          <select value={payForm.targetId} onChange={e => setPayForm(f => ({ ...f, targetId: e.target.value }))}>
                            <option value="">Whole order (combined payment)</option>
                            {job.serviceTargets.map(t => <option key={t.id} value={t.id}>{t.subject_name || t.job_label}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                    <button className="btn-primary" disabled={billingBusy} onClick={() => recordPayment(job)} style={{ marginTop: "0.5rem" }}>Save Payment Record</button>
                  </div>
                )}
              </div>
                );
              })()}

              {/* ── Admin Actions — deliberately separated from routine controls ── */}
              <div style={{ marginTop: "1.25rem", borderTop: "2px solid var(--light)", paddingTop: "1rem" }}>
                <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--gray)", marginBottom: "0.5rem" }}>
                  Admin Actions{job.isTest ? " · TEST ORDER" : ""}
                </div>
                {/* Payment status and totals live in the Billing card — not repeated here. */}
                {(job.amountRefunded || job.addon) && (
                  <div className="text-sm" style={{ marginBottom: "0.6rem" }}>
                    {job.amountRefunded ? <>Refunded: <strong>${job.amountRefunded}</strong></> : null}
                    {job.amountRefunded && job.addon ? <> &nbsp;|&nbsp; </> : null}
                    {job.addon && <>Add-On: <strong>{ADDONS[job.addon]?.label || job.addon} (+${ADDONS[job.addon]?.price ?? "?"})</strong></>}
                  </div>
                )}
                {job.needsStripeLookup && (
                  <div className="alert alert-warn" style={{ marginBottom: "0.6rem" }}>
                    This order predates stored Stripe references — a refund requires a manual Stripe lookup.
                  </div>
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                  {(job.paymentStatus === "paid" || job.paymentStatus === "partially_refunded") && ((job.price || 0) - (job.amountRefunded || 0) > 0) && (
                    refundsAllowed
                      ? <button className="btn-secondary" disabled={actionBusy} onClick={() => setRefundJob(job)}>Refund Payment</button>
                      : <button className="btn-secondary" disabled title="Refunds are currently disabled by the server (ENABLE_ADMIN_REFUNDS)" style={{ opacity: 0.6, cursor: "not-allowed" }}>Refunds Unavailable</button>
                  )}
                  {["pending_review", "accepted", "awaiting_client"].includes(job.status) && (
                    <button className="btn-secondary" disabled={actionBusy} onClick={async () => {
                      const reason = await askText("Reason for cancelling this order? (required)");
                      if (reason && reason.trim() && await askConfirm("Cancel this order?\n\nThis does NOT issue a refund and does NOT delete the order.", "Cancel Order")) {
                        adminAction("cancel", { reason: reason.trim() });
                      }
                    }}>Cancel Order</button>
                  )}
                  {job.paymentStatus === "paid" && !["completed", "cancelled", "refund_issued"].includes(job.status) && upgradeTargets(job.tier).map((t) => {
                    const q = tierUpgradeQuote(job.tier, t, job.addon);
                    return q.ok ? (
                      <button key={t} className="btn-secondary" data-testid={`upgrade-${t}`} disabled={actionBusy} onClick={() => sendTierUpgrade(job, t, q.diff)}>
                        Upgrade to {PRICING[t].label} (+${q.diff})
                      </button>
                    ) : null;
                  })}
                  {!job.archived
                    ? <button className="btn-secondary" disabled={actionBusy} onClick={() => adminAction("archive")}>Archive</button>
                    : <button className="btn-secondary" disabled={actionBusy} onClick={() => adminAction("restore")}>Restore</button>}
                  {job.isTest && (
                    <button className="btn-secondary" disabled={actionBusy} style={{ background: "#FDECEC", color: "var(--red)", fontWeight: 700 }}
                      onClick={async () => { if (await askConfirm("PERMANENTLY delete this TEST order and its documents?\n\nThis cannot be undone.", "Delete")) adminAction("delete_test"); }}>
                      Delete Test Order
                    </button>
                  )}
                </div>
                <div className="text-sm" style={{ color: "var(--gray)", marginTop: "0.5rem" }}>
                  Real orders are retained for 3 years and cannot be permanently deleted. Cancellation and refund are separate actions.
                </div>
              </div>
              {refundJob && <AdminRefundModal job={refundJob} busy={actionBusy} onClose={() => setRefundJob(null)} onSubmit={submitRefund} />}


              {/* Proof completion notification trigger */}
              {/* Phase 1: persisted attempt history from the server (read-only,
                  additive). Shows whenever structured attempts exist for the job;
                  a badge indicates whether saving new attempts is enabled. */}
              {lightbox && (
                <div className="photo-modal" onClick={() => setLightbox(null)}>
                  <img src={lightbox} alt="Evidence" />
                </div>
              )}
              {/* After a SERVED entry the next step is the official Proof (e.g.
                  SC-104) in Service Jobs — generated, reviewed and signed there,
                  where the signature, signer and date are SAVED and shown on every
                  reopen. The old in-memory "E-Sign" box signed nothing durable and
                  produced the non-filing "Completed Service Record"; it is gone. */}
              {showProof && !(job.serviceTargets || []).length && (
                <div className="alert" data-testid="proof-next-step" style={{ marginTop: "1rem", fontSize: "0.85rem" }}>
                  {autoProofMsg || <>Service saved. Next: prepare and sign the official Proof of Service in <strong>Service Jobs</strong>.</>}
                  {" "}<button type="button" className="btn-link" style={{ background: "none", border: "none", padding: 0, color: "var(--blue)", textDecoration: "underline", cursor: "pointer", fontSize: "0.85rem" }}
                    onClick={() => { const el = document.querySelector('[data-testid="service-jobs-panel"]'); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}>
                    Go to Proof signing
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="admin-empty-detail" style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "var(--gray)", fontSize: "0.9rem", padding: "3rem" }}>← Select a job to manage</div>
          )}
        </div>
      </>}
    </div>
  );
}

// ── MAIN APP ───────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("portal");
  const [portalTab, setPortalTab] = useState("order");
  const [orderPrefill, setOrderPrefill] = useState(null);
  const [availability, setAvailability] = useState("open"); const embedRef = useRef(null); const isCheckerEmbed = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("embed") === "checker"; useEffect(() => { if (!isCheckerEmbed) return; function sendCheckerHeight() { const h = embedRef.current ? embedRef.current.scrollHeight : document.documentElement.scrollHeight; if (window.parent) { window.parent.postMessage({ type: "corona-checker-resize", height: h }, "https://coronaprocessserving.com"); } } sendCheckerHeight(); const ro = new ResizeObserver(() => sendCheckerHeight()); if (embedRef.current) ro.observe(embedRef.current); window.addEventListener("resize", sendCheckerHeight); return () => { ro.disconnect(); window.removeEventListener("resize", sendCheckerHeight); }; }, [isCheckerEmbed]);

    useEffect(() => {
        // A Google sign-in returns to the site ROOT as ?code=…, and the portal
        // opens on New Order by default — so a completed sign-in used to land on
        // the order form and the account never rendered. An OAuth return belongs
        // on the Client Portal. The code itself is exchanged by the auth layer
        // (lib/authCallback, via useAuth), not here.
        if (readAuthCallback(window.location.search).present) {
            setPage("portal");
            setPortalTab("track");
        }
        else if (window.location.search.includes("server=true")) {
            // Another process server's login (jobs assigned to them only).
            setPage("server");
        }
        else if (window.location.search.includes("admin=true") || window.location.search.includes("admin_reset")) {
            // admin_reset=<token> is the emailed password-reset link; it must land on
            // the Admin page so the reset-password screen (inside AdminPanel) renders.
            setPage("admin");
        }
            else if (window.location.search.includes("payment=success")) {
                      setPage("portal");
                      setPortalTab("order");
            }
                  // prefill=1 comes from the embedded calculator; a bare ?zip=
                  // comes from the website's ZIP box. Both open the order form
                  // with the ZIP already filled in.
                  else if (window.location.search.includes("prefill=1") || /^\d{5}$/.test(new URLSearchParams(window.location.search).get("zip") || "")) {
                                  const p = new URLSearchParams(window.location.search);
                                  setOrderPrefill({ found: true, zip: p.get("zip") || "", tier: p.get("tier") || "standard", city: p.get("city") || "", price: p.get("price") ? Number(p.get("price")) : null, selectedDocType: p.get("docType") || "", exParte: p.get("exParte") === "1" });
                                  setPage("portal");
                                  setPortalTab("order");
                  }
    }, []);

  function handleOrderClick(zipInfo) {
    setOrderPrefill(zipInfo);
    setPage("portal");
    setPortalTab("order");
    setTimeout(() => document.getElementById("portal")?.scrollIntoView({ behavior: "smooth" }), 100);
  }

    function handleEmbedOrderClick(zipInfo) {
          const target = new URL("https://portal.coronaprocessserving.com/");
          target.searchParams.set("prefill", "1");
          target.searchParams.set("zip", zipInfo.zip || "");
          target.searchParams.set("tier", zipInfo.tier || "");
          target.searchParams.set("docType", zipInfo.selectedDocType || "");
          target.searchParams.set("city", zipInfo.city || "");
          target.searchParams.set("price", zipInfo.price != null ? String(zipInfo.price) : "");
          target.searchParams.set("exParte", zipInfo.exParte ? "1" : "");
          const dest = target.toString();
          if (window.top) { window.top.location.href = dest; } else { window.location.href = dest; }
    }

    const agreementToken = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("agreement") : null;
    const uploadToken = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("upload") : null;
    if (uploadToken) {
          return (<><style>{css}</style><UploadPage token={uploadToken} /></>);
    }
    const chargeToken = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("charge") : null;
    if (chargeToken) {
          return (<><style>{css}</style><ChargePage token={chargeToken} /></>);
    }
    if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("charge_paid")) {
          return (<><style>{css}</style><div data-testid="charge-thanks" style={{ maxWidth: 720, margin: "2rem auto", padding: "0 1rem" }}><h2 style={{ fontFamily: "Montserrat, sans-serif", color: "var(--navy)" }}>Payment received</h2><p>Thank you — we'll get started right away.</p></div></>);
    }
    if (agreementToken) {
          return (<><style>{css}</style><AgreementPage token={agreementToken} /></>);
    }
    if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("embed") === "checker") {
          return (
                  <>
                          <style>{css}</style>
                    <style>{`html, body { background: #0B1628 !important; } .zip-checker { max-width: 480px !important; }`}</style>
                          <div ref={embedRef} style={{ maxWidth: 480, margin: "0 auto", padding: "0 1rem" }}>
                                    <ZipChecker onOrderClick={handleEmbedOrderClick} availability={availability} />
                          </div>
                  </>
                );
    }

  return (
    <>
      <style>{css}</style>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        // Type + @id/url tie this portal to the SAME business entity as the
        // canonical marketing site (coronaprocessserving.com), whose verified
        // schema is LegalService. This is entity-consistency only — the portal
        // itself stays noindex; the canonical site remains the indexed source.
        "@type": ["LegalService", "LocalBusiness"],
        "@id": "https://coronaprocessserving.com/#business",
        "name": "Corona Process Serving",
        "url": "https://coronaprocessserving.com",
        "description": "Registered & Bonded California process server serving Corona, Norco, Eastvale, Riverside, Jurupa Valley, and surrounding Inland Empire communities.",
        "address": { "@type": "PostalAddress", "addressLocality": "Corona", "addressRegion": "CA", "postalCode": "92883", "addressCountry": "US" },
        "areaServed": ["Corona, CA","Norco, CA","Eastvale, CA","Riverside, CA","Jurupa Valley, CA","Rancho Cucamonga, CA","Ontario, CA","Moreno Valley, CA","Lake Elsinore, CA","Anaheim Hills, CA","Yorba Linda, CA","Placentia, CA","Orange, CA"],
        "priceRange": "$100-$175",
        "serviceType": "Process Serving"
      })}} />
      <div className="app">
        <nav>
          <a href="https://coronaprocessserving.com" className="nav-logo" style={{ textDecoration: "none", color: "var(--white)" }}>⚖️ <span>CORONA</span> PROCESS SERVING</a>
          <div className="nav-links"><a href="https://coronaprocessserving.com" className="nav-btn" style={{ textDecoration: "none" }}>Home</a>
                        <button className={`nav-btn${page === "portal" ? " active" : ""}`} onClick={() => setPage("portal")}>Client Portal</button>
                      </div>
        </nav>

        {page === "portal" && (
          <div id="portal" className="section" style={{ maxWidth: 900, width: "100%" }}>
            <h2 className="section-title">Client Portal</h2>
            <p className="section-sub">Place a new order or track an existing one</p>
            <div className="portal">
              <div className="portal-tabs">
                <button className={`portal-tab${portalTab === "order" ? " active" : ""}`} onClick={() => setPortalTab("order")}>New Order</button>
                <button className={`portal-tab${portalTab === "track" ? " active" : ""}`} onClick={() => setPortalTab("track")}>Client Login</button>
              </div>
              <div className="portal-body">
                {portalTab === "order" && <OrderForm key={JSON.stringify(orderPrefill)} prefillZip={orderPrefill} />}
                {portalTab === "track" && <ClientAccount onNewOrder={() => setPortalTab("order")} />}
              </div>
            </div>
          </div>
        )}

        <AutoUpdate />
        {page === "server" && <ServerApp tools={{ getCurrentGPS, stampPhoto, photoStampLines, scriptSignaturePng, openFileTab, printFile }} />}
        {page === "admin" && <AdminAppSetup />}
        {page === "admin" && (
          <div className="section admin-section" style={{ maxWidth: "none", padding: "2rem 1.25rem" }}>
            <h2 className="section-title">Admin Dashboard</h2>
            <p className="section-sub">Manage jobs, update statuses, add service notes, and generate proofs</p>
            <div className="portal">
              <div className="portal-body">
                <AdminPanel availability={availability} setAvailability={setAvailability} />
              </div>
            </div>
          </div>
        )}

        <footer>
          <strong>Corona Process Serving</strong> · Corona, CA 92883<br />
          Registered & Bonded California Process Server · Registration No. PS-002456<br />
          Serving Riverside County, San Bernardino County & Orange County
          <div style={{ marginTop: "0.75rem", fontSize: "0.75rem", color: "rgba(255,255,255,0.35)", lineHeight: 2 }}>
            Areas Served: Corona · Norco · Eastvale · Riverside · Jurupa Valley · Rancho Cucamonga · Ontario · Moreno Valley · Lake Elsinore · Anaheim Hills · Yorba Linda · Placentia · Orange
          </div>
          <span style={{ fontSize: "0.75rem", marginTop: "0.5rem", display: "block", color: "rgba(255,255,255,0.35)" }}>
            © 2026 · All service performed in compliance with California Code of Civil Procedure §415
          </span>
        </footer>
      </div>
    </>
  );
}
